import * as THREE from 'three'
import { useRef, useLayoutEffect, useState, useMemo, useCallback, useEffect } from 'react'
import { Canvas, useThree, useLoader } from '@react-three/fiber'
import { Center, OrbitControls, Environment, Lightformer, useGLTF, useTexture } from '@react-three/drei'
import { EffectComposer } from 'three/examples/jsm/postprocessing/EffectComposer.js'
import { mergeVertices } from 'three/examples/jsm/utils/BufferGeometryUtils.js'
import { OutputPass } from 'three/examples/jsm/postprocessing/OutputPass.js'
import { SMAAPass } from 'three/examples/jsm/postprocessing/SMAAPass.js'
import { N8AOPass } from 'n8ao'
import React, { startTransition } from 'react'

// Error boundary to catch DOM reconciliation errors during window resize
class ResizeErrorBoundary extends React.Component {
  constructor(props) {
    super(props)
    this.state = { hasError: false, key: 0 }
    this._recovering = false
  }
  static getDerivedStateFromError() {
    return { hasError: true }
  }
  componentDidCatch(err) {
    if (this._recovering) return
    const isDOMError =
      err &&
      err.message &&
      (err.message.includes('insertBefore') || err.message.includes('removeChild') || err.message.includes('NotFoundError') || err.message.includes('The node'))
    if (isDOMError) {
      this._recovering = true
      // Recover immediately — don't show blank screen
      this.setState(
        (s) => ({ hasError: false, key: s.key + 1 }),
        () => {
          this._recovering = false
        }
      )
    }
  }
  render() {
    if (this.state.hasError) return null
    return React.createElement(React.Fragment, { key: this.state.key }, this.props.children)
  }
}

import './styles.css' // This will import everything
/* Base styles - fonts, resets, shared components, desktop */

/* Mobile device-specific overrides */

import { useSpring, animated } from '@react-spring/web'
import { useDrag } from '@use-gesture/react'
import TWEEN from '@tweenjs/tween.js'
import { useFrame } from '@react-three/fiber'
import { BrowserRouter as Router, Route, Routes, useParams } from 'react-router-dom'
import ReactDOM from 'react-dom'
import AppRouter from './AppRouter'
import { useID } from './IDContext' // Adjust the path as necessary
import { IDProvider } from './IDContext'
import { createRoot } from 'react-dom/client'
import { createPortal } from 'react-dom'
// Loaded after the mic shows (not needed for the first paint): the desktop info/share popup and the color picker
const loadProductModal = () => import('./ProductModal')
const ProductModal = React.lazy(() => loadProductModal().then((m) => ({ default: m.ProductModal })))
const loadColorPicker = () => import('react-color')
const ChromePickerLazy = React.lazy(() => loadColorPicker().then((m) => ({ default: m.ChromePicker })))
const ChromePicker = (props) => (
  <React.Suspense fallback={<div style={{ width: props.width || 225, height: 240 }} />}>
    <ChromePickerLazy {...props} />
  </React.Suspense>
)
import MobileFooterTabs from './MobileFooterTabs'
import { uploadToCloudflare } from './cfUpload'
import { ensureFreshLogin } from './authRenew'
import BuildsDrawer from './BuildsDrawer'

// MOTION (default; ?motion=classic goes back, remembered): the look crossfades when you change a
// color, fabric, screen or mic type instead of snapping; the pop filter and camera moves use a
// spring-shaped curve (quick start, soft settle, no bounce) like the footer.
const MOTION_NEW = (() => {
  try {
    const q = new URLSearchParams(window.location.search).get('motion')
    if (q === 'new' || q === 'classic') localStorage.setItem('ohma_motion', q)
    return localStorage.getItem('ohma_motion') !== 'classic' // new is the default
  } catch (e) {
    return true
  }
})()
// critically damped spring from rest, squeezed into the tween's duration (0 -> 1, ~90% there halfway)
const SPRING_EASE = (k) => (k >= 1 ? 1 : 1 - (1 + 8 * k) * Math.exp(-8 * k))
const motionEase = (classic) => (MOTION_NEW ? SPRING_EASE : classic)
import { StudioEnvironment, MetalEnvironment, softboxLights, LightMarker, CameraAngleChecker, BackFill } from './sceneParts'
import { useHistory, useMediaQuery } from './historyHooks'
import { computeBoxFromNames, recenterControlsToBox, frameAndRenderThumbnail, tuneAndCaptureThumbnail } from './captureHelpers'
import { initAnalytics, track, colorLabel } from './analytics'

// Import Materials system for texture-based Cerakote
import { useCerakoteMaterials, METAL_ENV } from './materials'

const container = document.getElementById('root')
const root = createRoot(container)

// Suppress DOM reconciliation errors caused by resize — error boundary handles recovery
window.addEventListener('error', function (e) {
  if (
    e.message &&
    (e.message.includes('insertBefore') ||
      e.message.includes('removeChild') ||
      e.message.includes('NotFoundError') ||
      e.message.includes('The node before which'))
  ) {
    e.stopPropagation()
    e.preventDefault()
    return true
  }
})

import { Raycaster, Vector2 } from 'three'
import { motion, useAnimation, useMotionValue, useTransform, animate } from 'framer-motion'

// Colors and SKU code to update options
import { colorOptions, screenColorOptions, fabricColorOptions, bottomfabricColorOptions, options } from './colors'

const premiumColors = {
  Brass: 499,
  '#d5b173': 499,
  '#custom': 199
}

const premiumScreenColors = {
  '#custom': 199
}

const premiumFabricColor = {
  '#PRIDE': 49 // Pride logo fill — June only, +$49, partial proceeds to The Trevor Project
}

const premiumLogo = {
  'Custom Logo': 199,
  'Motif Bottom Screen': 25,
  'Scales Bottom Screen': 25,
  'Stripes Bottom Screen': 25,
  'Windows Bottom Screen': 25,
  'Holes Bottom Screen': 25,
  'Add Pattern Screen': 25
}

// Code for the loading
import LoadingIndicator from './LoadingIndicator'
import { builderBus } from './builderBus'
import { Suzi, SAATI_TOP_OPACITY, SCREEN_TYPES, AO_SETTINGS, modelUrl, fetchScreen, checkBaked, _bakedOk } from './micModel'
import { SCREEN_TYPE_OPTIONS } from './screenOptions'
import { loadTimingProps } from './loadTiming'
import { SALE, SALE_PREVIEW, SaleHeaderTag } from './sale'
import { SHOW_LIGHT_DEBUG, DEV_TOOLS, debugOverrides } from './debugParams'

root.render(
  <ResizeErrorBoundary>
    <Router>
      <App />
    </Router>
  </ResizeErrorBoundary>
)

// filtered palette (no Brass)
const accessoryPalette = Object.entries(colorOptions).filter(([value]) => value !== 'Brass')




function AnimationUpdater() {
  useFrame(() => TWEEN.update())
  return null // This component does not render anything
}


const AO_QUALITY = ['Performance', 'Low', 'Medium', 'High', 'Ultra']
// LENS - the product photos are shot on a 105mm macro (full frame), about a 13 degree vertical view.
// All camera framing code below is written for the original 35 degree camera, so instead of changing
// every distance, the live render pulls the camera straight back from the orbit target and narrows
// the field of view by the same factor. The mic stays the same size on screen; only perspective changes.
const BASE_FOV = 35

function prefetchScreen(type) {
  if (_bakedOk === false) return
  checkBaked().then(() => _bakedOk && fetchScreen(type))
}
// Desktop canvases are large, and at full retina density they cost several times more to draw than
// they gain in sharpness. Phones keep full density.
const MAX_DPR = () => (typeof window !== 'undefined' && window.innerWidth >= 1024 ? 1.5 : 2)
let LENS_FOV = 13
function lensScale() {
  return Math.tan(THREE.MathUtils.degToRad(BASE_FOV / 2)) / Math.tan(THREE.MathUtils.degToRad(LENS_FOV / 2))
}





function debounce(func, delay) {
  let timeoutId
  return function (...args) {
    clearTimeout(timeoutId)
    timeoutId = setTimeout(() => {
      func.apply(this, args)
    }, delay)
  }
}


// Outside the function (module scope)
let lastDotPressTimestamp = 0
let lastDotPressClass = null

// Light one view dot (front "dots", side "dots-side" or back "dots-back") without moving the camera; null clears
// them all. The desktop and phone copies stay in sync. The active color comes from the stylesheet (black).
function highlightDot(cls) {
  document.querySelectorAll('.control-dots div').forEach((d) => {
    d.classList.remove('active')
    d.style.backgroundColor = ''
  })
  if (cls) document.querySelectorAll(`.control-dots .${cls}`).forEach((el) => el.classList.add('active'))
}

// A dot tap: light it and move the camera. Every tap counts (a move already under way turns toward the
// new view from wherever it is); only a duplicate of the same tap within a fraction of a second is ignored.
function setActiveDot(dotOrClass, cameraFunction) {
  const cls = typeof dotOrClass === 'string' ? dotOrClass : dotOrClass instanceof Element ? ['dots', 'dots-side', 'dots-back'].find((c) => dotOrClass.classList.contains(c)) : null
  const now = Date.now()
  if (cls === lastDotPressClass && now - lastDotPressTimestamp < 250) return
  lastDotPressTimestamp = now
  lastDotPressClass = cls
  highlightDot(cls)
  if (cameraFunction) cameraFunction()
}

if (debugOverrides('ld').lensFov !== undefined) LENS_FOV = debugOverrides('ld').lensFov
(function captureUTMs() {
  try {
    const stored = JSON.parse(sessionStorage.getItem('ohma_utm') || 'null')
    // Only capture once per session (first touch attribution)
    if (stored) return
    const p = new URLSearchParams(window.location.search)
    const utm = {
      source: p.get('utm_source') || null,
      medium: p.get('utm_medium') || null,
      campaign: p.get('utm_campaign') || null,
      content: p.get('utm_content') || null,
      referrer: document.referrer || null
    }
    // Only save if we have at least one UTM param or a referrer
    if (utm.source || utm.medium || utm.campaign || utm.referrer) {
      sessionStorage.setItem('ohma_utm', JSON.stringify(utm))
    }
  } catch (e) {}
})()

function getStoredUTM() {
  try {
    return JSON.parse(sessionStorage.getItem('ohma_utm') || 'null') || {}
  } catch (e) {
    return {}
  }
}

// (Removed: leftover Shopify admin-app setup. It posted to Shopify's OAuth endpoint when a ?code= was in the
// link and redirected to the Shopify admin on ?host=, and pulled the App Bridge library into every page.)
let response, data, accessToken 
// the cart line's picture (also used by the abandoned cart emails and ShipStation)
const CART_PICTURE = { size: 1080, mimeType: 'image/jpeg' }

export default function App() {
  // put this above your `return` inside the same React function component

  // Add this with your other state declarations
  const [currency, setCurrency] = useState({ code: 'USD', symbol: '$' })

  // Track if component is mounted to prevent state updates after unmount
  const isMountedRef = useRef(true)
  useEffect(() => {
    isMountedRef.current = true
    return () => {
      isMountedRef.current = false
    }
  }, [])

  // Portrait (mobile) detection used across the UI (including the carousel dots)
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window === 'undefined') return true
    return window.innerHeight >= window.innerWidth
  })

  const [viewportDimensions, setViewportDimensions] = useState(() => {
    if (typeof window === 'undefined') return { width: 0, height: 0 }
    return { width: window.innerWidth, height: window.innerHeight }
  })

  useEffect(() => {
    if (typeof window === 'undefined') return
    let timer = null
    const update = () => {
      clearTimeout(timer)
      timer = setTimeout(() => {
        startTransition(() => {
          setIsMobile(window.innerHeight >= window.innerWidth)
          setViewportDimensions({ width: window.innerWidth, height: window.innerHeight })
        })
      }, 300)
    }
    update()
    window.addEventListener('resize', update)
    window.addEventListener('orientationchange', update)
    return () => {
      clearTimeout(timer)
      window.removeEventListener('resize', update)
      window.removeEventListener('orientationchange', update)
    }
  }, [])

  // Add this useEffect to load currency on mount
  useEffect(() => {
    if (window.SHOPIFY_CURRENCY) {
      setCurrency(window.SHOPIFY_CURRENCY)
    }
  }, [])

  const [loadingMessage, setLoadingMessage] = React.useState(null)
  const overlayLoadingMessage = loadingMessage

  // near the top of App
  const [modalOpen, setModalOpen] = React.useState(false)
  const [orbitControlsEnabled, setOrbitControlsEnabled] = React.useState(true)
  const modalOpenRef = React.useRef(false)
  const latestShareBlobRef = React.useRef(null)
  const latestShareBlobAtRef = React.useRef(0) // when latestShareBlobRef was last captured
  // Keep ref in sync so frameObject effect can read it without being in dep array
  React.useEffect(() => {
    modalOpenRef.current = modalOpen
  }, [modalOpen])
  const [modalInitialTab, setModalInitialTab] = React.useState('overview')
  const [modalInitialColor, setModalInitialColor] = React.useState(null) // Color to pre-select in Color Guide
  const [mobileFooterTab, setMobileFooterTab] = React.useState('overview')
  const mobileFooterTabRef = React.useRef('overview')
  React.useEffect(() => {
    mobileFooterTabRef.current = mobileFooterTab
  }, [mobileFooterTab])
  const [shareScreenshotUrl, setShareScreenshotUrl] = React.useState(null) // Screenshot for share modal
  const [appShareUrl, setAppShareUrl] = React.useState('') // Share link for both mobile and desktop
  const [shareTabActive, setShareTabActive] = React.useState(false) // Hide canvas immediately when share opens

  // Simple function to open footer with a specific tab
  const openFooterWithTab = (tabName, initialColor = null) => {
    // 1. Set the tab state
    setMobileFooterTab(tabName)
    setModalInitialColor(initialColor)

    // Screenshot is triggered by MobileFooterTabs useEffect watching activeTab,
    // which calls regenerateShareScreenshot (the guarded single-entry path).
    // Do NOT also trigger it here — that causes a race between two captures.

    // Stamp the browser URL immediately when opening share tab,
    // same as how the desktop modal path works
    if (tabName === 'share') {
      setShareTabActive(true)
      setAppShareUrl('') // reset so UI shows loading while generating
      shareProduct()
        .then((link) => setAppShareUrl(link))
        .catch(() => {})
    } else {
      setShareTabActive(false)
      setAppShareUrl('') // clear when navigating away from share
    }

    // 2. Open the footer (animate to min position on mobile)
    if (isMobile && typeof sheetPercent !== 'undefined' && Array.isArray(snapPercents) && snapPercents.length > 0) {
      const target = snapPercents[0] // min position (footer up)
      try {
        animate(sheetPercent, target, footerSpring({ type: 'spring', stiffness: 400, damping: 60 }))
        setLivePercent(target)
      } catch (err) {
        try {
          sheetPercent.set(target)
        } catch (err2) {}
      }
    }
  }

  // helper to open the modal to a specific tab
  const openModal =
    (tab = 'overview') =>
    (e) => {
      if (e && e.preventDefault) e.preventDefault()
      setModalInitialTab(tab)
      setModalOpen(true)
    }

  // Global function to open product modal to a specific tab
  const openProductModalTab = async (tab, initialColor = null) => {
    setModalInitialTab(tab)
    setModalInitialColor(initialColor)
    setModalOpen(true)
    // Screenshot is triggered by ProductModal's useEffect([activeTab]) → onRegenerateScreenshot
    // which calls regenerateShareScreenshot — no parallel capture needed here
  }

  // toggle: set to false to hide pop-filter + shock-mount
  const SHOW_ACCESSORIES = false

  const sceneRef = useRef()
  // Set to true during screenshot renders so useFrame skips visibility overrides
  // that would fight with the mesh state we set for the screenshot.
  const isCapturingRef = useRef(false)

  // When switching from mobile to desktop, the mic-type-tab hide logic (useFrame)
  // may have left meshes invisible. Desktop never hides them, so restore on transition.
  useEffect(() => {
    if (!isMobile && sceneRef.current) {
      const scene = sceneRef.current
      const allScreenTypes = ['condenser', 'ribbon', 'motif', 'stripes', 'scales', 'windows', 'holes']
      const saati = scene.getObjectByName('saati_top')
      const surround = scene.getObjectByName('surround_top')
      if (saati) {
        saati.visible = true
        if (saati.material) saati.material.opacity = SAATI_TOP_OPACITY
      }
      if (surround) surround.visible = true
      allScreenTypes.forEach((t) => {
        const m = scene.getObjectByName(`${t}_top`)
        if (m) m.visible = true
        if (t === 'motif') {
          const m1 = scene.getObjectByName('Motif_to_Blender_v1003')
          const m2 = scene.getObjectByName('Motif_to_Blender_v1003_1')
          if (m1) m1.visible = true
          if (m2) m2.visible = true
        }
      })
    }
  }, [isMobile])

  const cameraRef = useRef()
  const orbitControlsRef = useRef()
  const initialBoxRef = useRef(null)
  const baseDistanceRef = useRef(null)
  const initialMappingRef = useRef(null)
  const userZoomOffsetRef = useRef(0) // user scroll/pinch offset from computed distance
  const userGestureRef = useRef(false) // true while a finger/mouse/wheel gesture on the mic is in progress

  const containerRef = useRef(null) // DOM node that wraps the canvas
  const rendererRef = useRef(null) // store renderer if you create it manually
  const r3fAnimLoopRef = useRef(null) // R3F's own animation loop callback, saved so we can pause/resume without losing it

  const groupRef = useRef()
  const [forceUpdate, setForceUpdate] = useState(0)
  const [isViewingBack, setIsViewingBack] = useState(false) // Track if camera is facing back of mic

  /* ---------- reusable frameObject helper (module or inside component) ---------- */
  const frameObject = useCallback((object3d, padding = 1.2) => {
    if (!cameraRef.current || !object3d) return
    const camera = cameraRef.current
    const box = new THREE.Box3().setFromObject(object3d)
    const size = new THREE.Vector3()
    box.getSize(size)
    const maxDim = Math.max(size.x, size.y, size.z)
    if (maxDim === 0) return

    const fov = (camera.fov * Math.PI) / 180
    const distance = (maxDim * padding) / (2 * Math.tan(fov / 2))
    const center = box.getCenter(new THREE.Vector3())

    const dir = new THREE.Vector3(0, 0, 1).applyQuaternion(camera.quaternion)
    camera.position.copy(center.clone().add(dir.multiplyScalar(distance)))
    camera.lookAt(center)
    camera.updateProjectionMatrix()

    // Always sync OrbitControls target so it orbits the same point
    if (orbitControlsRef.current) {
      orbitControlsRef.current.target.copy(center)
      orbitControlsRef.current.update()
    }

    baseDistanceRef.current = distance
    initialBoxRef.current = box
  }, [])

  /* ---------- resize handler: only non-camera side effects ---------- */
  const handleResize = useCallback(() => {
    if (window.innerWidth < 768) setShowBuildsDrawer(false)
  }, [])

  /* ---------- install resize listener and vh helper ---------- */
  useEffect(() => {
    function setVhUnit() {
      document.documentElement.style.setProperty('--vh', `${window.innerHeight * 0.01}px`)
    }
    setVhUnit()
    window.addEventListener('resize', setVhUnit, { passive: true })
    window.addEventListener('resize', handleResize, { passive: true })

    return () => {
      window.removeEventListener('resize', handleResize)
      window.removeEventListener('resize', setVhUnit)
    }
  }, [handleResize])

  const footerBaselineRef = useRef(null)
  const baseTargetRef = useRef(new THREE.Vector3()) // store the canonical target if you need it

  // after you set camera position and orbitControls.target during initial placement
  if (cameraRef.current && orbitControlsRef.current) {
    // ensure camera is looking exactly at the target
    cameraRef.current.lookAt(orbitControlsRef.current.target)
    cameraRef.current.updateProjectionMatrix()

    // store baseline distance & target (used by footer logic)
    baseDistanceRef.current = cameraRef.current.position.distanceTo(orbitControlsRef.current.target)
    baseTargetRef.current.copy(orbitControlsRef.current.target)
  }

  const [showConfirmModal, setShowConfirmModal] = useState(false)
  const [pendingCartDesign, setPendingCartDesign] = React.useState(null) // design to add to cart after confirm
  const [pendingCartScreenshot, setPendingCartScreenshot] = React.useState(null) // screenshot for confirm modal
  const pendingUpload = useRef(null) // { fileid, fileKey, publicUrl, presignedUrl }
  const [quantity, setQuantity] = useState(1)
  const [variants, setVariants] = useState([])
  // Black Friday (src/sale.js): the cut Shopify's variants carry (compare-at minus price, in the shopper's currency, cents)
  const saleCutCents = (() => {
    const v = variants.find((x) => x && x.compare_at_price && x.compare_at_price > x.price)
    return v ? v.compare_at_price - v.price : 0
  })()
  const SALE_ON = SALE_PREVIEW || saleCutCents > 0
  const saleOnRef = useRef(SALE_ON)
  saleOnRef.current = SALE_ON
  // a variant's regular price (cents): the compare-at price during the sale
  const fullCents = (v) => (v && v.compare_at_price && v.compare_at_price > v.price ? v.compare_at_price : v ? v.price : 0)
  const [lightCopied, setLightCopied] = useState(false) // Moved from inside JSX
  const [matCopied, setMatCopied] = useState(false) // Moved from inside JSX

  // THE build picture (cart line, saved build, shared link): one square shot of the mic from the front-left,
  // on the Ohma blue. Every place that needs a picture calls this, so they all look the same.
  // Returns { blob, cancelled } or null. The capturing flag is always cleared, even if the capture fails.
  // Saved builds keep the small 360px PNG (guest builds live in the shopper's browser storage, which is small).
  // The cart picture passes CART_PICTURE: 1080px JPEG, sharp enough for the abandoned cart emails and ShipStation.
  const captureBuildPicture = async ({ size = 360, mimeType = 'image/png' } = {}) => {
    const scene = sceneRef.current
    if (!scene || !rendererRef.current) return null
    // the top screen and its fabric can be hidden or fading while the footer moves: show them as they rest
    const saatiMesh = scene.getObjectByName('saati_top')
    const topScreenMesh = scene.getObjectByName(`${screenType}_top`)
    if (saatiMesh?.material) {
      saatiMesh.visible = true
      saatiMesh.material.transparent = true
      saatiMesh.material.opacity = SAATI_TOP_OPACITY
    }
    if (topScreenMesh?.material) {
      topScreenMesh.visible = true
      topScreenMesh.material.transparent = true
      topScreenMesh.material.opacity = 1
    }
    if (isCapturingRef) isCapturingRef.current = true
    try {
      return await tuneAndCaptureThumbnail(scene, {
        initialFrameFill: popFilterEnabled ? 80 : 95,
        initialVBias: 0.18,
        initialAnchorK: 0.52,
        initialAzimuthDeg: -35,
        thumbSize: size,
        mimeType,
        quality: 0.92,
        bgColor: 0x91c3de,
        renderer: rendererRef.current // reuse the live renderer, no second WebGL context
      })
    } catch (e) {
      return null
    } finally {
      if (isCapturingRef) isCapturingRef.current = false
    }
  }

  const increaseQty = () => setQuantity((q) => q + 1)
  const decreaseQty = () => setQuantity((q) => Math.max(1, q - 1))
  const openConfirm = () => {
    track('add_to_cart_clicked', analyticsBuildProps())
    setShowConfirmModal(true)
    // Always regenerate screenshot so it reflects the current mic state
    setShareScreenshotUrl(null)
    // While they read the Final Step, capture the cart preview image and upload it, so "Continue to
    // Checkout" doesn't have to wait for it. The small cart image is captured FIRST (a fraction of a
    // second), then the big share image for this screen; the other way round, a slow device could still
    // be rendering the share image when Continue is pressed, and the cart line would get no picture.
    // addToCart() awaits prep.ready and only falls back to capturing again if this one failed.
    const fileid = Date.now()
    const prep = { fileid, uploadUrl: null, imageUrl: null, ready: null }
    pendingUpload.current = prep
    const urlReady = fetch(`https://customizer-test-nodejs-4c6a5bfd6dbd.herokuapp.com/api/cf-images-upload-url`)
      .then((r) => r.json())
      .then((data) => {
        prep.uploadUrl = data.uploadUrl
        prep.imageUrl = data.imageUrl
      })
      .catch(() => {})
    const thumb = captureBuildPicture(CART_PICTURE)
    // one capture at a time on the shared renderer: the share image starts once the cart image is done
    thumb
      .then(() => generateShareScreenshot())
      .then((blob) => {
        if (blob) {
          const url = URL.createObjectURL(blob)
          setShareScreenshotUrl(url)
        }
      })
      .catch(() => {})
    prep.ready = (async () => {
      try {
        const result = await thumb
        if (pendingUpload.current !== prep && !prep.claimed) return ''
        if (!result?.blob || result?.cancelled) return ''
        await urlReady
        return await uploadToCloudflare(result.blob, `${fileid}_thumbnail.jpg`, { uploadUrl: prep.uploadUrl, imageUrl: prep.imageUrl })
      } catch (e) {
        return ''
      }
    })()
  }

  // — history state for all of our config vars in one place
  const initialConfig = {
    color: '#d7be88',
    screenType: 'motif',
    screenColor: '#d7be88',
    fabricColor: '#1a1a1a', // top fabric
    bottomFabricColor: '#1a1a1a', // bottom fabric - circle color (maps to #404040 on mesh)
    bottomScreenType: 'Logo',
    microphoneType: 'Condenser',
    popFilterEnabled: false,
    popFilterColor: '#1c1d1d',
    popFilterMaterial: 'plastic', // 'plastic' or 'metal'
    shockMountEnabled: false,
    shockMountColor: '#000000'
  }
  const [config, setConfig, { undo, redo, canUndo, canRedo, tempSet }] = useHistory(initialConfig)

  // Saved builds state
  const [currentDesignId, setCurrentDesignId] = React.useState(null)
  const currentDesignIdRef = React.useRef(null)
  React.useEffect(() => {
    currentDesignIdRef.current = currentDesignId
  }, [currentDesignId])
  const [currentDesignName, setCurrentDesignName] = React.useState(null)
  const [hasUnsavedChanges, setHasUnsavedChanges] = React.useState(false)
  const isLoadingDesignRef = React.useRef(false) // Track when loading to prevent hasUnsavedChanges from being set
  const generateAndUploadThumbnailRef = React.useRef(null)
  const preSavedThumbnailRef = React.useRef(null) // stores pre-generated thumbnail URL
  const preSavedThumbnailPromiseRef = React.useRef(null) // the capture still running, if any

  // Keep the Shopify login fresh while the builder is open (it only lasts about an hour).
  // Before, this only ran while the My Builds tab was open, so desktop users got signed out.
  React.useEffect(() => {
    ensureFreshLogin()
    const iv = setInterval(ensureFreshLogin, 5 * 60 * 1000)
    const onVisible = () => {
      if (document.visibilityState === 'visible') ensureFreshLogin()
    }
    document.addEventListener('visibilitychange', onVisible)
    return () => {
      clearInterval(iv)
      document.removeEventListener('visibilitychange', onVisible)
    }
  }, [])

  // Builder analytics (PostHog): what people open, change, save and buy. No names or emails.
  const userInteractedRef = React.useRef(false)
  React.useEffect(() => {
    initAnalytics()
    // Option changes only count once the visitor has touched the page; before that, any change is the
    // builder restoring a saved or shared build on load, not a choice
    const markInteracted = () => {
      userInteractedRef.current = true
    }
    ;['pointerdown', 'keydown', 'touchstart'].forEach((t) => window.addEventListener(t, markInteracted, { capture: true, once: true, passive: true }))
    track('builder_opened', {
      device: window.innerWidth < 768 ? 'phone' : 'desktop',
      from_link: /[?&]id=/.test(window.location.search) || /\/\d{10,}\/?$/.test(window.location.pathname)
    })
  }, [])
  // One event per option someone changes (waits 0.8s so dragging a color picker counts once)
  const analyticsPrevConfigRef = React.useRef(null)
  const analyticsTimersRef = React.useRef({})
  React.useEffect(() => {
    const prev = analyticsPrevConfigRef.current
    analyticsPrevConfigRef.current = config
    if (!prev || isLoadingDesignRef.current || !userInteractedRef.current) return
    const describe = {
      color: ['body_color', (v) => colorLabel(v, colorOptions)],
      screenType: ['screen_type', (v) => (typeof v === 'string' && v ? v.charAt(0).toUpperCase() + v.slice(1) : v)],
      screenColor: ['screen_color', (v) => colorLabel(v, screenColorOptions)],
      fabricColor: ['fabric_color', (v) => colorLabel(v, fabricColorOptions)],
      bottomFabricColor: ['bottom_fabric_color', (v) => colorLabel(v, bottomfabricColorOptions)],
      bottomScreenType: ['bottom_screen_type', (v) => v],
      microphoneType: ['mic_type', (v) => v],
      popFilterEnabled: ['pop_filter', (v) => (v ? 'Added' : 'Removed')],
      popFilterColor: ['pop_filter_color', (v) => colorLabel(v, colorOptions)]
    }
    const changed = Object.keys(describe).filter((key) => prev[key] !== config[key])
    if (changed.length >= 3) {
      // Several options at once is a whole build being applied (a loaded build or a colorway), not one choice
      changed.forEach((key) => clearTimeout(analyticsTimersRef.current[key]))
      track('build_applied', { options_changed: changed.length, mic_type: config.microphoneType, body_color: colorLabel(config.color, colorOptions), screen_color: colorLabel(config.screenColor, screenColorOptions) })
      return
    }
    Object.entries(describe).forEach(([key, [option, label]]) => {
      if (prev[key] === config[key]) return
      clearTimeout(analyticsTimersRef.current[key])
      const value = config[key]
      const micType = config.microphoneType
      analyticsTimersRef.current[key] = setTimeout(() => {
        track('option_changed', {
          option,
          value: label(value),
          ...(typeof value === 'string' && value.startsWith('#') ? { hex: value } : {}),
          mic_type: micType
        })
      }, 800)
    })
  }, [config])

  // Pad add-on state ($100, condenser only)
  const [padEnabled, setPadEnabled] = React.useState(false)

  // Pick up where you left off: remember the mic being built in this browser, and bring it back next
  // visit (unless the link opens a specific build). Nothing goes to the server. No note: the undo arrow
  // takes them back to the default mic if they want to start over.
  const WIP_KEY = 'ohma_wip_build_v1'
  const wipSaveReadyRef = React.useRef(false)
  React.useEffect(() => {
    try {
      const url = new URL(window.location.href)
      const last = url.pathname.split('/').filter(Boolean).pop()
      if (url.searchParams.get('id') || /^\d+$/.test(last || '')) return
      const saved = JSON.parse(localStorage.getItem(WIP_KEY) || 'null')
      if (!saved || !saved.config || !saved.at || Date.now() - saved.at > 30 * 864e5) return
      const changed = Object.keys(initialConfig).some((k) => saved.config[k] !== undefined && saved.config[k] !== initialConfig[k])
      if (!changed && saved.padEnabled !== true) return
      isLoadingDesignRef.current = true
      setConfig({ ...initialConfig, ...saved.config })
      setPadEnabled(saved.padEnabled === true)
      track('build_restored', { days_since: Math.round((Date.now() - saved.at) / 864e5) })
      setTimeout(() => {
        isLoadingDesignRef.current = false
      }, 600)
    } catch (e) {
      /* storage unavailable: start fresh */
    }
  }, [])
  React.useEffect(() => {
    if (!wipSaveReadyRef.current) {
      wipSaveReadyRef.current = true // skip the first render so the default mic never overwrites a saved one
      return
    }
    try {
      localStorage.setItem(WIP_KEY, JSON.stringify({ config, padEnabled, at: Date.now() }))
    } catch (e) {}
  }, [config, padEnabled])
  const PAD_PRICE = 100


  const {
    color,
    screenType,
    screenColor,
    fabricColor, // top fabric
    bottomFabricColor, // bottom fabric
    bottomScreenType,
    microphoneType,
    popFilterEnabled,
    popFilterColor,
    popFilterMaterial,
    shockMountEnabled,
    shockMountColor
  } = config

  // Start downloading this build's screen style alongside the body (saved builds and links included)
  useEffect(() => {
    prefetchScreen(screenType)
  }, [screenType])


  // safe setters to use from the UI (guards against Brass)

  // filtered palette (no Brass)
  const accessoryPalette = Object.entries(colorOptions).filter(([value]) => value !== 'Brass')

  // --- Accessory price rules ---
  const POPFILTER_PRICE_MATCH = 99
  const POPFILTER_PRICE_NOMATCH = 199
  const POPFILTER_PRICE_BLACK = 79
  const POPFILTER_PRICE_CUSTOM = 149
  const SHOCKMOUNT_PRICE_MATCH = 99
  const SHOCKMOUNT_PRICE_NOMATCH = 149

  // Case-insensitive compare (works for hex color keys and names like "Brass")
  const colorsEqual = (a, b) => !!a && !!b && String(a).trim().toUpperCase() === String(b).trim().toUpperCase()

  const matchesBodyOrScreen = (accessoryColorKey, bodyColorKey, screenColorKey) =>
    colorsEqual(accessoryColorKey, bodyColorKey) || colorsEqual(accessoryColorKey, screenColorKey)

  // Price calculators (return $0 if not enabled)
  const getPopFilterPrice = (enabled, popColorKey, bodyColorKey, screenColorKey, material) => {
    if (!enabled) return 0
    // Plastic black is $79, metal (any color including black match) is $149
    if (material === 'plastic') return POPFILTER_PRICE_BLACK
    return POPFILTER_PRICE_CUSTOM
  }

  const getShockMountPrice = (enabled, shockColorKey, bodyColorKey, screenColorKey) => {
    if (!enabled) return 0
    return matchesBodyOrScreen(shockColorKey, bodyColorKey, screenColorKey) ? SHOCKMOUNT_PRICE_MATCH : SHOCKMOUNT_PRICE_NOMATCH
  }

  // --- Accessory add-ons for the current selection ---
  const popPrice = getPopFilterPrice(popFilterEnabled, popFilterColor, color, screenColor, popFilterMaterial)
  const shockPrice = getShockMountPrice(shockMountEnabled, shockMountColor, color, screenColor)

  // --- Your existing upcharges (assumed already defined above this line) ---
  // basePrice, bodyUpcharge, screenUpcharge, fabricUpcharge, logoUpcharge
  // (If any of these don't exist in your file, keep them as 0 or wire them in.)

  const basePrice = 899

  const bodyUpcharge =
    config.color === 'Brass' || config.color === '#d5b173'
      ? premiumColors['Brass']
      : !Object.keys(colorOptions).includes(config.color)
      ? premiumColors['#custom']
      : premiumColors[config.color] || 0

  // Whether body color is a custom hex (not in preset list)
  const isBodyCustomHex = !Object.keys(colorOptions).includes(config.color)
  // Whether screen color is a custom hex (not in preset list)
  const isScreenCustomHex = !Object.keys(screenColorOptions).includes(config.screenColor)
  // If both are custom AND they're the exact same hex, screen charge is waived
  const screenSameAsBodyCustom =
    isBodyCustomHex &&
    isScreenCustomHex &&
    typeof config.color === 'string' &&
    typeof config.screenColor === 'string' &&
    config.color.toLowerCase() === config.screenColor.toLowerCase()

  const screenUpcharge = isScreenCustomHex ? (screenSameAsBodyCustom ? 0 : premiumScreenColors['#custom']) : premiumScreenColors[config.screenColor] || 0

  const fabricUpcharge = premiumFabricColor[config.bottomFabricColor] || 0

  const bottomScreenMap = {
    C: 'Custom Logo',
    P: 'Add Pattern Screen',
    L: 'Logo'
  }

  const resolvedBottomScreen = bottomScreenMap[config.bottomScreenType] || config.bottomScreenType
  const logoUpcharge = premiumLogo[resolvedBottomScreen] || 0

  // --- Add-ons (accessories) summary + total ---
  const resolveColorLabel = (hex) => {
    if (!hex) return '—'
    const lower = hex.toLowerCase()
    if (['#232221', '#1c1d1d', '#1a1a1a', '#000000', '#000'].includes(lower)) return 'Black'
    return colorOptions[hex] || hex
  }
  const popFilterLabel = (material, hex) => {
    if (material === 'plastic') return 'Black (Plastic)'
    const colorName = resolveColorLabel(hex)
    return `${colorName} (Metal)`
  }
  const popLabel = popFilterEnabled ? popFilterLabel(popFilterMaterial, popFilterColor) : null

  const shockLabel = shockMountEnabled ? colorOptions[shockMountColor] || shockMountColor || '—' : null

  const popAddOnPrice = getPopFilterPrice(
    popFilterEnabled,
    popFilterColor,
    color, // body color key
    screenColor, // screen color key
    popFilterMaterial
  )

  const shockAddOnPrice = getShockMountPrice(shockMountEnabled, shockMountColor, color, screenColor)
  // Pad is included in the base price now — no longer an add-on.
  const padAddOnPrice = 0

  const addOnsTotal = (popAddOnPrice || 0) + (shockAddOnPrice || 0) + padAddOnPrice

  const addOnsParts = []
  if (popFilterEnabled) addOnsParts.push(`Pop Filter (${popLabel}) +$${popAddOnPrice}`)
  if (shockMountEnabled) addOnsParts.push(`Shock Mount (${shockLabel}) +$${shockAddOnPrice}`)

  const addOnsText = addOnsParts.length ? addOnsParts.join(' • ') : 'None'


  // Helper to convert USD price to display currency
  const convertPrice = (usdPrice) => {
    if (!usdPrice) return 0
    if (currency.code === 'USD' || !variants.length) {
      return Math.round(usdPrice)
    }
    // Use first variant to calculate conversion rate
    const conversionRate = fullCents(variants[0]) / 100 / 899 // 899 is base USD price
    return Math.round(usdPrice * conversionRate)
  }

  // ✅ Single declaration of total price
  // Calculate price in USD (your base currency)
  // Calculate price in USD (works exactly like before!)
  // NOTE: Pad is no longer added — it's included in the base $899 price for
  // all condensers. The padEnabled state still exists for backward compat with
  // saved builds and cart properties but doesn't affect the total.
  const totalPriceUSD = (basePrice ?? 0) + (bodyUpcharge ?? 0) + (screenUpcharge ?? 0) + (fabricUpcharge ?? 0) + (logoUpcharge ?? 0) + popPrice + shockPrice

  // For non-USD currencies, find the matching variant and use its converted price
  let displayPrice = totalPriceUSD
  // The current build, described for analytics events
  const analyticsBuildProps = () => ({
    mic_type: config.microphoneType,
    body_color: colorLabel(config.color, colorOptions),
    screen_type: typeof config.screenType === 'string' ? config.screenType.charAt(0).toUpperCase() + config.screenType.slice(1) : config.screenType,
    screen_color: colorLabel(config.screenColor, screenColorOptions),
    pop_filter: !!config.popFilterEnabled,
    pad: !!padEnabled,
    price_usd: Math.round(totalPriceUSD)
  })

  if (currency.code !== 'USD' && variants.length > 0) {
    // Find the variant that matches our USD calculation
    // In USD, variants have prices like 89900, 91700, etc.
    // In CAD, the SAME variants have prices like 113800, 117200, etc.
    // The trick: match by USD price to find the right variant, then use its local price

    // We need to know which variant corresponds to our calculated price
    // The variants are in the SAME order regardless of currency
    // So we can match by finding which USD price tier we're in

    const variantPrices = [899, 1147, 948, 1098, 1199, 978, 1226, 1027, 1177, 1278, 1048, 1296, 1097, 1247, 1348] // Base + pop filter combos
    const variantIndex = variantPrices.findIndex((p) => Math.abs(p - totalPriceUSD) < 5)

    if (variantIndex >= 0 && variants[variantIndex]) {
      displayPrice = Math.round(fullCents(variants[variantIndex]) / 100) // Changed
    } else {
      // Fallback: use first variant to calculate conversion rate
      const conversionRate = fullCents(variants[0]) / 100 / 899
      displayPrice = Math.round(totalPriceUSD * conversionRate) // Changed
    }
  } else {
    displayPrice = Math.round(totalPriceUSD) // Changed
  }

  const totalPrice = displayPrice
  // Black Friday: the price after the sale's discount (the equivalent of $100 outside the US)
  const saleOff = !SALE_ON ? 0 : saleCutCents > 0 ? Math.round(saleCutCents / 100) : currency.code === 'USD' || !variants.length ? SALE.offUSD : Math.round((SALE.offUSD * fullCents(variants[0])) / 100 / 899)
  const salePrice = totalPrice - saleOff
  const priceText = (v) => `${currency.code === 'CAD' ? 'C' : ''}${currency.symbol}${v}`
  const renderPrice = () =>
    SALE_ON ? (
      <>
        <s className="price-was">{priceText(totalPrice)}</s>
        {priceText(salePrice)}
      </>
    ) : (
      priceText(totalPrice)
    )
  // Black Friday: two prices can be wider than the Add to Cart button (four-digit prices, narrower screens).
  // When they would run past the edge, the button's text gets just small enough to fit (never below 75%).
  React.useLayoutEffect(() => {
    if (!SALE_ON) return
    const fit = () =>
      document.querySelectorAll('.add-to-cart.sale-on .cart-content').forEach((c) => {
        c.style.fontSize = ''
        const cs = getComputedStyle(c)
        const pad = parseFloat(cs.paddingLeft) + parseFloat(cs.paddingRight)
        const need = c.scrollWidth - pad
        const have = c.clientWidth - pad
        if (have > 0 && need > have - 2) c.style.fontSize = `${Math.max(0.75, (have - 8) / need) * 100}%`
      })
    fit()
    const t = setTimeout(fit, 400)
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(fit)
    window.addEventListener('resize', fit)
    return () => {
      clearTimeout(t)
      window.removeEventListener('resize', fit)
    }
  })

  const [draftColor, setDraftColor] = useState(color)
  const [showCustomColorPicker, setShowCustomColorPicker] = useState(false)
  const [draftScreenColor, setDraftScreenColor] = useState(screenColor)

  // When the picker is open, use draftColor; otherwise use the committed color
  const effectiveColor = showCustomColorPicker ? draftColor : color

  const [isCustomBodyColor, setIsCustomBodyColor] = useState(false)
  const [isCustomScreenColor, setIsCustomScreenColor] = useState(false)
  const [showCustomScreenColorPicker, setShowCustomScreenColorPicker] = useState(false)

  // Close color pickers on Enter key (mobile keyboard dismiss) — placed after all picker states declared
  const _commitBodyPickerRef = React.useRef(null)
  const _commitScreenPickerRef = React.useRef(null)
  React.useEffect(() => {
    const handler = (e) => {
      if (e.key !== 'Enter') return
      if (showCustomColorPicker) _commitBodyPickerRef.current?.()
      if (showCustomScreenColorPicker) _commitScreenPickerRef.current?.()
    }
    document.addEventListener('keydown', handler)
    return () => document.removeEventListener('keydown', handler)
  }, [showCustomColorPicker, showCustomScreenColorPicker])



  useEffect(() => {
    const pickerEl = document.querySelector('.my-picker')
    if (pickerEl) {
      pickerEl.style.setProperty('--picker-color', draftColor)
    }
  }, [draftColor])

  // Sync custom pop filter color with body color (avoiding duplicate history entries)
  const prevColorRef = useRef(config.color)
  useEffect(() => {
    // Only update pop filter color when:
    // 1. Pop filter is enabled
    // 2. Pop filter material is 'metal' (custom - should follow body)
    // 3. Body color actually changed
    if (config.popFilterEnabled && config.popFilterMaterial === 'metal' && prevColorRef.current !== config.color) {
      // For brass or black bodies, custom pop filter is lighter gray (#404040)
      // For other colors, custom pop filter matches body
      const isBrassBody = config.color === 'Brass' || config.color === '#d5b173'
      const isBlackBody = config.color === '#1A1A1A' || config.color === '#232221' || config.color === '#1c1d1d' || config.color.toUpperCase() === '#1A1A1A'
      const desiredColor = isBrassBody || isBlackBody ? '#232221' : config.color

      if (config.popFilterColor !== desiredColor) {
        setConfig({ ...config, popFilterColor: desiredColor })
      }
    }
    prevColorRef.current = config.color
  }, [config.color, config.popFilterEnabled, config.popFilterMaterial])

  React.useEffect(() => {
    // Don't mark as unsaved if we're currently loading a design
    if (currentDesignId && !isLoadingDesignRef.current) {
      setHasUnsavedChanges(true)
    }
  }, [config])

  // Auto-save after 5 minutes idle when logged in and there are unsaved changes
  React.useEffect(() => {
    if (!hasUnsavedChanges || !currentDesignId) return
    const token = (() => {
      try {
        const exp = localStorage.getItem('ohma_ctoken_exp')
        if (exp && Date.now() > parseInt(exp)) return null
        return localStorage.getItem('ohma_ctoken') || window.SHOPIFY_CUSTOMER_TOKEN || null
      } catch (e) {
        return null
      }
    })()
    if (!token) return
    const timer = setTimeout(async () => {
      if (hasUnsavedChanges && currentDesignId) {
        // Show loading indicator
        builderBus.setLoading?.('Auto-saving...')
        try {
          await handleUpdateDesign(currentDesignId, { name: currentDesignName || 'My Build' })
        } catch (e) {
          // Silent fail
        }
        builderBus.setLoading?.(null)
      }
    }, 300000) // 5 minutes
    return () => clearTimeout(timer)
  }, [config, hasUnsavedChanges, currentDesignId])

  // Sync current build to ?id= URL so refresh reloads it via the existing fileid loader
  const setDesignUrl = React.useCallback((fileid) => {
    if (!fileid) return
    const url = new URL(window.location.href)
    url.searchParams.set('id', String(fileid))
    window.history.replaceState(null, '', url.toString())
  }, [])

  const handleLoadDesign = (savedConfig, id, name) => {
    track('build_loaded', { from: id ? 'my_builds' : 'link' })
    // Set flag FIRST so the popFilter useEffect and the debugCameraDist useEffect
    // both see it and skip repositioning the camera during load.
    isLoadingDesignRef.current = true

    // Reset any accumulated user zoom so the loaded build starts clean
    userZoomOffsetRef.current = 0

    if (!isDesktop) builderBus.closeFooter?.()

    setConfig(savedConfig)
    setPadEnabled(savedConfig.padEnabled === true)
    setCurrentDesignId(id)
    setCurrentDesignName(name)
    setHasUnsavedChanges(false)

    // Compute correct distances synchronously from savedConfig (avoids stale state closure)
    const targetDist = getInitialCameraDist(savedConfig)

    // After the footer spring settles, rotate to side view at the correct distance
    setTimeout(() => {
      isLoadingDesignRef.current = false
      if (!cameraRef.current || !orbitControlsRef.current) return
      const footerPos = typeof sheetPercent?.get === 'function' ? sheetPercent.get() : snapPercents[1]
      const dist = getCameraDistance(footerPos, targetDist)
      if (typeof rotateCameraToSide === 'function') {
        const azimuth = savedConfig.popFilterEnabled ? -45 : -25
        rotateCameraToSide(dist, azimuth)
      }
    }, 600)

    // Write to users table with a new fileid so ?id= reload works without auth.
    // Tagged source:'load_clone' so the admin activity feed can filter these
    // phantom rows out — every Load in Builder action used to create a row that
    // looked like a new anonymous build to admins.
    const fileid = Date.now()
    fetch('https://customizer-test-nodejs-4c6a5bfd6dbd.herokuapp.com/api/receive-data', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'x-customer-token': window.SHOPIFY_CUSTOMER_TOKEN ?? '' },
      body: JSON.stringify({
        source: 'load_clone',
        fileid,
        color: savedConfig.color,
        screenType: savedConfig.screenType,
        screenColor: savedConfig.screenColor,
        fabricColor: savedConfig.fabricColor,
        bottomFabricColor: savedConfig.bottomFabricColor,
        bottomScreenType: savedConfig.bottomScreenType,
        microphoneType: savedConfig.microphoneType,
        popFilterEnabled: savedConfig.popFilterEnabled,
        popFilterColor: savedConfig.popFilterColor,
        shockMountEnabled: savedConfig.shockMountEnabled,
        shockMountColor: savedConfig.shockMountColor,
        padEnabled: savedConfig.padEnabled === true,
        name: name || 'My Build',
        email: '',
        currentDesignId: id,
        hasUnsavedChanges: false,
        skipCartMark: true
      })
    }).catch(() => {})
    setDesignUrl(fileid)
    // Regenerate thumbnail after scene settles — 2s gives materials/textures time to load
    setTimeout(async () => {
      try {
        const thumbnailUrl = await generateAndUploadThumbnailRef.current?.()
        if (thumbnailUrl) {
          await fetch(`https://customizer-test-nodejs-4c6a5bfd6dbd.herokuapp.com/api/designs/${id}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json', 'x-customer-token': window.SHOPIFY_CUSTOMER_TOKEN ?? '' },
            body: JSON.stringify({ thumbnail_url: thumbnailUrl })
          })
          // Update the card in My Builds immediately
          builderBus.refreshBuilds?.()
        }
      } catch (e) {}
    }, 2000)
  }

  const handleSaveDesign = async (name) => {
    await ensureFreshLogin()
    const token =
      window.SHOPIFY_CUSTOMER_TOKEN ||
      (() => {
        try {
          const exp = localStorage.getItem('ohma_ctoken_exp')
          if (exp && Date.now() > parseInt(exp)) return ''
          return localStorage.getItem('ohma_ctoken') || ''
        } catch (e) {
          return ''
        }
      })()
    try {
      const saveFileid = Date.now()
      const res = await fetch('https://customizer-test-nodejs-4c6a5bfd6dbd.herokuapp.com/api/designs', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'x-customer-token': token },
        body: JSON.stringify({
          name: name || 'My Build',
          config: {
            ...config,
            padEnabled,
            totalPriceUSD,
            fileid: saveFileid,
            utm: getStoredUTM(),
            cartProperties: {
              'Build Name': name || 'My Build',
              'Body Color': colorOptions[color] ?? color,
              'Screen Type': screenTypeOptions[config.screenType]?.label ?? config.screenType,
              'Screen Color': screenColorOptions[config.screenColor] ?? config.screenColor,
              'Fabric Color': fabricColorOptions[config.fabricColor] ?? config.fabricColor,
              'Bottom Fabric Color': bottomfabricColorOptions[config.bottomFabricColor] ?? config.bottomFabricColor,
              'Bottom Screen Type': config.bottomScreenType,
              'Microphone Type': config.microphoneType,
              ...(config.popFilterEnabled
                ? {
                    'Pop Filter': 'Yes',
                    'Pop Filter Color': popFilterLabel(config.popFilterMaterial, config.popFilterColor)
                  }
                : {}),
              ...(config.shockMountEnabled
                ? { 'Shock Mount': 'Yes', 'Shock Mount Color': colorOptions[config.shockMountColor] ?? config.shockMountColor }
                : {}),
              ...(padEnabled && config.microphoneType !== 'Ribbon' ? { Pad: 'Add Pad' } : {})
            }
          }
        })
      })
      const data = await res.json()
      if (data.design) {
        setCurrentDesignId(data.design.id)
        setCurrentDesignName(name || 'My Build')
        setHasUnsavedChanges(false)
        fetch('https://customizer-test-nodejs-4c6a5bfd6dbd.herokuapp.com/api/receive-data', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'x-customer-token': token },
          body: JSON.stringify({
            fileid: saveFileid,
            color: config.color,
            screenType: config.screenType,
            screenColor: config.screenColor,
            fabricColor: config.fabricColor,
            bottomFabricColor: config.bottomFabricColor,
            bottomScreenType: config.bottomScreenType,
            microphoneType: config.microphoneType,
            popFilterEnabled: config.popFilterEnabled,
            popFilterColor: config.popFilterColor,
            shockMountEnabled: config.shockMountEnabled,
            shockMountColor: config.shockMountColor,
            padEnabled,
            name: name || 'My Build',
            email: ''
          })
        }).catch(() => {})
        setDesignUrl(saveFileid)
        const designId = data.design.id
        // Store designId so My Builds page can patch the thumbnail if upload fails
        try {
          localStorage.setItem('ohma_pending_thumb_design_id', designId)
        } catch (e) {}
        // One picture capture per save: reuse the URL or the capture already running
        // (started when Save was pressed) instead of starting a second one alongside it.
        const thumbPromise = preSavedThumbnailRef.current
          ? Promise.resolve(preSavedThumbnailRef.current)
          : preSavedThumbnailPromiseRef.current || Promise.resolve(generateAndUploadThumbnailRef.current?.())
        preSavedThumbnailRef.current = null
        preSavedThumbnailPromiseRef.current = null
        const apiPut = (body) =>
          fetch(`https://customizer-test-nodejs-4c6a5bfd6dbd.herokuapp.com/api/designs/${designId}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json', 'x-customer-token': token },
            body: JSON.stringify(body)
          })
        const saveThumb = async (thumbnailUrl) => {
          if (!thumbnailUrl) return false
          try {
            const r = await apiPut({ thumbnail_url: thumbnailUrl })
            if (!r.ok) return false
            builderBus.patchDrawerDesign?.({ id: designId, thumbnail_url: thumbnailUrl })
            try {
              localStorage.removeItem('ohma_pending_thumb_design_id')
            } catch (e) {}
            return true
          } catch (e) {
            return false
          }
        }
        let thumbnailUrl = null
        let timedOut = false
        try {
          thumbnailUrl = await Promise.race([
            thumbPromise.catch(() => null),
            new Promise((resolve) =>
              setTimeout(() => {
                timedOut = true
                resolve(null)
              }, 15000)
            )
          ])
        } catch (e) {}
        if (timedOut) {
          // Slow picture: let the save finish now, and attach the picture when it lands
          // (before, a late picture was thrown away and the build stayed blank for good)
          thumbPromise
            .catch(() => null)
            .then(async (url) => {
              if (await saveThumb(url)) builderBus.refreshBuilds?.()
            })
          return data.design
        }
        await saveThumb(thumbnailUrl)
        // Share picture (the one cards show). Taken even if the small picture failed,
        // so the build still gets a picture.
        // On desktop the freeze overlay prevents any visible flash
        // On mobile temporarily set tab ref to bypass the mobile tab guard
        try {
          const prevTab = mobileFooterTabRef.current
          if (isMobile) mobileFooterTabRef.current = 'share'
          let shareBlob = null
          try {
            shareBlob = await generateShareScreenshotRef.current?.()
          } finally {
            if (isMobile) mobileFooterTabRef.current = prevTab
          }
          if (shareBlob) {
            latestShareBlobRef.current = shareBlob
            latestShareBlobAtRef.current = Date.now()
            const url = URL.createObjectURL(shareBlob)
            setShareScreenshotUrl((prev) => {
              if (prev?.startsWith('blob:')) URL.revokeObjectURL(prev)
              return url
            })
            const imageUrl = await uploadToCloudflare(shareBlob, `share_${designId}_${Date.now()}.jpg`)
            if (imageUrl) {
              await apiPut({ share_thumbnail_url: imageUrl })
              // Notify SavedDesignsTab and the drawer to patch this design's share URL in state
              builderBus.refreshBuilds?.()
              builderBus.patchDesignShareUrl?.({ id: designId, share_thumbnail_url: imageUrl })
              builderBus.patchDrawerDesign?.({ id: designId, share_thumbnail_url: imageUrl })
              return { ...data.design, thumbnail_url: thumbnailUrl, share_thumbnail_url: imageUrl }
            }
          }
        } catch (e) {}
        return { ...data.design, thumbnail_url: thumbnailUrl }
      }
      return data.design ?? null
    } catch (e) {
      return null
    }
  }

  const handleUpdateDesign = async (id, patch) => {
    try {
      await fetch(`https://customizer-test-nodejs-4c6a5bfd6dbd.herokuapp.com/api/designs/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'x-customer-token': window.SHOPIFY_CUSTOMER_TOKEN ?? '' },
        body: JSON.stringify({ ...patch, config: { ...config, padEnabled } })
      })
      setHasUnsavedChanges(false)
      try {
        const thumbnailUrl = await generateAndUploadThumbnailRef.current?.()
        if (thumbnailUrl) {
          await fetch(`https://customizer-test-nodejs-4c6a5bfd6dbd.herokuapp.com/api/designs/${id}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json', 'x-customer-token': window.SHOPIFY_CUSTOMER_TOKEN ?? '' },
            body: JSON.stringify({ thumbnail_url: thumbnailUrl })
          })
          // Upload fresh portrait share screenshot
          try {
            const prevTab = mobileFooterTabRef.current
            if (isMobile) mobileFooterTabRef.current = 'share'
            const shareBlob = await generateShareScreenshotRef.current?.()
            if (isMobile) mobileFooterTabRef.current = prevTab
            if (shareBlob) {
              latestShareBlobRef.current = shareBlob
              latestShareBlobAtRef.current = Date.now()
              const imageUrl = await uploadToCloudflare(shareBlob, `share_${id}_${Date.now()}.jpg`)
              if (imageUrl) {
                await fetch(`https://customizer-test-nodejs-4c6a5bfd6dbd.herokuapp.com/api/designs/${id}`, {
                  method: 'PUT',
                  headers: { 'Content-Type': 'application/json', 'x-customer-token': window.SHOPIFY_CUSTOMER_TOKEN ?? '' },
                  body: JSON.stringify({ share_thumbnail_url: imageUrl })
                })
                builderBus.patchDesignShareUrl?.({ id, share_thumbnail_url: imageUrl })
              }
            }
          } catch (e) {}
          return thumbnailUrl
        }
      } catch (e) {}
    } catch (e) {}
    return null
  }

  const setScreenType = (v) => setConfig({ ...config, screenType: v })
  const setFabricColor = (v) => setConfig({ ...config, fabricColor: v })
  const setBottomFabricColor = (v) => setConfig({ ...config, bottomFabricColor: v }) // new setter
  const setBottomScreenType = (v) => setConfig({ ...config, bottomScreenType: v })
  const setMicrophoneType = (v) => {
    setConfig({ ...config, microphoneType: v })
  }


  // Safe helpers — place near top of App component
  function parseBool(value, fallback = false) {
    if (value === undefined || value === null) return fallback
    if (typeof value === 'boolean') return value
    if (typeof value === 'number') return value !== 0
    const s = String(value).trim().toLowerCase()
    if (s === '') return fallback
    return s === 'true' || s === 't' || s === '1' || s === 'yes' || s === 'y'
  }

  function pickStr(value, fallback = null) {
    if (value === undefined || value === null) return fallback
    return String(value)
  }

  // — Load saved config on mount
  // — Load saved config on mount
  useEffect(() => {
    // 1) figure out fileid from ?id= or last URL segment
    const url = new URL(window.location.href)
    // ?id=123 or /products/build-your-ohma/123 (the cart link). Only a build number counts: the plain
    // product URL's last segment ("build-your-ohma") used to be looked up too, a wasted request on every load.
    const lastSegment = url.pathname.split('/').filter(Boolean).pop()
    const maybeFileid = url.searchParams.get('id') || (/^\d+$/.test(lastSegment || '') ? lastSegment : null)

    if (!maybeFileid) {
      // console.warn('⚠️ no fileid in URL; skipping load')
      return
    }

    // 2) hit your “by-fileid” endpoint
    const endpoint = `https://customizer-test-nodejs-4c6a5bfd6dbd.herokuapp.com/api/variables/fileid/${maybeFileid}`
    ;(async () => {
      try {
        const res = await fetch(endpoint)
        if (!res.ok) {
          // console.error('❌ fetch failed:', await res.text())
          return
        }
        const data = await res.json()

        // 3) seed your history‐backed state
        setConfig({
          color: data.color ?? initialConfig.color,
          screenType: data.screen_type ?? initialConfig.screenType,
          screenColor: data.screen_color ?? initialConfig.screenColor,
          fabricColor: data.fabric_color ?? initialConfig.fabricColor,
          bottomFabricColor: data.bottom_fabric_color ?? initialConfig.bottomFabricColor,

          bottomScreenType: data.bottom_screen_type ?? initialConfig.bottomScreenType,
          microphoneType: data.microphone_type ?? initialConfig.microphoneType,
          // accessories — ensure booleans and colors are present in the config
          // accessories — booleans coerced via !!
          popFilterEnabled: parseBool(data.pop_filter_enabled ?? data.popFilterEnabled, initialConfig.popFilterEnabled),
          popFilterColor: pickStr(data.pop_filter_color ?? data.popFilterColor, initialConfig.popFilterColor),
          // Derive material from color: black (#1A1A1A) = plastic, any other color = metal
          popFilterMaterial: (() => {
            const savedColor = (data.pop_filter_color ?? data.popFilterColor ?? '').toString().trim().toUpperCase()
            const isBlack = savedColor === '#1A1A1A' || savedColor === '' || savedColor === 'NULL'
            const enabled = parseBool(data.pop_filter_enabled ?? data.popFilterEnabled, false)
            return enabled && !isBlack ? 'metal' : 'plastic'
          })(),

          shockMountEnabled: parseBool(data.shock_mount_enabled ?? data.shockMountEnabled, initialConfig.shockMountEnabled),
          shockMountColor: pickStr(data.shock_mount_color ?? data.shockMountColor, initialConfig.shockMountColor)
        })
        // Restore pad state from share link data
        setPadEnabled(parseBool(data.pad_enabled ?? data.padEnabled, false))

        // Look up matching saved design so My Builds shows it as active
        // Retry a few times since Liquid token may not be injected yet at mount
        const tryMatchDesign = async (attemptsLeft) => {
          const token = window.SHOPIFY_CUSTOMER_TOKEN
          if (!token || token === 'null') {
            if (attemptsLeft > 0) setTimeout(() => tryMatchDesign(attemptsLeft - 1), 600)
            return
          }
          try {
            const buildsRes = await fetch('https://customizer-test-nodejs-4c6a5bfd6dbd.herokuapp.com/api/designs', {
              headers: { 'x-customer-token': token }
            })
            if (!buildsRes.ok) return
            const buildsData = await buildsRes.json()
            const designs = buildsData.designs ?? []
            // First try direct match via stored current_design_id
            let match = data.current_design_id ? designs.find((d) => String(d.id) === String(data.current_design_id)) : null
            // Fall back to config matching
            if (!match) {
              const color = data.color ?? initialConfig.color
              const screenType = data.screen_type ?? initialConfig.screenType
              const screenColor = data.screen_color ?? initialConfig.screenColor
              const microphoneType = data.microphone_type ?? initialConfig.microphoneType
              match = designs.find(
                (d) =>
                  d.config?.color === color &&
                  d.config?.screenType === screenType &&
                  d.config?.screenColor === screenColor &&
                  d.config?.microphoneType === microphoneType
              )
            }
            if (match) {
              setCurrentDesignId(match.id)
              setCurrentDesignName(match.name || 'My Build')
              setHasUnsavedChanges(false)
              // Regenerate thumbnail if missing (e.g. user navigated away before upload finished)
              if (!match.thumbnail_url) {
                setTimeout(async () => {
                  try {
                    const thumbnailUrl = await generateAndUploadThumbnailRef.current?.()
                    if (thumbnailUrl) {
                      await fetch(`https://customizer-test-nodejs-4c6a5bfd6dbd.herokuapp.com/api/designs/${match.id}`, {
                        method: 'PUT',
                        headers: { 'Content-Type': 'application/json', 'x-customer-token': window.SHOPIFY_CUSTOMER_TOKEN ?? '' },
                        body: JSON.stringify({ thumbnail_url: thumbnailUrl })
                      })
                      builderBus.refreshBuilds?.()
                    }
                  } catch (e) {}
                }, 2500)
              }
            }
          } catch (e) {}
        }
        tryMatchDesign(8) // retry up to 8 times over ~5 seconds

        // Look up matching saved design so My Builds shows it as active
        const token = window.SHOPIFY_CUSTOMER_TOKEN
        if (token && token !== 'null') {
          try {
            const buildsRes = await fetch('https://customizer-test-nodejs-4c6a5bfd6dbd.herokuapp.com/api/designs', {
              headers: { 'x-customer-token': token }
            })
            if (buildsRes.ok) {
              const buildsData = await buildsRes.json()
              const designs = buildsData.designs ?? []
              const color = data.color ?? initialConfig.color
              const screenType = data.screen_type ?? initialConfig.screenType
              const screenColor = data.screen_color ?? initialConfig.screenColor
              const microphoneType = data.microphone_type ?? initialConfig.microphoneType
              const match = designs.find(
                (d) =>
                  d.config?.color === color &&
                  d.config?.screenType === screenType &&
                  d.config?.screenColor === screenColor &&
                  d.config?.microphoneType === microphoneType
              )
              if (match) {
                setCurrentDesignId(match.id)
                setCurrentDesignName(match.name || 'My Build')
                setHasUnsavedChanges(false)
              }
            }
          } catch (e) {}
        }
      } catch (err) {
        // console.error(`❌ failed loading config for fileid=${maybeFileid}:`, err)
      }
    })()
  }, []) // ← far as React is concerned, “no deps” means “run exactly once”

  const [isDataLoaded, setIsDataLoaded] = useState(false)
  const [fetchedData, setFetchedData] = useState(null)

  const screenTypeOptions = SCREEN_TYPE_OPTIONS

  const bottomScreenTypeOptions = {
    Logo: {
      label: 'Ohma Logo',
      iconUrl: '' // Placeholder URL
    },
    'Custom Logo': {
      label: 'Custom Logo',
      iconUrl: '' // Placeholder URL
    }
  }

  const microphoneTypeOptions = {
    Condenser: {
      label: 'Condenser',
      iconUrl: 'https://cdn.shopify.com/s/files/1/0564/1820/5796/files/250116_Condenser_Icon_8e0c5abf-6c55-493d-8f25-64dc1fe3d6e3.png?v=1737070883'
    },
    Ribbon: {
      label: 'Ribbon',
      iconUrl: 'https://cdn.shopify.com/s/files/1/0564/1820/5796/files/250116_Transducer_Ribbon_Icon_7d6282c2-3f3d-4403-a894-cb35aeaa60ee.png?v=1737070883'
    }
    // Add other microphone types as necessary
  }


  const isDesktop = useMediaQuery('(min-width: 960px)')

  // Determine phone type based on viewport dimensions.
  function getPhoneType() {
    // Use clientWidth to avoid scrollbar width affecting detection
    const width = document.documentElement.clientWidth || window.innerWidth
    const height = window.innerHeight
    const screenWidth = window.screen.width
    const screenHeight = window.screen.height

    // Use screen dimensions for more reliable detection
    const deviceWidth = Math.min(screenWidth, screenHeight) // Portrait width
    const deviceHeight = Math.max(screenWidth, screenHeight) // Portrait height

    if (width > height) {
      return 'desktop' // Landscape mode (including iPad landscape)
    } else if (width >= 768) {
      return 'ipad' // iPad or tablet (768px and above in portrait)
    } else if (deviceWidth >= 428) {
      return 'promax' // iPhone 12/13/14/15/16 Pro Max (428-430)
    } else if (deviceWidth >= 414) {
      return 'promax' // iPhone 11 Pro Max, XS Max (414)
    } else if (deviceWidth >= 400) {
      return 'pro' // NEW: iPhone 16 Pro (402)
    } else if (deviceWidth >= 393) {
      return 'regular' // iPhone 14/15/16 (393)
    } else if (deviceWidth >= 390) {
      return 'regular' // iPhone 12/13 Pro (390)
    } else if (deviceWidth >= 375 && deviceHeight >= 812) {
      return 'regular' // iPhone 11, X, XS, 12 mini (375×812+)
    } else if (deviceWidth >= 375 && deviceHeight >= 667) {
      return 'se' // iPhone SE, 6, 7, 8 (375×667)
    } else {
      return 'desktop' // Fallback
    }
  }

  function getCameraDistance(footerValue, customDistances = null) {
    if (!customDistances) {
      const live = liveAutoFrame(footerValue)
      if (live) return live.dist
    }
    // Use custom distances if provided, otherwise use debug slider values
    const distances = customDistances || debugCameraDist
    const closeDist = distances.close
    const midDist = distances.mid
    const openDist = distances.open

    const closePoint = snapPercents[0]
    const midPoint = snapPercents[1]
    const openPoint = snapPercents[2]

    if (footerValue <= closePoint) {
      return closeDist
    } else if (footerValue <= midPoint) {
      const ratio = (footerValue - closePoint) / (midPoint - closePoint)
      return closeDist + ratio * (midDist - closeDist)
    } else if (footerValue <= openPoint) {
      const ratio = (footerValue - midPoint) / (openPoint - midPoint)
      return midDist + ratio * (openDist - midDist)
    } else {
      return openDist
    }
  }

  function updateCameraDistanceInRealTime(footerValue) {
    if (isCameraMoving) return
    if (isTweeningRef.current) return
    if (isCapturingRef.current) return
    if (!cameraRef.current || !orbitControlsRef.current) return

    const minSnap = snapPercents[0] ?? 0
    const maxSnap = snapPercents[snapPercents.length - 1] ?? 100
    const clampedValue = Math.max(minSnap, Math.min(maxSnap, footerValue))
    const baseDist = getCameraDistance(clampedValue)
    const desiredDist = baseDist + (userZoomOffsetRef.current || 0)
    const currentDist = cameraRef.current.position.distanceTo(orbitControlsRef.current.target)

    const target = orbitControlsRef.current.target.clone()
    const direction = cameraRef.current.position.clone().sub(target).normalize()

    const newPosition = target.clone().add(direction.multiplyScalar(Math.max(0.2, desiredDist)))
    cameraRef.current.position.copy(newPosition)
    cameraRef.current.updateProjectionMatrix()
    orbitControlsRef.current.update()
  }


  // --- Define helper functions for computing default groupY and range based on viewport size ---

  const [phoneType, setPhoneType] = useState(getPhoneType())
  const [viewportDims, setViewportDims] = useState({
    width: window.innerWidth,
    height: window.innerHeight
  })

  // ========== DEBUG STATES - ADD THESE ==========    } else if (phoneType === 'regular') {

  // ========== DEBUG STATES - INITIALIZE WITH CORRECT DEFAULTS ==========
  const classicCameraDist = (overrideConfig = null) => {
    const type = getPhoneType()

    // Allow passing a config directly (e.g. during handleLoadDesign before state updates)
    const hasPopFilter = (overrideConfig ?? config).popFilterEnabled

    if (type === 'se') {
      // iPhone SE - smaller screen needs closer camera with pop filter
      if (hasPopFilter) {
        return { close: 1.45, mid: 1.35, open: 1.05 }
      } else {
        return { close: 2.95, mid: 1.25, open: 0.95 }
      }
    } else if (type === 'regular') {
      // Regular iPhone - adjust for pop filter visibility
      if (hasPopFilter) {
        return { close: 1.45, mid: 1.35, open: 1.05 }
      } else {
        return { close: 2.95, mid: 1.25, open: 0.95 }
      }
    } else if (type === 'pro') {
      // iPhone Pro - similar to regular but slightly tighter framing
      if (hasPopFilter) {
        return { close: 2.85, mid: 1.3, open: 1.0 }
      } else {
        return { close: 2.95, mid: 1.2, open: 0.95 }
      }
    } else if (type === 'promax') {
      // iPhone Pro Max - larger screen, more dramatic adjustment needed
      // (pop filter: pulled back a little since the 105mm lens, which left the stem touching the footer)
      if (hasPopFilter) {
        return { close: 2.8, mid: 1.55, open: 1.2 }
      } else {
        return { close: 3.0, mid: 1.3, open: 1.0 }
      }
    } else if (type === 'ipad') {
      // iPad - largest mobile screen, needs most adjustment
      if (hasPopFilter) {
        return { close: 2.9, mid: 1.1, open: 1.0 }
      } else {
        return { close: 3.0, mid: 1.05, open: 0.95 }
      }
    } else if (type === 'desktop') {
      // Desktop - adjust camera when pop filter is enabled
      if (hasPopFilter) {
        return { close: 1.0, mid: 1.0, open: 1.0 }
      } else {
        return { close: 0.9, mid: 0.9, open: 0.9 }
      }
    }
    return { close: 3.0, mid: 1.9, open: 1.0 }
  }

  // Get initial canvas translation values (in pixels) per device
  const classicCanvasTranslate = (overrideConfig = null) => {
    const type = getPhoneType()

    // Check if pop filter is enabled
    const hasPopFilter = (overrideConfig ?? config).popFilterEnabled

    if (type === 'se') {
      // iPhone SE - needs more upward shift with pop filter
      if (hasPopFilter) {
        return { close: -100, mid: -120, open: -55 }
      } else {
        return { close: -100, mid: -125, open: -80 }
      }
    } else if (type === 'regular') {
      // Regular iPhone - moderate adjustment for pop filter
      if (hasPopFilter) {
        return { close: -100, mid: -120, open: -55 }
      } else {
        return { close: -100, mid: -125, open: -80 }
      }
    } else if (type === 'pro') {
      // iPhone Pro - similar to regular with slight variation
      if (hasPopFilter) {
        return { close: -105, mid: -150, open: -70 }
      } else {
        return { close: -100, mid: -140, open: -65 }
      }
    } else if (type === 'promax') {
      // iPhone Pro Max - larger screen needs more dramatic shift
      if (hasPopFilter) {
        return { close: -120, mid: -165, open: -70 }
      } else {
        return { close: -120, mid: -160, open: -90 }
      }
    } else if (type === 'ipad') {
      // iPad - largest adjustment for pop filter visibility
      if (hasPopFilter) {
        return { close: -125, mid: -120, open: -15 }
      } else {
        return { close: -120, mid: -170, open: -55 }
      }
    } else if (type === 'desktop') {
      // Desktop - shift canvas up when pop filter is enabled
      if (hasPopFilter) {
        return { close: 0, mid: 0, open: 0 }
      } else {
        return { close: 0, mid: -50, open: 0 }
      }
    }
    return { close: -100, mid: -50, open: 0 }
  }


  // ===================== AUTO-FRAMING =====================
  // Instead of a table of hand-tuned numbers per phone, work the camera distance and the mic's height
  // out from the mic's real size and the space left above the footer: the mic (with the pop filter when
  // it's on) fills AUTO_FILL of that space and sits at AUTO_VBIAS down it, at each footer stop. Desktop
  // keeps its own numbers. Try it with ?framing=auto, go back with ?framing=classic (remembered).
  // The constants were set from the classic tables (their typical fill and position across phones).
  // Calibrated to Sammy's iPhone 13 Pro Max framing (the one that looked right), measured on 4 phone sizes:
  // there the mic fills ~95% of the free height (~86% with the pop filter) and sits a touch low.
  // The old tables put it at 100-107% on the iPhone 13, 16 Pro and SE, i.e. touching or behind the footer.
  const AUTO_FILL_H = 0.95 // share of the free height the mic fills
  const AUTO_FILL_H_POP = 0.86 // same, with the pop filter on
  const AUTO_FILL_W = 0.82 // share of the screen width it may fill (pop filter side views)
  const AUTO_VBIAS = 0.52 // where the mic's middle sits: 0 = top of the free space, 1 = footer
  const AUTO_GAP_TOP = 8 // px below the My Builds row
  const AUTO_GAP_BOTTOM = 12 // px above the footer
  const framingModeRef = useRef(null)
  if (framingModeRef.current === null) {
    try {
      const q = new URLSearchParams(window.location.search).get('framing')
      if (q === 'auto' || q === 'classic') localStorage.setItem('ohma_framing', q)
      framingModeRef.current = localStorage.getItem('ohma_framing') === 'classic' ? 'classic' : 'auto' // auto is the default
    } catch (e) {
      framingModeRef.current = 'auto'
    }
  }
  // FOOTER SPRING: the footer moved with about a dozen different springs (some slow and heavy, the drag
  // release bouncy) and the release speed was handed over in the wrong units, so a flick lost its
  // momentum. ?footer=new uses one spring everywhere (just short of bouncing) and carries the flick
  // speed into it. Now the default; ?footer=classic goes back (remembered).
  const footerModeRef = useRef(null)
  if (footerModeRef.current === null) {
    try {
      const q = new URLSearchParams(window.location.search).get('footer')
      if (q === 'new' || q === 'classic') localStorage.setItem('ohma_footer', q)
      footerModeRef.current = localStorage.getItem('ohma_footer') === 'classic' ? 'classic' : 'new' // new is the default
    } catch (e) {
      footerModeRef.current = 'new'
    }
  }
  const FOOTER_SPRING = { type: 'spring', stiffness: 380, damping: 38, restDelta: 0.05 }
  const footerSpring = (classic, velocity) => (footerModeRef.current === 'new' ? { ...FOOTER_SPRING, ...(velocity !== undefined ? { velocity } : {}) } : classic)
  const POP_FILTER_PARTS = ['pop_filter_frame', 'pop_filter_ring_back', 'pop_filter_ring_front', 'pop_filter_saati_back', 'pop_filter_saati_front', 'pop_filter_spacer']

  // The mic's size on screen, in world units, from the side view the builder opens on
  function micExtentsForView(popOn) {
    // modelRef is the mic's scene; fallback: the first non-mesh child of the group around <Suzi>
    // (the group's other child is the small light marker mesh)
    let root = null
    try {
      root = modelRef.current || (groupRef.current && groupRef.current.children.find((c) => !c.isMesh)) || null
    } catch (e) {
      return null // not created yet on the first render
    }
    if (!root || !sceneRef.current) return null
    root.updateWorldMatrix(true, true)
    const box = new THREE.Box3()
    root.traverse((o) => {
      if (!o.isMesh) return
      const isPop = POP_FILTER_PARTS.includes(o.name)
      if (isPop ? !popOn : !o.visible) return
      box.expandByObject(o)
    })
    if (box.isEmpty()) return null
    // whichever top screen is showing (a newly picked pattern may still be loading; all are the same size)
    const screenName = SCREEN_TYPES.map((t) => `${t}_top`).find((n) => sceneRef.current.getObjectByName(n)?.visible) || null
    const names = screenName ? [...CORE_CAMERA_NAMES, screenName, 'saati_top'] : [...CORE_CAMERA_NAMES, 'saati_top']
    const coreBox = computeBoxFromNames(sceneRef.current, names) || box
    const pose = computeCameraPoseFromBox(coreBox, { azimuthDeg: popOn ? -45 : -25, frameFill: DEFAULT_FRAME_FILL, vBias: DEFAULT_VBIAS, anchorK: DEFAULT_ANCHORK, fov: DEFAULT_FOV })
    const fwd = pose.lookAt.clone().sub(pose.position).normalize()
    const right = new THREE.Vector3().crossVectors(fwd, new THREE.Vector3(0, 1, 0)).normalize()
    const up = new THREE.Vector3().crossVectors(right, fwd).normalize()
    let minU = Infinity, maxU = -Infinity, maxR = 0
    for (let i = 0; i < 8; i++) {
      const p = new THREE.Vector3(i & 1 ? box.max.x : box.min.x, i & 2 ? box.max.y : box.min.y, i & 4 ? box.max.z : box.min.z).sub(pose.lookAt)
      const u = p.dot(up), r = p.dot(right)
      minU = Math.min(minU, u)
      maxU = Math.max(maxU, u)
      maxR = Math.max(maxR, Math.abs(r))
    }
    return { h: maxU - minU, c: (maxU + minU) / 2, w: 2 * maxR }
  }

  // Screen space: canvas size and position, and the free space between the My Builds row and the footer
  function framingGeometry() {
    const el = containerRef.current
    if (!el || !el.clientHeight || !el.clientWidth) return null
    const m = (el.style.transform || '').match(/translateY\(([-\d.]+)px\)/)
    const curT = m ? parseFloat(m[1]) : 0
    const canvasTop = el.getBoundingClientRect().top - curT
    const pill = document.querySelector('.mobile-save-builds-header')
    const pillBottom = pill ? pill.getBoundingClientRect().bottom : canvasTop + 60
    return { Hc: el.clientHeight, Wc: el.clientWidth, canvasTop, top: Math.max(canvasTop, pillBottom) + AUTO_GAP_TOP, vh: window.innerHeight }
  }
  const BASE_VIEW_K = 2 * Math.tan(THREE.MathUtils.degToRad(35 / 2)) // the framing numbers are for the 35 degree camera

  // Framing for ANY footer position, from the free space above the footer at that moment
  // (c = { ext, g, popOn } measured once per mic/screen; see autoFramingTables)
  function autoFrameAt(footerPercent, c) {
    const { ext, g } = c
    const fillH = c.popOn ? AUTO_FILL_H_POP : AUTO_FILL_H
    const availH = Math.max(40, (footerPercent / 100) * g.vh - AUTO_GAP_BOTTOM - g.top)
    const dH = (ext.h * g.Hc) / (BASE_VIEW_K * fillH * availH)
    const dW = (ext.w * g.Hc) / (BASE_VIEW_K * AUTO_FILL_W * g.Wc)
    const dist = Math.min(3.5, Math.max(0.5, dH, dW))
    const micCenterY = g.canvasTop + g.Hc / 2 - (ext.c * g.Hc) / (BASE_VIEW_K * dist)
    return { dist, translate: g.top + availH * AUTO_VBIAS - micCenterY }
  }
  // Last measurement for the live view, so dragging the footer can frame continuously (not 3-point tables)
  const autoCacheRef = useRef(null)

  function autoFramingTables(popOn, force = false) {
    if (!force && framingModeRef.current !== 'auto') return null
    try {
      if (getPhoneType() === 'desktop') return null
      const ext = micExtentsForView(!!popOn)
      const g = framingGeometry()
      if (!ext || !g) return null
      const c = { ext, g, popOn: !!popOn }
      if (!force) autoCacheRef.current = c
      // Sheet fully open: the mic is behind it, so keep the classic numbers there
      const cd = classicCameraDist({ popFilterEnabled: !!popOn })
      const ct = classicCanvasTranslate({ popFilterEnabled: !!popOn })
      const dist = { close: cd.close }, translate = { close: ct.close }
      ;['mid', 'open'].forEach((key) => {
        const f = autoFrameAt(snapPercents[key === 'mid' ? 1 : 2], c)
        dist[key] = Math.round(f.dist * 1000) / 1000
        translate[key] = Math.round(f.translate)
      })
      return { dist, translate }
    } catch (e) {
      return null
    }
  }
  // Continuous framing between the middle and collapsed footer positions (auto mode). Above the middle the
  // sheet covers the mic, so the stored close/mid numbers are blended as before.
  function liveAutoFrame(footerValue) {
    const c = autoCacheRef.current
    if (framingModeRef.current !== 'auto' || !c || c.popOn !== !!config.popFilterEnabled) return null
    if (!(footerValue >= snapPercents[1])) return null
    return autoFrameAt(Math.min(footerValue, snapPercents[2]), c)
  }

  const getInitialCameraDist = (overrideConfig = null) => {
    const auto = autoFramingTables((overrideConfig ?? config).popFilterEnabled)
    return auto ? auto.dist : classicCameraDist(overrideConfig)
  }
  const getInitialCanvasTranslate = (overrideConfig = null) => {
    const auto = autoFramingTables((overrideConfig ?? config).popFilterEnabled)
    return auto ? auto.translate : classicCanvasTranslate(overrideConfig)
  }

  // For tuning: how full and how high the classic tables put the mic on this phone, next to auto
  if (typeof window !== 'undefined') {
    window.__ohmaFraming = () => {
      const out = { mode: framingModeRef.current, phone: getPhoneType(), geometry: framingGeometry(), snaps: snapPercents }
      ;[false, true].forEach((popOn) => {
        const ext = micExtentsForView(popOn)
        const g = out.geometry
        const cd = classicCameraDist({ popFilterEnabled: popOn })
        const ct = classicCanvasTranslate({ popFilterEnabled: popOn })
        const ref = {}
        if (ext && g) {
          ;['close', 'mid', 'open'].forEach((key, i) => {
            const availH = Math.max(40, (snapPercents[i] / 100) * g.vh - AUTO_GAP_BOTTOM - g.top)
            const pxPerWorld = g.Hc / (BASE_VIEW_K * cd[key])
            const micCenterY = g.canvasTop + g.Hc / 2 - ext.c * pxPerWorld + ct[key]
            ref[key] = { fill: +((ext.h * pxPerWorld) / availH).toFixed(3), widthFill: +((ext.w * pxPerWorld) / g.Wc).toFixed(3), vbias: +((micCenterY - g.top) / availH).toFixed(3), micTop: Math.round(micCenterY - (ext.h * pxPerWorld) / 2), micBottom: Math.round(micCenterY + (ext.h * pxPerWorld) / 2), footerTop: Math.round((snapPercents[i] / 100) * g.vh) }
          })
        }
        out[popOn ? 'pop' : 'noPop'] = { ext, classic: { dist: cd, translate: ct, ref }, auto: autoFramingTables(popOn, true) }
      })
      return out
    }
  }
  // ===================== END AUTO-FRAMING =====================

  // Get camera Y based on footer position (interpolate between close/mid/open)

  // NEW APPROACH: Instead of moving camera, move the canvas container
  function getCanvasTranslateY(footerValue = null) {
    // Get current footer position if not provided
    const currentFooterPos = footerValue !== null ? footerValue : typeof sheetPercent?.get === 'function' ? sheetPercent.get() : snapPercents[1]
    const live = liveAutoFrame(currentFooterPos)
    if (live) return live.translate

    // Use debug slider values instead of hardcoded values
    const translateClose = debugCanvasTranslate.close
    const translateMid = debugCanvasTranslate.mid
    const translateOpen = debugCanvasTranslate.open

    // Interpolate based on footer position
    const closePoint = snapPercents[0]
    const midPoint = snapPercents[1]
    const openPoint = snapPercents[2]

    let result
    if (currentFooterPos <= closePoint) {
      result = translateClose
    } else if (currentFooterPos <= midPoint) {
      const ratio = (currentFooterPos - closePoint) / (midPoint - closePoint)
      result = translateClose + ratio * (translateMid - translateClose)
    } else if (currentFooterPos <= openPoint) {
      const ratio = (currentFooterPos - midPoint) / (openPoint - midPoint)
      result = translateMid + ratio * (translateOpen - translateMid)
    } else {
      result = translateOpen
    }

    return result
  }

  const [debugCameraDist, setDebugCameraDist] = useState(getInitialCameraDist())
  const [debugCanvasTranslate, setDebugCanvasTranslate] = useState(getInitialCanvasTranslate())
  const [fabricRotation, setFabricRotation] = useState(0) // Rotation in degrees
  const [renderTrigger, setRenderTrigger] = useState(0) // Force re-render when sliders change

  // Update camera settings when pop filter state changes on mobile AND desktop devices
  const prevPopFilterRef = useRef(config.popFilterEnabled)
  const isTweeningRef = useRef(false)
  const activeTweensRef = useRef([]) // Store active tweens so we can stop them
  const pendingDotUpdateRef = useRef(null) // Store pending dot update timeout so we can cancel it

  useEffect(() => {
    const newDist = getInitialCameraDist()
    const newTranslate = getInitialCanvasTranslate()

    // Track if this is the first time the effect runs
    const isFirstRun = prevPopFilterRef.current === undefined
    const isToggle = prevPopFilterRef.current !== config.popFilterEnabled
    prevPopFilterRef.current = config.popFilterEnabled

    // Always update the state values
    if (isFirstRun || !isToggle) {
      setDebugCameraDist(newDist)
      setDebugCanvasTranslate(newTranslate)
      return
    }

    // Skip the toggle animation when loading a saved design
    if (isLoadingDesignRef.current) {
      setDebugCameraDist(newDist)
      setDebugCanvasTranslate(newTranslate)
      return
    }

    // User toggled pop filter - animate the change
    if (isToggle) {
      // Stop any ongoing animations
      if (isTweeningRef.current && activeTweensRef.current.length > 0) {
        activeTweensRef.current.forEach((tween) => tween.stop())
        activeTweensRef.current = []
      }

      // Cancel any pending dot update
      if (pendingDotUpdateRef.current) {
        clearTimeout(pendingDotUpdateRef.current)
        pendingDotUpdateRef.current = null
      }

      isTweeningRef.current = true
      userZoomOffsetRef.current = 0 // reset user zoom when pop filter animates
      const currentFooterPos = typeof sheetPercent?.get === 'function' ? sheetPercent.get() : snapPercents[1]
      const closePoint = snapPercents[0]
      const midPoint = snapPercents[1]
      const openPoint = snapPercents[2]

      // Determine which position we're at
      let targetDistKey = 'mid'
      let targetTranslateKey = 'mid'
      if (currentFooterPos <= closePoint) {
        targetDistKey = 'close'
        targetTranslateKey = 'close'
      } else if (currentFooterPos <= midPoint) {
        targetDistKey = 'mid'
        targetTranslateKey = 'mid'
      } else {
        targetDistKey = 'open'
        targetTranslateKey = 'open'
      }

      // Calculate target distance for the new state
      const targetDist = newDist[targetDistKey]

      // Rotate to side view with the NEW distance

      // Clear all dots immediately to prevent multiple active dots
      document.querySelectorAll('.control-dots div').forEach((d) => {
        d.classList.remove('active')
        d.style.backgroundColor = ''
      })

      if (typeof rotateCameraToSide === 'function') {
        rotateCameraToSide(targetDist)
      }

      // Light the side dot once the turn is under way
      setTimeout(() => highlightDot('dots-side'), 400)

      // Set camera distance immediately (no animation to avoid jitter)
      setDebugCameraDist(newDist)

      // Animate canvas Y translate via direct DOM manipulation (smoother than state)
      if (containerRef.current) {
        const startY = debugCanvasTranslate[targetTranslateKey]
        const endY = newTranslate[targetTranslateKey]
        const canvasY = { y: startY }

        const translateTween = new TWEEN.Tween(canvasY)
          .to({ y: endY }, 800)
          .easing(motionEase(TWEEN.Easing.Quadratic.InOut))
          .onUpdate(() => {
            if (containerRef.current) {
              containerRef.current.style.transform = `translateY(${canvasY.y}px)`
            }
          })
          .onComplete(() => {
            isTweeningRef.current = false
            setDebugCanvasTranslate(newTranslate) // Update state at end
          })
          .onStop(() => {
            isTweeningRef.current = false
          })
          .start()
      } else {
        // Fallback if no container ref
        setDebugCanvasTranslate(newTranslate)
        setTimeout(() => {
          isTweeningRef.current = false
        }, 800)
      }
    }
  }, [config.popFilterEnabled, phoneType])

  // Simple initial value for canvas translate - will be updated via direct DOM manipulation
  const initialCanvasTranslate = getInitialCanvasTranslate()
  const currentCanvasTranslateY = initialCanvasTranslate.mid // Use mid position as initial

  // ========== END DEBUG STATES ==========

  const [groupY, setGroupY] = useState(0)
  const cameraAnimationRef = useRef(null) // Track ongoing camera animation

  // FIX: Adjust Y position for desktop
  useEffect(() => {
    if (phoneType === 'desktop') {
      setGroupY(-0.2)
    } else {
      setGroupY(0)
    }

    // Also reset camera when groupY changes to apply the new Y position (not at startup: FIRST VIEW places it)
    const atStartup = !introDoneRef.current
    setTimeout(() => {
      if (atStartup) return
      if (typeof resetCamera === 'function' && sceneRef.current) {
        const freshCameraDist = getInitialCameraDist()
        resetCamera(freshCameraDist)

        // Update the active dot to match the camera view (front view for desktop)
        if (typeof setActiveDot === 'function') {
          setActiveDot('dots', resetCamera)
        }
      }
    }, 100)
  }, [phoneType])
  // 2. (Optional) Recompute on resize so that if the screen size changes your offsets update.

  // Force re-render when canvas translation values change
  useEffect(() => {
    setRenderTrigger((prev) => prev + 1)
  }, [debugCanvasTranslate])

  // Update camera distance and canvas translation when phone type changes (resize)
  useEffect(() => {
    // Update debug sliders to match current phone type
    const newCameraDist = getInitialCameraDist()
    const newCanvasTranslate = getInitialCanvasTranslate()

    setDebugCameraDist(newCameraDist)
    setDebugCanvasTranslate(newCanvasTranslate)
    setRenderTrigger((prev) => prev + 1) // Force re-render so canvas transform updates!

    // Update canvas transform immediately too!
    if (containerRef.current) {
      const currentFooterPos = typeof sheetPercent?.get === 'function' ? sheetPercent.get() : snapPercents[1]
      const translateY = getCanvasTranslateY(currentFooterPos)
      containerRef.current.style.transform = `translateY(${translateY}px)`
    }

    // FIX: Reset camera to proper default position for this device type
    // Wait a tiny bit for groupY to update first (not at startup: FIRST VIEW places it)
    const atStartup = !introDoneRef.current
    setTimeout(() => {
      if (atStartup) return
      if (typeof resetCamera === 'function') {
        resetCamera(newCameraDist)

        // Update the active dot to match the camera view
        // Desktop uses front view (first dot), mobile stays on whichever dot is active
        if (phoneType === 'desktop' && typeof setActiveDot === 'function') {
          setActiveDot('dots', resetCamera)
        }
      }
    }, 50)
  }, [phoneType])

  // Handle viewport dimension changes (like iPad rotation where phoneType stays same)
  useEffect(() => {
    // Update canvas transform for new orientation
    if (containerRef.current) {
      const currentFooterPos = typeof sheetPercent?.get === 'function' ? sheetPercent.get() : snapPercents[1]
      const translateY = getCanvasTranslateY(currentFooterPos)
      containerRef.current.style.transform = `translateY(${translateY}px)`
    }
  }, [viewportDims.width, viewportDims.height])

  // Create a state variable to force a re-render
  const [updateCounter, setUpdateCounter] = useState(0)

  // 2. Compute the initial footer height based on the phone type.
  function computeInitialFooterHeight() {
    const phoneType = getPhoneType()

    if (phoneType === 'se') {
      return 68 // Use 32% for iPhone SE
    } else if (phoneType === 'regular') {
      return 67 // Use 33% for regular devices
    } else if (phoneType === 'desktop') {
      return 67 // Use 33% for regular devices
    } else if (phoneType === 'promax') {
      return 65 // Use 35% for Pro Max
    } else if (phoneType === 'ipad') {
      return 74
    } else {
      return 65 // Fallback value
    }
  }

  // ---------- ADD / REPLACE: base snap + derived snapPositions / snapPercents ----------
  const MID_LIFT_OFFSET = -4 // negative => mid moves up by 10 percentage points when debug active

  function clamp(v, lo = 0, hi = 100) {
    return Math.max(lo, Math.min(hi, v))
  }

  // debug flag - toggled by pressing "l"
  const [debugLiftActive, setDebugLiftActive] = useState(false)

  // single source of truth for base snaps depending on phoneType
  const baseSnap = phoneType === 'se' ? [13, 68, 85] : phoneType === 'regular' ? [13, 67, 85] : phoneType === 'ipad' ? [10, 74, 92] : [13, 65, 85]

  // derived arrays that the rest of your code can continue to use unchanged
  const snapPositions = React.useMemo(() => {
    const out = [...baseSnap]
    if (debugLiftActive) out[1] = clamp(out[1] + MID_LIFT_OFFSET)
    return out
  }, [baseSnap, debugLiftActive])

  const snapPercents = snapPositions // keep same name so nothing else needs to change
  const snapPercentsRef = useRef(snapPercents)
  snapPercentsRef.current = snapPercents
  // ---------- end replace ----------

  const initialFooterValue = snapPercents[1]
  const initialDistance = getCameraDistance(initialFooterValue)

  const initialFooterHeight = computeInitialFooterHeight()
  const [currentFooterHeight, setCurrentFooterHeight] = useState(initialFooterHeight)

  const screenHeight = typeof window !== 'undefined' ? window.innerHeight : 800
  const maxOffset = Math.max(0, screenHeight - 50) // never negative


  const [liveFooterHeight, setLiveFooterHeight] = useState(snapPositions[1])
  const [isFooterDragging, setIsFooterDragging] = useState(false)
  const isFooterDraggingRef = React.useRef(false)
  const isProgrammaticSnapRef = React.useRef(false) // true during X/save programmatic footer moves
  React.useEffect(() => {
    isFooterDraggingRef.current = isFooterDragging
  }, [isFooterDragging])
  const animateControls = useAnimation()
  const sheetPercent = useMotionValue(snapPercents[1])
  const controls = useAnimation()

  const translateY = useTransform(sheetPercent, (val) => {
    const px = (val / 100) * screenHeight
    return `translateY(${px}px)`
  })

  // 3) We'll store it in local state to conditionally render content (optional)
  const [livePercent, setLivePercent] = useState(snapPercents[1])
  const [isFooterMoving, setIsFooterMoving] = useState(false)
  const isFooterMovingRef = React.useRef(false)
  React.useEffect(() => {
    isFooterMovingRef.current = isFooterMoving
  }, [isFooterMoving])

  // Trigger share screenshot only when footer is fully open + share tab active
  // Using livePercent avoids firing mid-animation (which caused canvas flash/zoom)
  const prevLivePercentRef = React.useRef(snapPercents[1])
  React.useEffect(() => {
    const prev = prevLivePercentRef.current
    prevLivePercentRef.current = livePercent
    const isNowOpen = livePercent <= snapPercents[0] + 2
    const wasOpen = prev <= snapPercents[0] + 2
    // Just arrived at open position while on share tab
    if (isNowOpen && !wasOpen && mobileFooterTab === 'share') {
      setShareScreenshotUrl(null) // show spinner immediately
      setTimeout(() => {
        if (!isFooterMovingRef.current)
          generateShareScreenshotRef
            .current?.()
            .then((blob) => {
              if (blob) {
                const url = URL.createObjectURL(blob)
                setShareScreenshotUrl((prev) => {
                  if (prev?.startsWith('blob:')) URL.revokeObjectURL(prev)
                  return url
                })
                latestShareBlobRef.current = blob
                latestShareBlobAtRef.current = Date.now()
              }
            })
            .catch(() => {})
      }, 300)
    }
  }, [livePercent])

  // Also trigger when switching to share tab while footer is already open
  React.useEffect(() => {
    if (mobileFooterTab !== 'share') return
    const isOpen = livePercent <= (snapPercents[0] ?? 0) + 2
    if (!isOpen) return
    const t = setTimeout(() => {
      setShareScreenshotUrl(null) // show spinner immediately
      if (!isFooterMovingRef.current)
        generateShareScreenshotRef
          .current?.()
          .then((blob) => {
            if (blob) {
              const url = URL.createObjectURL(blob)
              setShareScreenshotUrl((prev) => {
                if (prev?.startsWith('blob:')) URL.revokeObjectURL(prev)
                return url
              })
              latestShareBlobRef.current = blob
              latestShareBlobAtRef.current = Date.now()
            }
          })
          .catch(() => {})
    }, 300)
    return () => clearTimeout(t)
  }, [mobileFooterTab])

  // FIX: Update canvas translation on resize and footer movement
  useEffect(() => {
    if (!containerRef.current) return

    // Subscribe to sheetPercent changes
    const unsubscribe = sheetPercent.onChange((value) => {
      const translateY = getCanvasTranslateY(value)
      if (containerRef.current) {
        containerRef.current.style.transform = `translateY(${translateY}px)`
      }
    })

    // Also update immediately on mount/phoneType change
    const currentFooterPos = sheetPercent.get()
    const translateY = getCanvasTranslateY(currentFooterPos)
    containerRef.current.style.transform = `translateY(${translateY}px)`

    return () => unsubscribe()
  }, [phoneType, debugCanvasTranslate])



  // ========== END DEBUG HELPER FUNCTIONS ==========

  const [debugPanelVisible, setDebugPanelVisible] = useState(false)
  const [fabricRotationDebug, setFabricRotationDebug] = useState(0)
  // ADD THESE - individual locks for each value:
  const [lockedValues, setLockedValues] = useState({
    cameraClose: false,
    cameraMid: false,
    cameraOpen: false,
    translateClose: false,
    translateMid: false,
    translateOpen: false
  })

  // Texture debug panel
  const [texDebugVisible, setTexDebugVisible] = useState(false)
  const [texDebugLocks, setTexDebugLocks] = useState({
    bodyRepeat: false,
    screenRepeat: false,
    popFilterRepeat: false,
    bodyNormal: false,
    screenNormal: false,
    popFilterNormal: false
  })
  const [texDebugValues, setTexDebugValues] = useState({
    bodyRepeat: 1,
    screenRepeat: 1,
    popFilterRepeat: 1,
    bodyNormal: 0,
    screenNormal: 0,
    popFilterNormal: 0
  })
  const [brassDebug, setBrassDebug] = useState({ repeatX: 0.3, repeatY: 0.1, rotation: 0 })
  const [matDebugVisible, setMatDebugVisible] = useState(false)
  const [lightDebugVisible, setLightDebugVisible] = useState(false)
  const [shadowsEnabled, setShadowsEnabled] = useState(true)
  // Virtual studio lighting, modeled on the product photo setup:
  // a soft overhead panel (LiteMat 4L) plus two white bounce cards ~45 degrees to each side.
  const [lightDebug, setLightDebug] = useState({
    ambientIntensity: 0,
    overheadIntensity: 14,
    overheadWidth: 5,
    overheadDepth: 2.5,
    overheadHeight: 5,
    overheadForward: 1.5,
    bounceIntensity: 7.5,
    bounceAngle: 60,
    bounceDistance: 4,
    bounceWidth: 2.5,
    bounceHeight: 5,
    roomLevel: 0.1,
    metalRoom: 0.2, // brightness of the white sweep the metals mirror
    metalFront: 0.85, // soft fill from the camera side, what front-facing metal mirrors
    metalBox: 0, metalBoxW: 2, metalBoxH: 3, metalBoxX: 0.6, metalBoxY: 1,
    keyIntensity: 1.95,
    // Back fill: lights only what faces the back, and only while the camera is behind the mic, so the
    // front and three-quarter views are untouched
    backFill: 2.7, // the back reads about as bright as the front (was a third darker at 1.2)
    backFillHeight: 4,
    keyCount: 1,
    keySpread: 6,
    keyForward: 5,
    keyHeight: 10,
    keyLayout: 0,
    shadowType: 1,
    shadowExtent: 0.4,
    shadowMapSize: 2048,
    shadowRadius: 16,
    shadowBias: -0.00005,
    shadowNormalBias: 0.004,
    exposure: 1.19, // keeps the calibrated colors where they were matched
    toneMapping: 0, // 0 = none (true-to-swatch color), 1 = ACES filmic
    ...debugOverrides('ld')
  })
  const matDebugRef = React.useRef(null)
  const [matDebug, setMatDebug] = useState({
    cerakoteRoughness: 0.75,
    cerakoteMetalness: 0.1,
    cerakoteEnvMap: 1,
    cerakoteClearcoat: 0.1,
    cerakoteClearcoatRoughness: 0.4,
    cerakoteSheen: 0.0,
    cerakoteSheenRoughness: 0.5,
    metalRoughness: 0.21,
    metalMetalness: 0.88,
    metalEnvMap: 0.95,
    metalClearcoat: 0.0,
    metalClearcoatRoughness: 0.1,
    brassBodyNormalScale: 0.03,
    brassBodyNormalTiling: 0.3,
    ...debugOverrides('md')
  })
  const [stainlessDebug, setStainlessDebug] = useState({ repeatX: 0.15, repeatY: 0.75, rotation: 3 })
  const texDebugRef = React.useRef(texDebugValues)
  React.useEffect(() => {
    texDebugRef.current = texDebugValues
  }, [texDebugValues])
  React.useEffect(() => {
    matDebugRef.current = matDebug
  }, [matDebug])

  function texDebugStep(key, delta) {
    setTexDebugValues((prev) => {
      if (texDebugLocks[key]) return prev
      const isNormal = key.includes('Normal')
      const step = isNormal ? 0.01 : 1
      const min = isNormal ? 0 : 1
      const max = isNormal ? 2 : 30
      const next = Math.round(Math.min(max, Math.max(min, prev[key] + delta * step)) * 100) / 100
      return { ...prev, [key]: next }
    })
  }

  function texDebugToggleLock(key) {
    setTexDebugLocks((prev) => ({ ...prev, [key]: !prev[key] }))
  }

  // Footer moved: move the camera and canvas with it. This used to subscribe on EVERY render and never
  // unsubscribe, so hundreds of copies piled up, each with outdated camera distances; together they
  // ratcheted the zoom closer (the mic got stuck big after dragging the footer down and back up).
  // Now one subscription, which always calls the latest handler.
  const footerChangeRef = useRef(null)
  useEffect(() => sheetPercent.onChange((val) => footerChangeRef.current && footerChangeRef.current(val)), [sheetPercent])
  footerChangeRef.current = (val) => {
    updateCameraDistanceInRealTime(val)

    // Update canvas translation in real-time too (skip during pop filter animation)
    if (containerRef.current && !isTweeningRef.current) {
      const translateY = getCanvasTranslateY(val)
      containerRef.current.style.transform = `translateY(${translateY}px)`
    }

    // Detect footer movement and hide dice icon
    setIsFooterMoving(true)
    clearTimeout(window.footerMoveTimeout)
    window.footerMoveTimeout = setTimeout(() => {
      setIsFooterMoving(false)
    }, 150)

    setLivePercent(val)
  }

  function nearestSnap(currentPct, snapPercents) {
    let closest = snapPercents[0]
    let minDist = Infinity
    for (let s of snapPercents) {
      const dist = Math.abs(currentPct - s)
      if (dist < minDist) {
        minDist = dist
        closest = s
      }
    }
    return closest
  }

  function handleDragEnd(info) {
    const currentVal = sheetPercent.get() // e.g. 28
    const velocity = info?.velocity?.y || 0 // Get drag velocity
    const absVelocity = Math.abs(velocity)

    // Scale snap jumps based on velocity - harder flicks skip more
    const baseThreshold = 800 // Min velocity to trigger flick (increased from 500)
    let targetSnap = nearestSnap(currentVal, snapPercents)

    if (absVelocity > baseThreshold) {
      // Calculate how many snap points to skip based on velocity
      // Need much higher velocity to skip positions now
      const skipCount = Math.min(Math.floor(absVelocity / 1500), snapPercents.length - 1)

      // Flicking down (positive velocity) - go to higher snap point(s)
      if (velocity > 0) {
        const higherSnaps = snapPercents.filter((s) => s > currentVal)
        if (higherSnaps.length > 0) {
          // Skip multiple snap points for hard flicks
          const skipIndex = Math.min(skipCount, higherSnaps.length - 1)
          targetSnap = higherSnaps[skipIndex]
        }
      }
      // Flicking up (negative velocity) - go to lower snap point(s)
      else {
        const lowerSnaps = snapPercents.filter((s) => s < currentVal)
        if (lowerSnaps.length > 0) {
          // Skip multiple snap points for hard flicks
          const skipIndex = Math.max(lowerSnaps.length - 1 - skipCount, 0)
          targetSnap = lowerSnaps[skipIndex]
        }
      }
    }

    // Dynamic spring values - less bouncy, more controlled
    const dynamicStiffness = Math.min(550 + absVelocity / 12, 700)
    const dynamicDamping = Math.max(32 - absVelocity / 600, 28) // Higher damping = less bounce

    // Don't reset userZoomOffset instantly — it causes a camera jump
    // It gets reset naturally in resetCamera if needed
    animate(
      sheetPercent,
      targetSnap,
      footerSpring(
        {
          type: 'spring',
          stiffness: dynamicStiffness,
          damping: dynamicDamping,
          velocity: velocity / 100
        },
        (velocity / (window.innerHeight || 800)) * 100 // drag speed is px/s, the footer moves in % of the screen
      )
    )

    // If dragged to top snap and share tab is active, show blue overlay
    if (targetSnap === snapPercents[0] && mobileFooterTab === 'share') {
      setShareTabActive(true)
    }
  }

  useEffect(() => {
    let timer = null
    const handleResize = () => {
      clearTimeout(timer)
      timer = setTimeout(() => {
        startTransition(() => {
          const newPhoneType = getPhoneType()
          setPhoneType(newPhoneType)
          setViewportDims({
            width: window.innerWidth,
            height: window.innerHeight
          })
        })
      }, 300)
    }

    window.addEventListener('resize', handleResize)
    window.addEventListener('orientationchange', handleResize)

    return () => {
      clearTimeout(timer)
      window.removeEventListener('resize', handleResize)
      window.removeEventListener('orientationchange', handleResize)
    }
  }, [])

  useEffect(() => {
    // Only when the width changes (rotating, resizing a window). iPhone Safari fires "resize" whenever the
    // address bar slides in or out, which used to snap the footer back to the middle mid-use.
    let lastWidth = window.innerWidth
    const handleFooterReset = () => {
      if (window.innerWidth === lastWidth) return
      lastWidth = window.innerWidth
      // back to this screen's middle stop (the old per-phone list had the iPad at 65 instead of its 74)
      sheetPercent.set(snapPercentsRef.current[1])
    }

    window.addEventListener('resize', handleFooterReset)
    return () => window.removeEventListener('resize', handleFooterReset)
  }, [sheetPercent])

  const handleShareClick = (e) => {
    if (e && typeof e.preventDefault === 'function') e.preventDefault()
    if (e && typeof e.stopPropagation === 'function') e.stopPropagation()
    // Open footer with this tab
    if (isMobile && typeof openFooterWithTab === 'function') {
      openFooterWithTab('share')
    }
    // Open desktop modal
    if (!isMobile && typeof openProductModalTab === 'function') {
      openProductModalTab('share')
    }
  }



  // Add iPad detection at the component level
  const [isIpad, setIsIpad] = React.useState(() => {
    if (typeof window === 'undefined') return false
    const isPortrait = window.innerHeight >= window.innerWidth
    return isPortrait && window.innerWidth >= 768
  })

  React.useEffect(() => {
    if (typeof window === 'undefined') return
    let timer = null

    function update() {
      clearTimeout(timer)
      timer = setTimeout(() => {
        startTransition(() => {
          const isPortrait = window.innerHeight >= window.innerWidth
          setIsIpad(isPortrait && window.innerWidth >= 768)
        })
      }, 300)
    }

    // initial run and listeners
    update()
    window.addEventListener('resize', update)
    window.addEventListener('orientationchange', update)
    return () => {
      clearTimeout(timer)
      window.removeEventListener('resize', update)
      window.removeEventListener('orientationchange', update)
    }
  }, [])

  function handleShareClickDesktop(e) {
    if (e && typeof e.preventDefault === 'function') e.preventDefault()

    // Open footer with this tab
    if (isMobile && typeof openFooterWithTab === 'function') {
      openFooterWithTab('share')
    }
    // Open desktop modal
    if (!isMobile && typeof openProductModalTab === 'function') {
      openProductModalTab('share')
    }
  }

  // Freeze the canvas with a fixed overlay image — prevents any flash or zoom glitch
  // while async operations (screenshot, save, footer animation) run underneath.
  // Pass durationMs=0 for manual control — call the returned unfreeze() when done.
  function freezeCanvas(durationMs = 1500) {
    const canvas = rendererRef.current?.domElement
    if (!canvas) return () => {}
    let div = null
    let timer = null
    try {
      const rect = canvas.getBoundingClientRect()
      const dataUrl = canvas.toDataURL('image/jpeg', 0.85)
      div = document.createElement('div')
      div.style.cssText = `
        position:fixed;
        left:${rect.left}px;
        top:${rect.top}px;
        width:${rect.width}px;
        height:${rect.height}px;
        z-index:1;
        pointer-events:none;
        background-image:url(${dataUrl});
        background-size:100% 100%;
        background-repeat:no-repeat;
      `
      document.body.appendChild(div)
      if (durationMs > 0) {
        timer = setTimeout(() => {
          if (div && div.parentNode) div.parentNode.removeChild(div)
          div = null
        }, durationMs)
      }
    } catch (e) {
      return () => {}
    }
    return () => {
      if (timer) clearTimeout(timer)
      if (div && div.parentNode) div.parentNode.removeChild(div)
      div = null
    }
  }

  function handleFooterToMiddleMobile(e) {
    if (e && typeof e.preventDefault === 'function') e.preventDefault()
    setShareTabActive(false)
    // Cancel any in-flight screenshot and update ref immediately
    _cancelGenerateRef.current = true
    _isGeneratingRef.current = false
    mobileFooterTabRef.current = 'overview'
    setMobileFooterTab('overview')
    try {
      window.localStorage.setItem('ohma:footer:lastTab', 'middle')
    } catch (err) {}
    const mid = snapPercents[1]
    // already there: nudge so the camera/canvas listeners still run
    if (Number(sheetPercent.get()) === Number(mid)) sheetPercent.set(mid + 0.0001)
    animate(sheetPercent, mid, footerSpring({ type: 'spring', stiffness: 400, damping: 60 }))
    setLivePercent(mid)
  }

  // compute the top snap once
  const topSnap = React.useMemo(() => {
    if (Array.isArray(snapPercents) && snapPercents.length) return snapPercents[0]
    if (Array.isArray(window.snapPositions) && window.snapPositions.length) return window.snapPositions[0]
    return 20 // fallback if nothing available
  }, [snapPercents])

  const TOLERANCE = 0.8
  const hideBetaOnTop = typeof livePercent === 'number' && livePercent <= topSnap + TOLERANCE

  let touchStartTime = 0 // Variable to track the touch start time try
  let activeTempMaterials = [] // Array to track all temporary materials
  let activeBlinkTimeout = null // Timeout to control the active blink group



  const meshToFooterMapping = {
    // Screen Color Group
    motif_top: { footerOption: '3. Screen Color', group: 'screenGroup' },
    motif_bottom: { footerOption: '3. Screen Color', group: 'screenGroup' },
    scales_top: { footerOption: '3. Screen Color', group: 'screenGroup' },
    scales_bottom: { footerOption: '3. Screen Color', group: 'screenGroup' },
    holes_top: { footerOption: '3. Screen Color', group: 'screenGroup' },
    holes_bottom: { footerOption: '3. Screen Color', group: 'screenGroup' },
    windows_top: { footerOption: '3. Screen Color', group: 'screenGroup' },
    windows_bottom: { footerOption: '3. Screen Color', group: 'screenGroup' },
    stripes_top: { footerOption: '3. Screen Color', group: 'screenGroup' },
    stripes_bottom: { footerOption: '3. Screen Color', group: 'screenGroup' },
    stripes_bottomback: { footerOption: '3. Screen Color', group: 'screenGroup' },
    windows_bottomback: { footerOption: '3. Screen Color', group: 'screenGroup' },
    motif_bottomback: { footerOption: '3. Screen Color', group: 'screenGroup' },
    holes_bottomback: { footerOption: '3. Screen Color', group: 'screenGroup' },
    scales_bottomback: { footerOption: '3. Screen Color', group: 'screenGroup' },
    logo: { footerOption: '3. Screen Color', group: 'screenGroup' },
    logo_brass: { footerOption: '3. Screen Color', group: 'screenGroup' },
    custom_logo: { footerOption: '3. Screen Color', group: 'screenGroup' },
    custom_logo_back: { footerOption: '3. Screen Color', group: 'screenGroup' },

    // Fabric Color Group
    saati_top: { footerOption: '4. Fabric Color', group: 'fabricGroup' },
    saati_bottom: { footerOption: '4. Fabric Color', group: 'fabricGroup' },

    // Body Color Group
    body: { footerOption: '1. Body Color', group: 'bodyGroup' },

    // Microphone Type Group
    ribbon: { footerOption: '6. Microphone Type', group: 'microphoneGroup' },
    capsule: { footerOption: '6. Microphone Type', group: 'microphoneGroup' }
  }


  const lightPosition = [5, 5, 10]
  const canvasRef = useRef() // Define canvasRef here
  const [sceneReady, setSceneReady] = useState(false) // State to track scene readiness
  // The loading screen stays until the mic itself (body plus this build's screen) is in, not just the canvas
  const [modelReady, setModelReady] = useState(false)
  // Desktop popup + color picker code: mount the popup on first open, and fetch both in the background
  // a few seconds after the mic shows so opening them stays instant
  const [productModalMounted, setProductModalMounted] = useState(false)

  // MOTION: crossfade the mic when its look changes. Before the new look is drawn, copy the current
  // picture onto a canvas laid over the 3D view, then fade that copy out once the new look is on screen.
  // Works the same for paints (which swap whole texture sets), fabric, screens, logo and mic type.
  const crossfadeRef = useRef(null)
  const appearanceKey = [
    config.color,
    config.screenColor,
    config.screenType,
    config.fabricColor,
    config.bottomFabricColor,
    config.bottomScreenType,
    config.microphoneType,
    config.popFilterColor,
    config.popFilterMaterial
  ].join('|')
  const lastAppearanceRef = useRef(null)
  const lastPopOnRef = useRef(config.popFilterEnabled)
  useLayoutEffect(() => {
    const prev = lastAppearanceRef.current
    lastAppearanceRef.current = appearanceKey
    const popToggled = lastPopOnRef.current !== config.popFilterEnabled
    lastPopOnRef.current = config.popFilterEnabled
    if (!MOTION_NEW || prev === null || prev === appearanceKey || !modelReady) return
    if (isCapturingRef.current) return
    // Turning the pop filter on/off also sets its color, and the camera moves at the same time: a still
    // picture fading over a moving mic looks like motion blur. Same whenever the camera is already moving.
    if (popToggled || isTweeningRef.current || isCameraMoving) return
    const gl = rendererRef.current?.domElement
    const host = gl?.parentElement
    if (!gl || !host || !gl.width || !gl.height) return
    const FADE_MS = 220
    const copy = document.createElement('canvas')
    copy.width = gl.width
    copy.height = gl.height
    try {
      copy.getContext('2d').drawImage(gl, 0, 0) // the last frame, still showing the old look
    } catch (e) {
      return
    }
    if (crossfadeRef.current) crossfadeRef.current.remove() // tapping fast: start from what's on screen now
    copy.style.cssText = `position:absolute;left:0;top:0;width:${gl.clientWidth}px;height:${gl.clientHeight}px;pointer-events:none;opacity:1;transition:opacity ${FADE_MS}ms ease-out;z-index:1`
    host.appendChild(copy)
    crossfadeRef.current = copy
    let started = false
    const fadeOut = () => {
      if (started) return
      started = true
      copy.style.opacity = '0'
      setTimeout(() => {
        copy.remove()
        if (crossfadeRef.current === copy) crossfadeRef.current = null
      }, FADE_MS + 60)
    }
    // two frames: the new look has been drawn underneath (new textures uploaded) before the copy fades;
    // never hold the old picture longer than 1.5s, even if frames stall
    requestAnimationFrame(() => requestAnimationFrame(fadeOut))
    setTimeout(fadeOut, 1500)
  }, [appearanceKey])
  useEffect(() => {
    if (modalOpen) setProductModalMounted(true)
  }, [modalOpen])
  useEffect(() => {
    if (!modelReady) return
    const t = setTimeout(() => {
      loadProductModal().catch(() => {})
      loadColorPicker().catch(() => {})
    }, 3000)
    return () => clearTimeout(t)
  }, [modelReady])
  // How long people wait for the 3D mic: seconds from opening the page until the mic shows (once per visit)
  const loadTrackedRef = useRef(false)
  useEffect(() => {
    if (loadTrackedRef.current || !sceneReady || !modelReady) return
    loadTrackedRef.current = true
    track('builder_loaded', { ...loadTimingProps(), device: window.innerWidth < 768 ? 'phone' : 'desktop' })
  }, [sceneReady, modelReady])
  // Auto-framing needs the mic in the scene: work it out once the mic shows, and again when the mic
  // type or screen size changes (the pop filter switch already recomputes). Not on a screen pattern
  // change: all screens are the same size, and re-measuring while a new pattern loads moved the camera.
  useEffect(() => {
    if (framingModeRef.current !== 'auto' || !sceneReady || !modelReady) return
    const t = setTimeout(() => {
      const auto = autoFramingTables(config.popFilterEnabled)
      if (!auto) return
      setDebugCameraDist(auto.dist)
      setDebugCanvasTranslate(auto.translate)
    }, 300)
    return () => clearTimeout(t)
  }, [sceneReady, modelReady, config.microphoneType, viewportDims.width, viewportDims.height])

  // FIRST VIEW: when the mic is ready, place it once, instantly, in its final spot (framing measured now, not
  // 300 ms later), keep the loading screen up until that frame is drawn, then fade the mic in. Before, several
  // startup resets on timers each started a 2.5 s glide, so the mic slid or dropped in, then jumped again when
  // auto-framing kicked in.
  const [introPlaced, setIntroPlaced] = useState(false)
  const introDoneRef = useRef(false)
  useEffect(() => {
    if (!sceneReady || !modelReady || introDoneRef.current) return
    introDoneRef.current = true
    try {
      const dist = getInitialCameraDist()
      const translate = getInitialCanvasTranslate()
      setDebugCameraDist(dist)
      setDebugCanvasTranslate(translate)
      // The mic fades in facing front, then turns to the side view (the second dot). Without motion
      // (reduced-motion setting), it simply starts at the side view.
      const reduceMotion = typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches
      const frontPose = reduceMotion ? null : framedPoseAt(0, { customDistances: dist })
      if (frontPose) {
        if (frontPose.dist) baseDistanceRef.current = frontPose.dist
        moveCameraToPose(frontPose, 0)
        const placedAt = Date.now()
        setTimeout(() => {
          // someone already tapped a dot or grabbed the mic: leave the camera to them
          if (lastDotPressTimestamp > placedAt || userGestureRef.current) return
          resetCameraSide(dist, 1600)
        }, 700)
      } else {
        resetCameraSide(dist, 0)
      }
      highlightDot('dots-side')
      if (containerRef.current) {
        const mid = snapPercents[1]
        const live = liveAutoFrame(mid)
        containerRef.current.style.transform = `translateY(${live ? live.translate : translate.mid}px)`
      }
    } catch (e) {
      // placement is best effort; the mic still shows
    }
    requestAnimationFrame(() => requestAnimationFrame(() => setIntroPlaced(true)))
  }, [sceneReady, modelReady])
  useEffect(() => {
    const el = rendererRef.current?.domElement
    if (el && introPlaced) el.style.opacity = '1'
  }, [introPlaced])

  useEffect(() => {
    const on = () => setModelReady(true)
    window.addEventListener('ohma:model-ready', on)
    const failsafe = setTimeout(on, 45000) // never trap someone behind the loader
    return () => {
      window.removeEventListener('ohma:model-ready', on)
      clearTimeout(failsafe)
    }
  }, [])
  const initialSetupDone = useRef(false) // Track if initial camera setup has completed
  const modelRef = useRef()
  const [isAnimating, setIsAnimating] = useState(false)

  const componentRef = useRef(null)

  /* ---------- ensure camera is positioned after scene loads ---------- */
  useEffect(() => {
    if (!sceneReady) return
    // resetCameraSide fires at 150ms via the layoutEffect — we fire later as safety net
    // Using a longer delay so we don't clobber resetCameraSide
    const t = setTimeout(() => {
      if (!modalOpenRef.current && cameraRef.current && orbitControlsRef.current) {
        // Only reset if camera is clearly in wrong position (e.g. at origin)
        const pos = cameraRef.current.position
        const dist = pos.length()
        if (dist < 0.1 || isNaN(dist)) {
          const freshCameraDist = getInitialCameraDist()
          resetCamera(freshCameraDist)
        }
      }
    }, 1500)
    return () => clearTimeout(t)
  }, [sceneReady])

  // Camera watchdog — detects bad state on load and auto-resets
  useEffect(() => {
    if (!sceneReady) return
    const check = () => {
      if (!cameraRef.current || !orbitControlsRef.current) return
      const pos = cameraRef.current.position
      if (isNaN(pos.x) || isNaN(pos.y) || isNaN(pos.z)) {
        resetCamera()
        return
      }
      const target = orbitControlsRef.current.target
      const dist = pos.distanceTo(target)
      const footerPercent = typeof livePercent === 'number' ? livePercent : snapPercents[1]
      const expectedDist = getCameraDistance(footerPercent)
      if (!expectedDist) return
      if (dist > expectedDist * 3 || dist < expectedDist * 0.3) {
        resetCamera()
      }
    }
    // Only check during initial load — not continuously (continuous checks
    // can fight with user interactions and cause unexpected camera jumps).
    // Checked when the mic has finished loading (the camera is placed for it then), and once at 2.5s
    // in case that already happened before this ran.
    const onReady = () => requestAnimationFrame(check)
    window.addEventListener('ohma:model-ready', onReady)
    const t2 = setTimeout(check, 2500)
    return () => {
      window.removeEventListener('ohma:model-ready', onReady)
      clearTimeout(t2)
    }
  }, [sceneReady])

  React.useEffect(() => {
    if (!sceneRef.current) return

    // expose only for debugging — remove when finished
    window.__OHMA_DEBUG = {
      scene: sceneRef.current,
      camera: cameraRef.current,
      renderer: rendererRef.current, // from onCreated gl
      model: modelRef.current
    }

    // Expose refs for Instagram screenshot (runs after model loads)
    window.__ohmaSceneRef = sceneRef
    window.__ohmaModelRef = modelRef

    return () => {
      delete window.__OHMA_DEBUG
      delete window.__ohmaSceneRef
      delete window.__ohmaModelRef
    }
  }, [sceneReady, sceneRef.current, modelRef.current])

  const [shopName, setShopName] = useState('your-store.myshopify.com')

  // "Body Color" draggable state and references
  const colorScrollRef = useRef(null) // Ref for color options container
  const [isColorDragging, setIsColorDragging] = useState(false) // Color dragging state
  const [startColorX, setStartColorX] = useState(0) // Starting X for color drag
  const [scrollLeftColor, setScrollLeftColor] = useState(0) // Initial scroll position for color drag

  // "Screen Color" draggable state and references
  const screenColorScrollRef = useRef(null) // Ref for screen color options container
  const [isScreenColorDragging, setIsScreenColorDragging] = useState(false) // Screen color dragging state
  const [startScreenColorX, setStartScreenColorX] = useState(0) // Starting X for screen color drag
  const [scrollLeftScreenColor, setScrollLeftScreenColor] = useState(0) // Initial scroll position for screen color

  useLayoutEffect(() => {
    if (!cameraRef.current || !orbitControlsRef.current || phoneType === 'unknown') return

    // Only do the direct camera positioning on the FIRST setup
    if (!initialSetupDone.current) {
      const val = snapPercents[1] // mid position
      const desiredDist = getCameraDistance(val)

      // Keep camera and orbit target centered at Y=0
      orbitControlsRef.current.target.set(0, 0, 0)
      cameraRef.current.position.set(0, 0, desiredDist)
      cameraRef.current.lookAt(0, 0, 0)
      cameraRef.current.updateProjectionMatrix()
      orbitControlsRef.current.update()

      // Call resetCameraSide after a delay to ensure groupY has updated
      setTimeout(() => {
        if (introDoneRef.current) return // the mic is already placed (FIRST VIEW); don't start a glide over it
        if (typeof resetCameraSide === 'function') {
          resetCameraSide()
        }
        // Set the side dot as active
        if (typeof setActiveDot === 'function') {
          setActiveDot('dots-side', resetCameraSide)
        }

        // After resetCameraSide completes, check if pop filter is enabled and apply settings
        setTimeout(() => {
          if (introDoneRef.current) return // FIRST VIEW already framed it for the pop filter
          if (config.popFilterEnabled) {
            const newDist = getInitialCameraDist()
            const newTranslate = getInitialCanvasTranslate()

            // Animate camera distance change
            if (cameraRef.current && orbitControlsRef.current) {
              const currentFooterPos = typeof sheetPercent?.get === 'function' ? sheetPercent.get() : snapPercents[1]
              const currentDist = cameraRef.current.position.distanceTo(orbitControlsRef.current.target)
              const targetDist = getCameraDistance(currentFooterPos, newDist)

              const tweenObj = { dist: currentDist }
              new TWEEN.Tween(tweenObj)
                .to({ dist: targetDist }, 800)
                .easing(motionEase(TWEEN.Easing.Quadratic.InOut))
                .onUpdate(() => {
                  if (cameraRef.current && orbitControlsRef.current) {
                    const target = orbitControlsRef.current.target.clone()
                    const direction = cameraRef.current.position.clone().sub(target).normalize()
                    const newPosition = target.clone().add(direction.multiplyScalar(tweenObj.dist))
                    cameraRef.current.position.copy(newPosition)
                    cameraRef.current.updateProjectionMatrix()
                    orbitControlsRef.current.update()
                  }
                })
                .start()
            }

            // Animate canvas translation change
            if (containerRef.current) {
              const currentFooterPos = typeof sheetPercent?.get === 'function' ? sheetPercent.get() : snapPercents[1]
              const currentTranslateY = parseFloat(containerRef.current.style.transform.match(/-?\d+\.?\d*/)?.[0] || 0)
              const targetTranslateY = getCanvasTranslateY(currentFooterPos)

              const tweenTranslate = { y: currentTranslateY }
              new TWEEN.Tween(tweenTranslate)
                .to({ y: targetTranslateY }, 800)
                .easing(motionEase(TWEEN.Easing.Quadratic.InOut))
                .onUpdate(() => {
                  if (containerRef.current) {
                    containerRef.current.style.transform = `translateY(${tweenTranslate.y}px)`
                  }
                })
                .start()
            }

            // Update the debug states after animation
            setDebugCameraDist(newDist)
            setDebugCanvasTranslate(newTranslate)
          }
        }, 2500) // Wait 2.5 seconds after resetCameraSide for config to load
      }, 150)

      initialSetupDone.current = true
    }
  }, [phoneType, sceneReady])

  // Separate effect: When debug values change, update camera at CURRENT footer position
  useEffect(() => {
    if (!cameraRef.current || !orbitControlsRef.current) return
    // a camera move under way (pop filter turn, view dot) already heads for the new distance; snapping
    // the distance here made it jump for a frame
    if (cameraAnimationRef.current) return
    // Skip during design load — handleLoadDesign does its own clean reset after settling
    if (isLoadingDesignRef.current) return

    // Get current footer position from sheetPercent
    const currentFooterPos = typeof sheetPercent?.get === 'function' ? sheetPercent.get() : snapPercents[1]

    // Recalculate distance only (Y handled by container translation)
    const desiredDist = getCameraDistance(currentFooterPos)

    // Preserve current camera direction, only update distance
    const target = orbitControlsRef.current.target.clone()
    const direction = cameraRef.current.position.clone().sub(target).normalize()
    const newPosition = target.clone().add(direction.multiplyScalar(desiredDist))
    cameraRef.current.position.copy(newPosition)
    cameraRef.current.updateProjectionMatrix()
    orbitControlsRef.current.update()
  }, [debugCameraDist]) // Only run when debug distance changes

  const prevBoxRef = useRef(null)
  const prevScreenTypeRef = useRef(null)
  const prevBottomScreenRef = useRef(null)
  const prevMicrophoneRef = useRef(null)
  const isInitialMountRef = useRef(true) // Track if this is the first render

  useEffect(() => {
    if (!sceneRef.current || !cameraRef.current || !orbitControlsRef.current) return
    const root = sceneRef.current

    // Build names used to compute the box (screen-aware)
    const screenName = config?.screenType ? `${config.screenType}_top` : null
    const names = screenName ? [...CORE_CAMERA_NAMES, screenName, 'saati_top'] : [...CORE_CAMERA_NAMES, 'saati_top']

    const t = setTimeout(() => {
      // compute visible-first bounding box, fallback to full box
      let box = computeBoxFromNames(root, names, { requireVisible: true })
      if (box.isEmpty()) box = computeBoxFromNames(root, names, { requireVisible: false })
      if (!box || box.isEmpty()) return

      // Detect whether the *selection change* is one that we want to ignore recentering for:
      const screenChanged = prevScreenTypeRef.current && prevScreenTypeRef.current !== config.screenType
      const bottomChanged = prevBottomScreenRef.current && prevBottomScreenRef.current !== bottomScreenType
      const micChanged = prevMicrophoneRef.current && prevMicrophoneRef.current !== config.microphoneType

      // If the change came from screen/logo/mic, DO NOTHING (Option B)
      if (screenChanged || bottomChanged || micChanged) {
        // update the prev refs so subsequent changes are detected correctly
        prevScreenTypeRef.current = config.screenType
        prevBottomScreenRef.current = bottomScreenType
        prevMicrophoneRef.current = config.microphoneType
        prevBoxRef.current = box.clone()
        return
      }

      // Otherwise: small-guard logic — only recenter for meaningful geometry changes.
      // If there's no previous box, do the initial recenter.
      if (!prevBoxRef.current) {
        prevBoxRef.current = box.clone()
        recenterControlsToBox(box, orbitControlsRef.current, cameraRef.current, true)
        prevScreenTypeRef.current = config.screenType
        prevBottomScreenRef.current = bottomScreenType
        prevMicrophoneRef.current = config.microphoneType
        return
      }

      // Compare previous box -> new box; only recenter if the box changed noticeably.
      const prev = prevBoxRef.current
      const prevSize = new THREE.Vector3()
      prev.getSize(prevSize)
      const newSize = new THREE.Vector3()
      box.getSize(newSize)
      const prevLen = prevSize.length()
      const newLen = newSize.length()
      const sizeChange = prevLen > 1e-6 ? Math.abs(newLen - prevLen) / prevLen : 0

      const prevCenter = new THREE.Vector3()
      prev.getCenter(prevCenter)
      const newCenter = new THREE.Vector3()
      box.getCenter(newCenter)
      const centerShift = newCenter.distanceTo(prevCenter)
      const centerRel = prevLen > 1e-6 ? centerShift / prevLen : centerShift

      const SIZE_THRESH = 0.05 // 5% change in diagonal -> meaningful
      const CENTER_THRESH = 0.04 // 4% shift of diagonal -> meaningful

      if (sizeChange >= SIZE_THRESH || centerRel >= CENTER_THRESH) {
        // meaningful geometry change -> recenter normally (this will update baseDistanceRef)
        recenterControlsToBox(box, orbitControlsRef.current, cameraRef.current, true)
        prevBoxRef.current = box.clone()
      }

      // update prev selection refs for future diffs
      prevScreenTypeRef.current = config.screenType
      prevBottomScreenRef.current = bottomScreenType
      prevMicrophoneRef.current = config.microphoneType
    }, 0)

    return () => clearTimeout(t)
  }, [sceneReady, config.screenType, bottomScreenType, config.microphoneType])

  // Desktop mic type button hover — hides saati/surround/screens like mobile mic type tab
  const [isMicButtonHovered, setIsMicButtonHovered] = useState(false)

  // Event handlers for "Body Color" dragging functionality
  const [colorDragged, setColorDragged] = useState(false)
  const [isDraggingColors, setIsDraggingColors] = useState(false)
  const colorDragStart = useRef({ x: 0, scrollLeft: 0 })

  const handleColorMouseDown = (e) => {
    setIsDraggingColors(true)
    setColorDragged(false)
    colorDragStart.current = {
      x: e.pageX || e.touches?.[0]?.pageX,
      scrollLeft: colorScrollRef.current?.scrollLeft || 0
    }
  }

  const handleColorMouseMove = (e) => {
    if (!isDraggingColors) return
    const x = e.pageX || e.touches?.[0]?.pageX
    const walk = x - colorDragStart.current.x

    if (Math.abs(walk) > 5) {
      setColorDragged(true)
    }
  }

  const handleColorMouseUpOrLeave = () => {
    setIsDraggingColors(false)
  }

  const [screenColorDragged, setScreenColorDragged] = useState(false)
  const [isDraggingScreenColors, setIsDraggingScreenColors] = useState(false)
  const screenColorDragStart = useRef({ x: 0, scrollLeft: 0 })

  const handleScreenColorMouseDown = (e) => {
    setIsDraggingScreenColors(true)
    setScreenColorDragged(false)
    screenColorDragStart.current = {
      x: e.pageX || e.touches?.[0]?.pageX,
      scrollLeft: screenColorScrollRef.current?.scrollLeft || 0
    }
  }

  const handleScreenColorMouseMove = (e) => {
    if (!isDraggingScreenColors) return
    const x = e.pageX || e.touches?.[0]?.pageX
    const walk = x - screenColorDragStart.current.x

    if (Math.abs(walk) > 5) {
      setScreenColorDragged(true)
    }
  }

  const handleScreenColorMouseUpOrLeave = () => {
    setIsDraggingScreenColors(false)
  }

  // Simple scroll centering - only called on click, not drag
  const centerSelectedColor = () => {
    setTimeout(() => {
      if (!colorScrollRef.current) return
      const container = colorScrollRef.current
      const active = container.querySelector('.color-circle.active')
      if (!active) return

      container.scrollTo({
        left: active.offsetLeft - container.offsetWidth / 2 + active.offsetWidth / 2,
        behavior: 'smooth'
      })
    }, 0)
  }

  const centerSelectedScreenColor = () => {
    setTimeout(() => {
      if (!screenColorScrollRef.current) return
      const container = screenColorScrollRef.current
      const active = container.querySelector('.color-circle.active')
      if (!active) return

      container.scrollTo({
        left: active.offsetLeft - container.offsetWidth / 2 + active.offsetWidth / 2,
        behavior: 'smooth'
      })
    }, 0)
  }



  useEffect(() => {
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = '' // Reset on cleanup
    }
  }, [])

  // Desktop footer scaling using CSS zoom (most reliable!)
  useLayoutEffect(() => {
    const scaleDesktopFooter = () => {
      const footer = document.querySelector('.desktop-footer-pad-width')

      if (!footer) {
        return
      }

      // Only scale in landscape mode (desktop)
      if (window.innerHeight >= window.innerWidth) {
        footer.style.zoom = '1'
        return
      }

      const viewportWidth = window.innerWidth

      // More subtle scaling that still prevents wrapping
      // At 1024px: 0.75 (75% size)
      // At 1400px: 1.0 (normal)
      // At 1920px: 1.2 (120% size)
      const scale = Math.max(0.75, Math.min(1.2, 0.48 + viewportWidth * 0.00037))

      // Use zoom - this ACTUALLY resizes everything
      footer.style.zoom = scale.toString()
    }

    // The footer may not be on the page yet on the first pass: retry each frame until it is (max ~2s),
    // instead of guessing with timers at 100/500/1000ms
    let raf = 0
    let tries = 0
    const scaleWhenReady = () => {
      if (document.querySelector('.desktop-footer-pad-width')) scaleDesktopFooter()
      else if (tries++ < 120) raf = requestAnimationFrame(scaleWhenReady)
    }
    scaleWhenReady()

    window.addEventListener('resize', scaleDesktopFooter)
    return () => {
      cancelAnimationFrame(raf)
      window.removeEventListener('resize', scaleDesktopFooter)
    }
  }, [isDesktop])

  const swayCamera = () => {
    if (!cameraRef.current || isAnimating) {
      return // Prevent animation from starting again if it's already running
    }

    setIsAnimating(true)
    const initialRotationZ = cameraRef.current.rotation.z
    const swayAmount = Math.PI / 6 // This represents 30 degrees

    let cycles = 2 // Total back and forth motions

    const animateSway = (direction) => {
      if (cycles === 0) {
        new TWEEN.Tween(cameraRef.current.rotation)
          .to({ z: initialRotationZ }, 75)
          .easing(TWEEN.Easing.Quadratic.Out)
          .onComplete(() => setIsAnimating(false)) // Reset animation flag when done
          .start()
        return
      }

      const targetZ = direction ? initialRotationZ + swayAmount : initialRotationZ - swayAmount
      const tween = new TWEEN.Tween(cameraRef.current.rotation)
        .to({ z: targetZ }, 75)
        .easing(TWEEN.Easing.Quadratic.InOut)
        .onComplete(() => {
          cycles--
          animateSway(!direction) // Switch direction each call
        })

      tween.start()
    }

    animateSway(true) // Start the animation with the first sway to the right
  }

  const randomizeSelection = (options, weights = {}) => {
    let weightedOptions = []

    // Build an array containing each option repeated according to its weight
    Object.entries(options).forEach(([key, value]) => {
      const repeats = weights[key] || 1 // Use the weight if specified, or default to 1
      for (let i = 0; i < repeats; i++) {
        weightedOptions.push(key)
      }
    })

    // Select a random option from the weighted array
    const randomIndex = Math.floor(Math.random() * weightedOptions.length)
    return options[weightedOptions[randomIndex]]
  }

  const weights = {
    motif: 40, // Motif should come up 35% of the time
    stripes: 10,
    windows: 10,
    scales: 10,
    holes: 10
  }

  const newScreenType = randomizeSelection(Object.keys(screenTypeOptions), weights)

  // helper: keys is an array of candidate keys,
  // weightsMap is an object like { Motif: 5, Scales: 1, Windows: 1 }
  function weightedRandomFromMap(keys, weightsMap = {}, defaultWeight = 1) {
    const entries = keys.map((k) => ({ key: k, weight: weightsMap[k] ?? defaultWeight }))
    const total = entries.reduce((s, e) => s + e.weight, 0)
    let r = Math.random() * total
    for (const e of entries) {
      r -= e.weight
      if (r <= 0) return e.key
    }
    // fallback
    return entries[entries.length - 1].key
  }

  const handleDiceClick = () => {
    const newColor = randomizeSelection(Object.keys(colorOptions))

    // define weights for screen types
    const screenTypeWeights = {
      motif: 10, // higher weight => more likely
      scales: 1,
      stripes: 1,
      holes: 1,
      windows: 1
    }
    const newScreenType = weightedRandomFromMap(Object.keys(screenTypeOptions), screenTypeWeights)

    // optionally, weight specific screen colors only when Motif is chosen
    let newScreenColor
    if (newScreenType === 'Motif') {
      const motifColorWeights = { black: 3, gold: 1, silver: 1 } // example
      newScreenColor = weightedRandomFromMap(Object.keys(screenColorOptions), motifColorWeights)
    } else {
      newScreenColor = randomizeSelection(Object.keys(screenColorOptions))
    }
    const newFabricColor = randomizeSelection(Object.keys(fabricColorOptions))

    // Bottom fabric: usually matches top when top is black/white, but sometimes choose "none"
    const isBlackOrWhite = newFabricColor === '#FFFFFF' || newFabricColor === '#1a1a1a' || newFabricColor === '#1A1A1A'

    // probability that bottom fabric becomes "none" even if top is black/white
    const bottomNoneChanceWhenBW = 0.25 // 25% (change this number if you want)

    const newBottomFabricColor = isBlackOrWhite && Math.random() >= bottomNoneChanceWhenBW ? newFabricColor : 'none'

    // If pop filter is enabled with custom material (metal), update it to match new body color
    let newPopFilterColor = config.popFilterColor
    if (config.popFilterEnabled && config.popFilterMaterial === 'metal') {
      // Custom metal pop filter - lighter gray for brass/black, otherwise match body
      newPopFilterColor =
        newColor === 'Brass' || newColor === '#d5b173' || newColor === '#1A1A1A' || newColor === '#232221' || newColor === '#1c1d1d' ? '#1c1d1d' : newColor
    }

    // If shock mount is enabled with custom color, update it to match new body color
    let newShockMountColor = config.shockMountColor
    if (config.shockMountEnabled && config.shockMountColor !== '#1A1A1A') {
      // Custom shock mount - update to match body color (or black if body is brass)
      newShockMountColor = newColor === 'Brass' || newColor === '#d5b173' ? '#1A1A1A' : newColor
    }

    // Single setConfig call to avoid multiple history entries
    setConfig({
      ...config,
      color: newColor,
      screenType: newScreenType,
      screenColor: newScreenColor,
      fabricColor: newFabricColor,
      bottomFabricColor: newBottomFabricColor,
      popFilterColor: newPopFilterColor,
      shockMountColor: newShockMountColor
    })

    swayCamera()
  }



  const [isOrbiting, setIsOrbiting] = useState(false)
  const dotAfterDragTimerRef = useRef(null)
  const popOnForDotsRef = useRef(config.popFilterEnabled)
  popOnForDotsRef.current = config.popFilterEnabled
  const updateBodySelectability = (selectable) => {
    document.body.classList.toggle('no-select', !selectable)
  }

  useEffect(() => {
    if (!orbitControlsRef.current) return

    orbitControlsRef.current.mouseButtons = {
      LEFT: THREE.MOUSE.ROTATE,
      MIDDLE: null,
      RIGHT: null // Disable right-click dragging to rotate or pan
    }

    const controls = orbitControlsRef.current
    const onStart = () => {
      userGestureRef.current = true
      setIsOrbiting(true)
      updateBodySelectability(false)
    }
    const onEnd = () => {
      userGestureRef.current = false
      setIsOrbiting(false)
      updateBodySelectability(true)
      // after the spin has glided to a stop, light the nearest view's dot (or none if it's between views)
      clearTimeout(dotAfterDragTimerRef.current)
      const endedAt = Date.now()
      dotAfterDragTimerRef.current = setTimeout(() => {
        const cam = cameraRef.current
        const ctl = orbitControlsRef.current
        // skip if a dot was tapped since (its own highlight wins) or the camera is in a move
        if (!cam || !ctl || isTweeningRef.current || userGestureRef.current || cameraAnimationRef.current || lastDotPressTimestamp > endedAt) return
        const az = (Math.atan2(cam.position.x - ctl.target.x, cam.position.z - ctl.target.z) * 180) / Math.PI
        const views = [
          ['dots', 0],
          ['dots-side', sideAzimuthFor(popOnForDotsRef.current)],
          ['dots-back', 180]
        ]
        let best = null
        views.forEach(([cls, a]) => {
          let d = Math.abs(az - a) % 360
          if (d > 180) d = 360 - d
          if (!best || d < best.d) best = { cls, d }
        })
        highlightDot(best && best.d <= 15 ? best.cls : null)
      }, 1200)
    }

    controls.addEventListener('start', onStart)
    controls.addEventListener('end', onEnd)

    return () => {
      if (controls) {
        controls.removeEventListener('start', onStart)
        controls.removeEventListener('end', onEnd)
      }
    }
  }, [orbitControlsRef.current]) // Adding orbitControlsRef.current as a dependency


  const handleSceneCreated = () => {
    setSceneReady(true) // Set scene readiness state to true
  }



  const displayNames = {
    '1. Body Color': 'BODY COLOR', // Custom display name
    '2. Screen Color': 'SCREEN COLOR', // Custom display name
    '3. Screen Type': 'SCREEN TYPE', // Custom display name
    '4. Fabric Color': 'FABRIC', // Custom display name
    '5. Bottom Fabric Color': 'LOGO FILL', // Custom display name
    '5. Bottom Screen Type': 'LOGO', // Custom display name
    '6. Microphone Type': 'MIC TYPE',
    '7. Pop Filter': 'POP FILTER',
    '8. Shock Mount': 'SHOCK MOUNT' // Custom display name

    // Add additional mappings here as needed
  }

  const scrollRef = useRef(null) // Ref to access the scrollable footer
  const [isDragging, setIsDragging] = useState(false)
  const [dragged, setDragged] = useState(false) // Track if a significant drag occurred
  const [startX, setStartX] = useState(0)
  const [scrollLeft, setScrollLeft] = useState(0)
  const [selectionCanScrollMore, setSelectionCanScrollMore] = useState(false)


  const handleOptionClick = (optionLabel) => {
    if (!dragged) {
      setIsVisible(false) // Start fade out to hide the tip
      setActiveSelection(optionLabel) // Update the active selection instantly

      setTimeout(() => {
        // Determine and set the new tip after a brief delay
        const tips = selectionTips[optionLabel] || []
        if (tips.length > 0) {
          const randomTipIndex = Math.floor(Math.random() * tips.length)
          setActiveTip(tips[randomTipIndex])
        } else {
          setActiveTip('No tips available for this selection.')
        }
        setIsVisible(true) // Fade in the new tip
      }, 300) // Match this delay with the CSS fade duration for a smooth transition

      // Center the color circles based on the selected option
      if (optionLabel === '1. Body Color') {
        centerSelectedColor()
      } else if (optionLabel === '2. Screen Color') {
        centerSelectedScreenColor()
      }
    }
    setDragged(false) // Reset after processing the click
  }



  const [activeSelection, setActiveSelection] = useState('1. Body Color')

  // Center colors when footer goes to mid position or footer option changes
  const prevLivePercent = useRef(livePercent)

  useEffect(() => {
    // Check if footer just moved to mid position (transition happened)
    const wasMidPosition = prevLivePercent.current >= 60 && prevLivePercent.current <= 75
    const isMidPosition = livePercent >= 60 && livePercent <= 75

    // Only center if we just arrived at mid position (not already there)
    if (isMidPosition && !wasMidPosition) {
      // Center the appropriate color based on active selection
      if (activeSelection === '1. Body Color') {
        setTimeout(() => centerSelectedColor(), 100)
      } else if (activeSelection === '2. Screen Color') {
        setTimeout(() => centerSelectedScreenColor(), 100)
      }
    }

    prevLivePercent.current = livePercent
  }, [livePercent, activeSelection])

  // shareTabActive is cleared by onDismissOverlay when switching away from share tab

  // Center colors on initial page load
  useEffect(() => {
    setTimeout(() => {
      centerSelectedColor()
      centerSelectedScreenColor()
    }, 500) // Delay to ensure DOM is ready
  }, [])

  // Center body color whenever it changes (including randomize)
  useEffect(() => {
    if (color) {
      centerSelectedColor()
    }
  }, [color])

  // Center screen color whenever it changes (including randomize)
  useEffect(() => {
    if (screenColor) {
      centerSelectedScreenColor()
    }
  }, [screenColor])

  useEffect(() => {
    const el = colorScrollRef.current || document.querySelector('.color-options-container')
    if (el) setScrollLeftColor(el.scrollLeft || 0)

    const el2 = screenColorScrollRef.current || document.querySelector('.screen-options-container')
    if (el2) setScrollLeftScreenColor(el2.scrollLeft || 0)

    const el3 = scrollRef.current || document.querySelector('.scrollable-footer')
    if (el3) setScrollLeft(el3.scrollLeft || 0)
  }, []) // runs exactly once after first mount

  // add this near your other useEffect hooks in the same component
  useEffect(() => {
    if (typeof window === 'undefined') return
    const body = document.body

    if (showCustomColorPicker) {
      body.classList.add('picker-open')
      // lock scrolling but don't change body position
      body.style.overflow = 'hidden'
      // keep overscroll behavior conservative
      body.style.overscrollBehavior = 'contain'
    } else {
      body.classList.remove('picker-open')
      body.style.overflow = ''
      body.style.overscrollBehavior = ''
    }

    return () => {
      body.classList.remove('picker-open')
      body.style.overflow = ''
      body.style.overscrollBehavior = ''
    }
  }, [showCustomColorPicker])

  const selectionTips = {
    '1. Body Color': ['Choosing a lighter color can highlight the contours of the design.', 'Darker colors might hide fingerprints and smudges better.'],
    '2. Screen Type': ['Screens with larger openings can enhance sound clarity.', 'Tighter screens might be better for more controlled sound output.'],
    '3. Screen Color': ['Match the screen color with the body for a uniform look.', 'Contrasting colors can make the screen stand out more visually.'],
    '4. Fabric Color': ['Light fabric colors are great for vibrant setups.', 'Darker fabrics can give a more sophisticated look.'],
    '5. Bottom Screen Type': ['Adding a logo can increase brand visibility.', 'Patterned screens can add a unique touch to your design.'],
    '6. Microphone Type': [
      'Condenser microphones are excellent for capturing vocal subtleties.',
      'Dynamic microphones are better for high-volume environments.'
    ]
  }

  const tipsArray = [
    'Six month interest-free payments when you choose Shop Pay.',
    'Free shipping on orders over $150.',
    'Sign up for our newsletter and get 10% off.',
    'New customers receive a free gift with their first purchase.'
    // ... any number of tips you want to cycle through
  ]

  const [isVisible, setIsVisible] = useState(true)

  const initialTip = selectionTips[Object.keys(selectionTips)[0]][0] || 'Welcome to the tips section!' // Default tip to avoid empty content
  const [activeTip, setActiveTip] = useState(initialTip)

  useEffect(() => {
    const changeTip = () => {
      setIsVisible(false) // Start fade out

      setTimeout(() => {
        const tips = selectionTips[activeSelection] || [] // Ensure it defaults to an empty array
        if (tips.length > 0) {
          const randomTipIndex = Math.floor(Math.random() * tips.length)
          setActiveTip(tips[randomTipIndex]) // Set new tip from available tips
        } else {
          setActiveTip('No tips available for this selection.') // Default message when no tips are available
        }
        setIsVisible(true) // Start fade in
      }, 500)
    }

    const intervalId = setInterval(changeTip, 8000)
    return () => clearInterval(intervalId)
  }, [activeSelection])

  useEffect(() => {
    const selectionOrder = [
      '1. Body Color',
      '2. Screen Type',
      '3. Screen Color',
      '4. Fabric Color',
      '5. Bottom Screen Type',
      '6. Microphone Type',
      '7. Pop Filter',
      '8. Shock Mount'
    ]

    const handleKeyDown = (event) => {
      if (!DEV_TOOLS) return // arrow keys scroll the page and '1' would wipe a customer's saved builds
      if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        event.preventDefault() // Prevent the default arrow key scrolling behavior
        const currentIndex = selectionOrder.indexOf(activeSelection)
        const newIndex =
          event.key === 'ArrowDown' ? (currentIndex + 1) % selectionOrder.length : (currentIndex - 1 + selectionOrder.length) % selectionOrder.length
        setActiveSelection(selectionOrder[newIndex])
      }
      // Dev tool: press '1' to delete all local builds
      if (event.key === '1' && !event.metaKey && !event.ctrlKey && !event.altKey) {
        const target = event.target
        if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA')) return
        try {
          localStorage.removeItem('ohma_local_builds')
          builderBus.refreshBuilds?.()
        } catch (e) {}
      }
    }

    window.addEventListener('keydown', handleKeyDown)

    return () => {
      window.removeEventListener('keydown', handleKeyDown)
    }
  }, [activeSelection]) // Dependency array to update if activeSelection changes



  const selectionRef = useRef(null)
  const scrollIndicatorRef = useRef(null)


  /* ------------------ Centralized lift/revert logic (paste whole block) ------------------ */

  /* ------------------ Centralized lift/revert logic (replacement block) ------------------ */

  /* Config + refs */
  const LIFT_IGNORE_MS = 600 // ms to ignore revert after programmatic lift

  const pendingRevertRef = useRef(false) // deferred revert marker
  const liftIgnoreUntilRef = useRef(0) // timestamp until which revert is ignored
  const liftSourceRef = useRef(null) // 'keyboard' | 'pop' | 'shock' | null

  // previous values for toggles (to detect edges)
  const prevPopRef = useRef(popFilterEnabled)
  const prevShockRef = useRef(shockMountEnabled)


  /* Small helper to get base mid percent.
   This reads your snapPercents/baseSnap/snapPositions whichever shape you have.
*/
  function getBaseMid() {
    // Prefer explicit baseSnap array if present
    if (Array.isArray(baseSnap) && typeof baseSnap[1] === 'number') return baseSnap[1]
    // fall back to snapPercents (if it's an array)
    if (Array.isArray(snapPercents) && typeof snapPercents[1] === 'number') return snapPercents[1]
    // fall back to snapPositions
    if (Array.isArray(snapPositions) && typeof snapPositions[1] === 'number') return snapPositions[1]
    // last resort: 35%
    return 35
  }

  /* Centralized animator + state setter.
   setLift(true, source) -> animate to lifted mid and set debug flag
   setLift(false, 'revert') -> animate to base mid and clear debug flag
*/
  function setLift(enable, source = 'programmatic') {
    const baseMid = getBaseMid()
    if (typeof baseMid !== 'number') return

    const target = enable ? Math.min(100, Math.max(0, baseMid + MID_LIFT_OFFSET)) : baseMid

    // No-op if we're already at the target (avoid re-triggering animate)
    try {
      const current = typeof sheetPercent?.get === 'function' ? sheetPercent.get() : null
      if (typeof current === 'number' && Math.abs(current - target) < 0.5) {
        // still set the flags/timestamps so behavior is consistent
        liftIgnoreUntilRef.current = Date.now() + LIFT_IGNORE_MS
        liftSourceRef.current = enable ? source : null
        setDebugLiftActive(enable)
        return
      }
    } catch (err) {
      // ignore
    }

    // Try to animate; fallback to instant set
    try {
      animate(sheetPercent, target, footerSpring({ type: 'spring', stiffness: 300, damping: 28 }))
      liftIgnoreUntilRef.current = Date.now() + LIFT_IGNORE_MS
    } catch (err) {
      if (sheetPercent && typeof sheetPercent.set === 'function') {
        sheetPercent.set(target)
        liftIgnoreUntilRef.current = Date.now() + LIFT_IGNORE_MS
      }
    }

    liftSourceRef.current = enable ? source : null
    setDebugLiftActive(enable)
    // small informational log (only on real transitions)
    // // console.log(`[lift] ${enable ? 'ENGAGE' : 'DISMISS'} from ${source} -> target ${target}`)
  }


  /* ------------------ 1) keyboard 'l' handler (toggle lift) ------------------ */
  useEffect(() => {
    function onKeyDown(e) {
      if (!DEV_TOOLS) return
      const tag = (document.activeElement && document.activeElement.tagName) || ''
      const isEditable = tag === 'INPUT' || tag === 'TEXTAREA' || document.activeElement?.isContentEditable
      if (isEditable || e.ctrlKey || e.metaKey || e.altKey) return

      if (e.key && e.key.toLowerCase() === 'l') {
        e.preventDefault()
        if (isDragging) {
          // user interacting -> ignore toggle
          return
        }
        setLift(!debugLiftActive, 'keyboard')
      }
    }

    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
    // We want to re-register only when these primitives change:
  }, [debugLiftActive, isDragging])

  /* ------------------ 2) watch pop/shock toggles and auto-engage when toggled ON ------------------ */
  // DISABLED: All lift functionality removed - footer should not auto-adjust for accessories
  // This includes:
  // - Auto-lift when toggling pop filter/shock mount ON
  // - Re-engaging lift when navigating back to accessory tabs
  // - Reverting lift when navigating away or disabling accessories
  // - Flushing deferred reverts after interactions

  /* ------------------ end replacement block ------------------ */

  const scrollToActiveSelection = () => {
    // Ensure the update happens after state change renders
    setTimeout(() => {
      const activeElement = document.querySelector('.footer-option.active')
      if (activeElement && scrollRef.current) {
        scrollRef.current.scrollTo({
          left: activeElement.offsetLeft - scrollRef.current.offsetWidth / 2 + activeElement.offsetWidth / 2,
          behavior: 'smooth'
        })
      }
    }, 0)
  }

  useEffect(() => {
    scrollToActiveSelection()
  }, [activeSelection]) // This will trigger when activeSelection changes

  const handleBottomScreenTypeSelect = (value, label) => {
    setBottomScreenType(value)
  }

  const [error, setError] = useState(null)

  const id = useID() // Get ID from context
  const pickerPanelRef = useRef(null)
  const customCircleRef = useRef(null)
  const customScreenCircleRef = useRef(null)
  const [customPickerPosition, setCustomPickerPosition] = useState({ top: 0, left: 0 })
  const [customScreenPickerPosition, setCustomScreenPickerPosition] = useState({ top: 0, left: 0 })

  // requires: const pickerPanelRef = useRef(null)
  // and showCustomColorPicker in scope

  // 1) put this near your other option maps
  const accessoryCatalog = {
    popFilter: {
      label: 'Pop Filter',
      price: 49,
      meshName: 'pop_filter',
      colorOptions: ['#000000', '#FFFFFF', '#91C3DE']
    },
    shockMount: {
      label: 'Shock Mount',
      price: 89,
      meshName: 'shock_mount',
      colorOptions: ['#000000', '#FFFFFF', '#91C3DE']
    }
  }

  // 2) component state lives in the same component that renders selectionContent
  // AccessoryPanel function uses config directly for undo/redo support


  const selectionContent = {
    // Inside your component
    '1. Body Color': (() => {

      // inside your component (replace your current isMobile const)
      const [isMobile, setIsMobile] = React.useState(() => {
        if (typeof window === 'undefined') return true
        return window.innerHeight >= window.innerWidth // true for all portrait
      })

      React.useEffect(() => {
        if (typeof window === 'undefined') return

        function update() {
          const isPortrait = window.innerHeight >= window.innerWidth
          setIsMobile(isPortrait)
        }

        // initial run and listeners
        update()
        window.addEventListener('resize', update)
        window.addEventListener('orientationchange', update)
        return () => {
          window.removeEventListener('resize', update)
          window.removeEventListener('orientationchange', update)
        }
      }, [])

      const isCustomColorChosen = !Object.keys(colorOptions).includes(color) && color !== 'Brass' && color !== '#d5b173' && color !== 'Stainless Steel'
      // Only show the color in the custom circle if the user has actually picked a custom hex.
      // When no custom color is chosen, keep it transparent so the rainbow gradient shows cleanly.
      const customCircleColor = isCustomColorChosen ? color : showCustomColorPicker ? draftColor : 'transparent'
      const isActiveCustom = showCustomColorPicker || isCustomColorChosen

      const circleSize = isIpad ? 55 : isMobile ? 32 : 55
      // ringThickness controls the external outline / box-shadow spread for the active state (unchanged)
      const ringThickness = 3
      // rainbowInnerThickness controls how thick the rainbow band is inward. increase this to make inner band thicker.
      const rainbowInnerThickness = 8
      const presetInnerInset = 2.5
      // white core must account for the rainbowInnerThickness so the visible rainbow band grows inward only
      const whiteCoreSize = `${circleSize - rainbowInnerThickness * 2}px`

      const leftIndent = 0 // pixels - match whatever margin the header uses

      const gap = 12
      const maxCols = 4.5
      const extraPad = 0
      const visualPadding = ringThickness * 2 + presetInnerInset * 2 + 20
      // small tolerance to absorb rounding, scrollbar, outline, etc
      const tolerance = 10

      const renderedWidth = circleSize + visualPadding
      const maxColsWidth = `calc(${renderedWidth * maxCols}px + ${gap * (maxCols - 1)}px + ${extraPad}px + ${tolerance}px)`
      // ensure at least 4 columns on desktop
      const minColsDesktop = 4
      const minColsWidth = `calc(${circleSize}px * ${minColsDesktop} + ${gap}px * (${minColsDesktop} - 1))`

      // center shell, outer reserves layout space but is not rounded
      const circleContainerStyle = {
        width: circleSize, // visible circle box size (px)
        height: circleSize,
        flex: `0 0 ${renderedWidth}px`, // reserved space for layout
        minWidth: `${renderedWidth}px`,
        boxSizing: 'border-box',
        position: 'relative',
        display: 'flex', // center the visual circle
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'pointer',
        borderRadius: 0, // outer shell is rectangular so inner can stay circular
        background: 'transparent',
        margin: 0,
        marginBottom: isMobile ? 0 : 8 // mobile 0, desktop 8px
      }
      const innerDiameter = circleSize - presetInnerInset * 2 // numeric px for the visible inner circle

      // recent colors (MRU, up to 3)
      const [recentColors, setRecentColors] = React.useState(() => {
        try {
          const raw = typeof window !== 'undefined' ? localStorage.getItem('ohma_recent_colors_v1') : null
          return raw ? JSON.parse(raw) : []
        } catch (e) {
          return []
        }
      })

      // hovered swatch simple state - keeps parity with previous logic
      const [hoveredSwatch, setHoveredSwatch] = React.useState(null)

      // floating tooltip state - position in viewport, text, visible
      const [floatingTooltip, setFloatingTooltip] = React.useState({
        visible: false,
        text: '',
        left: 0,
        top: 0
      })

      // hide tooltip on scroll/resize to avoid stale positions and layout shifts
      React.useEffect(() => {
        function hide() {
          setFloatingTooltip((t) => (t.visible ? { visible: false, text: '', left: 0, top: 0 } : t))
          setHoveredSwatch(null)
        }
        window.addEventListener('scroll', hide, { passive: true })
        window.addEventListener('resize', hide)
        return () => {
          window.removeEventListener('scroll', hide)
          window.removeEventListener('resize', hide)
        }
      }, [])

      React.useEffect(() => {
        try {
          if (typeof window !== 'undefined') {
            localStorage.setItem('ohma_recent_colors_v1', JSON.stringify(recentColors.slice(0, 3)))
          }
        } catch (e) {}
      }, [recentColors])

      function pushRecentColor(hex) {
        if (!hex) return
        const normalized = hex.toLowerCase()
        setRecentColors((prev) => {
          const deduped = prev.filter((c) => c.toLowerCase() !== normalized)
          deduped.unshift(normalized)
          return deduped.slice(0, 3)
        })
      }

      function commitDraftColorAndClose() {
        setConfig({ ...config, color: draftColor })
        setDraftColor(draftColor)
        pushRecentColor(draftColor)
        setShowCustomColorPicker(false)
      }
      _commitBodyPickerRef.current = commitDraftColorAndClose

      // ---------- tiny safe centering helper - only called after user clicks ----------
      function scrollActiveIntoViewLocal() {
        if (typeof window === 'undefined') return
        const container = colorScrollRef && colorScrollRef.current ? colorScrollRef.current : document.querySelector('.color-options-container')
        if (!container) return

        const active = container.querySelector('.color-circle.active')
        if (!active) return

        try {
          if (typeof active.scrollIntoView === 'function') {
            active.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' })
            return
          }
          const containerRect = container.getBoundingClientRect()
          const activeRect = active.getBoundingClientRect()
          const offset = activeRect.left + activeRect.width / 2 - (containerRect.left + containerRect.width / 2)
          container.scrollLeft += offset
        } catch (err) {
          // best-effort, swallow errors
        }
      }
      // -------------------------------------------------------------------------------

      // Create floating tooltip and show it. Accept the DOM target to compute position.
      function showFloatingTooltip(text, targetEl) {
        if (!targetEl || typeof targetEl.getBoundingClientRect !== 'function') {
          setFloatingTooltip({ visible: true, text, left: window.innerWidth / 2, top: 100 })
          return
        }

        // Get the zoom level from the footer
        const footer = document.querySelector('.desktop-footer-pad-width')
        const zoom = footer ? parseFloat(footer.style.zoom || '1') : 1

        const r = targetEl.getBoundingClientRect()
        // Adjust for zoom: divide by zoom to get actual position
        const left = (r.left + r.width / 2) / zoom
        const top = (r.bottom + 8) / zoom
        setFloatingTooltip({ visible: true, text, left, top })
      }
      function hideFloatingTooltip() {
        setFloatingTooltip({ visible: false, text: '', left: 0, top: 0 })
      }

      // local CSS that prevents in-place tooltips from causing layout shifts.
      const localTooltipCss = `
        .recent-swatch-row { display: inline-flex; gap: 12px; align-items: center; margin-left: 12px; }
        /* hide per-swatch tooltip elements from layout and accessibility, floating one is authoritative */
        .color-circle .tooltip { display: none !important; }
        /* keep any old :hover rules from creating layout shifts */
        .color-circle { will-change: transform; }
        /* floating tooltip style (visual fallback) */
        .ohma-floating-tooltip {
          position: fixed;
          left: 0;
          top: 0;
          transform: translate(-50%, 0);
          padding: 6px 10px;
          border-radius: 6px;
          font-family: 'Jost', system-ui, -apple-system, 'Segoe UI', Roboto, 'Helvetica Neue', Arial;
          font-size: 13px;
          font-weight: 600;
          white-space: nowrap;
          background: #000;
          color: #fff;
          /* box-shadow removed */
          z-index: 1000000;
          pointer-events: none;
          transition: opacity 160ms cubic-bezier(0.2,0.9,0.2,1), transform 160ms cubic-bezier(0.2,0.9,0.2,1);
          max-width: 220px;
          overflow: hidden;
          text-overflow: ellipsis;
        }
      `

      return (
        <div className="option-content" style={{ position: 'relative' }}>
          <style dangerouslySetInnerHTML={{ __html: localTooltipCss }} />

          <div className="header-and-selected" style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '0' }}>
            <div className="option-header header-body-color" style={{ display: 'flex', alignItems: 'center', gap: 0, whiteSpace: 'nowrap', flexShrink: 0 }}>
              <h1 style={{ margin: 0, lineHeight: 1, display: 'inline-block', whiteSpace: 'nowrap' }}>{isMobile ? 'Body' : 'Body Color'}</h1>

              {/* Info icon button */}
              <button
                type="button"
                className="info-button"
                aria-label="More about body color options"
                onClick={() => {
                  // Open footer with this tab and current body color
                  if (isMobile && typeof openFooterWithTab === 'function') {
                    openFooterWithTab('colorGuide', color)
                  }
                  // Open desktop modal with current body color
                  if (!isMobile && typeof openProductModalTab === 'function') {
                    openProductModalTab('colorGuide', color)
                  }
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault()
                    e.currentTarget.click()
                  }
                }}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 22,
                  height: 22,
                  padding: 0,
                  borderRadius: 6,
                  border: 'none',
                  background: 'transparent',
                  color: 'var(--muted, #6b7280)',
                  cursor: 'pointer',
                  marginLeft: 8,
                  verticalAlign: 'middle',
                  lineHeight: 1,
                  boxShadow: 'none'
                }}>
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  aria-hidden="true"
                  style={{ pointerEvents: 'none' }}>
                  <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="1.4" />
                  <circle cx="12" cy="8" r="1" fill="currentColor" />
                  <path d="M11.25 11.5h1.5v5h-1.5z" fill="currentColor" />
                </svg>
              </button>
            </div>
            <div className="selected-option">
              <div className="selected-color">{colorOptions[color] || color}</div>
            </div>
          </div>

          <div className="color-options gradient-wrapper" style={{ position: 'relative' }}>
            {/* Left gradient - only show when scrolled */}
            {scrollLeftColor > 10 && (
              <div
                style={{
                  position: 'absolute',
                  left: 0,
                  top: 0,
                  bottom: 0,
                  width: '30px',
                  background: 'linear-gradient(to right, #ffffff 0%, rgba(255, 255, 255, 0) 100%)',
                  pointerEvents: 'none',
                  zIndex: 10
                }}
              />
            )}
            {/* Right gradient - only show when can scroll more */}
            {colorScrollRef.current && scrollLeftColor < colorScrollRef.current.scrollWidth - colorScrollRef.current.clientWidth - 10 && (
              <div
                className="color-circles-right-gradient"
                style={{
                  position: 'absolute',
                  right: 0,
                  top: 0,
                  bottom: 0,
                  width: '30px',
                  background: 'linear-gradient(to left, #ffffff 0%, rgba(255, 255, 255, 0) 100%)',
                  pointerEvents: 'none',
                  zIndex: 10
                }}
              />
            )}
            {/* Desktop custom picker + recent swatches row */}
            {!isMobile && (
              <div style={{ display: 'flex', alignItems: 'center', marginBottom: 8, marginLeft: 15 }}>
                <div
                  ref={customCircleRef}
                  className={`color-circle custom ${isActiveCustom ? 'active' : ''}`}
                  onClick={(e) => {
                    e.stopPropagation()
                    setDraftColor(config && config.color ? config.color : draftColor)

                    // Calculate position for the picker relative to circle
                    const rect = e.currentTarget.getBoundingClientRect()
                    setCustomPickerPosition({
                      top: rect.bottom + window.scrollY + 10, // Account for scroll
                      left: rect.left + window.scrollX + 10 // Account for scroll, slight right offset
                    })

                    setShowCustomColorPicker((v) => !v)
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault()
                      setDraftColor(config && config.color ? config.color : draftColor)
                      setShowCustomColorPicker((v) => !v)
                    }
                  }}
                  role="button"
                  tabIndex={0}
                  title="Match Color"
                  aria-label={`Custom color picker, ${customCircleColor}`}
                  // pointer events for hover/focus
                  onPointerEnter={(e) => {
                    setHoveredSwatch('custom')
                    showFloatingTooltip('Custom Color + $199', e.currentTarget)
                  }}
                  onPointerLeave={() => {
                    setHoveredSwatch(null)
                    hideFloatingTooltip()
                  }}
                  onFocus={(e) => {
                    setHoveredSwatch('custom')
                    showFloatingTooltip('Custom Color + $199', e.currentTarget)
                  }}
                  onBlur={() => {
                    setHoveredSwatch(null)
                    hideFloatingTooltip()
                  }}
                  style={{
                    ...circleContainerStyle,
                    flex: `0 0 ${circleSize}px`,
                    minWidth: `${circleSize}px`,
                    width: circleSize,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderRadius: 0,
                    background: 'transparent'
                  }}>
                  <div
                    className="picker-rainbow"
                    aria-hidden="true"
                    style={{
                      position: 'absolute',
                      left: '50%',
                      top: '50%',
                      transform: 'translate(-50%, -50%)',
                      width: circleSize, // exact visible diameter
                      height: circleSize,
                      borderRadius: '50%',
                      padding: rainbowInnerThickness,
                      background: 'conic-gradient(#ff0054, #ff8c00, #ffd600, #3cd500, #00d3c8, #0077ff, #a000ff, #ff0054)',
                      boxSizing: 'border-box',
                      pointerEvents: 'none',
                      zIndex: 0
                    }}
                  />
                  <div
                    className="picker-active-ring"
                    aria-hidden="true"
                    style={{
                      position: 'absolute',
                      left: '50%',
                      top: '50%',
                      transform: 'translate(-50%, -50%)',
                      width: circleSize,
                      height: circleSize,
                      borderRadius: '50%',
                      pointerEvents: 'none',
                      zIndex: 5,
                      boxSizing: 'border-box',
                      boxShadow: 'none',
                      opacity: isActiveCustom ? 1 : 0,
                      transition: 'box-shadow 400ms ease, opacity 200ms ease'
                    }}
                  />
                  <div
                    className="picker-white-core"
                    aria-hidden="true"
                    style={{
                      position: 'absolute',
                      left: '50%',
                      top: '50%',
                      transform: 'translate(-50%, -50%)',
                      width: whiteCoreSize,
                      height: whiteCoreSize,
                      borderRadius: '50%',
                      background: '#fff',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      boxSizing: 'border-box',
                      zIndex: 1,
                      pointerEvents: 'none',
                      boxShadow: 'none',
                      transition: 'transform 500ms cubic-bezier(0.2,0.9,0.2,1)'
                    }}>
                    <div
                      className="picker-color-core"
                      aria-hidden="true"
                      style={{
                        width: '55%',
                        height: '55%',
                        borderRadius: '50%',
                        background: customCircleColor,
                        pointerEvents: 'none',
                        zIndex: 2,
                        transition: 'transform 500ms cubic-bezier(0.2,0.9,0.2,1)'
                      }}
                    />
                    <svg
                      className="picker-plus"
                      width="14"
                      height="14"
                      viewBox="0 0 14 14"
                      xmlns="http://www.w3.org/2000/svg"
                      aria-hidden="true"
                      style={{
                        position: 'absolute',
                        left: '50%',
                        top: '50%',
                        transform: 'translate(-50%, -50%)',
                        zIndex: 3,
                        pointerEvents: 'none'
                      }}>
                      <line x1="7" y1="3" x2="7" y2="11" stroke="#111" strokeWidth="1.6" strokeLinecap="round" />
                      <line x1="3" y1="7" x2="11" y2="7" stroke="#111" strokeWidth="1.6" strokeLinecap="round" />
                    </svg>
                  </div>
                </div>

                <div className="recent-swatch-row" aria-hidden={recentColors.length === 0}>
                  {recentColors
                    .filter((c) => !Object.keys(colorOptions).some((k) => k.toLowerCase() === c.toLowerCase()))
                    .map((c) => {
                      const isActiveRecent = config && config.color && config.color.toLowerCase() === c.toLowerCase()
                      return (
                        <div
                          key={c}
                          className={`color-circle ${isActiveRecent ? 'active' : ''}`}
                          onClick={() => {
                            // Update pop filter color if it's a custom metal pop filter
                            let newPopFilterColor = config.popFilterColor
                            if (config.popFilterEnabled && config.popFilterMaterial === 'metal') {
                              newPopFilterColor = c === 'Brass' || c === '#1A1A1A' || c === '#1c1d1d' ? '#1c1d1d' : c
                            }

                            setConfig({ ...config, color: c, popFilterColor: newPopFilterColor })
                            setDraftColor(c)
                            // only perform scroll centering on mobile
                            if (isMobile) requestAnimationFrame(scrollActiveIntoViewLocal)
                          }}
                          title={c}
                          role="button"
                          tabIndex={0}
                          onPointerEnter={(e) => {
                            setHoveredSwatch(c)
                            showFloatingTooltip(c, e.currentTarget)
                          }}
                          onPointerLeave={() => {
                            setHoveredSwatch(null)
                            hideFloatingTooltip()
                          }}
                          onFocus={(e) => {
                            setHoveredSwatch(c)
                            showFloatingTooltip(c, e.currentTarget)
                          }}
                          onBlur={() => {
                            setHoveredSwatch(null)
                            hideFloatingTooltip()
                          }}
                          style={{
                            position: 'relative',
                            width: circleSize,
                            height: circleSize,
                            flex: '0 0 auto',
                            boxSizing: 'border-box',
                            borderRadius: '50%',
                            cursor: 'pointer',
                            background: 'transparent'
                          }}>
                          <div
                            className="inner"
                            style={{
                              position: 'absolute',
                              inset: presetInnerInset,
                              borderRadius: '50%',
                              pointerEvents: 'none',
                              backgroundColor: c
                            }}
                          />
                          <div className="tooltip" aria-hidden="true" />
                        </div>
                      )
                    })}
                </div>

                {recentColors.length === 0 ? (
                  <div
                    style={{
                      height: circleSize, // SAME value used for the swatch circle
                      display: 'flex',
                      alignItems: 'center',
                      marginLeft: 12
                    }}>
                    <h2
                      style={{
                        margin: 0,
                        fontSize: 16,
                        color: '#333',
                        fontWeight: 500,
                        fontFamily: 'Jost',
                        userSelect: 'none',
                        lineHeight: 1,
                        marginTop: -8
                      }}>
                      Choose any color
                    </h2>
                  </div>
                ) : null}
              </div>
            )}

            {/* color grid / row */}
            <div
              className="color-options-container"
              ref={colorScrollRef}
              onScroll={(e) => setScrollLeftColor(e.target.scrollLeft)}
              onMouseDown={handleColorMouseDown}
              onMouseMove={handleColorMouseMove}
              onMouseUp={handleColorMouseUpOrLeave}
              onMouseLeave={handleColorMouseUpOrLeave}
              // add touch handlers so mobile drag fires your handlers
              onTouchStart={handleColorMouseDown}
              onTouchMove={handleColorMouseMove}
              onTouchEnd={handleColorMouseUpOrLeave}
              onTouchCancel={handleColorMouseUpOrLeave}
              // only prevent wrapping on mobile; keep desktop wrapping as before
              style={{
                display: 'flex',
                gap: gap, // numeric is fine here
                alignItems: 'center',
                flexWrap: isMobile ? 'nowrap' : 'wrap',
                width: isMobile ? '100%' : 'auto',
                boxSizing: 'border-box',
                margin: !isMobile ? `0 auto 0 ${leftIndent}px` : undefined,
                overflowX: isMobile ? 'auto' : 'visible',
                WebkitOverflowScrolling: isMobile ? 'touch' : undefined,
                touchAction: isMobile ? 'pan-x' : undefined,
                scrollSnapType: 'none', // Prevent any snap behavior
                // enforce max columns on desktop using your existing calc string
                maxWidth: isMobile ? undefined : maxColsWidth
              }}>
              {/* Mobile: put custom circle first inside the row, then recentColors, then presets */}
              {isMobile && (
                <>
                  <div
                    className={`color-circle custom ${isActiveCustom ? 'active' : ''}`}
                    onClick={(e) => {
                      e.stopPropagation()
                      setDraftColor(config && config.color ? config.color : draftColor)
                      setShowCustomColorPicker((v) => !v)
                    }}
                    role="button"
                    tabIndex={0}
                    title="Match Color"
                    aria-label={`Custom color picker, ${customCircleColor}`}
                    onPointerEnter={(e) => {
                      setHoveredSwatch('custom')
                      showFloatingTooltip('Custom Color', e.currentTarget)
                    }}
                    onPointerLeave={() => {
                      setHoveredSwatch(null)
                      hideFloatingTooltip()
                    }}
                    onFocus={(e) => {
                      setHoveredSwatch('custom')
                      showFloatingTooltip('Custom Color', e.currentTarget)
                    }}
                    onBlur={() => {
                      setHoveredSwatch('custom')
                      hideFloatingTooltip()
                    }}
                    style={{
                      ...circleContainerStyle,
                      flex: `0 0 ${circleSize}px`,
                      minWidth: `${circleSize}px`,
                      width: circleSize,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                    <div
                      className="picker-rainbow"
                      aria-hidden="true"
                      style={{
                        position: 'absolute',
                        left: '50%',
                        top: '50%',
                        transform: 'translate(-50%, -50%)',
                        width: circleSize,
                        height: circleSize,
                        borderRadius: '50%',
                        padding: rainbowInnerThickness,
                        background: 'conic-gradient(#ff0054, #ff8c00, #ffd600, #3cd500, #00d3c8, #0077ff, #a000ff, #ff0054)',
                        boxSizing: 'border-box',
                        pointerEvents: 'none',
                        zIndex: 0
                      }}
                    />

                    <div
                      className="picker-white-core"
                      aria-hidden="true"
                      style={{
                        position: 'absolute',
                        left: '50%',
                        top: '50%',
                        transform: 'translate(-50%, -50%)',
                        width: whiteCoreSize,
                        height: whiteCoreSize,
                        borderRadius: '50%',
                        background: '#fff',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        boxSizing: 'border-box',
                        zIndex: 1,
                        pointerEvents: 'none',
                        boxShadow: 'none'
                      }}>
                      <div
                        className="picker-color-core"
                        aria-hidden="true"
                        style={{
                          width: '55%',
                          height: '55%',
                          borderRadius: '50%',
                          background: customCircleColor,
                          pointerEvents: 'none',
                          zIndex: 2
                        }}
                      />
                      {isIpad && (
                        <svg
                          className="picker-plus"
                          width="14"
                          height="14"
                          viewBox="0 0 14 14"
                          xmlns="http://www.w3.org/2000/svg"
                          aria-hidden="true"
                          style={{
                            position: 'absolute',
                            left: '50%',
                            top: '50%',
                            transform: 'translate(-50%, -50%)',
                            zIndex: 3,
                            pointerEvents: 'none'
                          }}>
                          <line x1="7" y1="3" x2="7" y2="11" stroke="#111" strokeWidth="1.6" strokeLinecap="round" />
                          <line x1="3" y1="7" x2="11" y2="7" stroke="#111" strokeWidth="1.6" strokeLinecap="round" />
                        </svg>
                      )}
                    </div>

                    <div className="tooltip" aria-hidden="true" />
                  </div>

                  {/* mobile recent swatches - same line */}
                  {recentColors
                    .filter((c) => !Object.keys(colorOptions).some((k) => k.toLowerCase() === c.toLowerCase()))
                    .map((c) => (
                      <div
                        key={c}
                        className={`color-circle ${config && config.color === c ? 'active' : ''}`}
                        onClick={() => {
                          // Update pop filter color if it's a custom metal pop filter
                          let newPopFilterColor = config.popFilterColor
                          if (config.popFilterEnabled && config.popFilterMaterial === 'metal') {
                            newPopFilterColor = c === 'Brass' || c === '#1A1A1A' || c === '#1c1d1d' ? '#1c1d1d' : c
                          }

                          setConfig({ ...config, color: c, popFilterColor: newPopFilterColor })
                          setDraftColor(c)
                          if (isMobile) requestAnimationFrame(scrollActiveIntoViewLocal)
                        }}
                        title=""
                        role="button"
                        tabIndex={0}
                        onPointerEnter={(e) => {
                          setHoveredSwatch(c)
                          showFloatingTooltip(c, e.currentTarget)
                        }}
                        onPointerLeave={() => {
                          setHoveredSwatch(null)
                          hideFloatingTooltip()
                        }}
                        onFocus={(e) => {
                          setHoveredSwatch(c)
                          showFloatingTooltip(c, e.currentTarget)
                        }}
                        onBlur={() => {
                          setHoveredSwatch(null)
                          hideFloatingTooltip()
                        }}
                        style={{
                          position: 'relative',
                          width: circleSize,
                          height: circleSize,
                          flex: '0 0 auto',
                          boxSizing: 'border-box',
                          borderRadius: '50%',
                          cursor: 'pointer',
                          background: 'transparent'
                        }}>
                        <div
                          className="inner"
                          style={{ position: 'absolute', inset: presetInnerInset, borderRadius: '50%', pointerEvents: 'none', backgroundColor: c }}
                        />
                        <div className="tooltip" aria-hidden="true" />
                      </div>
                    ))}
                </>
              )}

              {/* Preset swatches */}
              {Object.entries(colorOptions).map(([value, name]) => {
                // Map of colors that have texture/image swatches
                const R2 = 'https://pub-e2a1a41ee7e84eefbab9b339c21acfb0.r2.dev'
                const textureImages = {
                  '#ebe9e1': `${R2}/1-White.webp`,
                  '#d7be88': `${R2}/2-Coffee_Cream.webp`,
                  '#d5b173': `${R2}/3-Brass.webp`,
                  Brass: `${R2}/3-Brass.webp`,
                  '#bcbebc': `${R2}/4-Satin_Alluminum.webp`,
                  '#645648': `${R2}/5-Smoked_Bronze.webp`,
                  '#1c1d1d': `${R2}/6-Black.webp`,
                  '#975b3a': `${R2}/7-Terracotta.webp`,
                  '#a16f48': `${R2}/8-Copper.webp`,
                  '#d8763c': `${R2}/9-Burnt_Orange.webp`,
                  '#ce984c': `${R2}/10-Gold.webp`,
                  '#d4a53a': `${R2}/11-Sunflower.webp`,
                  '#ffca0d': `${R2}/12_Formula_Yellow.webp`,
                  '#a4402c': `${R2}/13-Calabrian_Red.webp`,
                  '#5e2833': `${R2}/14-Spiced_Berry.webp`,
                  '#802641': `${R2}/15-Dark-Fuchsia.webp`,
                  '#56475e': `${R2}/16-Vera_Violet.webp`,
                  '#926464': `${R2}/17-Rose_Gold.webp`,
                  '#c68883': `${R2}/18-Petal_Pink.webp`,
                  '#79712f': `${R2}/19-Mosslight_Green.webp`,
                  '#5e6d35': `${R2}/20-Verdant_Green.webp`,
                  '#394a37': `${R2}/21-Evergreen.webp`,
                  '#344e4a': `${R2}/22-Meridian_Blue.webp`,
                  '#196962': `${R2}/23-Turquoise_Teal.webp`,
                  '#4db494': `${R2}/24-Aqua_Lucia.webp`,
                  'Stainless Steel': 'https://imagedelivery.net/Ewh7_wCWrjoRURxzv9gKQg/92f87f17-d522-4770-8e3a-517ad6982800/public'
                }

                const isBrass = value === 'Brass'
                const bodyUpchargeAmt = premiumColors[value] || premiumColors[name] || 0
                const tooltipText = bodyUpchargeAmt ? `${name} + $${bodyUpchargeAmt}` : name
                const innerStyle = textureImages[value] ? { background: `url("${textureImages[value]}") center/cover no-repeat` } : { backgroundColor: value }

                const isActive = color === value

                return (
                  <div
                    key={value}
                    className={`color-circle ${isActive ? 'active' : ''}`}
                    onClick={() => {
                      if (!colorDragged) {
                        setConfig({ ...config, color: value })
                        setShowCustomColorPicker(false)
                        setTimeout(() => centerSelectedColor(), 10)
                      }
                      setColorDragged(false)
                    }}
                    title={tooltipText}
                    role="button"
                    tabIndex={0}
                    onPointerEnter={(e) => {
                      setHoveredSwatch(value)
                      showFloatingTooltip(tooltipText, e.currentTarget)
                    }}
                    onPointerLeave={() => {
                      setHoveredSwatch(null)
                      hideFloatingTooltip()
                    }}
                    onFocus={(e) => {
                      setHoveredSwatch(value)
                      showFloatingTooltip(tooltipText, e.currentTarget)
                    }}
                    onBlur={() => {
                      setHoveredSwatch(null)
                      hideFloatingTooltip()
                    }}
                    style={{
                      position: 'relative',
                      width: circleSize,
                      height: circleSize,
                      flex: '0 0 auto',
                      boxSizing: 'border-box',
                      borderRadius: '50%',
                      cursor: 'pointer',
                      background: 'transparent'
                    }}>
                    <div
                      className="inner"
                      style={{ position: 'absolute', inset: presetInnerInset, borderRadius: '50%', pointerEvents: 'none', ...innerStyle }}
                    />
                    <div className="tooltip" aria-hidden="true" />
                  </div>
                )
              })}
            </div>

            {/* ChromePicker modal - replaced with mobile-friendly version copied from Screen Color */}
            {showCustomColorPicker &&
              (isMobile || isIpad ? (
                <>
                  <div
                    className="ohma-picker-overlay"
                    aria-hidden="true"
                    style={{
                      position: 'fixed',
                      inset: 0,
                      backgroundColor: 'rgba(0,0,0,0)',
                      zIndex: 9998
                    }}
                    onClick={commitDraftColorAndClose}
                  />

                  <div
                    ref={pickerPanelRef}
                    style={
                      isIpad
                        ? {
                            position: 'fixed',
                            top: 0,
                            left: 0,
                            width: '100vw',
                            background: '#fff',
                            padding: '12px 16px',
                            zIndex: 9999,
                            borderRadius: '0 0 16px 16px',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }
                        : {
                            position: 'fixed',
                            top: 0,
                            left: 0,
                            width: '100vw',
                            background: '#fff',
                            padding: '12px 16px',
                            zIndex: 9999999,
                            borderRadius: '0 0 16px 16px',
                            height: '100vw',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center'
                          }
                    }
                    onClick={(e) => e.stopPropagation()}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') commitDraftColorAndClose()
                    }}
                    tabIndex={-1}>
                    {isMobile && (
                      <>
                        <div
                          className="custom-picker-close"
                          onClick={() => {
                            commitDraftColorAndClose()
                          }}
                          style={{
                            position: 'absolute',
                            top: 14,
                            right: 12,
                            fontSize: 24,
                            fontWeight: 700,
                            cursor: 'pointer',
                            color: '#333',
                            backgroundColor: '#f4f4f5',
                            padding: '4px 8px',
                            borderRadius: '9999px',
                            boxShadow: 'none',
                            zIndex: 1000000000,
                            userSelect: 'none'
                          }}>
                          ×
                        </div>

                        <div
                          className="picker-live-preview-mobile"
                          aria-hidden="true"
                          style={{
                            position: 'absolute',
                            left: 22, // match Screen's placement. tweak if you want
                            top: 188, // match Screen's placement. tweak if you want
                            width: 28,
                            height: 28,
                            borderRadius: '50%',
                            boxShadow: 'none',
                            pointerEvents: 'none',
                            zIndex: 1000000000,
                            display: 'none', // keep same behavior as Screen (screen had display none)
                            background: draftColor
                          }}></div>
                      </>
                    )}

                    <div style={isIpad ? { transform: 'scale(1.4)', transformOrigin: 'top center', marginBottom: '80px' } : {}}>
                      <div style={{ position: 'relative', display: 'inline-block' }}>
                        <ChromePicker
                          className="my-picker"
                          color={draftColor}
                          disableAlpha
                          onChange={(u) => setDraftColor(u.hex)}
                          styles={{
                            default: {
                              picker: {
                                width: isIpad ? 640 : isMobile ? '100%' : 320,
                                borderRadius: isMobile ? 0 : 8,
                                boxShadow: 'none'
                              },
                              saturation: { borderRadius: 15, overflow: 'hidden', height: 110, paddingBottom: 0 },
                              controls: { borderRadius: 8, position: 'relative' },
                              color: { display: 'none' },
                              swatch: { display: 'none' },
                              active: { display: 'none' }
                            }
                          }}
                        />

                        <div
                          className="picker-live-preview"
                          style={{
                            position: 'absolute',
                            left: 8,
                            top: 167,
                            width: 28,
                            height: 28,
                            borderRadius: '50%',
                            background: draftColor,
                            boxShadow: 'none',
                            pointerEvents: 'none',
                            zIndex: 20
                          }}
                        />
                      </div>
                    </div>
                  </div>
                </>
              ) : (
                createPortal(
                  <>
                    <div
                      className="ohma-picker-overlay-desktop"
                      aria-hidden="true"
                      style={{
                        position: 'fixed',
                        inset: 0,
                        backgroundColor: 'transparent',
                        zIndex: 9998
                      }}
                      onClick={commitDraftColorAndClose}
                    />
                    <div
                      ref={pickerPanelRef}
                      style={{
                        position: 'absolute',
                        top: `${customPickerPosition.top}px`,
                        left: `${customPickerPosition.left}px`,
                        background: '#fff',
                        padding: '12px',
                        zIndex: 9999999,
                        borderRadius: '10px',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        boxShadow: 'none'
                      }}
                      onClick={(e) => e.stopPropagation()}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') commitDraftColorAndClose()
                      }}
                      tabIndex={-1}>
                      <div style={{ position: 'relative', display: 'inline-block' }}>
                        <ChromePicker
                          className="my-picker"
                          color={draftColor}
                          disableAlpha
                          onChange={(u) => setDraftColor(u.hex)}
                          styles={{
                            default: {
                              picker: {
                                width: 320,
                                borderRadius: 8,
                                boxShadow: 'none'
                              },
                              saturation: { borderRadius: 15, overflow: 'hidden', height: 110, paddingBottom: 0 },
                              controls: { borderRadius: 8, position: 'relative' },
                              color: { display: 'none' },
                              swatch: { display: 'none' },
                              active: { display: 'none' }
                            }
                          }}
                        />

                        <div
                          className="picker-live-preview"
                          style={{
                            position: 'absolute',
                            left: 8,
                            top: 167,
                            width: 28,
                            height: 28,
                            borderRadius: '50%',
                            background: draftColor,
                            boxShadow: 'none',
                            pointerEvents: 'none',
                            zIndex: 20
                          }}
                        />
                      </div>
                    </div>
                  </>,
                  document.body
                )
              ))}

            {/* Floating tooltip - single element avoids layout shifts (appears on hover/focus) */}
            <div
              className="ohma-floating-tooltip"
              aria-hidden={!floatingTooltip.visible}
              style={{
                left: floatingTooltip.left,
                top: floatingTooltip.top,
                opacity: floatingTooltip.visible ? 1 : 0,
                visibility: floatingTooltip.visible ? 'visible' : 'hidden',
                transform: floatingTooltip.visible ? 'translate(-50%, 0)' : 'translate(-50%, 6px)'
              }}>
              {floatingTooltip.text && floatingTooltip.text.includes(' + $') ? (
                <span>
                  {floatingTooltip.text.split(' + $')[0]}
                  {' + '}
                  <span style={{ color: '#fff', fontWeight: 700 }}>${floatingTooltip.text.split(' + $')[1]}</span>
                </span>
              ) : (
                floatingTooltip.text
              )}
            </div>
          </div>
        </div>
      )
    })(),

    '2. Screen Color': (() => {

      // inside your component (replace your current isMobile const)
      const [isMobile, setIsMobile] = React.useState(() => {
        if (typeof window === 'undefined') return true
        return window.innerHeight >= window.innerWidth // true for all portrait
      })

      React.useEffect(() => {
        if (typeof window === 'undefined') return

        function update() {
          const isPortrait = window.innerHeight >= window.innerWidth
          setIsMobile(isPortrait)
        }

        // initial run and listeners
        update()
        window.addEventListener('resize', update)
        window.addEventListener('orientationchange', update)
        return () => {
          window.removeEventListener('resize', update)
          window.removeEventListener('orientationchange', update)
        }
      }, [])

      const isCustomChosen = !Object.keys(screenColorOptions).includes(screenColor)
      const customCircleColor = isCustomChosen ? screenColor : showCustomScreenColorPicker ? draftScreenColor : 'transparent'
      const isActiveCustom = showCustomScreenColorPicker || isCustomChosen

      const circleSize = isIpad ? 55 : isMobile ? 32 : 55
      const ringThickness = 3
      // make the inner rainbow band thicker like Body Color
      const rainbowInnerThickness = 8
      const presetInnerInset = 2.5
      // whiteCoreSize must subtract the rainbowInnerThickness so the visible white core is correct
      const whiteCoreSize = `${circleSize - rainbowInnerThickness * 2}px`

      const leftIndent = 0 // pixels - match whatever margin the header uses

      const gap = 12
      const maxCols = 4.5
      const extraPad = 0
      const visualPadding = ringThickness * 2 + presetInnerInset * 2 + 20
      // small tolerance to absorb rounding, scrollbar, outline, etc
      const tolerance = 10

      const renderedWidth = circleSize + visualPadding
      const maxColsWidth = `calc(${renderedWidth * maxCols}px + ${gap * (maxCols - 1)}px + ${extraPad}px + ${tolerance}px)`
      // ensure at least 4 columns on desktop
      const minColsDesktop = 4
      const minColsWidth = `calc(${circleSize}px * ${minColsDesktop} + ${gap}px * (${minColsDesktop} - 1))`

      // per-item style - reserve layout space while centering the visible circle
      const circleContainerStyle = {
        width: circleSize, // visible circle box size (px)
        height: circleSize,
        flex: `0 0 ${renderedWidth}px`, // reserved space for layout
        minWidth: `${renderedWidth}px`,
        boxSizing: 'border-box',
        position: 'relative',
        display: 'flex', // center the visual circle
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'pointer',
        borderRadius: 0, // outer shell rectangular so inner stays circular
        background: 'transparent',
        margin: 0,
        marginBottom: isMobile ? 0 : 8 // mobile 0, desktop 8px
      }
      const innerDiameter = circleSize - presetInnerInset * 2

      // recent screen colors (MRU, up to 3)
      const [recentScreenColors, setRecentScreenColors] = React.useState(() => {
        try {
          const raw = typeof window !== 'undefined' ? localStorage.getItem('ohma_recent_screen_colors_v1') : null
          return raw ? JSON.parse(raw) : []
        } catch (e) {
          return []
        }
      })

      // hovered swatch state - used only for accessibility parity
      const [hoveredSwatch, setHoveredSwatch] = React.useState(null)

      // floating tooltip state and helpers (single fixed tooltip to avoid layout shifts)
      const [floatingTooltip, setFloatingTooltip] = React.useState({
        visible: false,
        text: '',
        left: 0,
        top: 0
      })

      // hide tooltip on scroll/resize to avoid stale positions and layout shifts
      React.useEffect(() => {
        function hide() {
          setFloatingTooltip((t) => (t.visible ? { visible: false, text: '', left: 0, top: 0 } : t))
          setHoveredSwatch(null)
        }
        window.addEventListener('scroll', hide, { passive: true })
        window.addEventListener('resize', hide)
        return () => {
          window.removeEventListener('scroll', hide)
          window.removeEventListener('resize', hide)
        }
      }, [])

      React.useEffect(() => {
        try {
          if (typeof window !== 'undefined') {
            localStorage.setItem('ohma_recent_screen_colors_v1', JSON.stringify(recentScreenColors.slice(0, 3)))
          }
        } catch (e) {}
      }, [recentScreenColors])

      function pushRecentScreenColor(hex) {
        if (!hex) return
        const normalized = hex.toLowerCase()
        setRecentScreenColors((prev) => {
          const deduped = prev.filter((c) => c.toLowerCase() !== normalized)
          deduped.unshift(normalized)
          return deduped.slice(0, 3)
        })
      }

      function commitDraftScreenColorAndClose() {
        setConfig({ ...config, screenColor: draftScreenColor })
        setDraftScreenColor(draftScreenColor)
        pushRecentScreenColor(draftScreenColor)
        setShowCustomScreenColorPicker(false)
      }
      _commitScreenPickerRef.current = commitDraftScreenColorAndClose

      // tiny safe centering helper for screen colors
      function scrollActiveIntoViewLocal() {
        if (typeof window === 'undefined') return
        const container =
          screenColorScrollRef && screenColorScrollRef.current ? screenColorScrollRef.current : document.querySelector('.color-options-container')
        if (!container) return

        const active = container.querySelector('.color-circle.active')
        if (!active) return

        try {
          if (typeof active.scrollIntoView === 'function') {
            active.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' })
            return
          }
          const containerRect = container.getBoundingClientRect()
          const activeRect = active.getBoundingClientRect()
          const offset = activeRect.left + activeRect.width / 2 - (containerRect.left + containerRect.width / 2)
          container.scrollLeft += offset
        } catch (err) {
          // swallow errors; best-effort centering
        }
      }
      // -----------------------------------------------------------------

      // Floating tooltip helpers
      function showFloatingTooltip(text, targetEl) {
        if (!targetEl || typeof targetEl.getBoundingClientRect !== 'function') {
          setFloatingTooltip({ visible: true, text, left: window.innerWidth / 2, top: 100 })
          return
        }
        // Get the zoom level from the footer
        const footer = document.querySelector('.desktop-footer-pad-width')
        const zoom = footer ? parseFloat(footer.style.zoom || '1') : 1

        const r = targetEl.getBoundingClientRect()
        // Adjust for zoom: divide by zoom to get actual position
        const left = (r.left + r.width / 2) / zoom
        const top = (r.bottom + 8) / zoom
        setFloatingTooltip({ visible: true, text, left, top })
      }
      function hideFloatingTooltip() {
        setFloatingTooltip({ visible: false, text: '', left: 0, top: 0 })
      }

      const localTooltipCss = `
        .recent-swatch-row { display: inline-flex; gap: 12px; align-items: center; margin-left: 12px; }
        /* hide per-swatch inline tooltip elements from layout; floating tooltip is authoritative */
        .color-circle .tooltip { display: none !important; }
        .color-circle { will-change: transform; }
        .ohma-floating-tooltip {
          position: fixed;
          left: 0;
          top: 0;
          transform: translate(-50%, 0);
          padding: 6px 10px;
          border-radius: 6px;
          font-family: 'Jost', system-ui, -apple-system, 'Segoe UI', Roboto, 'Helvetica Neue', Arial;
          font-size: 13px;
          font-weight: 600;
          white-space: nowrap;
          background: #000;
          color: #fff;
          /* box-shadow removed */
          z-index: 1000000;
          pointer-events: none;
          transition: opacity 160ms cubic-bezier(0.2,0.9,0.2,1), transform 160ms cubic-bezier(0.2,0.9,0.2,1);
          max-width: 220px;
          overflow: hidden;
          text-overflow: ellipsis;
        }
      `

      const stainlessURL = 'https://pub-e2a1a41ee7e84eefbab9b339c21acfb0.r2.dev/25-Stainless_Steel.webp'
      const R2 = 'https://pub-e2a1a41ee7e84eefbab9b339c21acfb0.r2.dev'

      return (
        <div className="option-content" style={{ position: 'relative' }}>
          <style dangerouslySetInnerHTML={{ __html: localTooltipCss }} />

          {/* Header */}
          <div className="header-and-selected" style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '0' }}>
            <div className="option-header header-screen-color" style={{ display: 'flex', alignItems: 'center', gap: 0, whiteSpace: 'nowrap', flexShrink: 0 }}>
              <h1 style={{ margin: 0, lineHeight: 1, display: 'inline-block', whiteSpace: 'nowrap' }}>{isMobile ? 'Screen' : 'Screen Color'}</h1>

              {/* Info icon button */}
              <button
                type="button"
                className="info-button"
                aria-label="More about screen color options"
                onClick={() => {
                  // Open footer with this tab and current screen color
                  if (isMobile && typeof openFooterWithTab === 'function') {
                    openFooterWithTab('colorGuide', screenColor)
                  }
                  // Open desktop modal with current screen color
                  if (!isMobile && typeof openProductModalTab === 'function') {
                    openProductModalTab('colorGuide', screenColor)
                  }
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault()
                    e.currentTarget.click()
                  }
                }}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 22,
                  height: 22,
                  padding: 0,
                  borderRadius: 6,
                  border: 'none',
                  background: 'transparent',
                  color: 'var(--muted, #6b7280)',
                  cursor: 'pointer',
                  marginLeft: 8,
                  verticalAlign: 'middle',
                  lineHeight: 1,
                  boxShadow: 'none'
                }}>
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  aria-hidden="true"
                  style={{ pointerEvents: 'none' }}>
                  <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="1.4" />
                  <circle cx="12" cy="8" r="1" fill="currentColor" />
                  <path d="M11.25 11.5h1.5v5h-1.5z" fill="currentColor" />
                </svg>
              </button>
            </div>
            <div className="selected-option">
              <div className="selected-color">{screenColorOptions[screenColor] || screenColor}</div>
            </div>
          </div>

          {/* Swatches container */}
          <div className="color-options gradient-wrapper" style={{ position: 'relative' }}>
            {/* Left gradient - only show when scrolled */}
            {scrollLeftScreenColor > 10 && (
              <div
                style={{
                  position: 'absolute',
                  left: 0,
                  top: 0,
                  bottom: 0,
                  width: '30px',
                  background: 'linear-gradient(to right, #ffffff 0%, rgba(255, 255, 255, 0) 100%)',
                  pointerEvents: 'none',
                  zIndex: 10
                }}
              />
            )}
            {/* Right gradient - only show when can scroll more */}
            {screenColorScrollRef.current &&
              scrollLeftScreenColor < screenColorScrollRef.current.scrollWidth - screenColorScrollRef.current.clientWidth - 10 && (
                <div
                  className="color-circles-right-gradient"
                  style={{
                    position: 'absolute',
                    right: 0,
                    top: 0,
                    bottom: 0,
                    width: '30px',
                    background: 'linear-gradient(to left, #ffffff 0%, rgba(255, 255, 255, 0) 100%)',
                    pointerEvents: 'none',
                    zIndex: 10
                  }}
                />
              )}
            {/* Desktop custom picker + recent swatches row */}
            {!isMobile && (
              <div style={{ display: 'flex', alignItems: 'center', marginBottom: 0, marginLeft: 15 }}>
                <div
                  ref={customScreenCircleRef}
                  className={`color-circle custom ${isActiveCustom ? 'active' : ''}`}
                  onClick={(e) => {
                    e.stopPropagation()
                    setDraftScreenColor(config && config.screenColor ? config.screenColor : draftScreenColor)

                    // Calculate position for the picker relative to circle
                    const rect = e.currentTarget.getBoundingClientRect()
                    setCustomScreenPickerPosition({
                      top: rect.bottom + window.scrollY + 10,
                      left: rect.left + window.scrollX + 10
                    })

                    setShowCustomScreenColorPicker((v) => !v)
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault()
                      setDraftScreenColor(config && config.screenColor ? config.screenColor : draftScreenColor)
                      setShowCustomScreenColorPicker((v) => !v)
                    }
                  }}
                  role="button"
                  tabIndex={0}
                  title="Custom Screen Color"
                  aria-label={`Custom screen color picker, ${customCircleColor}`}
                  // pointer events for hover/focus
                  onPointerEnter={(e) => {
                    setHoveredSwatch('custom')
                    showFloatingTooltip(
                      screenSameAsBodyCustom
                        ? `Custom Screen Color (included)`
                        : `Custom Screen Color +${currency.code === 'CAD' ? 'C' : ''}${currency.symbol}${convertPrice(199)}`,
                      e.currentTarget
                    )
                  }}
                  onPointerLeave={() => {
                    setHoveredSwatch(null)
                    hideFloatingTooltip()
                  }}
                  onFocus={(e) => {
                    setHoveredSwatch('custom')
                    showFloatingTooltip(
                      screenSameAsBodyCustom
                        ? `Custom Screen Color (included)`
                        : `Custom Screen Color +${currency.code === 'CAD' ? 'C' : ''}${currency.symbol}${convertPrice(199)}`,
                      e.currentTarget
                    )
                  }}
                  onBlur={() => {
                    setHoveredSwatch(null)
                    hideFloatingTooltip()
                  }}
                  style={{
                    ...circleContainerStyle,
                    flex: `0 0 ${circleSize}px`,
                    minWidth: `${circleSize}px`,
                    width: circleSize,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderRadius: 0,
                    background: 'transparent'
                  }}>
                  <div
                    className="picker-rainbow"
                    aria-hidden="true"
                    style={{
                      position: 'absolute',
                      left: '50%',
                      top: '50%',
                      transform: 'translate(-50%, -50%)',
                      width: circleSize,
                      height: circleSize,
                      borderRadius: '50%',
                      padding: rainbowInnerThickness,
                      background: 'conic-gradient(#ff0054, #ff8c00, #ffd600, #3cd500, #00d3c8, #0077ff, #a000ff, #ff0054)',
                      boxSizing: 'border-box',
                      pointerEvents: 'none',
                      zIndex: 0
                    }}
                  />
                  <div
                    className="picker-active-ring"
                    aria-hidden="true"
                    style={{
                      position: 'absolute',
                      left: '50%',
                      top: '50%',
                      transform: 'translate(-50%, -50%)',
                      width: circleSize,
                      height: circleSize,
                      borderRadius: '50%',
                      pointerEvents: 'none',
                      zIndex: 5,
                      boxSizing: 'border-box',
                      boxShadow: 'none',
                      opacity: isActiveCustom ? 1 : 0,
                      transition: 'box-shadow 400ms ease, opacity 200ms ease'
                    }}
                  />
                  <div
                    className="picker-white-core"
                    aria-hidden="true"
                    style={{
                      position: 'absolute',
                      left: '50%',
                      top: '50%',
                      transform: 'translate(-50%, -50%)',
                      width: whiteCoreSize,
                      height: whiteCoreSize,
                      borderRadius: '50%',
                      background: '#fff',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      boxSizing: 'border-box',
                      zIndex: 1,
                      pointerEvents: 'none',
                      boxShadow: 'none',
                      transition: 'transform 500ms cubic-bezier(0.2,0.9,0.2,1)'
                    }}>
                    <div
                      className="picker-color-core"
                      aria-hidden="true"
                      style={{
                        width: '55%',
                        height: '55%',
                        borderRadius: '50%',
                        background: customCircleColor,
                        pointerEvents: 'none',
                        zIndex: 2,
                        transition: 'transform 500ms cubic-bezier(0.2,0.9,0.2,1)'
                      }}
                    />
                    <svg
                      className="picker-plus"
                      width="14"
                      height="14"
                      viewBox="0 0 14 14"
                      xmlns="http://www.w3.org/2000/svg"
                      aria-hidden="true"
                      style={{
                        position: 'absolute',
                        left: '50%',
                        top: '50%',
                        transform: 'translate(-50%, -50%)',
                        zIndex: 3,
                        pointerEvents: 'none'
                      }}>
                      <line x1="7" y1="3" x2="7" y2="11" stroke="#111" strokeWidth="1.6" strokeLinecap="round" />
                      <line x1="3" y1="7" x2="11" y2="7" stroke="#111" strokeWidth="1.6" strokeLinecap="round" />
                    </svg>
                  </div>
                </div>

                <div className="recent-swatch-row" aria-hidden={recentScreenColors.length === 0}>
                  {recentScreenColors
                    .filter((hex) => !Object.keys(screenColorOptions).some((k) => k.toLowerCase() === hex.toLowerCase()))
                    .map((hex) => {
                      const isActiveRecent = config && config.screenColor && config.screenColor.toLowerCase() === hex.toLowerCase()
                      return (
                        <div
                          key={hex}
                          className={`color-circle ${isActiveRecent ? 'active' : ''}`}
                          onClick={() => {
                            setConfig({ ...config, screenColor: hex })
                            setDraftScreenColor(hex)
                            // center after DOM updates - only used on mobile but harmless here
                            if (isMobile) requestAnimationFrame(scrollActiveIntoViewLocal)
                          }}
                          title={hex}
                          role="button"
                          tabIndex={0}
                          onPointerEnter={(e) => {
                            setHoveredSwatch(hex)
                            showFloatingTooltip(hex, e.currentTarget)
                          }}
                          onPointerLeave={() => {
                            setHoveredSwatch(null)
                            hideFloatingTooltip()
                          }}
                          onFocus={(e) => {
                            setHoveredSwatch(hex)
                            showFloatingTooltip(hex, e.currentTarget)
                          }}
                          onBlur={() => {
                            setHoveredSwatch(null)
                            hideFloatingTooltip()
                          }}
                          style={{
                            position: 'relative',
                            width: circleSize,
                            height: circleSize,
                            flex: '0 0 auto',
                            boxSizing: 'border-box',
                            borderRadius: '50%',
                            cursor: 'pointer',
                            background: 'transparent'
                          }}>
                          <div
                            className="inner"
                            style={{
                              position: 'absolute',
                              inset: presetInnerInset,
                              borderRadius: '50%',
                              pointerEvents: 'none',
                              backgroundColor: hex
                            }}
                          />
                          <div className="tooltip" aria-hidden="true" />
                        </div>
                      )
                    })}
                </div>

                {recentScreenColors.length === 0 ? (
                  <div
                    style={{
                      height: circleSize, // SAME value used for the swatch circle
                      display: 'flex',
                      alignItems: 'center',
                      marginLeft: 12
                    }}>
                    <h2
                      style={{
                        margin: 0,
                        fontSize: 16,
                        color: '#333',
                        fontWeight: 500,
                        fontFamily: 'Jost',
                        userSelect: 'none',
                        lineHeight: 1,
                        marginTop: -8
                      }}>
                      Choose any color
                    </h2>
                  </div>
                ) : null}
              </div>
            )}

            {/* color grid / row - match Body spacing and wrap (only nowrap on mobile) */}
            <div
              className="color-options-container"
              ref={screenColorScrollRef}
              onScroll={(e) => setScrollLeftScreenColor(e.target.scrollLeft)}
              onMouseDown={handleScreenColorMouseDown}
              onMouseMove={handleScreenColorMouseMove}
              onMouseUp={handleScreenColorMouseUpOrLeave}
              onMouseLeave={handleScreenColorMouseUpOrLeave}
              onTouchStart={handleScreenColorMouseDown}
              onTouchMove={handleScreenColorMouseMove}
              onTouchEnd={handleScreenColorMouseUpOrLeave}
              onTouchCancel={handleScreenColorMouseUpOrLeave}
              style={{
                display: 'flex',
                gap: gap,
                alignItems: 'center',
                flexWrap: isMobile ? 'nowrap' : 'wrap',
                width: isMobile ? '100%' : 'auto',
                boxSizing: 'border-box',
                margin: !isMobile ? `0 auto 0 ${leftIndent}px` : undefined,
                overflowX: isMobile ? 'auto' : 'visible',
                WebkitOverflowScrolling: isMobile ? 'touch' : undefined,
                touchAction: isMobile ? 'pan-x' : undefined,
                scrollSnapType: 'none', // Prevent any snap behavior
                maxWidth: isMobile ? undefined : maxColsWidth
              }}>
              {/* Mobile: put custom circle first inside the row, then recentScreenColors, then presets */}
              {isMobile && (
                <>
                  <div
                    className={`color-circle custom ${isActiveCustom ? 'active' : ''}`}
                    onClick={(e) => {
                      e.stopPropagation()
                      setDraftScreenColor(config && config.screenColor ? config.screenColor : draftScreenColor)
                      setShowCustomScreenColorPicker((v) => !v)
                    }}
                    role="button"
                    tabIndex={0}
                    title="Custom Screen Color"
                    aria-label={`Custom screen color picker, ${customCircleColor}`}
                    onPointerEnter={(e) => {
                      setHoveredSwatch('custom')
                      showFloatingTooltip('Custom Screen Color', e.currentTarget)
                    }}
                    onPointerLeave={() => {
                      setHoveredSwatch(null)
                      hideFloatingTooltip()
                    }}
                    onFocus={(e) => {
                      setHoveredSwatch('custom')
                      showFloatingTooltip('Custom Screen Color', e.currentTarget)
                    }}
                    onBlur={() => {
                      setHoveredSwatch('custom')
                      hideFloatingTooltip()
                    }}
                    style={{
                      ...circleContainerStyle,
                      flex: `0 0 ${circleSize}px`,
                      minWidth: `${circleSize}px`,
                      width: circleSize,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                    <div
                      className="picker-rainbow"
                      aria-hidden="true"
                      style={{
                        position: 'absolute',
                        left: '50%',
                        top: '50%',
                        transform: 'translate(-50%, -50%)',
                        width: circleSize,
                        height: circleSize,
                        borderRadius: '50%',
                        padding: rainbowInnerThickness,
                        background: 'conic-gradient(#ff0054, #ff8c00, #ffd600, #3cd500, #00d3c8, #0077ff, #a000ff, #ff0054)',
                        boxSizing: 'border-box',
                        pointerEvents: 'none',
                        zIndex: 0
                      }}
                    />

                    <div
                      className="picker-white-core"
                      aria-hidden="true"
                      style={{
                        position: 'absolute',
                        left: '50%',
                        top: '50%',
                        transform: 'translate(-50%, -50%)',
                        width: whiteCoreSize,
                        height: whiteCoreSize,
                        borderRadius: '50%',
                        background: '#fff',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        boxSizing: 'border-box',
                        zIndex: 1,
                        pointerEvents: 'none',
                        boxShadow: 'none'
                      }}>
                      <div
                        className="picker-color-core"
                        aria-hidden="true"
                        style={{
                          width: '55%',
                          height: '55%',
                          borderRadius: '50%',
                          background: customCircleColor,
                          pointerEvents: 'none',
                          zIndex: 2
                        }}
                      />
                      {isIpad && (
                        <svg
                          className="picker-plus"
                          width="14"
                          height="14"
                          viewBox="0 0 14 14"
                          xmlns="http://www.w3.org/2000/svg"
                          aria-hidden="true"
                          style={{
                            position: 'absolute',
                            left: '50%',
                            top: '50%',
                            transform: 'translate(-50%, -50%)',
                            zIndex: 3,
                            pointerEvents: 'none'
                          }}>
                          <line x1="7" y1="3" x2="7" y2="11" stroke="#111" strokeWidth="1.6" strokeLinecap="round" />
                          <line x1="3" y1="7" x2="11" y2="7" stroke="#111" strokeWidth="1.6" strokeLinecap="round" />
                        </svg>
                      )}
                    </div>

                    <div className="tooltip" aria-hidden="true" />
                  </div>

                  {recentScreenColors
                    .filter((hex) => !Object.keys(screenColorOptions).some((k) => k.toLowerCase() === hex.toLowerCase()))
                    .map((hex) => (
                      <div
                        key={hex}
                        className={`color-circle ${config && config.screenColor === hex ? 'active' : ''}`}
                        onClick={() => {
                          setConfig({ ...config, screenColor: hex })
                          setDraftScreenColor(hex)
                          if (isMobile) requestAnimationFrame(scrollActiveIntoViewLocal)
                        }}
                        title=""
                        role="button"
                        tabIndex={0}
                        onPointerEnter={(e) => {
                          setHoveredSwatch(hex)
                          showFloatingTooltip(hex, e.currentTarget)
                        }}
                        onPointerLeave={() => {
                          setHoveredSwatch(null)
                          hideFloatingTooltip()
                        }}
                        onFocus={(e) => {
                          setHoveredSwatch(hex)
                          showFloatingTooltip(hex, e.currentTarget)
                        }}
                        onBlur={() => {
                          setHoveredSwatch(null)
                          hideFloatingTooltip()
                        }}
                        style={{
                          position: 'relative',
                          width: circleSize,
                          height: circleSize,
                          flex: '0 0 auto',
                          boxSizing: 'border-box',
                          borderRadius: '50%',
                          cursor: 'pointer',
                          background: 'transparent'
                        }}>
                        <div
                          className="inner"
                          style={{ position: 'absolute', inset: presetInnerInset, borderRadius: '50%', pointerEvents: 'none', backgroundColor: hex }}
                        />
                        <div className="tooltip" aria-hidden="true" />
                      </div>
                    ))}
                </>
              )}

              {/* Preset swatches */}
              {Object.entries(screenColorOptions).map(([value, name]) => {
                // Map of colors that have texture/image swatches
                const R2 = 'https://pub-e2a1a41ee7e84eefbab9b339c21acfb0.r2.dev'
                const textureImages = {
                  '#ebe9e1': `${R2}/1-White.webp`,
                  '#d7be88': `${R2}/2-Coffee_Cream.webp`,
                  '#d5b173': `${R2}/3-Brass.webp`,
                  Brass: `${R2}/3-Brass.webp`,
                  '#bcbebc': `${R2}/4-Satin_Alluminum.webp`,
                  '#645648': `${R2}/5-Smoked_Bronze.webp`,
                  '#1c1d1d': `${R2}/6-Black.webp`,
                  '#975b3a': `${R2}/7-Terracotta.webp`,
                  '#a16f48': `${R2}/8-Copper.webp`,
                  '#d8763c': `${R2}/9-Burnt_Orange.webp`,
                  '#ce984c': `${R2}/10-Gold.webp`,
                  '#d4a53a': `${R2}/11-Sunflower.webp`,
                  '#ffca0d': `${R2}/12_Formula_Yellow.webp`,
                  '#a4402c': `${R2}/13-Calabrian_Red.webp`,
                  '#5e2833': `${R2}/14-Spiced_Berry.webp`,
                  '#802641': `${R2}/15-Dark-Fuchsia.webp`,
                  '#56475e': `${R2}/16-Vera_Violet.webp`,
                  '#926464': `${R2}/17-Rose_Gold.webp`,
                  '#c68883': `${R2}/18-Petal_Pink.webp`,
                  '#79712f': `${R2}/19-Mosslight_Green.webp`,
                  '#5e6d35': `${R2}/20-Verdant_Green.webp`,
                  '#394a37': `${R2}/21-Evergreen.webp`,
                  '#344e4a': `${R2}/22-Meridian_Blue.webp`,
                  '#196962': `${R2}/23-Turquoise_Teal.webp`,
                  '#4db494': `${R2}/24-Aqua_Lucia.webp`,
                  'Stainless Steel': stainlessURL
                }

                const style = textureImages[value] ? { background: `url("${textureImages[value]}") center/cover no-repeat` } : { backgroundColor: value }
                const isActive = screenColor === value

                return (
                  <div
                    key={value}
                    className={`color-circle ${isActive ? 'active' : ''}`}
                    onClick={() => {
                      if (!screenColorDragged) {
                        setConfig({ ...config, screenColor: value })
                        setShowCustomScreenColorPicker(false)
                        setTimeout(() => centerSelectedScreenColor(), 10)
                      }
                      setScreenColorDragged(false)
                    }}
                    style={{
                      position: 'relative',
                      width: circleSize,
                      height: circleSize,
                      flex: '0 0 auto',
                      boxSizing: 'border-box',
                      borderRadius: '50%',
                      cursor: 'pointer',
                      background: 'transparent'
                    }}
                    title={name}
                    role="button"
                    tabIndex={0}
                    onPointerEnter={(e) => {
                      setHoveredSwatch(value)
                      showFloatingTooltip(name, e.currentTarget)
                    }}
                    onPointerLeave={() => {
                      setHoveredSwatch(null)
                      hideFloatingTooltip()
                    }}
                    onFocus={(e) => {
                      setHoveredSwatch(value)
                      showFloatingTooltip(name, e.currentTarget)
                    }}
                    onBlur={() => {
                      setHoveredSwatch(null)
                      hideFloatingTooltip()
                    }}>
                    <div className="inner" style={{ position: 'absolute', inset: presetInnerInset, borderRadius: '50%', pointerEvents: 'none', ...style }} />
                    <div className="tooltip" aria-hidden="true" />
                  </div>
                )
              })}
            </div>

            {/* ChromePicker modal - match Body mobile styles exactly */}
            {showCustomScreenColorPicker &&
              (isMobile || isIpad ? (
                <>
                  <div
                    className="ohma-picker-overlay"
                    aria-hidden="true"
                    style={{
                      position: 'fixed',
                      inset: 0,
                      backgroundColor: 'rgba(0,0,0,0)',
                      zIndex: 9998
                    }}
                    onClick={commitDraftScreenColorAndClose}
                  />
                  <div
                    ref={pickerPanelRef}
                    style={
                      isIpad
                        ? {
                            position: 'fixed',
                            top: 0,
                            left: 0,
                            width: '100vw',
                            background: '#fff',
                            padding: '12px 16px',
                            zIndex: 9999,
                            borderRadius: '0 0 16px 16px',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center' // Center the picker vertically
                          }
                        : {
                            position: 'fixed',
                            top: 0,
                            left: 0,
                            width: '100vw',
                            background: '#fff',
                            padding: '12px 16px',
                            zIndex: 9999,
                            borderRadius: '0 0 16px 16px',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center'
                          }
                    }
                    onClick={(e) => e.stopPropagation()}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') commitDraftScreenColorAndClose()
                    }}
                    tabIndex={-1}>
                    {isMobile && (
                      <>
                        <div
                          className="custom-picker-close"
                          onClick={() => {
                            commitDraftScreenColorAndClose()
                          }}
                          style={{
                            position: 'absolute',
                            top: 14,
                            right: 12,
                            fontSize: 24,
                            fontWeight: 700,
                            cursor: 'pointer',
                            color: '#333',
                            backgroundColor: '#f4f4f5',
                            padding: '4px 8px',
                            borderRadius: '9999px',
                            boxShadow: 'none',
                            zIndex: 1000000000,
                            userSelect: 'none'
                          }}>
                          ×
                        </div>

                        <div
                          className="picker-live-preview-mobile"
                          aria-hidden="true"
                          style={{
                            position: 'absolute',
                            left: 22,
                            top: 188,
                            width: 28,
                            height: 28,
                            borderRadius: '50%',
                            boxShadow: 'none',
                            pointerEvents: 'none',
                            zIndex: 1000000000,
                            display: 'none',
                            background: draftScreenColor
                          }}></div>
                      </>
                    )}

                    <div style={isIpad ? { transform: 'scale(1.4)', transformOrigin: 'top center', marginBottom: '80px' } : {}}>
                      <div style={{ position: 'relative', display: 'inline-block' }}>
                        <ChromePicker
                          className="my-picker"
                          color={draftScreenColor}
                          disableAlpha
                          onChange={(u) => setDraftScreenColor(u.hex)}
                          styles={{
                            default: {
                              picker: {
                                width: isIpad ? 640 : isMobile ? '100%' : 320,
                                borderRadius: isMobile ? 0 : 8,
                                boxShadow: 'none'
                              },
                              saturation: { borderRadius: 15, overflow: 'hidden', height: 110, paddingBottom: 0 },
                              controls: { borderRadius: 8, position: 'relative' },
                              color: { display: 'none' },
                              swatch: { display: 'none' },
                              active: { display: 'none' }
                            }
                          }}
                        />

                        <div
                          className="picker-live-preview"
                          style={{
                            position: 'absolute',
                            left: 8,
                            top: 167,
                            width: 28,
                            height: 28,
                            borderRadius: '50%',
                            background: draftScreenColor,
                            boxShadow: 'none',
                            pointerEvents: 'none',
                            zIndex: 20
                          }}
                        />
                      </div>
                    </div>
                  </div>
                </>
              ) : (
                createPortal(
                  <>
                    <div
                      className="ohma-picker-overlay-desktop"
                      aria-hidden="true"
                      style={{
                        position: 'fixed',
                        inset: 0,
                        backgroundColor: 'transparent',
                        zIndex: 9998
                      }}
                      onClick={commitDraftScreenColorAndClose}
                    />
                    <div
                      ref={pickerPanelRef}
                      style={{
                        position: 'absolute',
                        top: `${customScreenPickerPosition.top}px`,
                        left: `${customScreenPickerPosition.left}px`,
                        background: '#fff',
                        padding: '12px',
                        zIndex: 9999,
                        borderRadius: '10px',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        boxShadow: 'none'
                      }}
                      onClick={(e) => e.stopPropagation()}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') commitDraftScreenColorAndClose()
                      }}
                      tabIndex={-1}>
                      <div style={{ position: 'relative', display: 'inline-block' }}>
                        <ChromePicker
                          className="my-picker"
                          color={draftScreenColor}
                          disableAlpha
                          onChange={(u) => setDraftScreenColor(u.hex)}
                          styles={{
                            default: {
                              picker: {
                                width: 320,
                                borderRadius: 8,
                                boxShadow: 'none'
                              },
                              saturation: { borderRadius: 15, overflow: 'hidden', height: 110, paddingBottom: 0 },
                              controls: { borderRadius: 8, position: 'relative' },
                              color: { display: 'none' },
                              swatch: { display: 'none' },
                              active: { display: 'none' }
                            }
                          }}
                        />

                        <div
                          className="picker-live-preview"
                          style={{
                            position: 'absolute',
                            left: 8,
                            top: 167,
                            width: 28,
                            height: 28,
                            borderRadius: '50%',
                            background: draftScreenColor,
                            boxShadow: 'none',
                            pointerEvents: 'none',
                            zIndex: 20
                          }}
                        />
                      </div>
                    </div>
                  </>,
                  document.body
                )
              ))}
          </div>

          {/* Floating tooltip - single element avoids layout shifts (appears on hover/focus) */}
          <div
            className="ohma-floating-tooltip"
            aria-hidden={!floatingTooltip.visible}
            style={{
              left: floatingTooltip.left,
              top: floatingTooltip.top,
              opacity: floatingTooltip.visible ? 1 : 0,
              visibility: floatingTooltip.visible ? 'visible' : 'hidden',
              transform: floatingTooltip.visible ? 'translate(-50%, 0)' : 'translate(-50%, 6px)'
            }}>
            {floatingTooltip.text}
          </div>
        </div>
      )
    })(),

    '3. Screen Type': (() => {
      // inside your component (replace your current isMobile const)
      const [isMobile, setIsMobile] = React.useState(() => {
        if (typeof window === 'undefined') return true
        return window.innerHeight >= window.innerWidth // true for all portrait
      })

      React.useEffect(() => {
        if (typeof window === 'undefined') return

        function update() {
          const isPortrait = window.innerHeight >= window.innerWidth
          setIsMobile(isPortrait)
        }

        // initial run and listeners
        update()
        window.addEventListener('resize', update)
        window.addEventListener('orientationchange', update)
        return () => {
          window.removeEventListener('resize', update)
          window.removeEventListener('orientationchange', update)
        }
      }, [])

      const isLandscape = typeof window !== 'undefined' && !isMobile && window.matchMedia('(orientation: landscape)').matches

      // local hover state - used only on landscape (desktop).
      const [hoveredScreenType, setHoveredScreenType] = React.useState(null)
      const highlightSize = 54
      const strokeWidth = isLandscape ? 1.4 : 1
      const rectInset = isLandscape ? 1 : 1.5
      const rectSize = isLandscape ? 22 : 21
      const cornerRadius = isLandscape ? 6 : 4

      // helper local to this section (same pattern as section 5 / 6)
      function openProductModalTab(tab = 'screenType') {
        // Map variants to canonical names
        const tabMap = {
          popfilter: 'popFilter',
          'pop-filter': 'popFilter',
          beta: 'feedback',
          // Mic Type variants
          'mic-type': 'micType',
          mic: 'micType',
          micTypeTab: 'micType',
          microphoneType: 'micType',
          // Screen Type variants
          'screen-type': 'screenType',
          screen: 'screenType',
          screenTypeTab: 'screenType',
          // Color Guide variants
          'color-guide': 'colorGuide',
          colors: 'colorGuide',
          // Custom Logo variants
          'custom-logo': 'customLogo',
          logo: 'customLogo',
          // Specs variants
          specifications: 'specs'
        }
        const canonicalTab = tabMap[tab] || tab

        // try module-scoped openModal (might return a handler)
        try {
          if (typeof openModal === 'function') {
            const maybeHandler = openModal(canonicalTab)
            if (typeof maybeHandler === 'function') maybeHandler()
            return
          }
        } catch (e) {}

        // try global/window openModal
        try {
          if (typeof window !== 'undefined' && typeof window.openModal === 'function') {
            const maybeHandler = window.openModal(canonicalTab)
            if (typeof maybeHandler === 'function') maybeHandler()
            return
          }
        } catch (e) {}

        // try modal state setters if they exist in scope
        try {
          if (typeof setModalInitialTab === 'function' && typeof setModalOpen === 'function') {
            setModalInitialTab(canonicalTab)
            setModalOpen(true)
            return
          }
        } catch (e) {}

        // final fallback: dispatch event
        if (typeof window !== 'undefined' && typeof window.dispatchEvent === 'function') {
          window.dispatchEvent(new CustomEvent('ohma:open-product-modal', { detail: { tab } }))
          return
        }

        // console.warn('openProductModalTab: no available method to open modal for tab', tab)
      }


      // >>> INSERT: expose IIFE-local helper to window so share button (and others) can call it

      return (
        <div>
          <div className="option-content">
            <div className="header-and-selected" style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '0' }}>
              <div className="option-header" style={{ display: 'flex', alignItems: 'center', gap: 0, whiteSpace: 'nowrap', flexShrink: 0 }}>
                <h1 style={{ margin: 0, lineHeight: 1, display: 'inline-block', whiteSpace: 'nowrap' }}>{isMobile ? 'Screen' : 'Screen Type'}</h1>

                {/* subtle info icon button */}
                {/* subtle info icon button */}
                {/* subtle info icon button - replace current button in '3. Screen Type' */}
                <button
                  type="button"
                  className="info-button"
                  aria-label="More about screen options"
                  onClick={() => {
                    // Open footer with this tab
                    if (isMobile && typeof openFooterWithTab === 'function') {
                      openFooterWithTab('screenType')
                    }
                    // Open desktop modal
                    if (!isMobile && typeof openProductModalTab === 'function') {
                      openProductModalTab('screenType')
                    }
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault()
                      e.currentTarget.click()
                    }
                  }}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                    <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="1.4" />
                    <circle cx="12" cy="8" r="1" fill="currentColor" />
                    <path d="M11.25 11.5h1.5v5h-1.5z" fill="currentColor" />
                  </svg>
                </button>
              </div>

              <div className="selected-option">
                <div className="selected-color">{screenTypeOptions[screenType].label}</div>
              </div>
            </div>

            <div className="color-options">
              <div className="screen-options-container" onMouseDown={(e) => e.preventDefault()}>
                {Object.entries(screenTypeOptions).map(([value, { label, iconUrl }]) => {
                  const isActive = screenType === value
                  const isHovered = hoveredScreenType === value

                  // attach hover/focus handlers only in landscape
                  const landscapeHandlers = isLandscape
                    ? {
                        onMouseEnter: () => setHoveredScreenType(value),
                        onMouseLeave: () => setHoveredScreenType((v) => (v === value ? null : v)),
                        onFocus: () => setHoveredScreenType(value),
                        onBlur: () => setHoveredScreenType((v) => (v === value ? null : v))
                      }
                    : {}

                  return (
                    <div
                      key={value}
                      className={`screen-type-option ${isActive ? 'is-active' : ''}`}
                      onClick={() => setScreenType(value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault()
                          setScreenType(value)
                        }
                      }}
                      role="button"
                      tabIndex={0}
                      aria-pressed={isActive}
                      {...landscapeHandlers}>
                      <div className="icon-container">
                        <img src={iconUrl} alt={label} className={`screen-type-icon ${isActive ? 'active' : ''}`} />

                        {/* BLUE highlight: render for active items on all viewports.
                            On desktop (landscape) CSS will draw the visible ring via box-shadow;
                            on mobile the SVG rect will be visible as a stroke. */}
                        {isActive && (
                          <svg
                            className="highlight-circle"
                            xmlns="http://www.w3.org/2000/svg"
                            width={highlightSize}
                            height={highlightSize}
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="#91c3de"
                            strokeWidth={strokeWidth}
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            aria-hidden="true"
                            focusable="false"
                            style={{ pointerEvents: 'none' }}>
                            <rect x={rectInset} y={rectInset} width={rectSize} height={rectSize} rx={cornerRadius} ry={cornerRadius} />
                          </svg>
                        )}
                      </div>

                      {/* Tooltip only on landscape (desktop). inline styles make its visibility reliable. */}
                      {isLandscape && (
                        <div
                          className="tooltip"
                          role="tooltip"
                          aria-hidden={!isHovered}
                          style={{
                            opacity: isHovered ? 1 : 0,
                            visibility: isHovered ? 'visible' : 'hidden',
                            transform: isHovered ? 'translate(-50%, 0)' : 'translate(-50%, 6px)',
                            pointerEvents: isHovered ? 'auto' : 'none'
                          }}>
                          {label}
                        </div>
                      )}
                    </div>
                  )
                })}
              </div>
            </div>
          </div>
        </div>
      )
    })(),

    '4. Fabric Color': (() => {
      // keep the same responsive sizing pattern as other swatches
      // inside your component (replace your current isMobile const)
      const [isMobile, setIsMobile] = React.useState(() => {
        if (typeof window === 'undefined') return true
        return window.innerHeight >= window.innerWidth // true for all portrait
      })

      React.useEffect(() => {
        if (typeof window === 'undefined') return

        function update() {
          const isPortrait = window.innerHeight >= window.innerWidth
          setIsMobile(isPortrait)
        }

        // initial run and listeners
        update()
        window.addEventListener('resize', update)
        window.addEventListener('orientationchange', update)
        return () => {
          window.removeEventListener('resize', update)
          window.removeEventListener('orientationchange', update)
        }
      }, [])

      const circleSize = isIpad ? 55 : isMobile ? 32 : 55
      const swatchBorder = 2.5 // matches your --swatch-border / visual rhythm
      const presetInnerInset = swatchBorder

      // helper to check a "white" value (loose so '#fff' also works)
      const isWhite = (val) => {
        if (!val) return false
        const v = String(val).trim().toLowerCase()
        return v === '#ffffff' || v === '#fff' || v === 'white'
      }

      // floating tooltip state
      const [floatingTooltip, setFloatingTooltip] = React.useState({
        visible: false,
        text: '',
        left: 0,
        top: 0
      })

      // hovered swatch for parity/accessibility
      const [hoveredSwatch, setHoveredSwatch] = React.useState(null)

      // hide tooltip on scroll/resize
      React.useEffect(() => {
        function hide() {
          setFloatingTooltip((t) => (t.visible ? { visible: false, text: '', left: 0, top: 0 } : t))
          setHoveredSwatch(null)
        }
        window.addEventListener('scroll', hide, { passive: true })
        window.addEventListener('resize', hide)
        return () => {
          window.removeEventListener('scroll', hide)
          window.removeEventListener('resize', hide)
        }
      }, [])

      function showFloatingTooltip(text, targetEl) {
        if (!targetEl || typeof targetEl.getBoundingClientRect !== 'function') {
          setFloatingTooltip({ visible: true, text, left: window.innerWidth / 2, top: 100 })
          return
        }
        // Get the zoom level from the footer
        const footer = document.querySelector('.desktop-footer-pad-width')
        const zoom = footer ? parseFloat(footer.style.zoom || '1') : 1

        const r = targetEl.getBoundingClientRect()
        // Adjust for zoom: divide by zoom to get actual position
        const left = (r.left + r.width / 2) / zoom
        const top = (r.bottom + 8) / zoom
        setFloatingTooltip({ visible: true, text, left, top })
      }
      function hideFloatingTooltip() {
        setFloatingTooltip({ visible: false, text: '', left: 0, top: 0 })
      }

      return (
        <div className="option-content" style={{ position: 'relative' }}>
          <style
            dangerouslySetInnerHTML={{
              __html: `
              /* hide per-swatch tooltip elements - floating tooltip is authoritative */
              .color-circle .tooltip { display: none !important; }
              .ohma-floating-tooltip {
                position: fixed;
                left: 0;
                top: 0;
                transform: translate(-50%, 0);
                padding: 6px 10px;
                border-radius: 6px;
                font-family: 'Jost', system-ui, -apple-system, 'Segoe UI', Roboto, 'Helvetica Neue', Arial;
                font-size: 13px;
                font-weight: 600;
                white-space: nowrap;
                background: rgba(0, 0, 0, 0.9);
                color: white;
                pointer-events: none;
                z-index: 10000;
              }
            `
            }}
          />

          <div className="header-and-selected" style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '0' }}>
            <div className="option-header header-fabric" style={{ display: 'flex', alignItems: 'center', gap: 0, whiteSpace: 'nowrap', flexShrink: 0 }}>
              <h1 style={{ margin: 0, lineHeight: 1, display: 'inline-block', whiteSpace: 'nowrap' }}>Fabric</h1>
            </div>
            <div className="selected-option">
              <div className="selected-color">{fabricColorOptions[fabricColor]}</div>
            </div>
          </div>

          <div className="color-options">
            <div
              className="color-options-container-fabric"
              // prevent accidental drag-select on desktop like the others
              onMouseDown={(e) => e.preventDefault()}>
              {Object.entries(fabricColorOptions).map(([value, name]) => {
                const isActive = fabricColor === value

                // outer container style mirrors the circleContainerStyle used elsewhere
                const circleContainerStyle = {
                  width: circleSize,
                  height: circleSize,
                  flex: '0 0 auto',
                  boxSizing: 'border-box',
                  position: 'relative',
                  borderRadius: '50%',
                  cursor: 'pointer',
                  background: 'transparent',
                  display: 'inline-block'
                }

                // choose border: black on mobile white swatch, custom color on desktop
                const whiteBorderDesktop = '2px solid #FBF9F3' // your requested desktop border color
                const whiteBorderMobile = '2px solid rgba(0,0,0,0.15)'
                const borderForWhite = isWhite(value) ? (isMobile ? whiteBorderMobile : whiteBorderDesktop) : 'none'

                // inner swatch style (the visible color/texture)
                const innerStyle = {
                  position: 'absolute',
                  inset: presetInnerInset, // keep consistent inset with other swatches
                  borderRadius: '50%',
                  pointerEvents: 'none',
                  zIndex: 1,
                  boxSizing: 'border-box',
                  backgroundColor: value, // assume simple color hex / named color
                  border: borderForWhite
                }

                return (
                  <div
                    key={value}
                    className={`color-circle ${isActive ? 'active' : ''}`}
                    role="button"
                    tabIndex={0}
                    aria-pressed={isActive}
                    title={name}
                    onClick={() => {
                      setFabricColor(value)
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault()
                        setFabricColor(value)
                      }
                    }}
                    onPointerEnter={(e) => {
                      setHoveredSwatch(value)
                      showFloatingTooltip(name, e.currentTarget)
                    }}
                    onPointerLeave={() => {
                      setHoveredSwatch(null)
                      hideFloatingTooltip()
                    }}
                    onFocus={(e) => {
                      setHoveredSwatch(value)
                      showFloatingTooltip(name, e.currentTarget)
                    }}
                    onBlur={() => {
                      setHoveredSwatch(null)
                      hideFloatingTooltip()
                    }}
                    style={circleContainerStyle}>
                    {/* inner visible color */}
                    <div className="inner" style={innerStyle} />

                    {/* tooltip (kept for markup parity but hidden via CSS) */}
                    <div className="tooltip" role="tooltip" aria-hidden="true">
                      {name}
                    </div>
                  </div>
                )
              })}
            </div>
          </div>

          {/* Floating tooltip - single element avoids layout shifts */}
          <div
            className="ohma-floating-tooltip"
            aria-hidden={!floatingTooltip.visible}
            style={{
              left: floatingTooltip.left,
              top: floatingTooltip.top,
              opacity: floatingTooltip.visible ? 1 : 0,
              visibility: floatingTooltip.visible ? 'visible' : 'hidden',
              transform: floatingTooltip.visible ? 'translate(-50%, 0)' : 'translate(-50%, 6px)'
            }}>
            {floatingTooltip.text}
          </div>
        </div>
      )
    })(),

    '5. Bottom Fabric Color': (() => {
      // inside your component (replace your current isMobile const)
      const [isMobile, setIsMobile] = React.useState(() => {
        if (typeof window === 'undefined') return true
        return window.innerHeight >= window.innerWidth // true for all portrait
      })

      React.useEffect(() => {
        if (typeof window === 'undefined') return

        function update() {
          const isPortrait = window.innerHeight >= window.innerWidth
          setIsMobile(isPortrait)
        }

        // initial run and listeners
        update()
        window.addEventListener('resize', update)
        window.addEventListener('orientationchange', update)
        return () => {
          window.removeEventListener('resize', update)
          window.removeEventListener('orientationchange', update)
        }
      }, [])

      const circleSize = isIpad ? 55 : isMobile ? 32 : 55
      const swatchBorder = 2.5
      const presetInnerInset = swatchBorder

      const isWhite = (val) => {
        if (!val) return false
        const v = String(val).trim().toLowerCase()
        return v === '#ffffff' || v === '#fff' || v === 'white'
      }

      // floating tooltip state
      const [floatingTooltip, setFloatingTooltip] = React.useState({
        visible: false,
        text: '',
        left: 0,
        top: 0
      })
      const [hoveredSwatch, setHoveredSwatch] = React.useState(null)

      React.useEffect(() => {
        function hide() {
          setFloatingTooltip((t) => (t.visible ? { visible: false, text: '', left: 0, top: 0 } : t))
          setHoveredSwatch(null)
        }
        window.addEventListener('scroll', hide, { passive: true })
        window.addEventListener('resize', hide)
        return () => {
          window.removeEventListener('scroll', hide)
          window.removeEventListener('resize', hide)
        }
      }, [])

      function showFloatingTooltip(text, targetEl) {
        if (!targetEl || typeof targetEl.getBoundingClientRect !== 'function') {
          setFloatingTooltip({ visible: true, text, left: window.innerWidth / 2, top: 100 })
          return
        }
        // Get the zoom level from the footer
        const footer = document.querySelector('.desktop-footer-pad-width')
        const zoom = footer ? parseFloat(footer.style.zoom || '1') : 1

        const r = targetEl.getBoundingClientRect()
        // Adjust for zoom: divide by zoom to get actual position
        const left = (r.left + r.width / 2) / zoom
        const top = (r.bottom + 8) / zoom
        setFloatingTooltip({ visible: true, text, left, top })
      }
      function hideFloatingTooltip() {
        setFloatingTooltip({ visible: false, text: '', left: 0, top: 0 })
      }

      return (
        <div className="option-content">
          <div className="header-and-selected" style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '0' }}>
            <div className="option-header header-logo-fill" style={{ display: 'flex', alignItems: 'center', gap: 0, whiteSpace: 'nowrap', flexShrink: 0 }}>
              <h1 style={{ margin: 0, lineHeight: 1, display: 'inline-block', whiteSpace: 'nowrap' }}>Logo Fill</h1>
            </div>
            <div className="selected-option">
              <div className="selected-color">
                {bottomfabricColorOptions[bottomFabricColor]}
                {premiumFabricColor[bottomFabricColor] && (
                  <span style={{ marginLeft: 6, color: '#e63946', fontSize: '0.9em', fontWeight: 'bold' }}>+${premiumFabricColor[bottomFabricColor]}</span>
                )}
              </div>
            </div>
          </div>

          <style
            dangerouslySetInnerHTML={{
              __html: `
              .color-circle .tooltip { display: none !important; }
              .ohma-floating-tooltip {
                position: fixed;
                left: 0;
                top: 0;
                transform: translate(-50%, 0);
                padding: 6px 10px;
                border-radius: 6px;
                font-family: 'Jost', system-ui, -apple-system, 'Segoe UI', Roboto, 'Helvetica Neue', Arial;
                font-size: 13px;
                font-weight: 600;
                white-space: nowrap;
                background: #000;
                color: #fff;
                /* box-shadow removed */
                z-index: 1000000;
                pointer-events: none;
                transition: opacity 160ms cubic-bezier(0.2,0.9,0.2,1), transform 160ms cubic-bezier(0.2,0.9,0.2,1);
                max-width: 220px;
                overflow: hidden;
                text-overflow: ellipsis;
              }
            `
            }}
          />

          <div className="color-options">
            <div className="color-options-container-fabric" onMouseDown={(e) => e.preventDefault()}>
              {Object.entries(bottomfabricColorOptions).map(([value, name]) => {
                const isNone = value === 'none'
                const isActive = bottomFabricColor === value

                // Keep the circle color as-is from colors.js (#404040 for black)
                // The mesh color mapping happens in material application

                const circleContainerStyle = {
                  width: circleSize,
                  height: circleSize,
                  flex: '0 0 auto',
                  boxSizing: 'border-box',
                  position: 'relative',
                  borderRadius: '50%',
                  cursor: 'pointer',
                  background: 'transparent',
                  display: 'inline-block'
                }

                // Only add border for white swatches — black/dark circles get no border
                const whiteBorder = isWhite(value) ? (isMobile ? '2px solid rgba(0,0,0,0.15)' : '2px solid #FBF9F3') : 'none'

                const isPride = value === '#PRIDE'

                const innerStyle = isNone
                  ? {
                      position: 'absolute',
                      inset: presetInnerInset,
                      borderRadius: '50%',
                      pointerEvents: 'none',
                      zIndex: 1,
                      boxSizing: 'border-box',
                      background: 'repeating-conic-gradient(#ccc 0% 25%, #eee 0% 50%)',
                      border: '2px dashed #888'
                    }
                  : isPride
                  ? {
                      // Rainbow stripes via CSS linear-gradient with hard color
                      // stops — no raster image means no pixelated edges at any
                      // swatch size, and the parent border-radius clips it
                      // cleanly into a circle.
                      position: 'absolute',
                      inset: presetInnerInset,
                      borderRadius: '50%',
                      pointerEvents: 'none',
                      zIndex: 1,
                      boxSizing: 'border-box',
                      background:
                        'linear-gradient(to bottom, ' +
                        '#e40303 0%, #e40303 16.66%, ' +
                        '#ff8c00 16.66%, #ff8c00 33.33%, ' +
                        '#ffed00 33.33%, #ffed00 50%, ' +
                        '#008026 50%, #008026 66.66%, ' +
                        '#004dff 66.66%, #004dff 83.33%, ' +
                        '#750787 83.33%, #750787 100%)',
                      border: 'none'
                    }
                  : {
                      position: 'absolute',
                      inset: presetInnerInset,
                      borderRadius: '50%',
                      pointerEvents: 'none',
                      zIndex: 1,
                      boxSizing: 'border-box',
                      background: value,
                      border: whiteBorder
                    }

                return (
                  <div key={value} className="color-circle-wrapper" style={{ display: 'inline-block' }}>
                    <div
                      className={`color-circle ${isActive ? 'active' : ''}`}
                      role="button"
                      tabIndex={0}
                      aria-pressed={isActive}
                      title={name}
                      onClick={() => setBottomFabricColor(value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault()
                          setBottomFabricColor(value)
                        }
                      }}
                      onPointerEnter={(e) => {
                        setHoveredSwatch(value)
                        showFloatingTooltip(name, e.currentTarget)
                      }}
                      onPointerLeave={() => {
                        setHoveredSwatch(null)
                        hideFloatingTooltip()
                      }}
                      onFocus={(e) => {
                        setHoveredSwatch(value)
                        showFloatingTooltip(name, e.currentTarget)
                      }}
                      onBlur={() => {
                        setHoveredSwatch(null)
                        hideFloatingTooltip()
                      }}
                      style={{ ...circleContainerStyle, position: 'relative' }}>
                      {/* inner visual (color or pattern) */}
                      <div className="inner" style={innerStyle} />

                      {/* diagonal slash for 'none' to make the "no fabric" explicit */}
                      {isNone && (
                        <div
                          aria-hidden="true"
                          style={{
                            position: 'absolute',
                            left: '50%',
                            top: '50%',
                            width: '70%',
                            height: 2,
                            backgroundColor: '#888',
                            transform: 'translate(-50%, -50%) rotate(45deg)',
                            pointerEvents: 'none',
                            zIndex: 2
                          }}
                        />
                      )}

                      {/* tooltip uses markup for parity but hidden via CSS */}
                      <div className="tooltip" role="tooltip" aria-hidden="true">
                        {name}
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>
          </div>

          {/* Floating tooltip */}
          <div
            className="ohma-floating-tooltip"
            aria-hidden={!floatingTooltip.visible}
            style={{
              left: floatingTooltip.left,
              top: floatingTooltip.top,
              opacity: floatingTooltip.visible ? 1 : 0,
              visibility: floatingTooltip.visible ? 'visible' : 'hidden',
              transform: floatingTooltip.visible ? 'translate(-50%, 0)' : 'translate(-50%, 6px)'
            }}>
            {floatingTooltip.text}
          </div>
        </div>
      )
    })(),

    '5. Bottom Screen Type': (() => {
      // inside your component (replace your current isMobile const)
      const [isMobile, setIsMobile] = React.useState(() => {
        if (typeof window === 'undefined') return true
        return window.innerHeight >= window.innerWidth // true for all portrait
      })

      React.useEffect(() => {
        if (typeof window === 'undefined') return

        function update() {
          const isPortrait = window.innerHeight >= window.innerWidth
          setIsMobile(isPortrait)
        }

        // initial run and listeners
        update()
        window.addEventListener('resize', update)
        window.addEventListener('orientationchange', update)
        return () => {
          window.removeEventListener('resize', update)
          window.removeEventListener('orientationchange', update)
        }
      }, [])

      const isLandscape = typeof window !== 'undefined' && !isMobile && window.matchMedia('(orientation: landscape)').matches

      const [hoveredBottomScreenType, setHoveredBottomScreenType] = React.useState(null)

      const highlightSize = 54
      const strokeWidth = isLandscape ? 1.4 : 1
      const rectInset = isLandscape ? 1 : 1.5
      const rectSize = isLandscape ? 22 : 21
      const cornerRadius = isLandscape ? 6 : 4



      return (
        <div>
          <div className="option-content">
            {/* header row - inline info icon that opens the modal to the Custom Logo tab */}
            <div className="header-and-selected" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div className="option-header header-logo" style={{ display: 'flex', alignItems: 'center', gap: 0 }}>
                <h1 style={{ margin: 0, lineHeight: 1, display: 'inline-block', whiteSpace: 'nowrap' }}>Logo</h1>

                {/* subtle info icon (not pill) */}
                <button
                  type="button"
                  className="info-button"
                  aria-label="More about logo options"
                  onClick={() => {
                    // Open footer with this tab
                    if (isMobile && typeof openFooterWithTab === 'function') {
                      openFooterWithTab('customLogo')
                    }
                    // Open desktop modal
                    if (!isMobile && typeof openProductModalTab === 'function') {
                      openProductModalTab('customLogo')
                    }
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault()
                      e.currentTarget.click()
                    }
                  }}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                    <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="1.4" />
                    <circle cx="12" cy="8" r="1" fill="currentColor" />
                    <path d="M11.25 11.5h1.5v5h-1.5z" fill="currentColor" />
                  </svg>
                </button>
              </div>

              <div className="selected-option">
                <div className="selected-color">
                  {bottomScreenTypeOptions[bottomScreenType].label}
                  {premiumLogo[bottomScreenTypeOptions[bottomScreenType].label] && (
                    <span style={{ marginLeft: 6, color: '#e63946', fontSize: '0.9em', fontWeight: 'bold' }}>
                      +${premiumLogo[bottomScreenTypeOptions[bottomScreenType].label]}
                    </span>
                  )}
                </div>
              </div>
            </div>

            <div className="microphone-options-container" onMouseDown={(e) => e.preventDefault()}>
              {Object.entries(bottomScreenTypeOptions).map(([value, { label }]) => {
                const isActive = bottomScreenType === value

                return (
                  <div
                    key={value}
                    role="button"
                    tabIndex={0}
                    aria-pressed={isActive}
                    className={`microphone-type-choice ${isActive ? 'active' : ''}`}
                    onClick={() => handleBottomScreenTypeSelect(value, label)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault()
                        handleBottomScreenTypeSelect(value, label)
                      }
                    }}>
                    <h2 className="microphone-type-label" style={{ margin: 0 }}>
                      {label}
                    </h2>
                  </div>
                )
              })}
            </div>
          </div>
        </div>
      )
    })(),

    '6. Microphone Type': (() => {
      // helper local to this section (same pattern as section 5)
      function openProductModalTab(tab = 'micType') {
        // try module-scoped openModal (might return a handler)
        try {
          if (typeof openModal === 'function') {
            const maybeHandler = openModal(tab)
            if (typeof maybeHandler === 'function') maybeHandler()
            return
          }
        } catch (e) {}

        // try global/window openModal
        try {
          if (typeof window !== 'undefined' && typeof window.openModal === 'function') {
            const maybeHandler = window.openModal(tab)
            if (typeof maybeHandler === 'function') maybeHandler()
            return
          }
        } catch (e) {}

        // try modal state setters if they exist in scope
        try {
          if (typeof setModalInitialTab === 'function' && typeof setModalOpen === 'function') {
            setModalInitialTab(tab)
            setModalOpen(true)
            return
          }
        } catch (e) {}

        // final fallback: dispatch event
        if (typeof window !== 'undefined' && typeof window.dispatchEvent === 'function') {
          window.dispatchEvent(new CustomEvent('ohma:open-product-modal', { detail: { tab } }))
          return
        }

        // console.warn('openProductModalTab: no available method to open modal for tab', tab)
      }


      return (
        <div onMouseEnter={() => setIsMicButtonHovered(true)} onMouseLeave={() => setIsMicButtonHovered(false)}>
          <div className="option-content">
            {/* header row with inline info icon */}
            <div className="header-and-selected" style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '0' }}>
              <div className="option-header" style={{ display: 'flex', alignItems: 'center', gap: 0, whiteSpace: 'nowrap', flexShrink: 0 }}>
                <h1 style={{ margin: 0, lineHeight: 1, display: 'inline-block', whiteSpace: 'nowrap' }}>Mic Type</h1>

                {/* subtle info icon button */}
                {/* Mic Type info button - robust replacement for section 6 */}
                <button
                  type="button"
                  className="info-button"
                  aria-label="More about microphone types"
                  onClick={() => {
                    // Open footer with this tab
                    if (isMobile && typeof openFooterWithTab === 'function') {
                      openFooterWithTab('micType')
                    }
                    // Open desktop modal
                    if (!isMobile && typeof openProductModalTab === 'function') {
                      openProductModalTab('micType')
                    }
                  }}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                    <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="1.4" />
                    <circle cx="12" cy="8" r="1" fill="currentColor" />
                    <path d="M11.25 11.5h1.5v5h-1.5z" fill="currentColor" />
                  </svg>
                </button>
              </div>

              <div className="selected-option">
                <div className="selected-color">{microphoneTypeOptions[microphoneType].label}</div>
              </div>
            </div>

            <div className="microphone-options-container">
              {Object.entries(microphoneTypeOptions).map(([value, { label }]) => {
                const isActive = microphoneType === value
                return (
                  <div
                    key={value}
                    role="button"
                    tabIndex={0}
                    aria-pressed={isActive}
                    className={`microphone-type-choice ${isActive ? 'active' : ''}`}
                    onClick={() => setMicrophoneType(value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault()
                        setMicrophoneType(value)
                      }
                    }}>
                    <h2 className="microphone-type-label" style={{ margin: 0 }}>
                      {label}
                    </h2>
                  </div>
                )
              })}
            </div>
          </div>
        </div>
      )
    })(),

    '7. Pop Filter': (() => {
      // Use isDesktop from outer scope (defined via useMediaQuery) — avoids illegal
      // useState/useEffect calls inside an IIFE which violate React's rules of hooks
      // and cause page reloads when setConfig triggers a re-render.
      const isMobile = !isDesktop

      function openProductModalTab(tab = 'popFilter', initialColor = null) {
        // Set initial color/selection if provided
        if (initialColor && typeof setModalInitialColor === 'function') {
          setModalInitialColor(initialColor)
        }
        try {
          if (typeof openModal === 'function') {
            const maybeHandler = openModal(tab)
            if (typeof maybeHandler === 'function') maybeHandler()
            return
          }
        } catch (e) {}

        try {
          if (typeof window !== 'undefined' && typeof window.openModal === 'function') {
            const maybeHandler = window.openModal(tab)
            if (typeof maybeHandler === 'function') maybeHandler()
            return
          }
        } catch (e) {}

        try {
          if (typeof setModalInitialTab === 'function' && typeof setModalOpen === 'function') {
            setModalInitialTab(tab)
            setModalOpen(true)
            return
          }
        } catch (e) {}

        if (typeof window !== 'undefined' && typeof window.dispatchEvent === 'function') {
          window.dispatchEvent(new CustomEvent('ohma:open-product-modal', { detail: { tab } }))
          return
        }

        // console.warn('openProductModalTab: no available method to open modal for tab', tab)
      }


      const popFilterOptions = {
        none: { label: 'None' },
        black: { label: 'Black' },
        custom: { label: 'Match Color' }
      }

      const currentPopFilterOption = !config.popFilterEnabled ? 'none' : config.popFilterMaterial === 'plastic' ? 'black' : 'custom'

      const handlePopFilterSelection = (option) => {
        if (option === 'none') {
          setConfig({ ...config, popFilterEnabled: false, popFilterColor: null, popFilterMaterial: 'plastic' })
        } else if (option === 'black') {
          // Black button = plastic black
          setConfig({ ...config, popFilterEnabled: true, popFilterColor: '#1c1d1d', popFilterMaterial: 'plastic' })
        } else if (option === 'custom') {
          // Custom button = painted metal, use body color (black for brass/black bodies)
          const isBrassBody = config.color === 'Brass' || config.color === '#d5b173'
          const isBlackBody = config.color === '#1A1A1A' || config.color === '#232221' || config.color === '#1c1d1d' || config.color.toUpperCase() === '#1A1A1A'

          const customColor = isBrassBody || isBlackBody ? '#232221' : config.color
          setConfig({ ...config, popFilterEnabled: true, popFilterColor: customColor, popFilterMaterial: 'metal' })
        }
      }

      return (
        <div>
          {/* Add-Ons Divider - Desktop only */}
          {!isMobile && (
            <div
              style={{
                borderTop: '1px solid #e5e7eb',
                marginTop: '24px',
                marginBottom: '16px',
                paddingTop: '16px'
              }}>
              <h2
                style={{
                  fontSize: '14px',
                  fontWeight: '700',
                  textTransform: 'uppercase',
                  letterSpacing: '0.1em',
                  color: '#6b7280',
                  margin: '0 0 16px 15px',
                  fontFamily: 'roc-grotesk-wide, sans-serif'
                }}>
                Add-Ons
              </h2>
            </div>
          )}

          <div className="option-content">
            <div className="header-and-selected" style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '0' }}>
              <div className="option-header" style={{ display: 'flex', alignItems: 'center', gap: 0, whiteSpace: 'nowrap', flexShrink: 0 }}>
                <h1 style={{ margin: 0, lineHeight: 1, display: 'inline-block', whiteSpace: 'nowrap' }}>
                  Pop Filter
                </h1>

                {/* Info icon button */}
                <button
                  type="button"
                  className="info-button"
                  aria-label="More about pop filters"
                  onClick={() => {
                    // Open footer with this tab, landing on whichOption
                    if (isMobile && typeof openFooterWithTab === 'function') {
                      openFooterWithTab('popFilter', 'whichOption')
                    }
                    // Open desktop modal
                    if (!isMobile && typeof openProductModalTab === 'function') {
                      openProductModalTab('popFilter', 'whichOption')
                    }
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault()
                      e.currentTarget.click()
                    }
                  }}>
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10" />
                    <path d="M12 16v-4M12 8h.01" />
                  </svg>
                </button>
              </div>

              <div className="selected-option">
                <div className="selected-color">
                  {currentPopFilterOption === 'none' ? 'None' : currentPopFilterOption === 'black' ? 'Black' : 'Custom'}
                  {currentPopFilterOption === 'black' && (
                    <span style={{ marginLeft: 6, color: '#e63946', fontSize: '0.9em', fontWeight: 'bold' }}>
                      +{currency.code === 'CAD' ? 'C' : ''}
                      {currency.symbol}
                      {convertPrice(POPFILTER_PRICE_BLACK)}
                    </span>
                  )}
                  {currentPopFilterOption === 'custom' && (
                    <span style={{ marginLeft: 6, color: '#e63946', fontSize: '0.9em', fontWeight: 'bold' }}>
                      +{currency.code === 'CAD' ? 'C' : ''}
                      {currency.symbol}
                      {convertPrice(POPFILTER_PRICE_CUSTOM)}
                    </span>
                  )}
                </div>
              </div>
            </div>

            <div className="microphone-options-container">
              {Object.entries(popFilterOptions).map(([key, { label }]) => {
                const isActive = currentPopFilterOption === key
                return (
                  <div
                    key={key}
                    role="button"
                    tabIndex={0}
                    aria-pressed={isActive}
                    className={`microphone-type-choice ${isActive ? 'active' : ''}`}
                    onClick={() => handlePopFilterSelection(key)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault()
                        handlePopFilterSelection(key)
                      }
                    }}>
                    <h2 className="microphone-type-label" style={{ margin: 0 }}>
                      {label}
                    </h2>
                  </div>
                )
              })}
            </div>
          </div>
        </div>
      )
    })()

    // Pad option removed — pads are now included by default in all condenser
    // mics (the $100 price increase to $899 reflects this). The padEnabled
    // state still exists for backward compat with saved builds, but there's
    // no longer a UI toggle. selectionContent simply omits the '7.5. Pad' key.
  }

  // The pad tab used to be filtered out for ribbon mics; now there's no pad
  // tab at all (pad is included by default in condensers, ribbons never had
  // it). filteredSelectionContent is kept as an alias so the rest of the file
  // continues to work without changes.
  const filteredSelectionContent = selectionContent


  const INITIAL_CAMERA_DISTANCE = 1.1 // Set this to your desired initial distance
  const INITIAL_CAMERA_POSITION = [0, 0.35, INITIAL_CAMERA_DISTANCE]
  const INITIAL_CAMERA_ROTATION = [0, 0, 0]
  const [isCameraMoving, setIsCameraMoving] = useState(false)

  // ---------- Reusable camera helpers (drop these in near your other camera functions) ----------

  // ---------- Unified camera-pose helpers (paste into same scope as your camera refs) ----------

  // tweak this to move everything farther / closer: lower -> farther, higher -> closer
  const DEFAULT_FRAME_FILL = 100 // try 75..95 (lower = farther away)
  const DEFAULT_VBIAS = 0.18
  const DEFAULT_ANCHORK = 0.52
  const DEFAULT_FOV = 35
  const CAMERA_DISTANCE_MULT = 1.6


  function boxToSphere(box) {
    const s = new THREE.Sphere()
    box.getBoundingSphere(s)
    return s
  }

  // R is sphere.radius, C is sphere.center
  function computeDistanceFromRadius(R, fovDeg = DEFAULT_FOV, frameFill = DEFAULT_FRAME_FILL, multiplier = CAMERA_DISTANCE_MULT) {
    // Convert FOV and compute the base distance for the radius R
    const fovRad = THREE.MathUtils.degToRad(fovDeg)
    const baseDist = R / Math.tan(fovRad * 0.5) // same as other math you used

    // frameFill is percent-like: 100 => exact fit, lower => farther away
    const dist = baseDist * (100 / Math.max(1e-6, frameFill))

    // Apply multiplier and clamp to sensible minimum
    const finalDist = dist * (typeof multiplier === 'number' ? multiplier : 1)
    return Math.max(finalDist, 0.01)
  }

  function computeCameraPoseFromBox(box, opts = {}) {
    opts = opts || {}
    const frameFill = typeof opts.frameFill === 'number' ? opts.frameFill : DEFAULT_FRAME_FILL
    const vBias = typeof opts.vBias === 'number' ? opts.vBias : DEFAULT_VBIAS
    const anchorK = typeof opts.anchorK === 'number' ? opts.anchorK : DEFAULT_ANCHORK
    const azimuthDeg = typeof opts.azimuthDeg === 'number' ? opts.azimuthDeg : 0
    const fov = typeof opts.fov === 'number' ? opts.fov : DEFAULT_FOV

    // allow an override multiplier per call (fallback to global)
    const multiplier = typeof opts.multiplier === 'number' ? opts.multiplier : CAMERA_DISTANCE_MULT

    const sphere = boxToSphere(box)
    const R = Math.max(1e-6, sphere.radius)
    const C = sphere.center.clone()

    // pass multiplier through to computeDistanceFromRadius
    const dist = computeDistanceFromRadius(R, fov, frameFill, multiplier)

    const fovRad = THREE.MathUtils.degToRad(fov)
    const viewH = 2 * dist * Math.tan(fovRad * 0.5)
    const yLift = vBias * viewH
    const targetY = C.y - R + anchorK * (2 * R)

    const az = THREE.MathUtils.degToRad(azimuthDeg)
    const camX = C.x + dist * Math.sin(az)
    const camZ = C.z + dist * Math.cos(az)
    const camY = targetY + yLift

    const position = new THREE.Vector3(camX, camY, camZ)
    const lookAt = new THREE.Vector3(C.x, targetY, C.z)

    return { position, lookAt, dist, R, center: C }
  }

  function moveCameraToPose(pose, duration = 800, opts = {}) {
    // opts: { preserveTargetY: boolean }

    // Cancel any ongoing camera animation
    if (cameraAnimationRef.current) {
      cameraAnimationRef.current.forEach((tween) => {
        if (tween && typeof tween.stop === 'function') tween.stop()
      })
      cameraAnimationRef.current = null
    }

    // Stop any glide left over from spinning the mic (the controls keep turning for a moment after a drag).
    // That leftover turn was fighting the move, so a click on a dot right after a spin sent the camera
    // around in circles. The controls apply and clear it in one update; the camera is put straight back.
    if (cameraRef.current && orbitControlsRef.current) {
      const controls = orbitControlsRef.current
      const keepPos = cameraRef.current.position.clone()
      const damping = controls.enableDamping
      controls.enableDamping = false
      controls.update()
      cameraRef.current.position.copy(keepPos)
      controls.update()
      controls.enableDamping = damping
    }

    if (!cameraRef.current || !orbitControlsRef.current) {
      if (cameraRef.current) {
        cameraRef.current.position.copy(pose.position)
        cameraRef.current.lookAt(pose.lookAt)
        cameraRef.current.updateProjectionMatrix()
      }
      if (orbitControlsRef.current) {
        orbitControlsRef.current.target.copy(pose.lookAt)
        orbitControlsRef.current.update()
      }
      baseDistanceRef.current = pose.dist
      return
    }

    // Check if camera is already at target position (within small threshold)
    const currentPos = cameraRef.current.position
    const currentTarget = orbitControlsRef.current.target
    const positionThreshold = 0.01 // Small threshold for position comparison
    const targetThreshold = 0.01

    const positionDistance = currentPos.distanceTo(pose.position)
    const targetDistance = currentTarget.distanceTo(pose.lookAt)

    if (positionDistance < positionThreshold && targetDistance < targetThreshold) {
      // Camera is already at target - skip animation
      baseDistanceRef.current = pose.dist
      return
    }

    // duration 0: place the camera directly (used for the first view, behind the loading screen)
    if (!(duration > 0)) {
      const lookAt = pose.lookAt.clone()
      if (opts.preserveTargetY) lookAt.y = orbitControlsRef.current.target.y
      cameraRef.current.position.copy(pose.position)
      cameraRef.current.lookAt(lookAt)
      cameraRef.current.updateProjectionMatrix()
      orbitControlsRef.current.target.copy(lookAt)
      orbitControlsRef.current.update()
      baseDistanceRef.current = pose.dist
      return
    }

    setIsCameraMoving(true)

    // if preserving Y, override pose.lookAt.y with the current orbit target's Y
    const finalLookAt = pose.lookAt.clone()
    if (opts.preserveTargetY && orbitControlsRef.current) {
      finalLookAt.y = orbitControlsRef.current.target.y
    }

    // Orbit around the mic instead of sliding in a straight line: front to back in a straight line passes
    // right over the mic, and the camera flips while it keeps looking at it. Angle, height and distance
    // are blended around a target that moves at the same pace.
    const startPos = cameraRef.current.position.clone()
    const startTarget = orbitControlsRef.current.target.clone()
    const fromSph = new THREE.Spherical().setFromVector3(startPos.clone().sub(startTarget))
    const toSph = new THREE.Spherical().setFromVector3(pose.position.clone().sub(finalLookAt))
    let dTheta = toSph.theta - fromSph.theta
    while (dTheta > Math.PI) dTheta -= 2 * Math.PI
    while (dTheta < -Math.PI) dTheta += 2 * Math.PI
    const lookNow = new THREE.Vector3()
    const sph = new THREE.Spherical()
    const prog = { t: 0 }
    // The footer can move while the camera turns (its own zoom waits until the turn is done). So the
    // distance the turn heads for is read from the footer every frame: the mic grows and shrinks with the
    // footer during the turn, and there's nothing left to catch up when it ends.
    const footerNow = () => (typeof sheetPercent?.get === 'function' ? sheetPercent.get() : snapPercents[1])
    const endRadius = () => {
      if (!pose.followFooter) return toSph.radius
      const d = getCameraDistance(footerNow())
      return d && typeof d === 'number' ? d : toSph.radius
    }
    const orbitTween = new TWEEN.Tween(prog)
      .to({ t: 1 }, duration)
      .easing(motionEase(TWEEN.Easing.Cubic.InOut))
      .onUpdate(() => {
        const t = prog.t
        lookNow.copy(startTarget).lerp(finalLookAt, t)
        sph.set(fromSph.radius + (endRadius() - fromSph.radius) * t, fromSph.phi + (toSph.phi - fromSph.phi) * t, fromSph.theta + dTheta * t)
        cameraRef.current.position.setFromSpherical(sph).add(lookNow)
        orbitControlsRef.current.target.copy(lookNow)
        cameraRef.current.lookAt(lookNow)
        orbitControlsRef.current.update()
      })
      .onComplete(() => {
        // final snap, at the distance for where the footer is now
        const r = endRadius()
        const endPos = pose.position.clone().sub(finalLookAt).normalize().multiplyScalar(r).add(finalLookAt)
        cameraRef.current.position.copy(endPos)
        cameraRef.current.lookAt(finalLookAt)
        orbitControlsRef.current.target.copy(finalLookAt)
        orbitControlsRef.current.update()

        // update canonical base distance
        baseDistanceRef.current = pose.followFooter ? r : pose.dist

        // re-anchor the mapping so future drag scales are relative to this canonical camera pose
        const uiPercent = typeof sheetPercent?.get === 'function' ? sheetPercent.get() : initialFooterValue
        initialMappingRef.current = getCameraDistance(uiPercent) || initialMappingRef.current || 1.0

        setIsCameraMoving(false)
        cameraAnimationRef.current = null
      })
      .onStop(() => setIsCameraMoving(false))
      .start()

    // Store tweens for cancellation
    cameraAnimationRef.current = [orbitTween]
  }

  // Core names you already use to compute center (adjust if you want)
  const CORE_CAMERA_NAMES = ['body' /* screen top will be resolved at runtime */]

  // ---------- public camera actions (replace previous implementations) ----------

  // One place that frames the mic from an angle: a box around the body, top screen and fabric, the pose at
  // that azimuth, and the distance from the footer framing (or a distance passed in). The front, side,
  // rotate-to-side and behind views all use it (they were four copies of the same code).
  function framedPoseAt(azimuthDeg, { customDistances = null, distance = null } = {}) {
    const root = sceneRef.current
    if (!root) return null
    const screenName = config?.screenType ? `${config.screenType}_top` : null
    const names = screenName ? [...CORE_CAMERA_NAMES, screenName, 'saati_top'] : [...CORE_CAMERA_NAMES, 'saati_top']
    let box = computeBoxFromNames(root, names)
    if (!box || box.isEmpty()) box = new THREE.Box3().setFromObject(root)
    const pose = computeCameraPoseFromBox(box, {
      azimuthDeg,
      frameFill: DEFAULT_FRAME_FILL,
      vBias: DEFAULT_VBIAS,
      anchorK: DEFAULT_ANCHORK,
      fov: DEFAULT_FOV
    })
    let dist = distance
    if (dist === null) {
      const footerPercent = typeof livePercent === 'number' ? livePercent : snapPercents[1]
      dist = getCameraDistance(footerPercent, customDistances)
    }
    if (dist && typeof dist === 'number') {
      const dir = pose.position.clone().sub(pose.lookAt)
      if (dir.lengthSq() > 1e-8) pose.position.copy(pose.lookAt.clone().add(dir.normalize().multiplyScalar(dist)))
      pose.dist = dist
    }
    // a view picked with the normal footer distances keeps following the footer while it moves
    pose.followFooter = customDistances === null && distance === null
    return pose
  }
  // the builder's side view: wider with the pop filter so it's in frame
  const sideAzimuthFor = (popOn) => (popOn ? -45 : -25)

  // How long a view change takes, from how far it turns: about 0.9 s for front to side, 2 s for front to back
  function turnDuration(pose) {
    const cam = cameraRef.current
    const ctl = orbitControlsRef.current
    if (!cam || !ctl || !pose) return 1500
    const a0 = Math.atan2(cam.position.x - ctl.target.x, cam.position.z - ctl.target.z)
    const a1 = Math.atan2(pose.position.x - pose.lookAt.x, pose.position.z - pose.lookAt.z)
    let d = Math.abs(a1 - a0)
    if (d > Math.PI) d = 2 * Math.PI - d
    return Math.round(700 + (1300 * d) / Math.PI)
  }

  function resetCamera(customDistances = null) {
    userZoomOffsetRef.current = 0
    const pose = framedPoseAt(0, { customDistances })
    if (!pose) return
    if (pose.dist) baseDistanceRef.current = pose.dist
    moveCameraToPose(pose, turnDuration(pose))
  }

  function resetCameraSide(customDistances = null, duration = null) {
    userZoomOffsetRef.current = 0
    const pose = framedPoseAt(sideAzimuthFor(config?.popFilterEnabled), { customDistances })
    if (!pose) return
    if (pose.dist) baseDistanceRef.current = pose.dist
    moveCameraToPose(pose, duration === null ? turnDuration(pose) : duration)
  }

  // Rotate to the side view keeping a distance (the current one unless given). azimuthOverride lets callers
  // pass the right angle when config may be stale.
  function rotateCameraToSide(targetDistance = null, azimuthOverride = null) {
    if (!cameraRef.current || !orbitControlsRef.current) return
    const useDist = targetDistance !== null ? targetDistance : cameraRef.current.position.distanceTo(orbitControlsRef.current.target)
    const pose = framedPoseAt(azimuthOverride !== null ? azimuthOverride : sideAzimuthFor(config?.popFilterEnabled), { distance: useDist })
    if (pose) moveCameraToPose(pose, 800)
  }

  function rotateCameraBehind() {
    userZoomOffsetRef.current = 0
    const pose = framedPoseAt(180)
    if (!pose) return
    if (pose.dist) baseDistanceRef.current = pose.dist
    moveCameraToPose(pose, turnDuration(pose))
  }

  function generateSKU({ micType, bodyColor, screenType, screenColor, fabricColor, logoType, bodyColorHex, screenColorHex }) {
    const micCode = options.micType[micType] || ''
    const screenCode = options.screenType[screenType] || ''
    const logoCode = options.logoType[logoType] || ''

    // Definitive hex → SKU code map — keys normalized to lowercase
    const hexToCode = {
      '#ebe9e1': 0,
      '#ffffff': 0,
      '#FFFFFF': 0, // White
      '#1c1d1d': 1,
      '#1a1a1a': 1,
      '#2b2b2b': 1, // Black
      'stainless steel': 2,
      '#9c9e9e': 2, // Satin Aluminum
      '#d5b173': 3, // Brass
      '#196962': 4, // Turquoise Teal
      '#d7be88': 9, // Coffee Cream
      '#975b3a': 10, // Terracotta
      '#d8763c': 11, // Burnt Orange
      '#ce984c': 13, // Gold
      '#c68883': 15, // Petal Pink
      '#926464': 19, // Rose Gold
      '#d4a53a': 21, // Sunflower
      '#645648': 100, // Smoked Bronze
      '#a16f48': 101, // Copper
      '#ffca0d': 102, // Formula Yellow
      '#5e2833': 103, // Spiced Berry
      '#a4402c': 104, // Calabrian Red
      '#56475e': 105, // Vera Violet
      '#79712f': 106, // Mosslight Green
      '#5e6d35': 107, // Verdant Green
      '#394a37': 108, // Evergreen
      '#344e4a': 109, // Meridian Blue
      '#4db494': 110, // Aqua Lucia
      '#802641': 111 // Dark Fuchsia
    }

    // Name → hex for reverse lookup
    const nameToHex = {}
    if (typeof colorOptions !== 'undefined') {
      Object.entries(colorOptions).forEach(([hex, name]) => {
        nameToHex[name] = hex
      })
    }

    const resolveCode = (val, hexFallback) => {
      if (!val && !hexFallback) return ''
      // If val is falsy but we have a hex fallback, use it directly
      if (!val && hexFallback) {
        const hex = String(hexFallback).trim().toUpperCase()
        return hex.startsWith('#') ? hex : `#${hex}`
      }
      const normalized = String(val).trim().toLowerCase()
      // Direct hex lookup (case-insensitive)
      if (hexToCode[normalized] !== undefined) return String(hexToCode[normalized])
      if (hexToCode[val] !== undefined) return String(hexToCode[val])
      // Name → hex → code
      const hexForName = nameToHex[val]
      if (hexForName) {
        const code = hexToCode[hexForName.toLowerCase()] ?? hexToCode[hexForName]
        if (code !== undefined) return String(code)
      }
      // Legacy options.colors name lookup
      const legacyCode = options.colors[val]
      if (legacyCode !== undefined && legacyCode !== null) return String(legacyCode)
      // Custom hex fallback
      if (hexFallback) {
        const hex = String(hexFallback).trim().toUpperCase()
        return hex.startsWith('#') ? hex : `#${hex}`
      }
      // val itself might be a hex
      if (String(val).startsWith('#') && String(val).length >= 4) {
        return String(val).toUpperCase()
      }
      return ''
    }

    const bodyCode = resolveCode(bodyColor, bodyColorHex)
    const screenColorCode = resolveCode(screenColor, screenColorHex)
    const fabricCode = fabricColor === 'none' ? '' : resolveCode(fabricColor, fabricColor?.startsWith?.('#') ? fabricColor : null)
    const padCode = padEnabled ? 'PAD' : ''

    return [micCode, bodyCode, screenCode, screenColorCode, fabricCode, logoCode, padCode].filter(Boolean).join('-')
  }

  const [undoStack, setUndoStack] = useState([])
  const undoStackRef = useRef([]) // for reading latest undo state inside handlers
  const lastPushedColorRef = useRef(color) // tracks last color pushed to avoid dupes



  function setLoadingState(button) {
    button.classList.add('loading')
    button.disabled = true
  }

  function clearLoadingState(button) {
    button.classList.remove('loading')
    button.disabled = false
  }

  useEffect(() => {
    if (window.productJson?.variants) {
      setVariants(window.productJson.variants)
    } else {
      fetch('/products/build-your-own.js')
        .then((r) => r.json())
        .then((p) => {
          setVariants(p.variants)
        })
        .catch(() => {})
    }
  }, [])

  const addToCart = async () => {
    const addToCartButton = document.querySelector('.add-to-cart')
    setLoadingState(addToCartButton)
    setLoadingMessage('Adding to Cart...')

    try {
      // --- selections / SKU ---
      const selectedOptions = {
        micType: microphoneType,
        bodyColor: colorOptions[color],
        screenType: screenTypeOptions[screenType]?.label,
        screenColor: screenColorOptions[screenColor],
        fabricColor: fabricColor,
        bottomFabricColor: bottomFabricColor,
        logoType: bottomScreenType,
        // Pass raw hex for custom colors so SKU can encode them
        bodyColorHex: colorOptions[color] ? null : color,
        screenColorHex: screenColorOptions[screenColor] ? null : screenColor
      }
      const sku = generateSKU(selectedOptions)

      if (!sceneRef.current) throw new Error('Scene not ready for thumbnail.')

      // The Final Step screen starts capturing and uploading the preview image as soon as it opens
      // (openConfirm), so by the time the button is pressed it is usually done already.
      const pending = pendingUpload.current
      if (pending) pending.claimed = true
      pendingUpload.current = null
      const fileid = pending?.fileid || Date.now()
      let awsThumbnailUrl = ''
      if (pending?.ready) {
        try {
          awsThumbnailUrl = (await Promise.race([pending.ready, new Promise((r) => setTimeout(() => r(''), 20000))])) || ''
        } catch (e) {
          awsThumbnailUrl = ''
        }
      }

      if (!awsThumbnailUrl) {
      // === Thumbnail capture (the Final Step one didn't finish) ===
      const result = await captureBuildPicture(CART_PICTURE)
      if (result?.cancelled) {
        clearLoadingState(addToCartButton)
        return
      }
      const thumbBlob = result?.blob || null

      // === Upload to Cloudflare Images ===

      if (thumbBlob) {
        try {
          // (a link the background upload already tried may be spent, so this always gets a fresh one)
          awsThumbnailUrl = await uploadToCloudflare(thumbBlob, `${fileid}_thumbnail.jpg`)
        } catch (err) {}
      } else {
      }

      }

      // === Heroku — also fire and forget ===
      ;(async () => {
        try {
          // Match the token-source pattern used everywhere else in this file:
          // prefer the CAA OAuth token from localStorage and fall back to
          // Shopify's Liquid-set global. Without this, users logged in via
          // OAuth show up as anonymous in admin builds.
          const token = localStorage.getItem('ohma_ctoken') || window.SHOPIFY_CUSTOMER_TOKEN || ''
          // anon_id groups anonymous cart-adds across visits on this device.
          let anonId = localStorage.getItem('ohma_anon_id')
          if (!anonId) {
            anonId = Math.random().toString(36).slice(2, 10) + Math.random().toString(36).slice(2, 10)
            localStorage.setItem('ohma_anon_id', anonId)
          }
          await fetch('https://customizer-test-nodejs-4c6a5bfd6dbd.herokuapp.com/api/receive-data', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'x-customer-token': token },
            body: JSON.stringify({
              source: 'cart',
              anon_id: anonId,
              color,
              colorName: colorOptions[color] ?? null,
              screenType,
              screenColor,
              fabricColor,
              bottomScreenType,
              microphoneType,
              Pic: awsThumbnailUrl,
              fileid,
              sku,
              bottomFabricColor,
              popFilterEnabled,
              popFilterColor,
              shockMountEnabled,
              shockMountColor,
              padEnabled,
              currentDesignId: currentDesignId ?? null,
              hasUnsavedChanges: hasUnsavedChanges ?? false
            })
          })
          // Refresh My Builds so the in-cart badge shows immediately
          setTimeout(() => {
            builderBus.refreshBuilds?.()
          }, 1000)
        } catch (e) {
          // Heroku failed silently — cart already completed
        }
      })()

      // Always update URL with fileid so cart links load the saved config
      // (build-your-own-ohma/<id> was a 404, so the back button from the cart landed on an error page)
      try {
        if (window.location.hostname.endsWith('ohmaworld.com')) window.history.pushState({}, '', `/products/build-your-ohma/?id=${fileid}`)
      } catch (e) {}

      // === Shopify ===
      const nameOrHex = (map, key) => map?.[key] ?? key ?? ''

      const shopifyProperties = {
        ...(currentDesignName ? { 'Build Name': currentDesignName } : {}),
        'Body Color': nameOrHex(colorOptions, color),
        'Screen Type': screenTypeOptions[screenType]?.label,
        'Screen Color': nameOrHex(screenColorOptions, screenColor),
        'Fabric Color': nameOrHex(fabricColorOptions, fabricColor),
        'Bottom Fabric Color': nameOrHex(bottomfabricColorOptions, bottomFabricColor),
        'Bottom Screen Type': bottomScreenType,
        'Microphone Type': microphoneType,
        // Accessories (only if enabled)
        ...(popFilterEnabled
          ? {
              'Pop Filter': 'Yes',
              'Pop Filter Color': popFilterLabel(config.popFilterMaterial, config.popFilterColor)
            }
          : {}),
        ...(shockMountEnabled
          ? {
              'Shock Mount': 'Yes',
              'Shock Mount Color': nameOrHex(colorOptions, shockMountColor)
            }
          : {}),
        ...(padEnabled && microphoneType !== 'Ribbon' ? { Pad: 'Add Pad' } : {}),
        // Underscored properties are hidden from the customer at checkout, in the cart and in order emails,
        // but still reach the order (admin, Heroku order webhook, ShipStation item options)
        _Pic: awsThumbnailUrl,
        _fileid: fileid,
        SKU: sku
      }

      let match = null

      // Pick the variant whose name describes this build (names set in Shopify, see shopify/variant-names.json).
      // Several upgrades cost the same, so matching on price alone could show the wrong description at checkout.
      const variantName = [
        bodyUpcharge >= 499 ? 'Brass body' : bodyUpcharge > 0 ? 'Custom paint color' : 'Painted body',
        screenUpcharge > 0 ? 'custom screen color' : null,
        logoUpcharge >= 199 ? 'custom logo' : null,
        popFilterEnabled ? (popFilterMaterial === 'plastic' ? 'black pop filter' : 'metal pop filter') : null,
        fabricUpcharge > 0 ? 'Pride logo fill' : null
      ]
        .filter(Boolean)
        .join(' + ')
      const named = variants.find((v) => v.title === variantName || v.option1 === variantName)
      if (named && (currency.code !== 'USD' || fullCents(named) === totalPriceUSD * 100)) match = named

      if (match) {
        // matched by name
      } else if (currency.code === 'USD') {
        // USD: Find exact match first, then closest within $5 tolerance
        // Pop filter add-on prices are handled as line item properties, not separate variants
        const targetCents = totalPriceUSD * 100
        match = variants.find((v) => fullCents(v) === targetCents)
        if (!match) {
          // Fall back to closest variant within $200 tolerance
          // (pop filter prices are line item properties, variant just needs to be close)
          const closest = variants.reduce((c, v) => {
            if (!c) return v
            return Math.abs(fullCents(v) - targetCents) < Math.abs(fullCents(c) - targetCents) ? v : c
          }, null)
          if (closest && Math.abs(fullCents(closest) - targetCents) <= 20000) match = closest
        }
        // No variant named for this build: it goes to checkout on a neighbouring variant (price or
        // description can be off). Tracked so the missing variant can be added in Shopify.
        if (match && !(named && match === named)) {
          track('cart_variant_fallback', { wanted: variantName, wanted_usd: totalPriceUSD, used: match.title || match.option1 || null, used_usd: fullCents(match) / 100 })
        }
      } else {
        // Other currencies: Calculate expected local price and find closest match
        if (variants.length > 0) {
          // Calculate conversion rate from first variant
          const firstVariantLocal = fullCents(variants[0]) / 100 // e.g., 1138 CAD
          const firstVariantUSD = 899 // Known base price
          const conversionRate = firstVariantLocal / firstVariantUSD

          // Expected price in local currency
          const expectedLocalPrice = Math.round(totalPriceUSD * conversionRate * 100)

          // Find variant with closest price
          match = variants.reduce((closest, variant) => {
            if (!closest) return variant
            const currentDiff = Math.abs(fullCents(variant) - expectedLocalPrice)
            const closestDiff = Math.abs(fullCents(closest) - expectedLocalPrice)
            return currentDiff < closestDiff ? variant : closest
          }, null)
        }
      }

      if (!match) {
        alert(`Sorry, we do not have a variant at $${totalPriceUSD} USD (${totalPrice} ${currency.code})`)
        throw new Error('No matching variant for price')
      }

      const shopifyResponse = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          items: [
            {
              id: match.id,
              quantity,
              properties: shopifyProperties
            }
          ]
        })
      })
      const respText = await shopifyResponse.text()
      if (!shopifyResponse.ok) throw new Error(`Shopify cart error ${shopifyResponse.status}: ${respText}`)
      // Sent as soon as the cart add succeeds (not at the redirect), so it has time to arrive
      track('added_to_cart', analyticsBuildProps(), { beacon: true })

      // Auto-save to My Builds if logged in and not already saved
      await ensureFreshLogin()
      const cartToken =
        window.SHOPIFY_CUSTOMER_TOKEN ||
        (() => {
          try {
            const exp = localStorage.getItem('ohma_ctoken_exp')
            if (exp && Date.now() > parseInt(exp)) return null
            return localStorage.getItem('ohma_ctoken') || null
          } catch (e) {
            return null
          }
        })()
      if (cartToken && !currentDesignId) {
        try {
          const screenLabel = screenTypeOptions[screenType]?.label ?? screenType ?? 'Motif'
          const bodyLabel = colorOptions[color] ?? color ?? 'Custom'
          const screenColorLabel = screenColorOptions[screenColor] ?? screenColor ?? ''
          const autoName = `${screenLabel} ${bodyLabel} / ${screenColorLabel} ${microphoneType ?? 'Condenser'}`.trim()
          // Use thumbnail already captured during this add-to-cart flow
          preSavedThumbnailRef.current = awsThumbnailUrl || null
          preSavedThumbnailPromiseRef.current = null
          const savedDesign = await handleSaveDesign(autoName)
          if (savedDesign) {
            builderBus.pendingDesign = savedDesign
            builderBus.pendingRefresh = true
          }
        } catch (e) {
          // Auto-save failed silently — cart add already succeeded
        }
      }

      // the button says Continue to Checkout, so go to checkout (the cart is still a tap away)
      window.location.href = '/checkout'
    } catch (err) {
      console.error('addToCart error:', err)
      alert('Sorry, something went wrong adding to cart. Please try again.')
    } finally {
      clearLoadingState(addToCartButton)
      setLoadingMessage(null)
    }
  }


  const shareProduct = useCallback(async () => {
    track('build_shared')
    try {
      // Generate a file ID immediately (without waiting for thumbnail generation)
      const fileid = Date.now() // Unique timestamp-based ID

      // Generate the shareable link immediately
      const shareableLink = `https://ohmaworld.com/products/build-your-ohma/?id=${fileid}`

      // Always update the current browser URL with the latest fileid
      // so a page refresh reloads this exact build from Heroku
      try {
        const currentUrl = new URL(window.location.href)
        currentUrl.searchParams.set('id', String(fileid))
        window.history.replaceState(null, '', currentUrl.toString())
      } catch (e) {
        // Non-fatal — URL update is best-effort (e.g. cross-origin iframe)
      }

      // Send data to Heroku in the background (don't wait for it)
      const initialData = {
        color: color,
        screenType: screenType,
        screenColor: screenColor,
        fabricColor: fabricColor,
        bottomScreenType: bottomScreenType,
        microphoneType: microphoneType,
        fileid: fileid, // Include the generated file ID
        bottomFabricColor: bottomFabricColor,
        popFilterEnabled: !!popFilterEnabled,
        popFilterColor: popFilterColor,
        popFilterMaterial: config.popFilterMaterial,
        shockMountEnabled: !!shockMountEnabled,
        shockMountColor: shockMountColor
      }

      // Send to Heroku in background (don't await)
      // Use the same token-source pattern as cart-add so share auto-saves
      // associate with logged-in customers (not anonymous in admin).
      // source: 'share' tells the server to tag this as a share-save in the
      // admin (vs. cart-add or manual save).
      // anon_id groups anonymous builds across visits on the same device so
      // they appear as one customer in admin rather than scattered NULL rows.
      const shareToken = localStorage.getItem('ohma_ctoken') || window.SHOPIFY_CUSTOMER_TOKEN || ''
      let anonId = localStorage.getItem('ohma_anon_id')
      if (!anonId) {
        anonId = Math.random().toString(36).slice(2, 10) + Math.random().toString(36).slice(2, 10)
        localStorage.setItem('ohma_anon_id', anonId)
      }
      fetch('https://customizer-test-nodejs-4c6a5bfd6dbd.herokuapp.com/api/receive-data', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-customer-token': shareToken
        },
        body: JSON.stringify({ ...initialData, source: 'share', anon_id: anonId })
      }).catch((err) => {
        // console.error('Heroku API error:', err)
      })

      // Generate and upload the thumbnail asynchronously (doesn't block sharing)
      generateThumbnailAndUpload(fileid)

      // Return the shareable link immediately
      return shareableLink
    } catch (error) {
      // console.error('Error generating share link:', error)
      throw error
    }
  }, [
    color,
    screenType,
    screenColor,
    fabricColor,
    bottomFabricColor,
    bottomScreenType,
    microphoneType,
    popFilterEnabled,
    popFilterColor,
    config.popFilterMaterial,
    shockMountEnabled,
    shockMountColor
  ])

  // Function to generate and upload the thumbnail
  // Picture for a shared build. Uses the share screenshot when one is captured for THIS share
  // (the share tab opening takes one); a screenshot from earlier could show a different build, so it
  // isn't reused. Copying the link without opening the share tab takes no screenshot, so after a short
  // wait it falls back to the same picture the cart uses. About 1 in 5 shares used to end up with no
  // picture because of these two cases.
  const generateThumbnailAndUpload = async (fileid) => {
    try {
      const startedAt = Date.now()
      const fresh = () => (latestShareBlobRef.current && latestShareBlobAtRef.current >= startedAt - 1500 ? latestShareBlobRef.current : null)
      let blob = fresh()
      let waited = 0
      while (!blob && waited < 12000) {
        await new Promise((r) => setTimeout(r, 250))
        waited += 250
        blob = fresh()
        // No share screenshot being taken (link copied without opening share): stop waiting
        if (!blob && waited >= 1500 && !_isGeneratingRef.current) break
      }

      let imageUrl = ''
      if (blob) imageUrl = await uploadToCloudflare(blob, `${fileid}_share_thumbnail.png`)
      if (!imageUrl) imageUrl = (await generateAndUploadThumbnailRef.current?.()) || ''
      if (!imageUrl) throw new Error('no picture')

      // Link the URL to the existing build on the server. /api/update-thumbnail
      // looks up the build by fileid (stored in config JSON) and patches in
      // the thumbnail URL using COALESCE so it never overwrites an existing one.
      const token = localStorage.getItem('ohma_ctoken') || window.SHOPIFY_CUSTOMER_TOKEN || ''
      const updateRes = await fetch('https://customizer-test-nodejs-4c6a5bfd6dbd.herokuapp.com/api/update-thumbnail', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-customer-token': token
        },
        body: JSON.stringify({ fileid: fileid, thumbnail_url: imageUrl })
      })
      if (!updateRes.ok) throw new Error('update-thumbnail HTTP ' + updateRes.status)
    } catch (error) {
      console.warn('[share-thumb] failed:', error?.message || error)
    }
  }

  // === END THUMBNAIL ===

  // === HIGH-RES INSTAGRAM SCREENSHOT (exposes to MobileFooterTabs) ===
  const generateInstagramScreenshot = async () => {
    // Reuse the share screenshot (same camera, same quality) and composite into 1080x1920 portrait
    const shareBlob = await generateShareScreenshotRef.current()
    if (!shareBlob) return null

    // Load the share blob into an image
    const shareImg = new Image()
    const shareUrl = URL.createObjectURL(shareBlob)
    await new Promise((resolve, reject) => {
      shareImg.onload = () => {
        if (!isMountedRef.current) {
          URL.revokeObjectURL(shareUrl)
          reject(new Error('Component unmounted'))
          return
        }
        resolve()
      }
      shareImg.onerror = reject
      shareImg.src = shareUrl
    })
    URL.revokeObjectURL(shareUrl)

    // Share screenshot is already 1080x1920 — use it directly for Instagram
    return shareBlob
  }

  // === SHARE MODAL SCREENSHOT (45 degrees on opposite side + logo overlay) ===
  const generateShareScreenshot = async () => {
    // On mobile, cancel if share tab is no longer active (X was pressed)
    if (isMobile && mobileFooterTabRef.current !== 'share') return null
    if (!sceneRef.current) {
      console.warn('[share image] no sceneRef')
      return null
    }

    // Find the microphone model
    let micRoot = null
    sceneRef.current.traverse((obj) => {
      if (!micRoot && obj.children.length > 10) {
        const hasMicParts = obj.children.some(
          (child) =>
            child.name &&
            (child.name.includes('Body') ||
              child.name.includes('body') ||
              child.name.includes('Logo') ||
              child.name.includes('Motif') ||
              child.name.includes('saati'))
        )
        if (hasMicParts) {
          micRoot = obj
        }
      }
    })

    if (!micRoot && groupRef?.current) {
      micRoot = groupRef.current
    }

    if (!micRoot) {
      console.warn('[share image] no micRoot')
      return null
    }

    // Share output: 1080x1920 (9:16 portrait)
    const SHARE_WIDTH = 1080
    const SHARE_HEIGHT = 1920
    const SHARE_FOV = LENS_FOV

    const hasPopFilter = popFilterEnabled
    const SHARE_PAD = hasPopFilter ? 1.124 : 0.998 // split difference between original and 75% larger

    // Before rendering, force the correct "visible" state as if the user is on
    // a normal tab (not mic type). This mirrors the useFrame else-branch exactly.
    // After the render we re-apply the hide if activeSelection is mic type.
    const _applyShowState = () => {
      const scene = sceneRef.current
      const saatiMesh = scene.getObjectByName('saati_top')
      const surroundTop = scene.getObjectByName('surround_top')
      const allScreenTypes = ['motif', 'stripes', 'scales', 'windows', 'holes', 'condenser', 'ribbon']
      const currentScreenMeshes = [
        scene.getObjectByName(`${screenType}_top`),
        ...(screenType === 'motif' ? [scene.getObjectByName('Motif_to_Blender_v1003'), scene.getObjectByName('Motif_to_Blender_v1003_1')] : [])
      ].filter(Boolean)
      const allScreenMeshes = allScreenTypes
        .flatMap((t) => [
          scene.getObjectByName(`${t}_top`),
          ...(t === 'motif' ? [scene.getObjectByName('Motif_to_Blender_v1003'), scene.getObjectByName('Motif_to_Blender_v1003_1')] : [])
        ])
        .filter(Boolean)

      if (saatiMesh) {
        saatiMesh.visible = true
        if (saatiMesh.material) {
          saatiMesh.material.transparent = true
          saatiMesh.material.opacity = SAATI_TOP_OPACITY
          saatiMesh.material.needsUpdate = true
        }
      }
      if (surroundTop) surroundTop.visible = true
      allScreenMeshes.forEach((m) => {
        if (m) m.visible = currentScreenMeshes.includes(m)
      })

      // Enforce mic type visibility.
      // IMPORTANT: exclude screen-type meshes (_top, _bottomback, _bottom suffixes)
      // e.g. 'ribbon_top' is a screen pattern mesh, not a mic internal part —
      // even though it contains 'ribbon'. Those are controlled by screenType, not microphoneType.
      const screenSuffixes = ['_top', '_bottomback', '_bottom']
      scene.traverse((obj) => {
        if (!obj.isMesh) return
        if (screenSuffixes.some((s) => obj.name.endsWith(s))) return // skip screen meshes
        const hasCapsule = obj.name.includes('capsule')
        const hasRibbon = obj.name.includes('ribbon')
        if (!hasCapsule && !hasRibbon) return
        if (microphoneType === 'Condenser') {
          obj.visible = hasCapsule
        } else if (microphoneType === 'Ribbon') {
          obj.visible = hasRibbon
        }
      })
    }
    const _applyHideState = () => {
      if (activeSelection !== '6. Microphone Type') return
      const scene = sceneRef.current
      const saatiMesh = scene.getObjectByName('saati_top')
      const surroundTop = scene.getObjectByName('surround_top')
      const allScreenTypes = ['condenser', 'ribbon', 'motif', 'stripes', 'scales', 'windows', 'holes']
      const allScreenMeshes = allScreenTypes
        .flatMap((t) => [
          scene.getObjectByName(`${t}_top`),
          ...(t === 'motif' ? [scene.getObjectByName('Motif_to_Blender_v1003'), scene.getObjectByName('Motif_to_Blender_v1003_1')] : [])
        ])
        .filter(Boolean)

      if (saatiMesh) {
        saatiMesh.visible = false
        if (saatiMesh.material) saatiMesh.material.opacity = 0
      }
      if (surroundTop) surroundTop.visible = false
      allScreenMeshes.forEach((m) => {
        if (m) m.visible = false
      })
    }

    // Load logo BEFORE locking the capture — so there are zero awaits
    // between _applyShowState() and renderer.render(). Any await in that
    // window lets the Suzi useEffect fire and overwrite mesh visibility.
    // Load logo with crossOrigin for clean canvas compositing
    const logoImg = new Image()
    logoImg.crossOrigin = 'anonymous'
    const logoDidLoad = await new Promise((resolve) => {
      logoImg.onload = () => {
        if (!isMountedRef.current) {
          resolve(false)
          return
        }
        resolve(true)
      }
      logoImg.onerror = () => resolve(false)
      setTimeout(() => resolve(false), 3000)
      logoImg.src = 'https://cdn.shopify.com/s/files/1/0564/1820/5796/files/Ohma_Logo_White_Full.webp'
    })

    // Calculate bounds (no awaits from here to render)
    const bbox = new THREE.Box3().setFromObject(micRoot)
    if (bbox.isEmpty()) {
      bbox.min.set(-0.1, -0.1, -0.1)
      bbox.max.set(0.1, 0.1, 0.1)
    }
    const sphere = bbox.getBoundingSphere(new THREE.Sphere())
    const center = sphere.center.clone()
    const radius = Math.max(sphere.radius, 1e-5)

    center.y -= radius * (hasPopFilter ? 0.068 : 0.078)
    center.x -= radius * 0.14

    // Now lock capture, wait for frame to drain, set mesh state, render — all synchronous
    isCapturingRef.current = true
    await new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(resolve)))

    const liveRendererShare = rendererRef.current

    // Pause R3F's animation loop so it doesn't fire a live render frame
    // while mesh visibility is in the mutated capture state — prevents the blink.
    if (liveRendererShare) liveRendererShare.setAnimationLoop(null)

    _applyShowState()

    const _restoreAll = () => {
      _applyHideState()
      isCapturingRef.current = false
      if (liveRendererShare && r3fAnimLoopRef.current) {
        liveRendererShare.setAnimationLoop(r3fAnimLoopRef.current)
      }
    }

    const cam = new THREE.PerspectiveCamera(SHARE_FOV, SHARE_WIDTH / SHARE_HEIGHT, 0.01, 1000)
    cam.userData.useAO = true // share images get the same crevice shadows as the live view

    micRoot.updateWorldMatrix(true, true)
    const worldQuat = new THREE.Quaternion()
    micRoot.getWorldQuaternion(worldQuat)

    // 25° without pop filter, 45° with pop filter (from left side)
    // 25°: sin=0.423, cos=0.906 | 45°: sin=cos=0.707
    const forward = hasPopFilter
      ? new THREE.Vector3(-0.707, 0, 0.707).applyQuaternion(worldQuat).normalize()
      : new THREE.Vector3(-0.423, 0, 0.906).applyQuaternion(worldQuat).normalize()
    const up = new THREE.Vector3(0, 1, 0).applyQuaternion(worldQuat).normalize()

    const fovHalfRad = (SHARE_FOV * Math.PI) / 360
    const distance = (radius * SHARE_PAD) / Math.sin(fovHalfRad)

    cam.position.copy(center).add(forward.multiplyScalar(distance))
    cam.up.copy(up)
    cam.lookAt(center)
    cam.near = Math.max(0.01, distance - radius * 5)
    cam.far = distance + radius * 5
    cam.updateProjectionMatrix()

    const renderer = rendererRef.current
    if (!renderer) {
      console.warn('[share image] no renderer')
      _restoreAll()
      return null
    }

    // ── Live-canvas capture ──────────────────────────────────────────────────
    // Render directly into the live canvas (hardware MSAA from antialias:true).
    // WebGLRenderTarget never gets hardware MSAA — antialias only applies to
    // the default framebuffer. This is the confirmed working approach.
    //
    // Desktop: render at 2×  (2160×3840) → downsample to 1080×1920
    //          MSAA + supersampling = maximum quality
    // Mobile:  render at 1×  (1080×1920) → toBlob directly
    //          MSAA only — avoids GPU memory pressure / context loss
    // ────────────────────────────────────────────────────────────────────────

    const isDesktop = phoneType === 'desktop'
    const liveCanvas = renderer.domElement
    // Phones: never make the canvas bigger than it already is. Growing it to 1080x1920 (about 70% more
    // pixels than a phone's live canvas, with antialiasing) went over iPhone Safari's graphics memory and
    // the page reloaded. Instead the picture is rendered in horizontal strips, each no bigger than the live
    // canvas, and stitched into the full 1080x1920 (an offset camera makes each strip an exact slice of the
    // same shot, so there's no seam and no loss of sharpness).
    const livePx = liveCanvas.width * liveCanvas.height || SHARE_WIDTH * SHARE_HEIGHT
    const strips = isDesktop ? 1 : Math.max(1, Math.ceil((SHARE_WIDTH * SHARE_HEIGHT) / livePx))
    const captureW = isDesktop ? SHARE_WIDTH * 2 : SHARE_WIDTH
    const captureH = isDesktop ? SHARE_HEIGHT * 2 : SHARE_HEIGHT

    const prevDPR = renderer.getPixelRatio()
    const prevSize = renderer.getSize(new THREE.Vector2())

    const prevCC = new THREE.Color()
    renderer.getClearColor(prevCC)
    const prevCA = renderer.getClearAlpha()

    // Final 1080x1920 picture (desktop downsamples 2x into it, phones draw their strips straight in)
    const finalCanvas = document.createElement('canvas')
    finalCanvas.width = SHARE_WIDTH
    finalCanvas.height = SHARE_HEIGHT
    const finalCtx = finalCanvas.getContext('2d')

    // Freeze the canvas visually on mobile — snapshot current frame and overlay it
    let freezeImg = null
    try {
      const rect = liveCanvas.getBoundingClientRect()
      freezeImg = document.createElement('img')
      freezeImg.src = liveCanvas.toDataURL('image/jpeg', 0.8)
      freezeImg.style.cssText = `position:fixed;left:${rect.left}px;top:${rect.top}px;width:${rect.width}px;height:${rect.height}px;z-index:1;pointer-events:none;`
      const mountPoint = document.getElementById('root') || document.body
      mountPoint.appendChild(freezeImg)
    } catch (e) {
      freezeImg = null
    }

    renderer.setPixelRatio(1)
    renderer.setRenderTarget(null)

    cam.aspect = SHARE_WIDTH / SHARE_HEIGHT
    cam.updateProjectionMatrix()

    renderer.setClearColor(0x91c3de, 1)

    let rawBlob = null
    if (isDesktop) {
      renderer.setSize(captureW, captureH, false)
      renderer.clear()
      renderer.render(sceneRef.current, cam)
      // Must toBlob BEFORE restoring canvas size — resizing wipes the pixel buffer
      rawBlob = await new Promise((resolve) => liveCanvas.toBlob(resolve, 'image/png', 1.0))
    } else {
      const stripH = Math.ceil(SHARE_HEIGHT / strips)
      for (let y = 0; y < SHARE_HEIGHT; y += stripH) {
        const h = Math.min(stripH, SHARE_HEIGHT - y)
        renderer.setSize(SHARE_WIDTH, h, false)
        cam.setViewOffset(SHARE_WIDTH, SHARE_HEIGHT, 0, y, SHARE_WIDTH, h)
        renderer.clear()
        renderer.render(sceneRef.current, cam)
        // copy this strip out before the next resize clears the canvas
        if (finalCtx) finalCtx.drawImage(liveCanvas, 0, 0, SHARE_WIDTH, h, 0, y, SHARE_WIDTH, h)
      }
      cam.clearViewOffset()
    }

    // Now safe to restore
    renderer.setPixelRatio(prevDPR)
    renderer.setSize(prevSize.x, prevSize.y, false)
    renderer.setClearColor(prevCC, prevCA)
    if (cameraRef.current) {
      cameraRef.current.aspect = prevSize.x / prevSize.y
      cameraRef.current.updateProjectionMatrix()
    }
    _restoreAll()

    // Remove freeze overlay now that canvas is restored
    if (freezeImg) {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          if (freezeImg.parentNode) freezeImg.parentNode.removeChild(freezeImg)
        })
      })
    }

    if (isDesktop && !rawBlob) {
      console.warn('[share image] toBlob returned null')
      return null
    }

    if (finalCtx) {
      if (isDesktop) {
        const img = new Image()
        const imgUrl = URL.createObjectURL(rawBlob)
        await new Promise((resolve, reject) => {
          img.onload = () => {
            if (!isMountedRef.current) {
              URL.revokeObjectURL(imgUrl)
              reject(new Error('Component unmounted'))
              return
            }
            resolve()
          }
          img.src = imgUrl
        })
        URL.revokeObjectURL(imgUrl)
        finalCtx.imageSmoothingEnabled = true
        finalCtx.imageSmoothingQuality = 'high'
        finalCtx.drawImage(img, 0, 0, captureW, captureH, 0, 0, SHARE_WIDTH, SHARE_HEIGHT)
      }

      if (logoDidLoad && logoImg.naturalWidth > 0) {
        try {
          const logoWidth = 279 // 273 + 2%
          const logoHeight = (logoImg.naturalHeight / logoImg.naturalWidth) * logoWidth
          const x = (SHARE_WIDTH - logoWidth) / 2 - 3
          const y = 1732

          finalCtx.drawImage(logoImg, x, y, logoWidth, logoHeight)
        } catch (e) {}
      }
    }

    const blob = await new Promise((resolve) => finalCanvas.toBlob(resolve, isDesktop ? 'image/png' : 'image/jpeg', isDesktop ? 1.0 : 0.92))
    finalCanvas.width = 0
    finalCanvas.height = 0
    return blob
  }

  const _igRef = React.useRef(generateInstagramScreenshot)
  useEffect(() => {
    _igRef.current = generateInstagramScreenshot
  })
  const _saveRef = useRef(null)
  useEffect(() => {
    builderBus.generateInstagramScreenshot = (...a) => _igRef.current(...a)
    builderBus.saveDesign = () => _saveRef.current?.()
    builderBus.loadDesign = (cfg, id, name) => handleLoadDesign(cfg, id, name)
    builderBus.setLoading = (msg) => setLoadingMessage(msg)
    builderBus.precaptureThumbnail = () => {
      preSavedThumbnailRef.current = null
      preSavedThumbnailPromiseRef.current = Promise.resolve(generateAndUploadThumbnailRef.current?.()).catch(() => null)
    }
    builderBus.closeFooter = () => {
      // Drop mobile footer to mid position when opening a build
      try {
        if (Array.isArray(snapPercents) && snapPercents.length > 0) {
          const midPercent = snapPercents[1] ?? snapPercents[snapPercents.length - 1]
          if (typeof animate === 'function') {
            animate(sheetPercent, midPercent, footerSpring({ type: 'spring', stiffness: 300, damping: 30 }))
          } else if (typeof sheetPercent?.set === 'function') {
            sheetPercent.set(midPercent)
          }
          if (typeof setLivePercent === 'function') setLivePercent(midPercent)
        }
      } catch (e) {}
    }
    builderBus.openConfirmForDesign = (design) => {
      setPendingCartDesign(design)
      const screenshot = design.share_thumbnail_url || design.share_thumbnail_base64 || design.thumbnail_url || null
      setPendingCartScreenshot(screenshot)
      setShowConfirmModal(true)
    }

    return () => {
      delete builderBus.generateInstagramScreenshot
      delete builderBus.saveDesign
      delete builderBus.loadDesign
      delete builderBus.setLoading
      delete builderBus.closeFooter
    }
  }, [])
  // === END INSTAGRAM SCREENSHOT ===

  // regenerateShareScreenshot uses useCallback for a stable reference — without it,
  // every App re-render creates a new function, causing the useEffect in
  // MobileFooterTabs/ProductModal to fire on every render while share tab is open.
  // The ref wrapper lets useCallback have empty deps while still calling the latest fn.
  const generateShareScreenshotRef = React.useRef(generateShareScreenshot)
  useEffect(() => {
    generateShareScreenshotRef.current = generateShareScreenshot
    generateAndUploadThumbnailRef.current = async () => {
      try {
        const result = await captureBuildPicture()
        const blob = result?.blob
        if (!blob) return null

        // Stash blob as base64 in localStorage keyed by a pending ID
        // so My Builds page can upload it if user navigates away before upload completes
        const pendingKey = `ohma_pending_thumb_${Date.now()}`
        try {
          const reader = new FileReader()
          const base64 = await new Promise((res, rej) => {
            reader.onload = () => {
              if (!isMountedRef.current) {
                rej(new Error('Component unmounted'))
                return
              }
              res(reader.result)
            }
            reader.readAsDataURL(blob)
          })
          localStorage.setItem('ohma_pending_thumb_key', pendingKey)
          localStorage.setItem(pendingKey, base64)
        } catch (e) {
          /* localStorage full or unavailable */
        }

        const imageUrl = await uploadToCloudflare(blob, `build_${Date.now()}.jpg`)
        // Failed: keep the pending copy in localStorage so My Builds can upload it later
        if (!imageUrl) return null

        // Upload succeeded — clear the pending blob from localStorage
        try {
          localStorage.removeItem(localStorage.getItem('ohma_pending_thumb_key') || pendingKey)
          localStorage.removeItem('ohma_pending_thumb_key')
        } catch (e) {}

        return imageUrl ?? null
      } catch (e) {
        return null
      }
    }
  })
  const _isGeneratingRef = useRef(false)
  const _cancelGenerateRef = useRef(false)
  const regenerateShareScreenshot = useCallback(async () => {
    if (_isGeneratingRef.current) return
    _isGeneratingRef.current = true
    _cancelGenerateRef.current = false
    // Clear immediately so spinner shows while we wait
    setShareScreenshotUrl(null)
    try {
      // Wait for renderer, animations, AND footer movement to settle
      const MAX_WAIT_MS = 6000
      const start = Date.now()
      while (Date.now() - start < MAX_WAIT_MS) {
        // Cancel if explicitly cancelled (mobile X button / tab switch)
        if (_cancelGenerateRef.current) {
          _isGeneratingRef.current = false
          return
        }
        const tweensRunning = TWEEN.getAll().length > 0
        const footerMoving = isFooterMovingRef.current
        if (!rendererRef.current || tweensRunning || footerMoving) {
          await new Promise((resolve) => setTimeout(resolve, 100))
        } else {
          // All settled: let two frames draw (the settled view is on the canvas) then capture
          await new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(resolve)))
          break
        }
      }
      // Final check
      if (!rendererRef.current || _cancelGenerateRef.current) return

      const blob = await generateShareScreenshotRef.current()
      if (blob) {
        const url = URL.createObjectURL(blob)
        // Revoke previous blob URL to free memory — each 1080px PNG is ~300KB
        // and without revocation they accumulate in the browser until page reload
        setShareScreenshotUrl((prev) => {
          if (prev && prev.startsWith('blob:')) URL.revokeObjectURL(prev)
          return url
        })
        // Store blob for upload at save time
        latestShareBlobRef.current = blob
        latestShareBlobAtRef.current = Date.now()
      }
    } catch (error) {
      console.warn('[share image] generateShareScreenshot failed:', error)
    } finally {
      _isGeneratingRef.current = false
    }
  }, []) // stable — uses ref internally

  function capitalizeFirstLetter(string) {
    return string.charAt(0).toUpperCase() + string.slice(1)
  }

  // Inside your component
  useEffect(() => {
    // Prepare the initial states for each selection to Shopify, except "Body Color"
    const selections = [
      { property: 'Bottom Screen Type', value: capitalizeFirstLetter(bottomScreenType) },
      { property: 'Microphone Type', value: capitalizeFirstLetter(microphoneType) }
    ]

    const bodyColorName = colorOptions[color] // Retrieve the display name for "Body Color
    const screenColorName = screenColorOptions[screenColor] // Directly retrieve the display name
    const fabricColorName = fabricColor // Use value directly
    const bottomfabricColorName = bottomFabricColor // Use value directly
    const screenTypeName = screenTypeOptions[screenType].label // Fetch the label using the key

    // Then, send the initial states for other selections
    selections.forEach((selection) => {})
  }, []) // The empty dependency array ensures this effect runs once on mount.

  const sendColorToShopify = (selectedColor) => {}

  // Repeat a similar structure for sending other types of selections
  const sendScreenTypeToShopify = useMemo(() => debounce((selectedScreenType) => {}, 500), [])

  const sendColorDebounced = useMemo(() => debounce(sendColorToShopify, 500), [])

  const sendCurrentStateToParent = () => {
    const currentState = {
      color,
      screenType,
      screenColor,
      fabricColor,
      bottomScreenType,
      microphoneType,
      bottomFabricColor,
      // accessory fields (booleans + colors)
      popFilterEnabled: !!popFilterEnabled,
      popFilterColor: popFilterColor || null,
      shockMountEnabled: !!shockMountEnabled,
      shockMountColor: shockMountColor || null
    }
  }

  const sendStateDebounced = useMemo(
    () => debounce(sendCurrentStateToParent, 500),
    [
      sendCurrentStateToParent,
      color,
      screenType,
      screenColor,
      fabricColor,
      bottomScreenType,
      microphoneType,
      bottomFabricColor,
      popFilterEnabled,
      popFilterColor,
      shockMountEnabled,
      shockMountColor
    ]
  )


  const selectionSummary = {
    'Body Color': colorOptions[color],
    'Screen Color': screenColorOptions[screenColor],
    'Screen Type': screenTypeOptions[screenType].label,
    'Fabric Color': fabricColorOptions[fabricColor],
    'Bottom Fabric Color': bottomfabricColorOptions[bottomFabricColor],
    'Bottom Screen Type': bottomScreenTypeOptions[bottomScreenType].label,
    'Microphone Type': microphoneTypeOptions[microphoneType].label
  }

  // Add this handler function near your other handlers (like handleShareClickDesktop)
  const [copyLinkCopied, setCopyLinkCopied] = React.useState(false)
  const [canvasSaveToast, setCanvasSaveToast] = React.useState(null) // null | 'saving' | 'saved'
  const [showBuildsDrawer, setShowBuildsDrawer] = React.useState(false)
  const [buildCount, setBuildCount] = React.useState(0)
  React.useEffect(() => {
    builderBus.incrementBuildCount = () => setBuildCount((prev) => prev + 1)
    return () => {
      delete builderBus.incrementBuildCount
    }
  }, [])
  const [saveButtonHover, setSaveButtonHover] = React.useState(false)
  const [buildsButtonHover, setBuildsButtonHover] = React.useState(false)

  // Update build count on mount and when drawer opens/closes
  React.useEffect(() => {
    const updateCount = async () => {
      await ensureFreshLogin()
      const token =
        window.SHOPIFY_CUSTOMER_TOKEN ||
        (() => {
          try {
            const exp = localStorage.getItem('ohma_ctoken_exp')
            if (exp && Date.now() > parseInt(exp)) return null
            return localStorage.getItem('ohma_ctoken') || null
          } catch (e) {
            return null
          }
        })()

      if (token) {
        // Logged in - fetch actual count from server
        try {
          const res = await fetch('https://customizer-test-nodejs-4c6a5bfd6dbd.herokuapp.com/api/designs', {
            headers: {
              'Content-Type': 'application/json',
              'x-customer-token': token
            }
          })
          const data = await res.json()
          const count = (data.designs || []).length
          setBuildCount(count)
        } catch (e) {
          // On error, default to 0
          setBuildCount(0)
        }
      } else {
        // Not logged in - count local builds
        const localBuilds = getLocalBuilds()
        setBuildCount(localBuilds.length)
      }
    }
    updateCount()
    // Also update when drawer state changes (might indicate save happened)
    const interval = setInterval(updateCount, 5000) // Check every 5 seconds instead of 1
    return () => clearInterval(interval)
  }, [showBuildsDrawer, canvasSaveToast])
  React.useEffect(() => {
    if (modalOpen || showBuildsDrawer) {
      setOrbitControlsEnabled(false)
    } else {
      const t = setTimeout(() => {
        setOrbitControlsEnabled(true)
        // Sync OrbitControls target after re-enabling so camera doesn't drift
        requestAnimationFrame(() => {
          if (orbitControlsRef.current) orbitControlsRef.current.update()
        })
      }, 500)
      return () => clearTimeout(t)
    }
  }, [modalOpen, showBuildsDrawer])
  const [drawerRefreshKey, setDrawerRefreshKey] = React.useState(0)
  const [drawerLoadConfirm, setDrawerLoadConfirm] = React.useState(null) // design to load after confirm

  // ── Local builds (non-logged-in) ──────────────────────────────────────────
  const LOCAL_BUILDS_KEY = 'ohma_local_builds'
  const LOCAL_BUILDS_LIMIT = 2

  const getLocalBuilds = () => {
    try {
      return JSON.parse(localStorage.getItem(LOCAL_BUILDS_KEY) || '[]')
    } catch (e) {
      return []
    }
  }

  // Returns false if the build couldn't be stored. Browser storage on ohmaworld.com is small and shared
  // with the whole site, so if it's full the build is stored without its large share picture (My Builds
  // falls back to the thumbnail), then without pictures, before giving up. It used to fail silently and
  // still show "Saved".
  const saveLocalBuild = (build) => {
    let builds
    try {
      builds = getLocalBuilds()
      builds.unshift(build)
    } catch (e) {
      return false
    }
    const attempt = (list) => {
      localStorage.setItem(LOCAL_BUILDS_KEY, JSON.stringify(list))
      return true
    }
    try {
      return attempt(builds)
    } catch (e) {}
    const slim = builds.map(({ share_thumbnail_base64, ...b }) => b)
    try {
      return attempt(slim)
    } catch (e) {}
    try {
      return attempt(slim.map(({ thumbnail_base64, ...b }) => b))
    } catch (e) {
      return false
    }
  }

  // ────────────────────────────────────────────────────────────────────────────

  const handleCanvasIconSave = React.useCallback(async () => {
    await ensureFreshLogin() // renew an expiring login first so a signed-in save isn't treated as a guest
    const token =
      window.SHOPIFY_CUSTOMER_TOKEN ||
      (() => {
        try {
          const exp = localStorage.getItem('ohma_ctoken_exp')
          if (exp && Date.now() > parseInt(exp)) return null
          return localStorage.getItem('ohma_ctoken') || null
        } catch (e) {
          return null
        }
      })()

    // Already saving — don't double-fire
    if (canvasSaveToast === 'saving') return

    // Auto-name from current config
    const screenLabel = screenTypeOptions[config.screenType]?.label ?? config.screenType ?? 'Motif'
    const bodyLabel = colorOptions[color] ?? color ?? 'Custom'
    const screenColorLabel = screenColorOptions[config.screenColor] ?? config.screenColor ?? ''
    const micLabel = config.microphoneType ?? 'Condenser'
    const autoName = `${screenLabel} ${bodyLabel} / ${screenColorLabel} ${micLabel}`.trim()

    if (!token) {
      // Not logged in — save locally up to limit
      const localBuilds = getLocalBuilds()
      if (localBuilds.length >= LOCAL_BUILDS_LIMIT) {
        setCanvasSaveToast('limit')
        setTimeout(() => setCanvasSaveToast(null), 3000)
        return
      }
      // Capture thumbnail as base64 for local storage
      const unfreezeLocal = !isDesktop ? freezeCanvas(0) : () => {}
      if (!isDesktop) {
        // Block camera updates during save — footer will animate while screenshot captures
        isCapturingRef.current = true
        builderBus.closeFooter?.()
      }
      let savedLocally = true
      setCanvasSaveToast('saving')
      setLoadingMessage('Saving Build...')
      try {
        const result = await captureBuildPicture()
        const blob = result?.blob
        let thumbBase64 = null
        if (blob) {
          thumbBase64 = await new Promise((res, rej) => {
            const reader = new FileReader()
            reader.onload = () => {
              if (!isMountedRef.current) {
                rej(new Error('Component unmounted'))
                return
              }
              res(reader.result)
            }
            reader.readAsDataURL(blob)
          }).catch(() => null)
        }
        const localFileid = Date.now()
        savedLocally = saveLocalBuild({
          id: `local_${localFileid}`,
          name: autoName,
          thumbnail_base64: thumbBase64,
          share_thumbnail_base64: await (async () => {
            try {
              // Always capture fresh so screenshot matches current config
              // Temporarily set tab ref to 'share' to bypass mobile tab guard
              const prevCancel = _cancelGenerateRef.current
              const prevTab = mobileFooterTabRef.current
              _cancelGenerateRef.current = false
              mobileFooterTabRef.current = 'share'
              const shareBlob = await generateShareScreenshotRef.current?.()
              mobileFooterTabRef.current = prevTab
              _cancelGenerateRef.current = prevCancel
              if (!shareBlob) return null
              // Update the live share URL too
              const url = URL.createObjectURL(shareBlob)
              setShareScreenshotUrl((prev) => {
                if (prev?.startsWith('blob:')) URL.revokeObjectURL(prev)
                return url
              })
              latestShareBlobRef.current = shareBlob
              latestShareBlobAtRef.current = Date.now()
              // Convert to JPEG for compact localStorage storage (PNG can be 3-4MB)
              const compressedBlob = await new Promise((res, rej) => {
                const img = new Image()
                img.onload = () => {
                  if (!isMountedRef.current) {
                    rej(new Error('Component unmounted'))
                    return
                  }
                  const c = document.createElement('canvas')
                  c.width = img.width
                  c.height = img.height
                  c.getContext('2d').drawImage(img, 0, 0)
                  c.toBlob((b) => res(b), 'image/jpeg', 0.88)
                }
                img.src = url
              })
              return await new Promise((res, rej) => {
                const reader = new FileReader()
                reader.onload = () => {
                  if (!isMountedRef.current) {
                    rej(new Error('Component unmounted'))
                    return
                  }
                  res(reader.result)
                }
                reader.readAsDataURL(compressedBlob || shareBlob)
              })
            } catch (e) {
              return null
            }
          })(),
          config: {
            ...config,
            padEnabled,
            totalPriceUSD,
            fileid: localFileid,
            cartProperties: {
              'Build Name': autoName,
              'Body Color': colorOptions[color] ?? color,
              'Screen Type': screenTypeOptions[config.screenType]?.label ?? config.screenType,
              'Screen Color': screenColorOptions[config.screenColor] ?? config.screenColor,
              'Fabric Color': fabricColorOptions[config.fabricColor] ?? config.fabricColor,
              'Bottom Fabric Color': bottomfabricColorOptions[config.bottomFabricColor] ?? config.bottomFabricColor,
              'Bottom Screen Type': config.bottomScreenType,
              'Microphone Type': config.microphoneType,
              ...(config.popFilterEnabled
                ? {
                    'Pop Filter': 'Yes',
                    'Pop Filter Color': popFilterLabel(config.popFilterMaterial, config.popFilterColor)
                  }
                : {}),
              ...(config.shockMountEnabled
                ? { 'Shock Mount': 'Yes', 'Shock Mount Color': colorOptions[config.shockMountColor] ?? config.shockMountColor }
                : {}),
              ...(padEnabled && config.microphoneType !== 'Ribbon' ? { Pad: 'Add Pad' } : {})
            }
          },
          created_at: new Date().toISOString(),
          source: 'local'
        })
        // ALSO send to admin tracker so unlogged saves show up in the admin
        // activity feed (previously localStorage-only). Uses the same anon_id
        // grouping as cart-adds + shares so this build appears under the same
        // "Anonymous" customer as the user's other activity. We upload the
        // already-captured thumbnail blob to CF Images first so the admin
        // can show the actual screenshot instead of a colored swatch.
        ;(async () => {
          try {
            let anonId = localStorage.getItem('ohma_anon_id')
            if (!anonId) {
              anonId = Math.random().toString(36).slice(2, 10) + Math.random().toString(36).slice(2, 10)
              localStorage.setItem('ohma_anon_id', anonId)
            }
            // Upload the thumbnail blob to CF Images
            let cfImageUrl = null
            try {
              if (blob) {
                cfImageUrl = (await uploadToCloudflare(blob, `local_save_${localFileid}.jpg`)) || null
              }
            } catch (e) {
              /* CF upload failed — proceed without thumbnail */
            }

            await fetch('https://customizer-test-nodejs-4c6a5bfd6dbd.herokuapp.com/api/receive-data', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                source: 'manual',
                anon_id: anonId,
                fileid: localFileid,
                name: autoName,
                Pic: cfImageUrl, // CF Images URL, or null if upload failed
                color,
                screenType,
                screenColor,
                fabricColor,
                bottomFabricColor,
                bottomScreenType,
                microphoneType: config.microphoneType,
                popFilterEnabled: config.popFilterEnabled,
                popFilterColor: config.popFilterColor,
                shockMountEnabled: config.shockMountEnabled,
                shockMountColor: config.shockMountColor,
                padEnabled
              })
            })
          } catch (e) {
            /* silent — admin tracking is best-effort */
          }
        })()
        builderBus.refreshBuilds?.()
      } catch (e) {}
      if (!savedLocally) {
        // Couldn't store it on this device: say so (the guest message offers signing in) instead of "Saved"
        setLoadingMessage(null)
        unfreezeLocal()
        if (!isDesktop) isCapturingRef.current = false
        setCanvasSaveToast('limit')
        setTimeout(() => setCanvasSaveToast(null), 3000)
        return
      }
      setBuildCount((prev) => prev + 1)
      track('build_saved', { ...analyticsBuildProps(), signed_in: false })
      setCanvasSaveToast('saved')
      setLoadingMessage(null)
      unfreezeLocal()
      if (!isDesktop) isCapturingRef.current = false
      setTimeout(() => setCanvasSaveToast(null), 2000)
      if (!isDesktop) {
        setTimeout(() => openFooterWithTab('savedDesigns'), 800)
      } else {
        setDrawerRefreshKey((k) => k + 1)
        setShowBuildsDrawer(true)
      }
      return
    }

    // Logged in — check account build limit (24)
    const accountBuildsCount = typeof buildCount === 'number' ? buildCount : 0
    if (accountBuildsCount >= 24) {
      setCanvasSaveToast('limit')
      setTimeout(() => setCanvasSaveToast(null), 3000)
      return
    }

    const unfreezeLoggedIn = !isDesktop ? freezeCanvas(0) : () => {}
    if (!isDesktop) {
      isCapturingRef.current = true
      builderBus.closeFooter?.()
    }
    setCanvasSaveToast('saving')
    setLoadingMessage('Saving Build...')
    // Kick off thumbnail capture immediately
    preSavedThumbnailRef.current = null
    preSavedThumbnailPromiseRef.current = Promise.resolve(generateAndUploadThumbnailRef.current?.()).catch(() => null)

    const savedDesign = await handleSaveDesign(autoName)
    if (savedDesign) {
      builderBus.pendingDesign = savedDesign
      builderBus.pendingRefresh = true
    }
    setBuildCount((prev) => prev + 1)
    if (typeof builderBus.refreshBuilds === 'function') builderBus.refreshBuilds()
    setTimeout(() => {
      if (typeof builderBus.refreshBuilds === 'function') builderBus.refreshBuilds()
    }, 3000)
    track('build_saved', { ...analyticsBuildProps(), signed_in: true, saved_to_account: !!savedDesign })
    setCanvasSaveToast('saved')
    setLoadingMessage(null)
    unfreezeLoggedIn()
    if (!isDesktop) isCapturingRef.current = false
    setTimeout(() => setCanvasSaveToast(null), 2000)
    if (!isDesktop) setTimeout(() => openFooterWithTab('savedDesigns'), 800)
    else {
      setDrawerRefreshKey((k) => k + 1)
      setShowBuildsDrawer(true)
    }
  }, [canvasSaveToast, config, color, isDesktop, buildCount])
  // Keep _saveRef pointing to latest handleCanvasIconSave for builderBus.saveDesign
  _saveRef.current = handleCanvasIconSave

  const handleCopyLink = async (e) => {
    try {
      const shareLink = await shareProduct()
      await navigator.clipboard.writeText(shareLink)
      setCopyLinkCopied(true)
      setTimeout(() => setCopyLinkCopied(false), 1500)
    } catch (err) {}
  }

  const defaultSnap = snapPercents[1]

  const summaryRows = [
    {
      key: '1. Body Color',
      header: 'Body Color',
      label: colorOptions[color],
      icon: (
        <div
          style={{
            backgroundColor: color,
            width: '20px',
            height: '20px',
            borderRadius: '50%',
            marginRight: '8px'
          }}
        />
      )
    },
    {
      key: '2. Screen Color',
      header: 'Screen Color',
      label: screenColorOptions[screenColor],
      icon: (
        <div
          style={{
            backgroundColor: screenColor,
            width: '20px',
            height: '20px',
            borderRadius: '50%',
            marginRight: '8px'
          }}
        />
      )
    },
    {
      key: '3. Screen Type',
      header: 'Screen Type',
      label: screenTypeOptions[screenType].label,
      icon: (
        <img
          src={screenTypeOptions[screenType].iconUrl}
          alt={screenTypeOptions[screenType].label}
          style={{ width: '20px', height: '20px', marginRight: '8px' }}
        />
      )
    },
    {
      key: '4. Fabric Color',
      header: 'Fabric Color',
      label: fabricColorOptions[fabricColor],
      icon: (
        <div
          style={{
            backgroundColor: fabricColor,
            width: '20px',
            height: '20px',
            borderRadius: '50%',
            marginRight: '8px'
          }}
        />
      )
    },
    {
      key: 'Bottom Fabric Color',
      header: 'Logo Fabric Color',
      label: bottomfabricColorOptions[bottomFabricColor],
      icon: (
        <div
          style={{
            backgroundColor: bottomFabricColor,
            width: '20px',
            height: '20px',
            borderRadius: '50%',
            marginRight: '8px'
          }}
        />
      )
    },
    {
      key: '5. Bottom Screen Type',
      header: 'Bottom Screen Type',
      label: bottomScreenTypeOptions[bottomScreenType].label,
      icon: (
        <img
          src={bottomScreenTypeOptions[bottomScreenType].iconUrl}
          alt={bottomScreenTypeOptions[bottomScreenType].label}
          style={{ width: '20px', height: '20px', marginRight: '8px' }}
        />
      )
    },
    {
      key: '6. Microphone Type',
      header: 'Microphone Type',
      label: microphoneTypeOptions[microphoneType].label,
      icon: (
        <img
          src={microphoneTypeOptions[microphoneType].iconUrl}
          alt={microphoneTypeOptions[microphoneType].label}
          style={{ width: '20px', height: '20px', marginRight: '8px' }}
        />
      )
    }
  ]

  return (
    <React.Fragment>
      {/* Builds Drawer — portal-level, outside any transform context */}
      <BuildsDrawer
        open={showBuildsDrawer}
        refreshKey={drawerRefreshKey}
        onClose={() => setShowBuildsDrawer(false)}
        onOpenMyBuilds={() => {
          setShowBuildsDrawer(false)
          openProductModalTab('savedDesigns')
        }}
        onLoadConfirm={setDrawerLoadConfirm}
        onAddToCart={openConfirm}
        colorNames={colorOptions}
        currentDesignId={currentDesignId}
        hasUnsavedChanges={hasUnsavedChanges}
        totalBuildCount={buildCount}
      />
      {/* Drawer load confirm modal */}
      {drawerLoadConfirm && (
        <div
          onClick={() => setDrawerLoadConfirm(null)}
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 9998,
            background: 'rgba(0,0,0,0.45)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 24
          }}>
          <div
            onClick={(e) => e.stopPropagation()}
            style={{ background: '#fff', borderRadius: 16, maxWidth: 400, width: '100%', padding: '28px 28px 24px', boxShadow: 'none' }}>
            <h2
              style={{
                fontFamily: "'roc-grotesk-wide', sans-serif",
                fontWeight: 600,
                fontSize: '0.9em',
                letterSpacing: '0.04em',
                marginTop: 0,
                marginBottom: 10
              }}>
              Load Build
            </h2>
            <p style={{ fontFamily: 'Jost, sans-serif', fontSize: 14, color: '#555', lineHeight: 1.6, marginBottom: 22 }}>
              Load <strong>{drawerLoadConfirm.name || 'this build'}</strong>?{hasUnsavedChanges && ' Your current build has unsaved changes.'}
            </p>
            <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
              <button
                onClick={() => setDrawerLoadConfirm(null)}
                style={{
                  flex: 1,
                  padding: '10px 0',
                  border: '1px solid #e0e0e0',
                  borderRadius: 10,
                  background: '#fff',
                  fontFamily: "'roc-grotesk-wide', sans-serif",
                  fontWeight: 700,
                  fontSize: '0.58em',
                  letterSpacing: '0.1em',
                  textTransform: 'uppercase',
                  cursor: 'pointer',
                  color: '#808082',
                  transition: 'all 0.15s'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = '#f5f5f5'
                  e.currentTarget.style.borderColor = '#ccc'
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = '#fff'
                  e.currentTarget.style.borderColor = '#e0e0e0'
                }}>
                Cancel
              </button>
              {hasUnsavedChanges && (
                <button
                  onClick={async () => {
                    const d = drawerLoadConfirm
                    setDrawerLoadConfirm(null)
                    setLoadingMessage('Saving Build...')
                    await handleSaveDesign(currentDesignName || 'My Build')
                    setLoadingMessage(null)
                    handleLoadDesign(d.config, d.id, d.name)
                    setShowBuildsDrawer(false)
                  }}
                  style={{
                    flex: 1,
                    padding: '10px 0',
                    border: 'none',
                    borderRadius: 10,
                    background: '#eeeeef',
                    fontFamily: "'roc-grotesk-wide', sans-serif",
                    fontWeight: 700,
                    fontSize: '0.58em',
                    letterSpacing: '0.1em',
                    textTransform: 'uppercase',
                    cursor: 'pointer',
                    color: '#555',
                    transition: 'all 0.15s'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = '#dcdcdb'
                    e.currentTarget.style.color = '#333'
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = '#eeeeef'
                    e.currentTarget.style.color = '#555'
                  }}>
                  Save &amp; Load
                </button>
              )}
              <button
                onClick={() => {
                  const d = drawerLoadConfirm
                  setDrawerLoadConfirm(null)
                  handleLoadDesign(d.config, d.id, d.name)
                  setShowBuildsDrawer(false)
                }}
                style={{
                  flex: 1,
                  padding: '10px 0',
                  border: 'none',
                  borderRadius: 10,
                  background: '#6186f7',
                  color: '#fff',
                  fontFamily: "'roc-grotesk-wide', sans-serif",
                  fontWeight: 700,
                  fontSize: '0.58em',
                  letterSpacing: '0.1em',
                  textTransform: 'uppercase',
                  cursor: 'pointer',
                  transition: 'all 0.15s'
                }}
                onMouseEnter={(e) => (e.currentTarget.style.background = '#4f70e0')}
                onMouseLeave={(e) => (e.currentTarget.style.background = '#6186f7')}>
                {hasUnsavedChanges ? "Don't Save" : 'Load'}
              </button>
            </div>
          </div>
        </div>
      )}
      <div className="dice-icon">
        <div className="dice-spinner">
          <img src="https://cdn.shopify.com/s/files/1/0564/1820/5796/files/260212_Randomize.svg?v=1771036629" alt="Dice Icon" onClick={handleDiceClick} />
        </div>

        {/* Undo Button - positioning now in CSS */}
        <div
          onClick={undo}
          className="control-button"
          style={{
            opacity: canUndo ? 1 : 0.3,
            pointerEvents: canUndo ? 'auto' : 'none'
          }}
          title="Undo">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round">
            <path d="M9 14L4 9l5-5" />
            <path d="M20 20c0-5.523-4.477-10-10-10H4" />
          </svg>
        </div>

        {/* Redo Button - positioning now in CSS */}
        <div
          onClick={redo}
          className="control-button"
          style={{
            opacity: canRedo ? 1 : 0.3,
            pointerEvents: canRedo ? 'auto' : 'none'
          }}
          title="Redo">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round">
            <path d="M15 14l5-5-5-5" />
            <path d="M4 20c0-5.523 4.477-10 10-10h6" />
          </svg>
        </div>
      </div>
      {/* Share and Copy Link buttons - keep in vertical layout */}
      <div
        className="desktop-icons-container"
        style={{
          zIndex: 100,
          pointerEvents: 'auto'
        }}>
        {/* Share Button */}
        <div
          className="desktop-share"
          role="button"
          tabIndex={0}
          onClick={handleShareClickDesktop}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault()
              handleShareClickDesktop(e)
            }
          }}
          aria-label="Share product (desktop)">
          <img
            src="https://pub-e2a1a41ee7e84eefbab9b339c21acfb0.r2.dev/Share.svg"
            width="30"
            height="30"
            alt="Share"
            style={{ cursor: 'pointer', display: 'block' }}
          />
        </div>

        {/* Copy Link Button */}
        <div
          className="desktop-copy-link"
          role="button"
          tabIndex={0}
          onClick={handleCopyLink}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault()
              handleCopyLink(e)
            }
          }}
          aria-label="Copy link to clipboard"
          style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', position: 'relative', width: 30 }}>
          <img
            src="https://pub-e2a1a41ee7e84eefbab9b339c21acfb0.r2.dev/Link.svg"
            width="30"
            height="30"
            alt="Copy Link"
            style={{ cursor: 'pointer', display: 'block' }}
          />
          <span
            style={{
              position: 'absolute',
              top: '50%',
              left: '38px',
              transform: 'translateY(-50%)',
              background: '#000',
              color: '#fff',
              fontSize: '13px',
              fontFamily: 'Jost, system-ui, sans-serif',
              fontWeight: 600,
              padding: '6px 10px',
              borderRadius: '6px',
              whiteSpace: 'nowrap',
              pointerEvents: 'none',
              opacity: copyLinkCopied ? 1 : 0,
              transition: 'opacity 160ms cubic-bezier(0.2,0.9,0.2,1)',
              boxShadow: 'none',
              zIndex: 1000
            }}>
            ✓ Link copied
          </span>
        </div>
      </div>
      <div className="maincontainer">
        {isDesktop && (
          <div className="desktop-footer">
            <div className="desktop-footer-pad-width">
              {/* Desktop layout: stack every footer section vertically */}

              <div className="product-card" style={{ maxWidth: '100%', width: 'fit-content' }}>
                <div className="product-header">
                  <div className="header-row">
                    <h1 className="product-title-page">Build Your</h1>
                    <h3 className="product-price">
                      {renderPrice()}
                    </h3>
                  </div>
                  <div className="header-row">
                    <h1 className="product-title-page">Own Ohma</h1>
                  </div>
                  {SALE_ON && (
                    <div className="sale-pill sale-pill-desktop">
                      {SALE.label} · ${SALE.offUSD} off every Ohma
                    </div>
                  )}
                </div>
                <div className="product-description">
                  <p style={{ fontFamily: 'Jost' }}>
                    Each Ohma is built to order in our East Los Angeles workshop, shaped by your choices so it belongs in your world.
                  </p>
                </div>
                <div className="product-footer" style={{ display: 'flex', gap: '24px', alignItems: 'center' }}>
                  <a href="#" className="read-more" onClick={openModal('overview')} style={{ textDecoration: 'underline' }}>
                    Read More
                  </a>
                  <button
                    type="button"
                    onClick={() => openProductModalTab('specs')}
                    className="tech-specs"
                    style={{
                      textDecoration: 'underline',
                      background: 'none',
                      border: 'none',
                      padding: 0,
                      cursor: 'pointer',
                      fontFamily: 'inherit',
                      fontSize: 'inherit',
                      fontWeight: 'inherit'
                    }}>
                    Tech Specs
                  </button>
                </div>
              </div>

              {productModalMounted && (
              <React.Suspense fallback={null}>
              <ProductModal
                open={modalOpen}
                initialTab={modalInitialTab}
                initialColor={modalInitialColor}
                onInitialColorConsumed={() => setModalInitialColor(null)}
                shareScreenshotUrl={shareScreenshotUrl}
                onRegenerateScreenshot={regenerateShareScreenshot}
                config={{
                  ...config,
                  colorLabel: colorOptions[config.color] || config.color,
                  screenTypeLabel: screenTypeOptions[config.screenType]?.label || config.screenType,
                  screenColorLabel: screenColorOptions[config.screenColor] || config.screenColor,
                  fabricColorLabel: fabricColorOptions[config.fabricColor] || config.fabricColor,
                  bottomFabricColorLabel: bottomfabricColorOptions[config.bottomFabricColor] || config.bottomFabricColor,
                  bottomScreenTypeLabel: bottomScreenTypeOptions[config.bottomScreenType]?.label || config.bottomScreenType,
                  microphoneTypeLabel: microphoneTypeOptions[config.microphoneType]?.label || config.microphoneType,
                  popFilterMaterial: config.popFilterMaterial,
                  popFilterColorLabel: config.popFilterEnabled ? popFilterLabel(config.popFilterMaterial, config.popFilterColor) : null
                }}
                onGenerateShareLink={() => {
                  setShareTabActive(true)
                  setAppShareUrl('')
                  return shareProduct()
                    .then((link) => {
                      setAppShareUrl(link)
                      return link
                    })
                    .catch(() => {})
                }}
                onApplyColors={(bodyHex, screenHex) => {
                  const newColor = bodyHex || config.color
                  const isBrass = newColor === 'Brass' || newColor === '#d5b173'
                  const isBlack = newColor === '#1A1A1A' || newColor === '#232221' || newColor === '#1c1d1d'
                  const newPopFilterColor =
                    config.popFilterEnabled && config.popFilterMaterial === 'metal' ? (isBrass || isBlack ? '#1c1d1d' : newColor) : config.popFilterColor
                  setConfig({
                    ...config,
                    ...(bodyHex ? { color: bodyHex } : {}),
                    ...(screenHex ? { screenColor: screenHex } : {}),
                    popFilterColor: newPopFilterColor
                  })
                  setModalOpen(false)
                }}
                onClose={() => {
                  setModalOpen(false)
                  try {
                    const isMobile = typeof window !== 'undefined' && window.innerWidth < 768
                    if (!isMobile) {
                      // On desktop, just dispatch the event but DON'T animate sheetPercent
                      // Animating sheetPercent triggers updateCameraDistanceInRealTime and moves the camera
                      window.dispatchEvent(new CustomEvent('ohma:footer-set-position', { detail: { position: 'close' } }))
                    }
                  } catch (e) {}
                }}
                title="Custom Microphone"
                currentDesignId={currentDesignId}
                hasUnsavedChanges={hasUnsavedChanges}
                currentDesignName={currentDesignName}
                onLoadDesign={(cfg, id, name) => {
                  handleLoadDesign(cfg, id, name)
                  setModalOpen(false)
                }}
                onSaveDesign={handleSaveDesign}
                onUpdateDesign={handleUpdateDesign}
                onMigratedId={(newId) => {
                  setCurrentDesignId(newId)
                  setHasUnsavedChanges(false)
                }}
              />
              </React.Suspense>
              )}

              {Object.entries(filteredSelectionContent).map(([optionLabel, content]) => (
                <div key={optionLabel} className="desktop-footer-section">
                  <div className={`footer-option ${activeSelection === optionLabel ? 'active' : ''}`} onClick={() => handleOptionClick(optionLabel)}>
                    <h1>{displayNames[optionLabel] || optionLabel}</h1>
                  </div>
                  <div className="desktop-footer-section-content">{content}</div>
                </div>
              ))}

              <div className="desktop-footer-extra-padding"></div>
              <div className="desktop-add-to-cart-container">
                <div className="add-to-cart-wrapper">
                  {/* quantity picker */}
                  <div className="qty-picker">
                    <button type="button" onClick={decreaseQty}>
                      –
                    </button>
                    <span className="qty">{quantity}</span>
                    <button type="button" onClick={increaseQty}>
                      +
                    </button>
                  </div>

                  {/* add to cart trigger */}
                  <div className={SALE_ON ? 'add-to-cart sale-on' : 'add-to-cart'} onClick={openConfirm}>
                    <div className="cart-content">
                      <h2>Add to Cart</h2>
                      <h2 className="price">
                        {renderPrice()}
                      </h2>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="modelcontainer">
          <div className="app-container">
            <div className="loading-indicator-wrap">
              {loadingMessage ? <LoadingIndicator message={loadingMessage} /> : (!sceneReady || !modelReady || !introPlaced) && <LoadingIndicator message="Loading..." />}
            </div>

            <div
              ref={containerRef}
              className="canvas-wrap"
              style={{
                height: 'calc(var(--vh, 1vh) * 100)',
                willChange: 'transform',
                position: 'relative',
                visibility: 'visible',
                pointerEvents: orbitControlsEnabled ? 'auto' : 'none'
              }}>
              {/* Mobile Save Build / My Builds Header - Top Left */}
              {isMobile && (
                <div
                  className="mobile-save-builds-header"
                  style={{
                    position: 'fixed',
                    top: '12px',
                    left: '12px',
                    zIndex: 10000,
                    pointerEvents: 'auto',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0px',
                    background: 'rgba(255, 255, 255, 0.3)',
                    borderRadius: '30px',
                    padding: '0px',
                    boxShadow: 'none',
                    fontFamily: 'Jost, sans-serif',
                    transition: 'background 180ms ease'
                  }}>
                  {/* Save Build Button - Bookmark Icon Only */}
                  <div
                    role="button"
                    tabIndex={0}
                    onClick={(e) => {
                      handleCanvasIconSave()
                    }}
                    onMouseEnter={(e) => {
                      if (canvasSaveToast !== 'saved') {
                        e.currentTarget.style.background = 'rgba(255, 255, 255, 0.5)'
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (canvasSaveToast !== 'saved') {
                        e.currentTarget.style.background = 'transparent'
                      }
                    }}
                    aria-label="Save build"
                    title="Save build"
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: 'pointer',
                      padding: '8px',
                      borderRadius: '20px',
                      transition: 'background 180ms ease',
                      background: canvasSaveToast === 'saved' ? '#d88978' : 'transparent'
                    }}>
                    {/* Bookmark Icon */}
                    <img
                      src="https://pub-e2a1a41ee7e84eefbab9b339c21acfb0.r2.dev/Boookmark.svg"
                      width="18"
                      height="18"
                      alt="Save"
                      style={{
                        display: 'block',
                        filter: canvasSaveToast === 'saved' ? 'brightness(0) invert(1)' : 'none'
                      }}
                    />
                  </div>

                  {/* My Builds Button */}
                  <div
                    role="button"
                    tabIndex={0}
                    onClick={() => {
                      if (typeof openFooterWithTab === 'function') {
                        openFooterWithTab('savedDesigns')
                      }
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = 'rgba(255, 255, 255, 0.5)'
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = 'transparent'
                    }}
                    aria-label="My Builds"
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      cursor: 'pointer',
                      padding: '5px 10px',
                      borderRadius: '20px',
                      transition: 'background 180ms ease, color 180ms ease',
                      background: 'transparent',
                      color: '#333',
                      position: 'relative'
                    }}>
                    {/* Text */}
                    <span style={{ fontSize: '13px', fontWeight: 700, whiteSpace: 'nowrap' }}>My Builds</span>
                    {/* Badge Counter */}
                    <span
                      style={{
                        background: '#e98969',
                        color: '#fff',
                        fontSize: '10px',
                        fontWeight: 600,
                        width: '18px',
                        height: '18px',
                        borderRadius: '50%',
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        lineHeight: '1',
                        flexShrink: 0
                      }}>
                      {buildCount > 99 ? '99+' : buildCount}
                    </span>
                  </div>

                  {/* Toast notification for save limit - mobile - portalled to escape willChange:transform */}
                  {canvasSaveToast === 'limit' &&
                    ReactDOM.createPortal(
                      <div
                        style={{
                          position: 'fixed',
                          top: '106px',
                          left: '92px',
                          background: '#000',
                          color: '#fff',
                          fontSize: '12px',
                          fontFamily: 'Jost, system-ui, sans-serif',
                          fontWeight: 600,
                          padding: '6px 10px',
                          borderRadius: '6px',
                          whiteSpace: 'nowrap',
                          zIndex: 19000,
                          lineHeight: '1.3'
                        }}>
                        {(() => {
                          const token = window.SHOPIFY_CUSTOMER_TOKEN || localStorage.getItem('ohma_ctoken')
                          const isLoggedIn = !!token
                          if (isLoggedIn) return 'Save limit reached (24 builds max)'
                          return (
                            <span>
                              Save this build and more by{' '}
                              <span onClick={() => openProductModalTab('savedDesigns')} style={{ textDecoration: 'underline', cursor: 'pointer' }}>
                                signing in
                              </span>
                              .
                            </span>
                          )
                        })()}
                      </div>,
                      document.body
                    )}
                </div>
              )}

              <Canvas
                shadows={shadowsEnabled ? ['soft', 'percentage', 'variance'][lightDebug.shadowType] || 'soft' : false}
                dpr={[1, MAX_DPR()]}
                flat={lightDebug.toneMapping === 0}
                gl={{
                  preserveDrawingBuffer: true,
                  gammaOutput: true,
                  gammaFactor: 1,
                  antialias: true,
                  alpha: false
                }}
                ref={canvasRef}
                onCreated={({ gl, scene, camera }) => {
                  // hidden until the first view is placed, then faded in (see FIRST VIEW)
                  gl.domElement.style.opacity = '0'
                  gl.domElement.style.transition = 'opacity 420ms ease-out'
                  // store references we need
                  rendererRef.current = gl
                  sceneRef.current = scene
                  cameraRef.current = camera

                  // Lens: render the live camera as if shot from farther back with a longer lens.
                  // The camera is restored right after each render, so orbit, zoom, raycasting and
                  // all existing framing code keep working with the original values.
                  const _origRender = gl.render.bind(gl)
                  const _lensTarget = new THREE.Vector3()
                  const _savedPos = new THREE.Vector3()
                  const _bufSize = new THREE.Vector2()
                  let _inComposer = false
                  let _composer = null
                  let _aoPass = null
                  // MOTION-AWARE AO: while the camera or mic is moving, draw the plain scene with the canvas's own
                  // antialiasing (smooth, no flicker, fast). Once everything settles, fade the crevice shadows in.
                  // When nothing at all has changed for a few frames, skip drawing: the canvas keeps its last image.
                  let _lastSig = -1
                  let _lastChange = 0
                  let _staticFrames = 0
                  // Cheap numeric fingerprint of everything that changes the picture (no strings, no garbage)
                  let _h = 0
                  const _mix = (v) => {
                    _h = (_h * 31 + (v * 1000.123 || 0)) % 1e12
                  }
                  const sceneSignature = (sc, cam) => {
                    _h = 7
                    const e = cam.matrixWorld.elements
                    for (let i = 0; i < 16; i++) _mix(e[i])
                    _mix(cam.projectionMatrix.elements[0])
                    _mix(_bufSize.x)
                    _mix(_bufSize.y)
                    sc.traverse((o) => {
                      if (!o.isMesh) return
                      const w = o.matrixWorld.elements
                      _mix(o.visible ? 1 : 2)
                      _mix(w[0]); _mix(w[2]); _mix(w[12]); _mix(w[13]); _mix(w[14])
                      const m = o.material
                      if (m && !Array.isArray(m)) {
                        _mix(m.id); _mix(m.version); _mix(m.opacity); _mix(m.roughness); _mix(m.metalness); _mix(m.envMapIntensity); _mix(o.castShadow ? 1 : 0); _mix(m.aoMap ? m.aoMap.id : 0)
                        if (m.color) { _mix(m.color.r); _mix(m.color.g); _mix(m.color.b) }
                        _mix(m.map ? m.map.id : 0)
                      }
                    })
                    return _h
                  }
                  // Any resize or pixel-ratio change wipes the canvas, so force a fresh draw afterwards
                  const _origSetSize = gl.setSize.bind(gl)
                  gl.setSize = (...args) => {
                    _lastSig = -1
                    return _origSetSize(...args)
                  }
                  const _origSetPR = gl.setPixelRatio.bind(gl)
                  gl.setPixelRatio = (...args) => {
                    _lastSig = -1
                    return _origSetPR(...args)
                  }
                  // Renders the scene with crevice shadows (ambient occlusion), or plainly if AO is off or fails.
                  const renderScene = (sc, cam, live = false) => {
                    if (!AO_SETTINGS.enabled || gl.getRenderTarget() !== null) return _origRender(sc, cam)
                    let blend = 1
                    if (live) {
                      gl.getDrawingBufferSize(_bufSize)
                      const now = performance.now()
                      const sig = sceneSignature(sc, cam)
                      if (sig !== _lastSig) {
                        _lastSig = sig
                        _lastChange = now
                        _staticFrames = 0
                      }
                      const still = now - _lastChange
                      blend = Math.min(1, Math.max(0, (still - AO_SETTINGS.settleMs) / AO_SETTINGS.fadeMs))
                      if (blend <= 0) return _origRender(sc, cam)
                      if (blend >= 1) {
                        _staticFrames++
                        if (_staticFrames > 3) return // nothing changed: keep the finished frame on screen
                      }
                    }
                    try {
                      gl.getDrawingBufferSize(_bufSize)
                      if (!_composer) {
                        _composer = new EffectComposer(gl)
                        _aoPass = new N8AOPass(sc, cam, _bufSize.x, _bufSize.y)
                        _aoPass.configuration.aoRadius = AO_SETTINGS.radius
                        _aoPass.configuration.distanceFalloff = AO_SETTINGS.falloff
                        _aoPass.configuration.intensity = AO_SETTINGS.intensity
                        _aoPass.configuration.halfRes = !!AO_SETTINGS.halfRes
                        _aoPass.configuration.transparencyAware = false
                        _aoPass.configuration.depthAwareUpsampling = true
                        _aoPass.setQualityMode(AO_QUALITY[AO_SETTINGS.quality] || 'Medium')
                        _composer.addPass(_aoPass)
                        // The AO pass renders off-screen, which skips the canvas antialiasing, so smooth edges with SMAA.
                        // OutputPass converts to sRGB first, which SMAA expects.
                        if (AO_SETTINGS.smaa) {
                          _aoPass.configuration.gammaCorrection = false
                          _composer.addPass(new OutputPass())
                          _composer.addPass(new SMAAPass(_bufSize.x, _bufSize.y))
                        }
                      }
                      if (_composer._w !== _bufSize.x || _composer._h !== _bufSize.y) {
                        _composer.setPixelRatio(1)
                        _composer.setSize(_bufSize.x, _bufSize.y)
                        _composer._w = _bufSize.x
                        _composer._h = _bufSize.y
                      }
                      _aoPass.scene = sc
                      _aoPass.camera = cam
                      _aoPass.configuration.intensity = AO_SETTINGS.intensity * blend
                      _inComposer = true
                      _composer.render()
                    } catch (e) {
                      console.warn('AO disabled after error', e)
                      AO_SETTINGS.enabled = 0
                      _origRender(sc, cam)
                    } finally {
                      _inComposer = false
                    }
                  }
                  gl.render = (sc, cam) => {
                    if (_inComposer) return _origRender(sc, cam)
                    const k = lensScale()
                    if (cam !== cameraRef.current || !cam.isPerspectiveCamera) {
                      return cam?.userData?.useAO ? renderScene(sc, cam) : _origRender(sc, cam)
                    }
                    if (Math.abs(k - 1) < 1e-3) return renderScene(sc, cam, true)
                    const savedFov = cam.fov
                    const savedNear = cam.near
                    const savedFar = cam.far
                    _savedPos.copy(cam.position)
                    if (orbitControlsRef.current) _lensTarget.copy(orbitControlsRef.current.target)
                    else _lensTarget.set(0, 0, 0)
                    cam.position.sub(_lensTarget).multiplyScalar(k).add(_lensTarget)
                    cam.fov = LENS_FOV
                    // Tight clipping range around the mic keeps depth precise, which the crevice shadows rely on
                    const _d = cam.position.distanceTo(_lensTarget)
                    cam.near = Math.max(savedNear, _d * 0.5)
                    cam.far = Math.min(savedFar * k, _d * 2)
                    cam.updateProjectionMatrix()
                    cam.updateMatrixWorld()
                    try {
                      renderScene(sc, cam, true)
                    } finally {
                      cam.position.copy(_savedPos)
                      cam.fov = savedFov
                      cam.near = savedNear
                      cam.far = savedFar
                      cam.updateProjectionMatrix()
                      cam.updateMatrixWorld()
                    }
                  }

                  // Intercept setAnimationLoop to capture R3F's own callback the moment
                  // it registers it — so we can pause/resume cleanly during screenshot capture.
                  const _origSetLoop = gl.setAnimationLoop.bind(gl)
                  gl.setAnimationLoop = (fn) => {
                    if (fn !== null) r3fAnimLoopRef.current = fn
                    _origSetLoop(fn)
                  }

                  // Set blue background
                  gl.setClearColor('#91c3de', 1)

                  // ANTI-BANDING: Enable dithering
                  gl.dithering = true

                  // clamp pixel ratio right away for consistent cross-browser rendering
                  gl.setPixelRatio(Math.min(window.devicePixelRatio || 1, MAX_DPR()))
                  // ensure drawing buffer matches container immediately
                  if (containerRef.current) {
                    gl.setSize(containerRef.current.clientWidth, containerRef.current.clientHeight, false)
                  }

                  handleSceneCreated() // your existing logic
                  setSceneReady(true)
                }}
                camera={{ fov: 35 }}
                resize={{ scroll: false, debounce: { scroll: 50, resize: 0 } }}
                className="canvas-container"
                style={{
                  backgroundColor: '#6c8ce6',
                  position: 'relative',
                  left: 0,
                  height: '100%',
                  zIndex: 0
                }}>
                {/* Virtual photo studio: drives reflections and soft image-based light */}
                <StudioEnvironment settings={lightDebug} />
                {!lightDebug.metalEnvOff && <MetalEnvironment settings={lightDebug} />}

                <ambientLight intensity={lightDebug.ambientIntensity * lightDebug.exposure} />

                {/* Overhead key: most of the softbox light now comes through the environment, where the baked occlusion darkens
                    the crevices; this soft shadowed key adds gentle top-down shading. Originally: the photo softbox is the main light, and the dark crevices in the photos are its
                    shadows. The environment can't cast shadows, so the softbox is also represented by a small grid of
                    shadow-casting lights spread across its area, which gives soft, realistic shadow edges.
                    The shadow box is fitted tightly around the mic so the shadows are sharp enough for the seams. */}
                {softboxLights(lightDebug).map((p, i, arr) => (
                  <directionalLight
                    key={`key-${arr.length}-${i}`}
                    position={p}
                    intensity={(lightDebug.keyIntensity * lightDebug.exposure) / arr.length}
                    color="#ffffff"
                    castShadow={shadowsEnabled}
                    shadow-mapSize-width={lightDebug.shadowMapSize}
                    shadow-mapSize-height={lightDebug.shadowMapSize}
                    shadow-camera-near={5}
                    shadow-camera-far={15}
                    shadow-camera-left={-lightDebug.shadowExtent}
                    shadow-camera-right={lightDebug.shadowExtent}
                    shadow-camera-top={lightDebug.shadowExtent}
                    shadow-camera-bottom={-lightDebug.shadowExtent}
                    shadow-radius={lightDebug.shadowRadius}
                    shadow-bias={lightDebug.shadowBias}
                    shadow-normalBias={lightDebug.shadowNormalBias}
                  />
                ))}

                <BackFill cameraRef={cameraRef} groupRef={groupRef} intensity={lightDebug.backFill * lightDebug.exposure} height={lightDebug.backFillHeight} />

                <group ref={groupRef} position={[0, groupY, 0]}>
                  <Suzi
                    ref={modelRef}
                    rotation={[0, 0, 0]}
                    scale={1}
                    color={showCustomColorPicker ? draftColor : config.color} // ← real-time draft vs. committed
                    screenType={screenType}
                    screenColor={showCustomScreenColorPicker ? draftScreenColor : config.screenColor}
                    fabricColor={fabricColor}
                    bottomFabricColor={bottomFabricColor}
                    bottomScreenType={bottomScreenType}
                    microphoneType={microphoneType}
                    activeSelection={activeSelection}
                    isThumbnail={false}
                    isCapturingRef={isCapturingRef}
                    isMobile={isMobile}
                    isMicButtonHovered={isMicButtonHovered}
                    popFilterEnabled={popFilterEnabled}
                    popFilterColor={popFilterColor}
                    popFilterMaterial={config.popFilterMaterial}
                    shockMountEnabled={shockMountEnabled}
                    shockMountColor={shockMountColor}
                    fabricRotation={fabricRotationDebug}
                    texDebugValues={texDebugVisible ? texDebugValues : null}
                    brassDebug={brassDebug}
                    stainlessDebug={stainlessDebug}
                    matDebugRef={matDebugRef}
                    matDebug={matDebug}
                    shadowsEnabled={shadowsEnabled}
                  />

                  <LightMarker position={lightPosition} />
                </group>
                <OrbitControls
                  ref={orbitControlsRef}
                  minPolarAngle={0}
                  enabled={orbitControlsEnabled}
                  enableZoom={!isFooterDragging && orbitControlsEnabled}
                  enableRotate={orbitControlsEnabled}
                  zoomSpeed={0.8}
                  minDistance={0.5}
                  maxDistance={3.5}
                  enablePan={false}
                  maxPolarAngle={Math.PI / 2}
                  enableDamping
                  dampingFactor={0.05}
                  onChange={() => {
                    if (!cameraRef.current || !orbitControlsRef.current || isCameraMoving || isTweeningRef.current) return
                    // Only a pinch / scroll the person is doing right now counts as their zoom. Before, any camera
                    // move (footer, resets, other code) was read back as "the user zoomed" and could stick.
                    if (!userGestureRef.current) return
                    const target = orbitControlsRef.current.target
                    const toCamera = cameraRef.current.position.clone().sub(target)
                    const currentDist = toCamera.length()
                    if (currentDist < 0.001) return // avoid degenerate state
                    const footerValue = typeof sheetPercent?.get === 'function' ? sheetPercent.get() : snapPercents[1]
                    const baseDist = getCameraDistance(footerValue)
                    const offset = currentDist - baseDist
                    const maxOffset = 0
                    const minOffset = -(baseDist * 0.45)
                    const clampedOffset = Math.min(maxOffset, Math.max(minOffset, offset))
                    userZoomOffsetRef.current = clampedOffset
                    if (Math.abs(offset - clampedOffset) > 0.001) {
                      const clampedDist = Math.max(0.5, baseDist + clampedOffset)
                      const dir = toCamera.normalize()
                      cameraRef.current.position.copy(target.clone().add(dir.multiplyScalar(clampedDist)))
                    }
                  }}
                />
                <AnimationUpdater />
                <CameraAngleChecker cameraRef={cameraRef} groupRef={groupRef} setIsViewingBack={setIsViewingBack} />
              </Canvas>
            </div>

            <div className="dots-container-desktop">
              <div className="control-dots">
                <div className="dots" role="button" tabIndex={0} aria-label="Front view" onClick={(e) => setActiveDot(e.currentTarget, resetCamera)} onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && (e.preventDefault(), setActiveDot(e.currentTarget, resetCamera))}></div>
                <div className="dots-side" role="button" tabIndex={0} aria-label="Side view" onClick={(e) => setActiveDot(e.currentTarget, resetCameraSide)} onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && (e.preventDefault(), setActiveDot(e.currentTarget, resetCameraSide))}></div>
                <div className="dots-back" role="button" tabIndex={0} aria-label="Back view" onClick={(e) => setActiveDot(e.currentTarget, rotateCameraBehind)} onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && (e.preventDefault(), setActiveDot(e.currentTarget, rotateCameraBehind))}></div>
                <div
                  style={{
                    position: 'absolute',
                    bottom: '-278px',
                    left: '-43px',
                    color: 'white',
                    fontSize: 13,
                    fontWeight: 700,
                    fontFamily: 'Jost, sans-serif',
                    pointerEvents: 'none',
                    whiteSpace: 'nowrap',
                    lineHeight: 1,
                    opacity: isViewingBack ? 1 : 0,
                    transition: 'opacity 0.4s ease'
                  }}>
                  BACK SIDE
                </div>

                {/* Save Build / My Builds Header moved to its own sibling container */}
              </div>
            </div>

            {/* Save Build / My Builds Header - Desktop - own positioned container */}
            <div className="desktop-save-builds-header-container">
              <div className="desktop-save-builds-header">
                {/* Save Build Button */}
                <div
                  className={`save-build-btn ${canvasSaveToast === 'saved' ? 'saved' : ''}`}
                  role="button"
                  tabIndex={0}
                  onClick={(e) => {
                    handleCanvasIconSave()
                  }}
                  aria-label="Save build"
                  title="Save build">
                  {/* Bookmark Icon */}
                  <img
                    src="https://pub-e2a1a41ee7e84eefbab9b339c21acfb0.r2.dev/Boookmark.svg"
                    width="12"
                    height="12"
                    alt="Save"
                    style={{
                      width: '12px',
                      height: '12px',
                      opacity: canvasSaveToast === 'saved' ? 1 : 0.6
                    }}
                  />
                  {/* Text */}
                  <span style={{ fontSize: '14px', fontWeight: 500, whiteSpace: 'nowrap' }}>{canvasSaveToast === 'saved' ? '✓ Saved!' : 'Save Build'}</span>
                </div>

                {/* My Builds Button */}
                <div className="my-builds-btn" role="button" tabIndex={0} onClick={() => setShowBuildsDrawer(true)} aria-label="My Builds">
                  {/* Text */}
                  <span style={{ fontSize: '14px', fontWeight: 700, whiteSpace: 'nowrap' }}>My Builds</span>
                  {/* Badge Counter */}
                  <span
                    style={{
                      background: '#e98969',
                      color: '#fff',
                      fontSize: '11px',
                      fontWeight: 600,
                      width: '20px',
                      height: '20px',
                      borderRadius: '50%',
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      lineHeight: '1',
                      flexShrink: 0
                    }}>
                    {buildCount > 99 ? '99+' : buildCount}
                  </span>
                </div>

                {/* Toast notification for save limit */}
                {canvasSaveToast === 'limit' && (
                  <div
                    style={{
                      position: 'absolute',
                      top: '100%',
                      right: '0',
                      marginTop: '8px',
                      background: '#000',
                      color: '#fff',
                      fontSize: '13px',
                      fontFamily: 'Jost, system-ui, sans-serif',
                      fontWeight: 600,
                      padding: '8px 12px',
                      borderRadius: '8px',
                      whiteSpace: 'nowrap',
                      boxShadow: 'none',
                      zIndex: 1000
                    }}>
                    {(() => {
                      const token = window.SHOPIFY_CUSTOMER_TOKEN || localStorage.getItem('ohma_ctoken')
                      const isLoggedIn = !!token
                      if (isLoggedIn) return 'Save limit reached (24 builds max)'
                      return (
                        <span>
                          Save this build and more by{' '}
                          <span
                            onClick={() => openProductModalTab('savedDesigns')}
                            onMouseEnter={(e) => (e.currentTarget.style.opacity = '0.7')}
                            onMouseLeave={(e) => (e.currentTarget.style.opacity = '1')}
                            style={{ textDecoration: 'underline', cursor: 'pointer' }}>
                            signing in
                          </span>
                          .
                        </span>
                      )
                    })()}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* ═══ TEXTURE DEBUG PANEL ═══ */}
        {(() => {
          const rows = [
            { key: 'bodyRepeat', label: 'Body Repeat', isInt: true },
            { key: 'bodyNormal', label: 'Body Normal', isInt: false },
            { key: 'screenRepeat', label: 'Screen Repeat', isInt: true },
            { key: 'screenNormal', label: 'Screen Normal', isInt: false },
            { key: 'popFilterRepeat', label: 'Pop Filter Repeat', isInt: true },
            { key: 'popFilterNormal', label: 'Pop Filter Normal', isInt: false }
          ]
          const copyOutput = () => {
            const v = texDebugValues
            const txt = `// Texture debug values\nbodyRepeat: ${v.bodyRepeat},\nbodyNormal: ${v.bodyNormal},\nscreenRepeat: ${v.screenRepeat},\nscreenNormal: ${v.screenNormal},\npopFilterRepeat: ${v.popFilterRepeat},\npopFilterNormal: ${v.popFilterNormal},`
            navigator.clipboard.writeText(txt).catch(() => {})
          }
          return (
            <>
              <button type="button" onClick={() => setTexDebugVisible((v) => !v)} style={{ display: 'none' }}>
                TEX
              </button>

              {texDebugVisible && (
                <div
                  style={{
                    position: 'fixed',
                    bottom: 20,
                    right: 20,
                    zIndex: 99999,
                    background: 'rgba(20,20,20,0.92)',
                    color: '#fff',
                    borderRadius: 12,
                    padding: '12px 14px',
                    fontFamily: 'monospace',
                    fontSize: 12,
                    minWidth: 280,
                    boxShadow: 'none',
                    backdropFilter: 'blur(8px)'
                  }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                    <span style={{ fontWeight: 700, fontSize: 13, letterSpacing: '0.05em' }}>🎛 Texture Debug</span>
                    <button
                      type="button"
                      onClick={copyOutput}
                      style={{
                        background: '#6c8ce6',
                        border: 'none',
                        borderRadius: 6,
                        color: '#fff',
                        padding: '3px 10px',
                        fontSize: 11,
                        cursor: 'pointer',
                        fontFamily: 'monospace',
                        fontWeight: 700
                      }}>
                      Copy
                    </button>
                  </div>

                  {rows.map(({ key, label, isInt }) => {
                    const locked = texDebugLocks[key]
                    const val = texDebugValues[key]
                    return (
                      <div key={key} style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 7 }}>
                        <button
                          type="button"
                          onClick={() => texDebugToggleLock(key)}
                          title={locked ? 'Unlock' : 'Lock'}
                          style={{
                            background: 'none',
                            border: `1.5px solid ${locked ? '#6c8ce6' : '#555'}`,
                            borderRadius: 4,
                            color: locked ? '#6c8ce6' : '#555',
                            width: 22,
                            height: 22,
                            cursor: 'pointer',
                            fontSize: 11,
                            flexShrink: 0,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}>
                          {locked ? '🔒' : '🔓'}
                        </button>
                        <span style={{ flex: 1, color: locked ? '#6c8ce6' : '#ccc', fontSize: 11 }}>{label}</span>
                        <button
                          type="button"
                          onClick={() => texDebugStep(key, -1)}
                          disabled={locked}
                          style={{
                            background: locked ? '#333' : '#444',
                            border: 'none',
                            borderRadius: 4,
                            color: locked ? '#666' : '#fff',
                            width: 24,
                            height: 24,
                            cursor: locked ? 'not-allowed' : 'pointer',
                            fontSize: 16,
                            fontWeight: 700,
                            flexShrink: 0
                          }}>
                          −
                        </button>
                        <span style={{ minWidth: 38, textAlign: 'center', fontWeight: 700, color: locked ? '#6c8ce6' : '#fff', fontSize: 12 }}>
                          {isInt ? val : val.toFixed(2)}
                        </span>
                        <button
                          type="button"
                          onClick={() => texDebugStep(key, 1)}
                          disabled={locked}
                          style={{
                            background: locked ? '#333' : '#444',
                            border: 'none',
                            borderRadius: 4,
                            color: locked ? '#666' : '#fff',
                            width: 24,
                            height: 24,
                            cursor: locked ? 'not-allowed' : 'pointer',
                            fontSize: 16,
                            fontWeight: 700,
                            flexShrink: 0
                          }}>
                          +
                        </button>
                      </div>
                    )
                  })}

                  <div
                    style={{
                      marginTop: 10,
                      padding: '8px 10px',
                      background: 'rgba(0,0,0,0.4)',
                      borderRadius: 8,
                      fontSize: 10,
                      color: '#888',
                      lineHeight: 1.8
                    }}>
                    {Object.entries(texDebugValues).map(([k, v]) => (
                      <div key={k}>
                        <span style={{ color: '#6c8ce6' }}>{k}:</span> {typeof v === 'number' && !Number.isInteger(v) ? v.toFixed(2) : v}
                      </div>
                    ))}
                    <div style={{ marginTop: 6, color: '#6c8ce6' }}>brass: {JSON.stringify(brassDebug)}</div>
                    <div style={{ color: '#6c8ce6' }}>stainless: {JSON.stringify(stainlessDebug)}</div>
                  </div>

                  {/* Brass controls */}
                  {[
                    { label: 'Brass RepeatX', state: brassDebug, key: 'repeatX', set: setBrassDebug, step: 0.01, min: 0.01, max: 20 },
                    { label: 'Brass RepeatY', state: brassDebug, key: 'repeatY', set: setBrassDebug, step: 0.01, min: 0.01, max: 20 },
                    { label: 'Stainless RepeatX', state: stainlessDebug, key: 'repeatX', set: setStainlessDebug, step: 0.01, min: 0.01, max: 20 },
                    { label: 'Stainless RepeatY', state: stainlessDebug, key: 'repeatY', set: setStainlessDebug, step: 0.01, min: 0.01, max: 20 }
                  ].map(({ label, state, key, set, step, min, max }) => (
                    <div key={label} style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 6 }}>
                      <span style={{ fontSize: 10, color: '#ccc', flex: 1 }}>{label}</span>
                      <span style={{ fontSize: 11, color: '#fff', minWidth: 32, textAlign: 'center' }}>{state[key].toFixed(2)}</span>
                      <button
                        type="button"
                        onClick={() => set((p) => ({ ...p, [key]: Math.max(min, Math.round((p[key] - step) * 100) / 100) }))}
                        style={{
                          background: '#444',
                          border: 'none',
                          borderRadius: 4,
                          color: '#fff',
                          width: 24,
                          height: 24,
                          cursor: 'pointer',
                          fontSize: 16,
                          fontWeight: 700
                        }}>
                        −
                      </button>
                      <button
                        type="button"
                        onClick={() => set((p) => ({ ...p, [key]: Math.min(max, Math.round((p[key] + step) * 100) / 100) }))}
                        style={{
                          background: '#444',
                          border: 'none',
                          borderRadius: 4,
                          color: '#fff',
                          width: 24,
                          height: 24,
                          cursor: 'pointer',
                          fontSize: 16,
                          fontWeight: 700
                        }}>
                        +
                      </button>
                    </div>
                  ))}
                  {/* Rotation controls */}
                  {[
                    { label: 'Brass Rotation', state: brassDebug, set: setBrassDebug },
                    { label: 'Stainless Rotation', state: stainlessDebug, set: setStainlessDebug }
                  ].map(({ label, state, set }) => (
                    <div key={label} style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 6 }}>
                      <span style={{ fontSize: 10, color: '#ccc', flex: 1 }}>
                        {label} ({state.rotation * 90}°)
                      </span>
                      <button
                        type="button"
                        onClick={() => set((p) => ({ ...p, rotation: (p.rotation + 3) % 4 }))}
                        style={{
                          background: '#444',
                          border: 'none',
                          borderRadius: 4,
                          color: '#fff',
                          padding: '0 8px',
                          height: 24,
                          cursor: 'pointer',
                          fontSize: 11
                        }}>
                        −90°
                      </button>
                      <button
                        type="button"
                        onClick={() => set((p) => ({ ...p, rotation: (p.rotation + 1) % 4 }))}
                        style={{
                          background: '#444',
                          border: 'none',
                          borderRadius: 4,
                          color: '#fff',
                          padding: '0 8px',
                          height: 24,
                          cursor: 'pointer',
                          fontSize: 11
                        }}>
                        +90°
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </>
          )
        })()}

        {/* ═══ LIGHT DEBUG PANEL ═══ (open with ?lightdebug in the URL) */}
        {SHOW_LIGHT_DEBUG &&
          (() => {
            const lightRows = [
              { key: 'overheadIntensity', label: 'Overhead Panel', min: 0, max: 10, step: 0.1 },
              { key: 'overheadWidth', label: 'Overhead Width', min: 0.5, max: 12, step: 0.25 },
              { key: 'overheadDepth', label: 'Overhead Depth', min: 0.5, max: 12, step: 0.25 },
              { key: 'overheadHeight', label: 'Overhead Height', min: 1, max: 10, step: 0.25 },
              { key: 'overheadForward', label: 'Overhead Fwd/Back', min: -5, max: 5, step: 0.25 },
              { key: 'bounceIntensity', label: 'Bounce Cards', min: 0, max: 5, step: 0.05 },
              { key: 'bounceAngle', label: 'Bounce Angle', min: 15, max: 90, step: 1 },
              { key: 'bounceDistance', label: 'Bounce Distance', min: 1, max: 10, step: 0.25 },
              { key: 'bounceWidth', label: 'Bounce Width', min: 0.5, max: 8, step: 0.25 },
              { key: 'bounceHeight', label: 'Bounce Height', min: 0.5, max: 10, step: 0.25 },
              { key: 'roomLevel', label: 'Room Brightness', min: 0, max: 1, step: 0.01 },
              { key: 'keyIntensity', label: 'Overhead Key (shading)', min: 0, max: 5, step: 0.05 },
              { key: 'ambientIntensity', label: 'Ambient', min: 0, max: 2, step: 0.05 },
              { key: 'exposure', label: 'Exposure', min: 0.1, max: 3, step: 0.05 },
              { key: 'shadowRadius', label: 'Shadow Softness', min: 0, max: 32, step: 1 },
              { key: 'shadowBias', label: 'Shadow Bias', min: -0.001, max: 0.001, step: 0.00001 },
              { key: 'shadowNormalBias', label: 'Shadow Normal Bias', min: 0, max: 0.01, step: 0.0001 },
              { key: 'keyCount', label: 'Softbox Shadow Lights', min: 1, max: 9, step: 1 },
              { key: 'keySpread', label: 'Softbox Size', min: 0, max: 6, step: 0.25 },
              { key: 'shadowExtent', label: 'Shadow Box Size', min: 0.2, max: 3, step: 0.05 },
              { key: 'toneMapping', label: 'Tone Map (0 none, 1 ACES)', min: 0, max: 1, step: 1 }
            ]
            const copyLight = () => {
              const txt = Object.entries(lightDebug)
                .map(([k, v]) => `${k}: ${v}`)
                .join('\n')
              navigator.clipboard.writeText(txt).catch(() => {})
              setLightCopied(true)
              setTimeout(() => setLightCopied(false), 2000)
            }
            return (
              <>
                <button
                  type="button"
                  onClick={() => setLightDebugVisible((v) => !v)}
                  style={{
                    position: 'fixed',
                    bottom: 54,
                    right: lightDebugVisible ? 360 : 20,
                    zIndex: 100000,
                    background: lightDebugVisible ? '#dea891' : 'rgba(20,20,20,0.7)',
                    color: '#fff',
                    border: 'none',
                    borderRadius: 8,
                    padding: '4px 10px',
                    fontSize: 10,
                    fontFamily: 'monospace',
                    cursor: 'pointer',
                    letterSpacing: '0.08em',
                    transition: 'right 0.2s'
                  }}>
                  LIGHT
                </button>
                {lightDebugVisible && (
                  <div
                    style={{
                      position: 'fixed',
                      bottom: 20,
                      right: 20,
                      zIndex: 99999,
                      background: 'rgba(20,20,20,0.92)',
                      color: '#fff',
                      borderRadius: 12,
                      padding: '12px 14px',
                      fontFamily: 'monospace',
                      fontSize: 12,
                      minWidth: 280,
                      boxShadow: 'none',
                      maxHeight: 'calc(100vh - 100px)',
                      overflowY: 'auto'
                    }}>
                    <div style={{ fontWeight: 700, marginBottom: 8, color: '#dea891', letterSpacing: '0.1em' }}>LIGHT DEBUG</div>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10, cursor: 'pointer', fontSize: 11, color: '#fff' }}>
                      <input type="checkbox" checked={shadowsEnabled} onChange={(e) => setShadowsEnabled(e.target.checked)} />
                      Internal Shadows
                    </label>
                    {lightRows.map(({ key, label, min, max, step }) => (
                      <div key={key} style={{ marginBottom: 8 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 3 }}>
                          <span style={{ fontSize: 10, color: '#ccc', flex: 1 }}>{label}</span>
                          <span style={{ fontSize: 11, color: '#fff', minWidth: 36, textAlign: 'center' }}>{lightDebug[key].toFixed(2)}</span>
                          <button
                            type="button"
                            onClick={() => setLightDebug((p) => ({ ...p, [key]: Math.round(Math.max(min, p[key] - step) * 100) / 100 }))}
                            style={{
                              background: '#444',
                              border: 'none',
                              borderRadius: 4,
                              color: '#fff',
                              width: 24,
                              height: 24,
                              cursor: 'pointer',
                              fontSize: 16,
                              fontWeight: 700
                            }}>
                            −
                          </button>
                          <button
                            type="button"
                            onClick={() => setLightDebug((p) => ({ ...p, [key]: Math.round(Math.min(max, p[key] + step) * 100) / 100 }))}
                            style={{
                              background: '#444',
                              border: 'none',
                              borderRadius: 4,
                              color: '#fff',
                              width: 24,
                              height: 24,
                              cursor: 'pointer',
                              fontSize: 16,
                              fontWeight: 700
                            }}>
                            +
                          </button>
                        </div>
                        <input
                          type="range"
                          min={min}
                          max={max}
                          step={step}
                          value={lightDebug[key]}
                          onChange={(e) => setLightDebug((p) => ({ ...p, [key]: parseFloat(e.target.value) }))}
                          style={{ width: '100%', accentColor: '#dea891', cursor: 'pointer' }}
                        />
                      </div>
                    ))}
                    <button
                      type="button"
                      onClick={copyLight}
                      style={{
                        marginTop: 10,
                        background: lightCopied ? '#6c8ce6' : '#dea891',
                        color: '#1a1a1a',
                        border: 'none',
                        borderRadius: 6,
                        padding: '4px 12px',
                        fontSize: 11,
                        fontFamily: 'monospace',
                        cursor: 'pointer',
                        fontWeight: 700,
                        transition: 'background 0.2s'
                      }}>
                      {lightCopied ? '✓ Copied!' : 'Copy Values'}
                    </button>
                  </div>
                )}
              </>
            )
          })()}

        {/* ═══ MATERIAL DEBUG PANEL ═══ (open with ?lightdebug in the URL) */}
        {SHOW_LIGHT_DEBUG &&
          (() => {
            const matRows = [
              { key: 'cerakoteRoughness', label: 'Cerakote Roughness', min: 0, max: 1, step: 0.05 },
              { key: 'cerakoteMetalness', label: 'Cerakote Metalness', min: 0, max: 1, step: 0.05 },
              { key: 'cerakoteEnvMap', label: 'Cerakote EnvMap', min: 0, max: 3, step: 0.05 },
              { key: 'cerakoteClearcoat', label: 'Clearcoat', min: 0, max: 1, step: 0.05 },
              { key: 'cerakoteClearcoatRoughness', label: 'Clearcoat Roughness', min: 0, max: 1, step: 0.05 },
              { key: 'cerakoteSheen', label: 'Cerakote Sheen', min: 0, max: 1, step: 0.05 },
              { key: 'cerakoteSheenRoughness', label: 'Sheen Roughness', min: 0, max: 1, step: 0.05 },
              { key: 'metalRoughness', label: 'Metal Roughness', min: 0, max: 1, step: 0.01 },
              { key: 'metalMetalness', label: 'Metal Metalness', min: 0, max: 1, step: 0.01 },
              { key: 'metalEnvMap', label: 'Metal EnvMap', min: 0, max: 3, step: 0.05 },
              { key: 'metalClearcoat', label: 'Metal Clearcoat', min: 0, max: 1, step: 0.05 },
              { key: 'metalClearcoatRoughness', label: 'Metal Clearcoat Rough', min: 0, max: 1, step: 0.05 }
            ]
            const copyMat = () => {
              const v = matDebug
              const txt = Object.entries(v)
                .map(([k, val]) => `${k}: ${val}`)
                .join('\n')
              navigator.clipboard.writeText(txt).catch(() => {})
              setMatCopied(true)
              setTimeout(() => setMatCopied(false), 2000)
            }
            return (
              <>
                <button
                  type="button"
                  onClick={() => setMatDebugVisible((v) => !v)}
                  style={{
                    position: 'fixed',
                    bottom: 20,
                    right: matDebugVisible ? 360 : 20,
                    zIndex: 100000,
                    background: matDebugVisible ? '#c3de91' : 'rgba(20,20,20,0.7)',
                    color: '#fff',
                    border: 'none',
                    borderRadius: 8,
                    padding: '4px 10px',
                    fontSize: 10,
                    fontFamily: 'monospace',
                    cursor: 'pointer',
                    letterSpacing: '0.08em',
                    transition: 'right 0.2s'
                  }}>
                  MAT
                </button>
                {matDebugVisible && (
                  <div
                    style={{
                      position: 'fixed',
                      bottom: 20,
                      right: 20,
                      zIndex: 99999,
                      background: 'rgba(20,20,20,0.92)',
                      color: '#fff',
                      borderRadius: 12,
                      padding: '12px 14px',
                      fontFamily: 'monospace',
                      fontSize: 12,
                      minWidth: 300,
                      boxShadow: 'none',
                      maxHeight: 'calc(100vh - 100px)',
                      overflowY: 'auto'
                    }}>
                    <div style={{ fontWeight: 700, marginBottom: 8, color: '#c3de91', letterSpacing: '0.1em' }}>MATERIAL DEBUG</div>
                    {matRows.map(({ key, label, min, max, step }) => {
                      const val = matDebug[key]
                      return (
                        <div key={key} style={{ marginBottom: 8 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 3 }}>
                            <span style={{ fontSize: 10, color: '#ccc', flex: 1 }}>{label}</span>
                            <span style={{ fontSize: 11, color: '#fff', minWidth: 36, textAlign: 'center' }}>{val.toFixed(2)}</span>
                            <button
                              type="button"
                              onClick={() => setMatDebug((p) => ({ ...p, [key]: Math.round(Math.max(min, p[key] - step) * 100) / 100 }))}
                              style={{
                                background: '#444',
                                border: 'none',
                                borderRadius: 4,
                                color: '#fff',
                                width: 24,
                                height: 24,
                                cursor: 'pointer',
                                fontSize: 16,
                                fontWeight: 700
                              }}>
                              −
                            </button>
                            <button
                              type="button"
                              onClick={() => setMatDebug((p) => ({ ...p, [key]: Math.round(Math.min(max, p[key] + step) * 100) / 100 }))}
                              style={{
                                background: '#444',
                                border: 'none',
                                borderRadius: 4,
                                color: '#fff',
                                width: 24,
                                height: 24,
                                cursor: 'pointer',
                                fontSize: 16,
                                fontWeight: 700
                              }}>
                              +
                            </button>
                          </div>
                          <input
                            type="range"
                            min={min}
                            max={max}
                            step={step}
                            value={val}
                            onChange={(e) => setMatDebug((p) => ({ ...p, [key]: parseFloat(e.target.value) }))}
                            style={{ width: '100%', accentColor: '#c3de91', cursor: 'pointer' }}
                          />
                        </div>
                      )
                    })}
                    <div style={{ borderTop: '1px solid #333', paddingTop: 8, marginTop: 8 }}>
                      <div style={{ fontWeight: 700, fontSize: 10, color: '#c3de91', letterSpacing: '0.1em', marginBottom: 6 }}>BRASS BODY ROUGHNESS MAP</div>
                      <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 10, color: '#ccc', cursor: 'pointer', marginBottom: 8 }}>
                        <input
                          type="checkbox"
                          checked={(matDebug.brassBodyNormalScale ?? 0) === 0}
                          onChange={(e) => setMatDebug((p) => ({ ...p, brassBodyNormalScale: e.target.checked ? 0 : 0.03 }))}
                        />
                        Off
                      </label>
                      {[
                        { key: 'brassBodyNormalScale', label: 'Intensity', min: 0, max: 0.5, step: 0.005 },
                        { key: 'brassBodyNormalTiling', label: 'Tiling', min: 0.1, max: 8, step: 0.1 }
                      ].map(({ key, label, min, max, step }) => (
                        <div key={key} style={{ marginBottom: 6 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 2 }}>
                            <span style={{ fontSize: 10, color: '#ccc', flex: 1 }}>{label}</span>
                            <span style={{ fontSize: 11, color: '#fff', minWidth: 36, textAlign: 'center' }}>{(matDebug[key] ?? 0).toFixed(2)}</span>
                          </div>
                          <input
                            type="range"
                            min={min}
                            max={max}
                            step={step}
                            value={matDebug[key] ?? 0}
                            onChange={(e) => setMatDebug((p) => ({ ...p, [key]: parseFloat(e.target.value) }))}
                            style={{ width: '100%', accentColor: '#c3de91', cursor: 'pointer' }}
                          />
                        </div>
                      ))}
                    </div>
                    <button
                      type="button"
                      onClick={copyMat}
                      style={{
                        marginTop: 10,
                        background: matCopied ? '#6c8ce6' : '#c3de91',
                        color: '#1a1a1a',
                        border: 'none',
                        borderRadius: 6,
                        padding: '4px 12px',
                        fontSize: 11,
                        fontFamily: 'monospace',
                        cursor: 'pointer',
                        fontWeight: 700,
                        transition: 'background 0.2s'
                      }}>
                      {matCopied ? '✓ Copied!' : 'Copy Values'}
                    </button>
                  </div>
                )}
              </>
            )
          })()}

        {SALE_ON && !isDesktop && <SaleHeaderTag label={SALE.label} off={SALE.offUSD} />}

        <div
          className="mobile-builds-badge"
          style={{
            display: isDesktop ? 'none' : 'flex'
          }}>
          {/* Save Build Button - Bookmark Icon Only */}
          <div
            className="mobile-builds-bookmark"
            role="button"
            tabIndex={0}
            onClick={(e) => {
              handleCanvasIconSave()
            }}
            aria-label="Save build"
            title="Save build"
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              padding: '8px',
              borderRadius: '20px',
              transition: 'background 180ms ease',
              marginLeft: '6px',
              marginRight: '2px',
              background: canvasSaveToast === 'saved' ? '#d88978' : 'transparent'
            }}>
            {/* Bookmark Icon */}
            <img
              src="https://pub-e2a1a41ee7e84eefbab9b339c21acfb0.r2.dev/Boookmark.svg"
              width="11"
              height="11"
              alt="Save"
              style={{
                display: 'block',
                width: '11px',
                height: '11px',
                filter: canvasSaveToast === 'saved' ? 'brightness(0) invert(1)' : 'none'
              }}
            />
          </div>

          {/* My Builds Button */}
          <div
            className="mobile-builds-save"
            role="button"
            tabIndex={0}
            onClick={() => {
              if (typeof openFooterWithTab === 'function') {
                openFooterWithTab('savedDesigns')
              }
            }}
            aria-label="My Builds"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              cursor: 'pointer',
              padding: '5px 10px',
              transition: 'background 180ms ease, color 180ms ease',
              background: 'transparent',
              color: '#333',
              position: 'relative'
            }}>
            {/* Text */}
            <span style={{ fontSize: '13px', fontWeight: 700, whiteSpace: 'nowrap' }}>My Builds</span>
            {/* Badge Counter */}
            <span
              style={{
                background: '#e98969',
                color: '#fff',
                fontSize: '10px',
                fontWeight: 600,
                width: '18px',
                height: '18px',
                borderRadius: '50%',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                lineHeight: '1',
                flexShrink: 0
              }}>
              {buildCount > 99 ? '99+' : buildCount}
            </span>
          </div>
        </div>

        <div className="dots-container">
          {/* Save Build / My Builds Badge — mobile - in same container as dots */}

          <div className="control-dots">
            <div className="dots" role="button" tabIndex={0} aria-label="Front view" onClick={(e) => setActiveDot(e.currentTarget, resetCamera)} onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && (e.preventDefault(), setActiveDot(e.currentTarget, resetCamera))}></div>
            <div className="dots-side" role="button" tabIndex={0} aria-label="Side view" onClick={(e) => setActiveDot(e.currentTarget, resetCameraSide)} onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && (e.preventDefault(), setActiveDot(e.currentTarget, resetCameraSide))}></div>
            <div className="dots-back" role="button" tabIndex={0} aria-label="Back view" onClick={(e) => setActiveDot(e.currentTarget, rotateCameraBehind)} onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && (e.preventDefault(), setActiveDot(e.currentTarget, rotateCameraBehind))}></div>
            <div
              style={{
                position: 'absolute',
                // Per-device offsets — tweak bottom/left independently per phoneType:
                bottom: (() => {
                  // When footer is fully open (livePercent ≥ 80), shift down extra 100px
                  const footerOpen = livePercent >= 80
                  const extra = footerOpen ? 100 : 0
                  if (phoneType === 'se') return `${-202 - extra}px`
                  if (phoneType === 'promax') return `${-214 - extra}px`
                  if (phoneType === 'ipad') return `${-208 - extra}px`
                  return `${-202 - extra}px` /* regular + fallback */
                })(),
                left: '-43px',
                color: 'white',
                fontSize: phoneType === 'ipad' ? 13 : 11,
                fontWeight: 700,
                fontFamily: 'Jost, sans-serif',
                pointerEvents: 'none',
                whiteSpace: 'nowrap',
                lineHeight: 1,
                opacity: isViewingBack ? 1 : 0,
                transition: 'opacity 0.4s ease'
              }}>
              BACK SIDE
            </div>
          </div>
        </div>

        <div className="bottomhalfcontainer">
          <div className="rectangles-container" style={{ transform: 'translateY(calc(-30vh - 85px))' }}>
            {/* Share — mobile only */}
            <div className="share" style={{ display: isDesktop ? 'none' : 'block' }}>
              <img
                src="https://cdn.shopify.com/s/files/1/0564/1820/5796/files/241101_Share.png?v=1730477928"
                width={isIpad ? 60 : 30}
                height={isIpad ? 60 : 30}
                alt="Share"
                role="button"
                aria-label="Share this product"
                tabIndex={0}
                style={{ cursor: 'pointer' }}
                onClick={handleShareClick}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault()
                    e.stopPropagation()
                    handleShareClick(e)
                  }
                }}
              />
            </div>
            {/* Copy Link Button — mobile only */}
            <div
              className="copy-link"
              style={{
                display: isDesktop ? 'none' : 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                position: 'relative',
                width: isIpad ? 60 : 30
              }}
              role="button"
              tabIndex={0}
              onClick={handleCopyLink}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault()
                  handleCopyLink(e)
                }
              }}
              aria-label="Copy link to clipboard">
              <img
                src="https://cdn.shopify.com/s/files/1/0564/1820/5796/files/Link-Icon-Circle.svg?v=1771895554"
                width={isIpad ? 60 : 30}
                height={isIpad ? 60 : 30}
                alt="Copy Link"
                style={{ cursor: 'pointer', display: 'block' }}
              />
              <span
                style={{
                  position: 'absolute',
                  top: '50%',
                  left: `${(isIpad ? 60 : 30) + 8}px`,
                  transform: 'translateY(-50%)',
                  background: '#000',
                  color: '#fff',
                  fontSize: '13px',
                  fontFamily: 'Jost, system-ui, sans-serif',
                  fontWeight: 600,
                  padding: '6px 10px',
                  borderRadius: '6px',
                  whiteSpace: 'nowrap',
                  pointerEvents: 'none',
                  opacity: copyLinkCopied ? 1 : 0,
                  transition: 'opacity 160ms cubic-bezier(0.2,0.9,0.2,1)',
                  boxShadow: 'none',
                  zIndex: 1000
                }}>
                ✓ Link copied
              </span>
            </div>
          </div>
        </div>
      </div>{' '}
      {/* Blue overlay — covers canvas when footer is fully open on mobile */}
      {phoneType !== 'desktop' && livePercent <= 20 && <div className="share-canvas-overlay" />}
      <div style={{ width: '100vw', height: '100vh', backgroundColor: '#ddeeff' }}>
        <motion.div
          className="mobile-footer" // <-- add this class
          onTouchStart={(e) => {
            if (e.touches.length > 1) e.stopPropagation()
          }}
          style={{
            position: 'fixed',
            top: 0,
            bottom: 0,
            left: 0,
            right: 0,
            // Fill the screen, but push it down by translateY(...)
            transform: translateY,
            background: '#ffffff',
            borderTopLeftRadius: 24,
            borderTopRightRadius: 24,
            boxShadow: 'none',
            padding: '10px 10px 0 10px', // Top, right, bottom (0), left
            zIndex: 100
          }}
          drag={showCustomColorPicker || showCustomScreenColorPicker ? false : 'y'}
          dragElastic={0.15}
          onDragStart={() => {
            isFooterDraggingRef.current = true
            setIsFooterDragging(true)
          }}
          onDrag={(evt, info) => {
            // info.delta.y => px movement
            // If user drags up => delta.y is negative => we want to DECREASE sheetPercent
            const deltaPx = info.delta.y
            const deltaPct = (deltaPx / screenHeight) * 100 // convert px => percent

            const prevVal = sheetPercent.get() // e.g. 35
            const nextVal = prevVal + deltaPct // we add because deltaPx is positive downward

            // clamp between 0..100 if you want to ensure it never leaves the screen
            // or 0..90 if your max is 90
            const minP = snapPercents[0] // e.g. 0
            const maxP = snapPercents[snapPercents.length - 1] // e.g. 90
            const clamped = Math.max(minP, Math.min(maxP, nextVal))

            sheetPercent.set(clamped)
          }}
          onDragEnd={(evt, info) => {
            isFooterDraggingRef.current = false
            setIsFooterDragging(false)
            handleDragEnd(info)
          }}>
          {/* Invisible grab area extending above the handle */}
          <div
            style={{
              position: 'absolute',
              top: -30,
              left: 0,
              right: 0,
              height: 40,
              cursor: 'grab',
              touchAction: 'none',
              zIndex: 1
            }}
            aria-hidden="true"
          />

          {/* Show min/mid/max content based on livePercent */}
          <div className="sheet-handle" aria-hidden="true" />

          {livePercent < 20 ? (
            <div className="footer-summary">
              {/* Add Ons */}
              {/* handle bar */}

              {/* middle-snap X - top-right, keyboard accessible */}
              <button
                type="button"
                className="footer-collapse-btn"
                onMouseDown={(ev) => ev.stopPropagation()} // prevents interfering with drag
                onClick={handleFooterToMiddleMobile}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault()
                    handleFooterToMiddleMobile(e)
                  }
                }}
                aria-label="Return footer to middle position">
                <svg
                  viewBox="0 0 24 24"
                  width="14"
                  height="14"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>

              <MobileFooterTabs
                initialTab={mobileFooterTab}
                initialColor={modalInitialColor}
                onInitialColorConsumed={() => setModalInitialColor(null)}
                shareScreenshotUrl={shareScreenshotUrl}
                externalShareUrl={appShareUrl}
                onRegenerateScreenshot={regenerateShareScreenshot}
                onShareTabActive={() => {
                  mobileFooterTabRef.current = 'share'
                  setMobileFooterTab('share')
                  setShareTabActive(true)
                }}
                config={{
                  ...config,
                  colorLabel: colorOptions[config.color] || config.color,
                  screenTypeLabel: screenTypeOptions[config.screenType]?.label || config.screenType,
                  screenColorLabel: screenColorOptions[config.screenColor] || config.screenColor,
                  fabricColorLabel: fabricColorOptions[config.fabricColor] || config.fabricColor,
                  bottomFabricColorLabel: bottomfabricColorOptions[config.bottomFabricColor] || config.bottomFabricColor,
                  bottomScreenTypeLabel: bottomScreenTypeOptions[config.bottomScreenType]?.label || config.bottomScreenType,
                  microphoneTypeLabel: microphoneTypeOptions[config.microphoneType]?.label || config.microphoneType,
                  popFilterMaterial: config.popFilterMaterial,
                  popFilterColorLabel: config.popFilterEnabled ? popFilterLabel(config.popFilterMaterial, config.popFilterColor) : null
                }}
                onGenerateShareLink={() => {
                  setShareTabActive(true)
                  setAppShareUrl('')
                  return shareProduct()
                    .then((link) => {
                      setAppShareUrl(link)
                      return link
                    })
                    .catch(() => {})
                }}
                onApplyColors={(bodyHex, screenHex) => {
                  const newColor = bodyHex || config.color
                  const isBrass = newColor === 'Brass' || newColor === '#d5b173'
                  const isBlack = newColor === '#1A1A1A' || newColor === '#232221' || newColor === '#1c1d1d'
                  const newPopFilterColor =
                    config.popFilterEnabled && config.popFilterMaterial === 'metal' ? (isBrass || isBlack ? '#1c1d1d' : newColor) : config.popFilterColor
                  setConfig({
                    ...config,
                    ...(bodyHex ? { color: bodyHex } : {}),
                    ...(screenHex ? { screenColor: screenHex } : {}),
                    popFilterColor: newPopFilterColor
                  })
                }}
                onFooterToMiddle={handleFooterToMiddleMobile}
                onDismissOverlay={() => setShareTabActive(false)}
                currentDesignId={currentDesignId}
                hasUnsavedChanges={hasUnsavedChanges}
                currentDesignName={currentDesignName}
                onLoadDesign={handleLoadDesign}
                onSaveDesign={handleSaveDesign}
                onUpdateDesign={handleUpdateDesign}
                onMigratedId={(newId) => {
                  setCurrentDesignId(newId)
                  setHasUnsavedChanges(false)
                }}
              />
            </div>
          ) : livePercent < 78 ? (
            <div style={{}}>
              <div className="dice-icon">
                {/* Dice content wrapper - opacity effect removed */}
                <div style={{ opacity: 1, transition: 'opacity 0.2s' }}>
                  <div className="dice-spinner">
                    <img
                      src="https://cdn.shopify.com/s/files/1/0564/1820/5796/files/260212_Randomize.svg?v=1771036629"
                      alt="Dice Icon"
                      onClick={handleDiceClick}
                    />
                  </div>
                  {/* Undo Button */}
                  <div
                    onClick={undo}
                    className="control-button"
                    style={{
                      position: 'absolute',
                      top: isIpad ? -60 : -25,
                      right: isIpad ? 100 : 65,
                      zIndex: 10,
                      opacity: canUndo ? 1 : 0.3,
                      pointerEvents: canUndo ? 'auto' : 'none'
                    }}
                    title="Undo">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round">
                      <path d="M3 7v6h6" />
                      <path d="M21 17a9 9 0 0 0-9-9 9 9 0 0 0-6 2.3L3 13" />
                    </svg>
                  </div>

                  {/* Redo Button */}
                  <div
                    onClick={redo}
                    className="control-button"
                    style={{
                      position: 'absolute',
                      top: isIpad ? -60 : -25,
                      right: isIpad ? 0 : 0,
                      zIndex: 10,
                      opacity: canRedo ? 1 : 0.3,
                      pointerEvents: canRedo ? 'auto' : 'none'
                    }}
                    title="Redo">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round">
                      <path d="M21 7v6h-6" />
                      <path d="M3 17a9 9 0 0 1 9-9 9 9 0 0 1 6 2.3L21 13" />
                    </svg>
                  </div>
                </div>

                {/* Add to Cart Wrapper - inside dice-icon but outside opacity wrapper */}
                <div
                  className="add-to-cart-wrapper"
                  style={{
                    position: 'absolute',
                    bottom: -270,
                    left: 0,
                    right: -15,
                    width: 'calc(100vw - 30px)',
                    zIndex: 10,
                    display: 'flex',
                    gap: '8px',
                    background: 'transparent',
                    padding: '0'
                  }}>
                  <div className="cart-controls" style={{ flex: '0 0 auto' }}>
                    <div className="qty-picker">
                      <button type="button" onClick={decreaseQty}>
                        –
                      </button>
                      <span className="qty">{quantity}</span>
                      <button type="button" onClick={increaseQty}>
                        +
                      </button>
                    </div>
                  </div>
                  <div className={SALE_ON ? 'add-to-cart sale-on' : 'add-to-cart'} onClick={openConfirm} style={{ flex: '1' }}>
                    <div className="cart-content">
                      <h2>Add to Cart</h2>
                      <h2 className="price">
                        {renderPrice()}
                      </h2>
                    </div>
                  </div>
                </div>
              </div>

              <div className="gradient-wrapper-cream">
                {/* Left gradient - only show when scrolled right, non-iPad */}
                {scrollLeft > 10 && !isIpad && (
                  <div
                    style={{
                      position: 'absolute',
                      left: 0,
                      top: 0,
                      bottom: 0,
                      width: '40px',
                      background: 'linear-gradient(to right, #ffffff 0%, rgba(255, 255, 255, 0) 100%)',
                      pointerEvents: 'none',
                      zIndex: 10
                    }}
                  />
                )}
                <div className="scrollable-footer" ref={scrollRef} onScroll={(e) => setScrollLeft(e.target.scrollLeft)} style={{ touchAction: 'pan-x' }}>
                  {Object.keys(filteredSelectionContent).map((optionLabel, index, array) => {
                    // Insert Add-Ons divider before Pop Filter
                    const isPopFilter = optionLabel === '7. Pop Filter'

                    return (
                      <React.Fragment key={optionLabel}>
                        {isPopFilter && (
                          <div
                            className="footer-addons-divider"
                            role="button"
                            tabIndex={0}
                            onClick={() => handleOptionClick('7. Pop Filter')}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault()
                                handleOptionClick('7. Pop Filter')
                              }
                            }}
                            style={{
                              background: 'transparent',
                              cursor: 'pointer',
                              padding: '0 12px',
                              fontFamily: 'roc-grotesk-wide, sans-serif',
                              fontWeight: '700',
                              fontSize: '11px',
                              textTransform: 'uppercase',
                              letterSpacing: '0.1em',
                              color: '#6b7280',
                              display: 'flex',
                              alignItems: 'center',
                              whiteSpace: 'nowrap',
                              flexShrink: 0
                            }}>
                            <span
                              style={{
                                borderLeft: '1px solid #e5e7eb',
                                paddingLeft: '12px',
                                height: '20px',
                                display: 'flex',
                                alignItems: 'center'
                              }}>
                              Add-Ons
                            </span>
                          </div>
                        )}
                        <div className={`footer-option ${activeSelection === optionLabel ? 'active' : ''}`} onClick={() => handleOptionClick(optionLabel)}>
                          <h1>{displayNames[optionLabel] || optionLabel}</h1>
                        </div>
                      </React.Fragment>
                    )
                  })}
                </div>
                {/* Right gradient - non-iPad */}
                {scrollRef.current && scrollLeft < scrollRef.current.scrollWidth - scrollRef.current.clientWidth - 10 && (
                  <div
                    style={{
                      position: 'absolute',
                      right: 0,
                      top: isIpad ? '24px' : 0,
                      bottom: isIpad ? 'auto' : 0,
                      height: isIpad ? '86px' : undefined,
                      width: '60px',
                      background: isIpad
                        ? 'linear-gradient(to left, #faf8f3 0%, rgba(250, 248, 243, 0) 100%)'
                        : 'linear-gradient(to left, #ffffff 0%, rgba(255, 255, 255, 0) 100%)',
                      pointerEvents: 'none',
                      zIndex: isIpad ? 100 : 10
                    }}
                  />
                )}
                {/* iPad left gradient - after scrollable-footer so it paints on top */}
                {scrollLeft > 10 && isIpad && (
                  <div
                    style={{
                      position: 'absolute',
                      left: 0,
                      top: '24px',
                      height: '86px',
                      width: '40px',
                      background: 'linear-gradient(to right, #faf8f3 0%, rgba(250, 248, 243, 0) 100%)',
                      pointerEvents: 'none',
                      zIndex: 100
                    }}
                  />
                )}
              </div>

              <div
                className="selection"
                ref={selectionRef}
                onScroll={(e) => {
                  const element = e.target
                  const canScrollMore = element.scrollTop < element.scrollHeight - element.clientHeight - 20
                  setSelectionCanScrollMore(canScrollMore)
                }}>
                {filteredSelectionContent[activeSelection] || <div>screw an option</div>}
              </div>

              {/* Bottom gradient - only show when there's more content to scroll */}
              {selectionCanScrollMore && (
                <div
                  style={{
                    position: 'absolute',
                    left: 0,
                    right: 0,
                    bottom: 0,
                    height: '60px',
                    background: 'linear-gradient(to top, #ffffff 0%, rgba(255, 255, 255, 0) 100%)',
                    pointerEvents: 'none',
                    zIndex: 10
                  }}
                />
              )}
            </div>
          ) : (
            <div style={{}}>
              <div className="dice-icon">
                <div style={{ opacity: 1, transition: 'opacity 0.2s' }}>
                  <div className="dice-spinner">
                    <img
                      src="https://cdn.shopify.com/s/files/1/0564/1820/5796/files/260212_Randomize.svg?v=1771036629"
                      alt="Dice Icon"
                      onClick={handleDiceClick}
                    />
                  </div>
                  {/* Undo Button */}
                  <div
                    onClick={undo}
                    className="control-button"
                    style={{
                      position: 'absolute',
                      top: isIpad ? -60 : -25,
                      right: isIpad ? 100 : 65,
                      zIndex: 10,
                      opacity: canUndo ? 1 : 0.3,
                      pointerEvents: canUndo ? 'auto' : 'none'
                    }}
                    title="Undo">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round">
                      <path d="M3 7v6h6" />
                      <path d="M21 17a9 9 0 0 0-9-9 9 9 0 0 0-6 2.3L3 13" />
                    </svg>
                  </div>
                  {/* Redo Button */}
                  <div
                    onClick={redo}
                    className="control-button"
                    style={{
                      position: 'absolute',
                      top: isIpad ? -60 : -25,
                      right: isIpad ? 0 : 0,
                      zIndex: 10,
                      opacity: canRedo ? 1 : 0.3,
                      pointerEvents: canRedo ? 'auto' : 'none'
                    }}
                    title="Redo">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round">
                      <path d="M21 7v6h-6" />
                      <path d="M3 17a9 9 0 0 1 9-9 9 9 0 0 1 6 2.3L21 13" />
                    </svg>
                  </div>
                </div>
              </div>
              <div
                className="add-to-cart-wrapper"
                style={{
                  position: 'absolute',
                  top: phoneType === 'promax' ? '42px' : phoneType === 'regular' ? '34px' : '42px',
                  left: 14,
                  right: -15,
                  width: 'calc(100vw - 30px)',
                  zIndex: 10,
                  display: 'flex',
                  gap: '8px',
                  background: 'transparent',
                  padding: '0'
                }}>
                <div className="cart-controls" style={{ flex: '0 0 auto' }}>
                  <div className="qty-picker">
                    <button type="button" onClick={decreaseQty}>
                      –
                    </button>
                    <span className="qty">{quantity}</span>
                    <button type="button" onClick={increaseQty}>
                      +
                    </button>
                  </div>
                </div>
                <div className={SALE_ON ? 'add-to-cart sale-on' : 'add-to-cart'} onClick={openConfirm} style={{ flex: '1' }}>
                  <div className="cart-content">
                    <h2>Add to Cart</h2>
                    <h2 className="price">
                      {renderPrice()}
                    </h2>
                  </div>
                </div>
              </div>
            </div>
          )}
        </motion.div>
      </div>
      {showConfirmModal &&
        createPortal(
          <div
            className="confirm-overlay"
            onClick={() => {
              setShowConfirmModal(false)
              setPendingCartDesign(null)
              setPendingCartScreenshot(null)
            }}
            style={{ zIndex: 2147483647 }}>
            <div className="confirm-dialog" onClick={(e) => e.stopPropagation()}>
              {/* Left column — screenshot image matching share modal */}
              <div className="confirm-dialog-image">
                {pendingCartScreenshot || shareScreenshotUrl ? (
                  <img src={pendingCartScreenshot || shareScreenshotUrl} alt="Your build" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 14 }}>
                    <style>{`@keyframes ohma-spin{to{transform:rotate(360deg)}} .ohma-spinner{animation:ohma-spin 0.9s linear infinite}`}</style>
                    <svg
                      className="ohma-spinner"
                      width="40"
                      height="40"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="rgba(255,255,255,0.9)"
                      strokeWidth="2.5"
                      strokeLinecap="round">
                      <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" />
                    </svg>
                    <span style={{ color: 'rgba(255,255,255,0.8)', fontSize: 13, fontFamily: 'Jost, sans-serif', letterSpacing: '0.05em' }}>
                      Generating preview...
                    </span>
                  </div>
                )}
              </div>
              {/* Right column — content */}
              <div className="confirm-dialog-content" style={{ marginTop: bottomScreenType === 'Custom Logo' ? 62 : 90 }}>
                <h2 style={{ fontFamily: "'roc-grotesk-wide', sans-serif", fontWeight: 500, marginTop: 0 }}>Final Step</h2>

                <ul style={{ paddingLeft: 18, margin: '0 0 20px', lineHeight: 1.8, fontFamily: 'Jost, sans-serif', color: 'inherit' }}>
                  <li>Built in East Los Angeles</li>
                  <li>Production takes about 4–6 weeks</li>
                  <li>This is a digital preview. The real mic has more depth and texture</li>
                  <li>Once production begins, your build is set</li>
                </ul>

                {bottomScreenType === 'Custom Logo' && (
                  <div style={{ marginBottom: 20, color: 'inherit' }}>
                    <p style={{ margin: '0 0 4px', fontFamily: 'Jost, sans-serif', color: 'inherit' }}>If your build includes a custom logo</p>
                    <ul style={{ paddingLeft: 18, margin: 0, lineHeight: 1.8, fontFamily: 'Jost, sans-serif', color: 'inherit' }}>
                      <li>
                        Send artwork to{' '}
                        <a href="mailto:sammy@ohmaworld.com" style={{ color: 'inherit', textDecoration: 'none' }}>
                          <span style={{ textDecoration: 'underline' }}>sammy@ohmaworld.com</span>
                        </a>{' '}
                        to begin production
                      </li>
                    </ul>
                  </div>
                )}

                {SALE_ON && (
                  <div className="sale-final">
                    <div className="sale-row">
                      <span>{quantity > 1 ? `Your builds (${quantity})` : 'Your build'}</span>
                      <span>{priceText(totalPrice * quantity)}</span>
                    </div>
                    <div className="sale-row sale-row-off">
                      <span>{SALE.label}</span>
                      <span>−{priceText(saleOff * quantity)}</span>
                    </div>
                    <div className="sale-row sale-row-total">
                      <strong>You pay</strong>
                      <strong>{priceText(salePrice * quantity)}</strong>
                    </div>
                    <div className="sale-note">Already in the price.</div>
                  </div>
                )}

                {currency.code === 'USD' && totalPriceUSD > 0 && (
                  <div
                    style={{
                      border: '1.5px solid #4a4af4',
                      background: '#f4f4ff',
                      borderRadius: 10,
                      padding: '12px 14px',
                      margin: '0 0 18px',
                      fontFamily: 'Jost, sans-serif',
                      lineHeight: 1.45,
                      color: '#111'
                    }}>
                    <strong style={{ fontWeight: 600 }}>
                      Pay over time with <span style={{ color: '#4a4af4' }}>affirm</span>
                    </strong>
                    <br />
                    About <strong style={{ fontWeight: 600 }}>${Math.ceil((totalPriceUSD - (SALE_ON ? SALE.offUSD : 0)) / 12)}/mo</strong> for 12 months at 0% APR, if you qualify.
                    {isDesktop && (
                      <>
                        <br />
                        <span style={{ color: '#555', fontSize: 13 }}>Choose Affirm at checkout.</span>
                      </>
                    )}
                  </div>
                )}

                <p style={{ margin: '0 0 0', fontFamily: 'Jost, sans-serif', color: 'inherit' }}>
                  Questions anytime? Email{' '}
                  <a href="mailto:info@ohmaworld.com" style={{ color: 'inherit', textDecoration: 'none' }}>
                    <span style={{ textDecoration: 'underline' }}>info@ohmaworld.com</span>
                  </a>
                </p>

                <div className="confirm-buttons" style={{ marginTop: 24 }}>
                  <button
                    type="button"
                    className="secondary"
                    onClick={() => {
                      setShowConfirmModal(false)
                      setPendingCartDesign(null)
                      setPendingCartScreenshot(null)
                    }}
                    style={{ fontFamily: 'Jost, sans-serif' }}>
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="primary"
                    onClick={() => {
                      track('checkout_clicked', analyticsBuildProps())
                      const pd = pendingCartDesign
                      setShowConfirmModal(false)
                      setPendingCartDesign(null)
                      setPendingCartScreenshot(null)
                      if (pd) {
                        if (builderBus.doAddToCart) builderBus.doAddToCart(pd)
                        else addToCart()
                        setModalOpen(false)
                        builderBus.closeFooter?.()
                      } else {
                        setModalOpen(false)
                        builderBus.closeFooter?.()
                        addToCart()
                      }
                    }}>
                    Continue to Checkout
                  </button>
                </div>
              </div>
            </div>
          </div>,
          document.body
        )}
    </React.Fragment>
  )
}

checkBaked().then(() => useGLTF.preload(modelUrl()))
